// src/index.ts
import { initContract as initContract37 } from "@ts-rest/core";
import { z as z39 } from "zod";

// src/members.ts
import { initContract } from "@ts-rest/core";
import { z as z2 } from "zod";

// src/common.ts
import { z } from "zod";
import {
  digitsOnly,
  isBrazilianState,
  isValidCnpj,
  isValidCpf,
  normalizePhone,
  THEMES
} from "@evogard/domain";
var apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional()
});
var defaultErrors = {
  400: apiErrorSchema,
  401: apiErrorSchema,
  403: apiErrorSchema,
  404: apiErrorSchema
};
var paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50)
});
function paginated(item) {
  return z.object({
    items: z.array(item),
    total: z.number().int(),
    page: z.number().int(),
    perPage: z.number().int()
  });
}
var moneySchema = z.string().regex(/^-?\d+(\.\d{1,2})?$/);
var instantSchema = z.string().datetime({ offset: true });
var businessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use o formato AAAA-MM-DD.").refine((v) => {
  const [year, month, day] = v.split("-").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}, "Essa data n\xE3o existe no calend\xE1rio.");
var cpfSchema = z.string().trim().max(20).transform((v) => digitsOnly(v) ?? "").refine(isValidCpf, "CPF inv\xE1lido: confira os d\xEDgitos.");
var documentSchema = z.string().trim().max(20).transform((v) => digitsOnly(v) ?? "").refine(
  (d) => d.length === 14 ? isValidCnpj(d) : isValidCpf(d),
  "CPF ou CNPJ inv\xE1lido: confira os d\xEDgitos."
);
var phoneSchema = z.string().trim().max(30).transform((v, ctx) => {
  const phone = normalizePhone(v);
  if (phone === null) {
    ctx.addIssue({ code: "custom", message: "Telefone inv\xE1lido: DDD e n\xFAmero." });
    return z.NEVER;
  }
  return phone;
});
var optionalText = z.string().trim().max(300).nullable().optional().transform((v) => v ? v : null);
var addressWriteSchema = z.object({
  /** Com ou sem hífen; chega ao serviço só com os 8 dígitos. */
  zipCode: z.string().trim().transform((v) => digitsOnly(v) ?? "").refine((d) => d === "" || d.length === 8, "CEP inv\xE1lido: s\xE3o 8 d\xEDgitos.").nullable().optional().transform((v) => v ? v : null),
  state: z.string().trim().toUpperCase().refine((v) => v === "" || isBrazilianState(v), "Escolha uma das 27 UFs.").nullable().optional().transform((v) => v ? v : null),
  city: optionalText,
  neighborhood: optionalText,
  street: optionalText,
  number: optionalText,
  complement: optionalText,
  referencePoint: optionalText,
  locationLink: optionalText,
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional()
});
var addressSchema = z.object({
  id: z.number().int(),
  zipCode: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  neighborhood: z.string().nullable(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  referencePoint: z.string().nullable(),
  locationLink: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable()
});
var themeSchema = z.enum(THEMES);
var channelsSchema = z.object({ email: z.boolean() });
var MIN_PASSWORD_LENGTH = 10;

// src/members.ts
var c = initContract();
var memberStatus = ["ativo", "convidado", "bloqueado", "sem_acesso"];
var memberStatusSchema = z2.enum(memberStatus);
var tenantMemberSchema = z2.object({
  id: z2.string(),
  nome: z2.string(),
  email: z2.string(),
  /** `•••.533.447-••` — o inteiro só no detalhe */
  maskedCpf: z2.string(),
  telefoneMasked: z2.string(),
  situacao: memberStatusSchema,
  /** o código no ERP, quando já vinculado. O espelho entra na onda 3. */
  erpCode: z2.string().nullable(),
  blockedAt: instantSchema.nullable(),
  vinculadoAt: instantSchema,
  /** quando há convite pendente, até quando ele vale */
  inviteExpiresAt: instantSchema.nullable()
});
var memberDetailSchema = tenantMemberSchema.extend({
  /** no detalhe o documento aparece inteiro, e a abertura vira evento */
  cpf: z2.string().nullable(),
  telefone: z2.string().nullable(),
  lastAccessAt: instantSchema.nullable(),
  sessionsActive: z2.number().int()
});
var erpCandidateSchema = z2.object({
  /** o `codigo_associado` — é por ele que a criação pede o cadastro */
  erpCode: z2.string(),
  nome: z2.string().nullable(),
  /**
   * Só dígitos: 11 (CPF) ou **14 (CNPJ)**. No SGA o campo se chama `cpf` e
   * guarda os dois; quem formatar na tela precisa olhar `tipoPessoa` antes, ou
   * mostra catorze dígitos com máscara de CPF — um documento que não existe.
   */
  document: z2.string().nullable(),
  /** `FISICA` / `JURIDICA`, como o ERP responde. `null` quando ele não disse. */
  tipoPessoa: z2.string().nullable(),
  /** o e-mail do cadastro no ERP. `null` é o motivo de recusa mais comum. */
  email: z2.string().nullable(),
  /** a situação no ERP, sem classificação da casa — associado não tem tabela. */
  situacao: z2.object({
    code: z2.string().nullable(),
    descricao: z2.string().nullable()
  }),
  /** quando este cadastro foi lido do ERP */
  syncedAt: instantSchema,
  /**
   * O acesso que **já existe** nesta base para este código do ERP.
   *
   * `null` é "ainda não tem". Com valor, a tela leva para a ficha em vez de
   * oferecer criar — criar de novo faria o segundo cadastro de quem já tem um.
   */
  member: z2.object({ id: z2.string(), email: z2.string(), situacao: memberStatusSchema }).nullable(),
  /**
   * Por que este cadastro **não** vira acesso agora. `null` é "dá para criar".
   *
   * `sem_email`: a plataforma tem um canal só, e é e-mail
   * (`app.portal_members.email` é `not null`). Medido em `portal-27`: **5.379
   * associados ativos da CE não têm e-mail** — 18,8%. A tela precisa dizer isso
   * em vez de recusar um formulário sem explicar; o caminho de volta é o
   * cadastro ganhar um e-mail **no SGA**, não aqui.
   *
   * É `enum` de um valor só, e não um booleano, porque a próxima recusa é
   * questão de tempo e um booleano a receberia sem nome.
   */
  refusal: z2.enum(["sem_email"]).nullable()
});
var membersContract = c.router(
  {
    list: {
      method: "GET",
      path: "",
      query: paginationQuery.extend({
        search: z2.string().optional(),
        situacao: memberStatusSchema.optional()
      }),
      responses: { 200: paginated(tenantMemberSchema), ...defaultErrors },
      summary: "Associados com v\xEDnculo ativo na base atual"
    },
    /**
     * Procura no ERP quem tem este documento — `portal-42`.
     *
     * ## As quatro respostas, e por que duas são status e duas são corpo
     *
     * "Não existe" e "o ERP não respondeu" são **404** e **503**: são o
     * resultado da pergunta, e o cliente já precisa tratar status. "Achou" e
     * "achou e não dá" são os dois **200**, separados por `refusal` — porque
     * nos dois casos existe um cadastro para mostrar, e um 4xx esconderia o
     * nome da pessoa que está do outro lado do telefone.
     *
     * A diferença entre 404 e 503 é a tarefa: com os dois iguais, quem atende
     * manda o associado reclamar da coisa errada.
     *
     * ## Por que o documento é o corpo, e não o caminho nem uma query
     *
     * Ele não é filtro de lista, é a **chave da busca** — `POST /listar/associado`
     * do SGA aceita um filtro `cpf` e **o ignora**, devolvendo a base inteira com
     * status 200, e a confusão entre "buscar um" e "filtrar muitos" já custou
     * caro deste lado da fronteira. Isso continua valendo, e é por isso que não
     * virou query.
     *
     * **Mas ele era o caminho, e isso estava errado.** Até 12/09 esta rota era
     * `GET /erp/:document`: um CPF na URL, que acaba em log de servidor,
     * histórico de navegador e cabeçalho `Referer` — o defeito exato que
     * `contract.test.ts` existe para impedir. A régua não a pegava porque a
     * expressão dela ficou em português quando os identificadores foram
     * traduzidos; corrigida a expressão, esta rota foi o único violador que
     * apareceu.
     *
     * `POST` com o valor no corpo é o que a própria régua manda, e o que
     * `POST /erp/sync` já faz com o código do associado.
     */
    searchInErp: {
      method: "POST",
      path: "/erp/search",
      body: z2.object({
        /** CPF ou CNPJ, com ou sem máscara — a normalização é da API */
        document: z2.string().min(11).max(20)
      }),
      responses: { 200: erpCandidateSchema, ...defaultErrors },
      summary: "Procura no ERP o associado com este CPF ou CNPJ"
    },
    /**
     * Cria o acesso do associado a partir do cadastro do ERP — `portal-42`.
     *
     * ## O que ela existe para consertar
     *
     * O contrato listava, via, convidava, gravava código do ERP, bloqueava e
     * derrubava sessão — e **não criava**. `createMember` só era chamado pelo
     * seed e pelo `db-demo`; em produção não existiria associado nenhum para
     * convidar. Decidido em 07/09/2026: ninguém recebe convite em massa, o
     * acesso nasce avulso, pela operação.
     *
     * ## Recebe o CÓDIGO, e não o documento
     *
     * Quem já procurou tem o código na mão, e é ele a chave natural do cadastro
     * — o mesmo valor que `linkErp` grava. Receber o documento faria esta rota
     * repetir a busca e escolher sozinha entre cadastros que compartilham CPF;
     * a escolha é de quem está olhando a tela.
     *
     * Não manda convite: quem convida é `POST /members/:id/invite`, que já
     * existe. Duas ações, dois atos na trilha.
     */
    create: {
      method: "POST",
      path: "",
      body: z2.object({ erpCode: z2.string().min(1).max(60) }),
      responses: { 201: memberDetailSchema, ...defaultErrors },
      summary: "Cria o acesso de um associado a partir do cadastro do ERP"
    },
    get: {
      method: "GET",
      path: "/:id",
      pathParams: z2.object({ id: z2.string() }),
      responses: { 200: memberDetailSchema, ...defaultErrors },
      summary: "Um associado da base atual"
    },
    /**
     * Corrige o nome e o e-mail do associado (03/11/2026) — o mesmo ato de
     * `platform.editUser`, do lado do portal.
     *
     * O acesso nasce com o nome e o e-mail do cadastro no ERP, e a correção
     * aqui **não volta ao SGA**: daqui em diante os dois podem divergir, e o
     * login é o daqui. É a IDENTIDADE (`portal_members` não tem base), então
     * vale em todas as bases da pessoa.
     */
    edit: {
      method: "PATCH",
      path: "/:id",
      pathParams: z2.object({ id: z2.string() }),
      body: z2.object({
        nome: z2.string().trim().min(2).max(120),
        email: z2.string().trim().email().max(200)
      }),
      responses: {
        /*
         * `inviteResent`: o e-mail mudou com um convite de primeiro acesso
         * ainda aberto, e por isso o convite velho foi arquivado e um novo
         * saiu para o endereço novo. A tela diz isso a quem corrigiu.
         */
        200: memberDetailSchema.extend({ inviteResent: z2.boolean() }),
        ...defaultErrors
      },
      summary: "Corrige o nome e o e-mail de um associado da base atual"
    },
    /**
     * Convida — e reconvida.
     *
     * Um convite novo **arquiva o anterior**: dois links válidos ao mesmo tempo
     * para a mesma pessoa é um a mais do que precisa existir, e o que sobra é o
     * que alguém encaminhou por engano.
     */
    invite: {
      method: "POST",
      path: "/:id/invite",
      pathParams: z2.object({ id: z2.string() }),
      body: z2.object({}).optional(),
      responses: {
        201: z2.object({
          expiresAt: instantSchema,
          /**
           * O link só volta **fora de produção**. Em produção ele existe no
           * e-mail e em nenhum outro lugar: devolvê-lo na resposta o põe no log
           * do navegador, no histórico da aba e no print que alguém manda.
           */
          link: z2.string().nullable()
        }),
        ...defaultErrors
      },
      summary: "Envia (ou reenvia) o convite de primeiro acesso"
    },
    linkErp: {
      method: "PUT",
      path: "/:id/erp-code",
      pathParams: z2.object({ id: z2.string() }),
      body: z2.object({ erpCode: z2.string().min(1).max(60).nullable() }),
      responses: { 200: memberDetailSchema, ...defaultErrors },
      summary: "Liga o associado ao cadastro dele no ERP"
    },
    block: {
      method: "POST",
      path: "/:id/blocked",
      pathParams: z2.object({ id: z2.string() }),
      body: z2.object({
        blocked: z2.boolean(),
        reason: z2.string().max(500).optional()
      }),
      responses: { 200: memberDetailSchema, ...defaultErrors },
      summary: "Bloqueia ou desbloqueia o acesso do associado"
    },
    /**
     * Derrubar a sessão **não é** bloquear a conta, e por isso tem ação própria
     * no catálogo (`revogar_sessoes`). O suporte que ajuda alguém a sair de um
     * dispositivo perdido não deveria precisar do poder de bloquear ninguém.
     */
    revokeSessions: {
      method: "DELETE",
      path: "/:id/sessions",
      pathParams: z2.object({ id: z2.string() }),
      body: z2.void(),
      responses: { 200: z2.object({ ended: z2.number().int() }), ...defaultErrors },
      summary: "Derruba todas as sess\xF5es do associado"
    }
  },
  { pathPrefix: "/members" }
);

// src/billing.ts
import { initContract as initContract3 } from "@ts-rest/core";
import { DISPATCH_DISCARD_REASONS } from "@evogard/domain";
import { z as z5 } from "zod";

// src/platform.ts
import { initContract as initContract2 } from "@ts-rest/core";
import { z as z4 } from "zod";

// src/labels.ts
import { z as z3 } from "zod";
import { LABEL_COLORS, LABEL_NAME_MAX } from "@evogard/domain";
var labelColorSchema = z3.enum(LABEL_COLORS);
var labelSchema = z3.object({
  id: z3.number().int(),
  departmentId: z3.string(),
  name: z3.string(),
  color: labelColorSchema,
  position: z3.number().int(),
  archivedAt: instantSchema.nullable()
});
var departmentLabelsSchema = z3.object({
  departmentId: z3.string(),
  departmentName: z3.string(),
  labels: z3.array(labelSchema)
});
var appliedLabelSchema = z3.object({
  id: z3.number().int(),
  name: z3.string(),
  color: labelColorSchema,
  departmentId: z3.string(),
  departmentName: z3.string(),
  archivedAt: instantSchema.nullable()
});
var labelName = z3.string().trim().min(1, "D\xEA um nome \xE0 etiqueta.").max(LABEL_NAME_MAX);
var createLabelSchema = z3.object({
  name: labelName,
  color: labelColorSchema
});
var updateLabelSchema = z3.object({
  name: labelName.optional(),
  color: labelColorSchema.optional(),
  position: z3.number().int().min(0).max(999).optional(),
  archived: z3.boolean().optional()
});

// src/platform.ts
var c2 = initContract2();
var tenantSchema = z4.object({
  id: z4.number().int(),
  slug: z4.string(),
  nome: z4.string(),
  active: z4.boolean()
});
var userSchema = z4.object({
  id: z4.string(),
  nome: z4.string(),
  email: z4.string(),
  active: z4.boolean(),
  blockedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var sessionSchema = z4.object({
  id: z4.string(),
  createdAt: instantSchema,
  expiresAt: instantSchema,
  ip: z4.string().nullable(),
  userAgent: z4.string().nullable(),
  current: z4.boolean()
});
var preferencesSchema = z4.object({
  theme: themeSchema,
  defaultTenant: z4.number().int().nullable(),
  channels: channelsSchema,
  /** tocar som quando um aviso chega em tempo real */
  notificationSound: z4.boolean(),
  /**
   * As colunas que a pessoa recolheu em cada quadro, pela chave do quadro
   * (`billing.desk` é a Mesa de cobrança). As colunas nascem abertas; a lista
   * guarda só as exceções.
   *
   * Mora aqui, e não num `localStorage`, pela regra da casa: preferência de
   * pessoa é do servidor, e vale no computador e no telefone. Gravar troca a
   * lista do quadro inteira e mantém a dos outros.
   */
  collapsedColumns: z4.record(
    z4.string().min(1).max(100),
    z4.array(z4.string().min(1).max(300)).max(300)
  ),
  /**
   * O olho de "Meu saldo" no topo (21/10/2026): o valor sai como R$ ••••••.
   * Para quem abre a tela na frente de um cliente — e fica gravado, porque o
   * consultor que oculta uma vez não quer o número de volta a cada recarga.
   */
  hideBalance: z4.boolean()
});
var contactBlockSchema = z4.object({
  id: z4.number().int(),
  /** e-mail normalizado ou telefone só com dígitos e DDD */
  contact: z4.string(),
  tipo: z4.enum(["email", "telefone"]),
  /**
   * `pedido_do_titular` é direito da pessoa e não se desfaz por conveniência;
   * `retorno_invalido` é dado operacional e some quando o contato é corrigido.
   */
  origin: z4.enum(["pedido_do_titular", "retorno_invalido", "operacional"]),
  reason: z4.string().nullable(),
  since: z4.string(),
  /**
   * Nulo enquanto o bloqueio vale.
   *
   * A tela mostra ativos e arquivados na mesma lista quando se pede. Sem este
   * campo ela não conseguia distinguir os dois — listava juntos, sem marca, e
   * oferecia "Desbloquear" para quem já estava desbloqueado.
   */
  archivedAt: instantSchema.nullable()
});
var integrationCredentialSchema = z4.object({
  integration: z4.string(),
  label: z4.string(),
  configured: z4.boolean(),
  updatedAt: instantSchema.nullable(),
  /**
   * `7b2e1••••••••••••` — os cinco primeiros caracteres da credencial desta
   * base e a máscara. Nulo sem credencial, com credencial
   * ilegível ou com segredo curto demais para mostrar qualquer pedaço.
   */
  secretPreview: z4.string().nullable(),
  verifiedAt: instantSchema.nullable(),
  verifiedOk: z4.boolean().nullable(),
  /** a credencial própria existe e não abre com a chave-mestra atual */
  unreadable: z4.boolean(),
  /**
   * Se dá para GERAR a credencial informando um login do fornecedor, em vez de
   * colar um token pronto.
   *
   * `true` só para o Hinova hoje: o SGA troca usuário e senha por um token que
   * não expira. Onde for `false`, a tela oferece só o campo de colar — um botão
   * "gerar" que não gera é a mentira de interface de sempre.
   */
  canGenerate: z4.boolean(),
  /**
   * Se esta integração já tem porta que saiba conversar com o fornecedor.
   *
   * `false` para Oryon e gateway hoje: dá para guardar a credencial, e testar
   * conexão não dá, porque não há o que testar. A tela diz isso — regra 14, o
   * que o código não faz a interface não afirma.
   */
  hasPort: z4.boolean()
});
var testResultSchema = z4.object({
  ok: z4.boolean(),
  /** em pt-BR, para a tela mostrar. Nunca contém segredo. */
  detail: z4.string(),
  verificadoAt: instantSchema
});
var tenantConfigurationSchema = z4.object({
  /** Uma ou duas letras, no canto do menu. Nunca vazio: o servidor aplica o padrão. */
  brandInitials: z4.string()
});
var configurableKeySchema = z4.object({
  key: z4.string(),
  label: z4.string(),
  help: z4.string(),
  /** O módulo que LÊ esta chave. A tela mostra: chave sem dono não deveria existir. */
  owner: z4.string(),
  value: z4.unknown()
});
var departmentSchema = z4.object({
  id: z4.string(),
  nome: z4.string(),
  descricao: z4.string().nullable(),
  /**
   * Quantas pessoas estão nele hoje.
   *
   * Vem na listagem porque "departamento sem ninguém" é o estado que a tela
   * precisa denunciar: uma categoria apontada para um setor vazio roteia o
   * aviso para lista nenhuma, e sem este número a tela não teria como avisar.
   */
  memberCount: z4.number().int(),
  /**
   * Departamento arquivado continua na lista e **não recebe demanda nova**.
   *
   * Some da lista seria esconder de quem administra por que aquela categoria
   * parou de rotear. As demandas antigas continuam apontando para ele.
   */
  archivedAt: instantSchema.nullable(),
  /**
   * O grupo do líder (29/10/2026): quem LIDERA este setor recebe as
   * permissões deste grupo, somadas às do grupo dela. Nulo é "liderar não
   * concede nada" — o padrão. O nome vem junto para a tela dizer, ao lado da
   * caixa "lidera", o que marcar a caixa concede. `archived` diz que o grupo
   * foi arquivado e, por isso, não concede mais nada — a tela não pode
   * afirmar uma concessão que o código não cumpre.
   *
   * Opcional na leitura pela mesma razão do `collapsedColumns` da Mesa: a
   * interna e a API sobem separadas, e a interna nova lendo a API velha
   * trata a ausência como nulo.
   */
  leaderGroup: z4.object({ id: z4.string(), nome: z4.string(), archived: z4.boolean() }).nullable().optional()
});
var editableDepartmentSchema = z4.object({
  nome: z4.string().min(1).max(120),
  descricao: z4.string().max(500).nullable(),
  /**
   * O grupo do líder, um grupo da MESMA base. `null` tira; AUSENTE não mexe —
   * a interna de antes do deploy manda só nome e descrição, e não pode apagar
   * sem querer o que a nova gravou. Escolher o grupo é conceder acesso: exige
   * `platform.users:editar` além de `platform.departments:editar`.
   */
  leaderGroupId: z4.string().nullable().optional()
});
var departmentRefSchema = departmentSchema.pick({ id: true, nome: true }).extend({ leads: z4.boolean().default(false) });
var departmentPersonSchema = z4.object({
  id: z4.string(),
  nome: z4.string(),
  email: z4.string(),
  /** Bloqueada continua na lista: tirá-la do setor é decisão de quem edita. */
  blocked: z4.boolean(),
  member: z4.boolean(),
  leads: z4.boolean()
});
var contextSchema = z4.object({
  user: userSchema,
  currentTenant: tenantSchema,
  /** todos os tenants em que o usuário tem vínculo */
  tenants: z4.array(tenantSchema),
  /** permissões efetivas NO tenant atual, no formato "recurso:acao" */
  permissions: z4.array(z4.string()),
  /**
   * Os setores da pessoa NA BASE ATUAL, com a liderança de cada um.
   *
   * Está aqui porque a tela precisa dizer **por que** uma ação não está
   * disponível — "só quem lidera o setor pode encaminhar" exige saber se a
   * pessoa lidera aquele setor. Sem isso o botão sumiria sem explicação, e
   * botão que some sem motivo faz quem olha achar que o sistema quebrou.
   *
   * **Esconder não é proteger**, e continua não sendo: quem nega é o serviço, a
   * cada requisição, com `refusalToActOnTicket`. Isto serve só para a tela
   * antecipar a mesma frase.
   *
   * O contexto é cacheado na SPA e `PUT /platform/users/:id/departments` não o
   * invalida — quem virar líder vê o botão depois de recarregar. É o erro na
   * direção segura: um botão a mais que devolve 403, nunca um acesso a mais.
   */
  departments: z4.array(departmentRefSchema),
  /**
   * A pessoa tem vínculo de consultor (`sales_consultants`, não arquivado) NA
   * BASE ATUAL.
   *
   * Existe para o menu esconder o que só faz sentido a consultor — "Meus
   * pagamentos" — de quem tem a permissão pelo grupo e nenhum vínculo. A
   * permissão diz o que a pessoa PODE; isto diz se há algo dela para ver.
   * Mesmo cache e mesma direção segura de `departments`: quem virar
   * consultor vê o item depois de recarregar.
   */
  consultant: z4.boolean(),
  preferences: preferencesSchema,
  /** o segundo bloco irmão, previsto acima: o que a moldura pinta desta base */
  setting: tenantConfigurationSchema,
  /**
   * A sigla de CADA base da pessoa (`marca.sigla`, com o padrão do catálogo) —
   * o menu de troca de base a mostra ao lado de cada nome, igual à da base
   * atual no gatilho. Desde 09/10/2026.
   */
  tenantInitials: z4.array(z4.object({ tenantId: z4.number().int(), initials: z4.string() }))
});
var permissionExceptionSchema = z4.object({
  resource: z4.string(),
  action: z4.string(),
  status: z4.enum(["permitir", "negar"])
});
var tenantUserSchema = userSchema.extend({
  groupId: z4.string().nullable(),
  groupName: z4.string().nullable(),
  /**
   * Os departamentos da pessoa **nesta base** — lista, e não campo único.
   *
   * Pelo mesmo argumento do `groupId` acima, e com uma diferença que importa: o
   * grupo é singular porque o PAPEL é um só por base, e o departamento é plural
   * porque a FILA não é. O `ticketgard` já provava isso com
   * `lista_departamento_users`, uma string de JSON com vários ids dentro.
   */
  departments: z4.array(departmentRefSchema),
  /** As exceções individuais desta pessoa nesta base — ver `permissionExceptionSchema`. */
  permissionExceptions: z4.array(permissionExceptionSchema)
});
var editableGroupSchema = z4.object({
  nome: z4.string().min(1).max(120),
  descricao: z4.string().max(500).nullable(),
  permissions: z4.array(z4.string())
});
var groupSchema = z4.object({
  id: z4.string(),
  tenantId: z4.number().int(),
  nome: z4.string(),
  descricao: z4.string().nullable(),
  permissions: z4.array(z4.string()),
  /**
   * Grupo arquivado continua na lista e **não concede nada**.
   *
   * Sai da lista seria esconder de quem administra por que fulano perdeu o
   * acesso. `permissionsOf` o ignora, e é isso que faz arquivar valer.
   */
  archivedAt: instantSchema.nullable()
});
var eventSchema = z4.object({
  id: z4.string(),
  occurredAt: instantSchema,
  actorType: z4.string(),
  actorId: z4.string().nullable(),
  /**
   * O nome de quem agiu, resolvido na leitura — `null` quando não dá.
   *
   * A trilha guarda o **id**, e é assim que tem que ser: ela registra quem era
   * no momento do ato, e o nome muda. Mas mostrar só o id faz a tela não
   * responder a própria pergunta — "quem fez isso" vira um hash de 32
   * caracteres. Resolver na leitura dá o nome de hoje a quem ainda existe, e
   * `null` a quem saiu; a tela mostra o id nesse caso, que é a verdade
   * disponível.
   */
  actorName: z4.string().nullable(),
  entity: z4.string(),
  entityId: z4.string(),
  tipo: z4.string(),
  data: z4.unknown()
});
var notificationSchema = z4.object({
  id: z4.number().int(),
  subject: z4.string(),
  entity: z4.string(),
  entityId: z4.string(),
  type: z4.string(),
  createdAt: instantSchema,
  readAt: instantSchema.nullable()
});
var auditTrailQuery = z4.object({
  beforeOf: z4.string().optional(),
  limit: z4.coerce.number().int().min(1).max(200).default(50),
  entity: z4.string().optional(),
  tipo: z4.string().optional(),
  actorId: z4.string().optional(),
  /*
   * Data de negócio (`AAAA-MM-DD`), e não instante: quem filtra a trilha pensa
   * em dias, não em fusos. A conversão para o intervalo de `timestamptz`
   * acontece no servidor, uma vez — se cada tela convertesse, a de São Paulo e
   * a de Goiás discordariam sobre onde o dia começa.
   */
  de: businessDateSchema.optional(),
  until: businessDateSchema.optional()
});
var validUserInviteSchema = z4.object({
  nome: z4.string(),
  emailMasked: z4.string()
});
var platformContract = c2.router(
  {
    /** contexto da sessão atual — usuário, tenant e permissões */
    context: {
      method: "GET",
      path: "/context",
      responses: { 200: contextSchema, ...defaultErrors },
      summary: "Contexto da sess\xE3o atual"
    },
    switchTenant: {
      method: "POST",
      path: "/current-tenant",
      body: z4.object({ tenantId: z4.number().int() }),
      responses: { 200: contextSchema, ...defaultErrors },
      summary: "Troca o tenant ativo da sess\xE3o"
    },
    /*
     * Preferência é da PRÓPRIA pessoa: `@SomenteAutenticado`, sem recurso no
     * catálogo. Não existe — e não deve existir — rota que grave a preferência
     * de outro; mudar o tema alheio não é administração, é confusão.
     */
    getPreferences: {
      method: "GET",
      path: "/preferences",
      responses: { 200: preferencesSchema, ...defaultErrors },
      summary: "Tema, base padr\xE3o e canais da pr\xF3pria pessoa"
    },
    writePreferences: {
      method: "PUT",
      path: "/preferences",
      body: preferencesSchema.partial(),
      responses: { 200: preferencesSchema, ...defaultErrors },
      summary: "Salva tema, base padr\xE3o e canais"
    },
    /*
     * Configuração é da BASE, não da pessoa: recurso `platform.tenants` no
     * catálogo, ao contrário da preferência logo acima. Quem edita muda o que
     * todo mundo daquela praça vê.
     */
    listSetting: {
      method: "GET",
      path: "/tenant-settings",
      responses: {
        200: z4.object({ keys: z4.array(configurableKeySchema) }),
        ...defaultErrors
      },
      summary: "As chaves configur\xE1veis da base atual, com r\xF3tulo e valor"
    },
    writeSetting: {
      method: "PUT",
      path: "/tenant-settings",
      /*
       * A chave vai no CORPO e não no caminho. Duas razões: ela tem ponto
       * (`marca.sigla`), que num segmento de caminho pede escape e vira ruído em
       * log e em teste; e o `valor` é `unknown` de propósito — quem sabe a forma
       * de cada chave é o catálogo no servidor, e repeti-la aqui seria a segunda
       * cópia, do mesmo jeito que o catálogo de permissões não vem para cá.
       */
      body: z4.object({ key: z4.string(), value: z4.unknown() }),
      responses: {
        200: z4.object({ keys: z4.array(configurableKeySchema) }),
        ...defaultErrors
      },
      summary: "Grava uma chave de configura\xE7\xE3o da base atual"
    },
    /*
     * Credenciais de integração — a regra 12.
     *
     * Recurso próprio (`platform.integrations`), separado de
     * `platform.tenants`: quem ajusta a sigla da base no menu não é quem
     * aplica a credencial que dá escrita no ERP de produção.
     */
    listIntegrations: {
      method: "GET",
      path: "/integrations",
      responses: {
        200: z4.object({ integrations: z4.array(integrationCredentialSchema) }),
        ...defaultErrors
      },
      summary: "O estado das credenciais de integra\xE7\xE3o da base atual"
    },
    applyCredential: {
      method: "PUT",
      path: "/integrations/:integration/credential",
      /*
       * `trocou: false` quando o valor colado é igual ao que já estava lá.
       *
       * Não é erro — a tela nunca devolve o valor salvo, então a pessoa não
       * tinha como saber. Mas também não é troca: gravar mexeria em
       * `updatedAt` e registraria uma rotação que não houve, e é
       * `updatedAt` que alguém vai olhar para responder "quando essa
       * credencial mudou?".
       */
      body: z4.object({ secret: z4.string().min(1) }),
      responses: {
        200: z4.object({
          switched: z4.boolean(),
          integrations: z4.array(integrationCredentialSchema)
        }),
        ...defaultErrors
      },
      summary: "Aplica uma credencial pr\xF3pria a esta base"
    },
    discardCredential: {
      method: "DELETE",
      path: "/integrations/:integration/credential",
      responses: {
        200: z4.object({
          discarded: z4.boolean(),
          integrations: z4.array(integrationCredentialSchema)
        }),
        ...defaultErrors
      },
      summary: "Remove a credencial desta base \u2014 ela fica sem integra\xE7\xE3o"
    },
    generateCredential: {
      method: "POST",
      path: "/integrations/:integration/generate-credential",
      /*
       * O que entra aqui é insumo de uma chamada ao fornecedor: não é gravado,
       * nem devolvido, nem registrado no evento. Os três são obrigatórios —
       * desde 14/10/2026 não há padrão no ambiente.
       */
      body: z4.object({
        user: z4.string().trim().min(1).max(200),
        password: z4.string().min(1).max(500),
        /**
         * O token da associação, da Área Cliente do SGA. Sem tamanho conferido:
         * quem diz se ele vale é o `/usuario/autenticar`.
         */
        associationToken: z4.string().trim().min(1).max(4096)
      }),
      responses: {
        200: z4.object({
          /** `false` quando o fornecedor recusou o login — com `detalhe` dizendo por quê */
          generated: z4.boolean(),
          detail: z4.string(),
          integrations: z4.array(integrationCredentialSchema)
        }),
        ...defaultErrors
      },
      summary: "Gera a credencial desta base a partir de um login do fornecedor"
    },
    testIntegration: {
      method: "POST",
      path: "/integrations/:integration/test",
      body: z4.object({}),
      responses: {
        200: z4.object({
          result: testResultSchema,
          integrations: z4.array(integrationCredentialSchema)
        }),
        ...defaultErrors
      },
      summary: "Testa a conex\xE3o com o fornecedor usando a credencial que vale aqui"
    },
    /*
     * A tela é de MANUTENÇÃO, não de atendimento. Quem grava o opt-out no
     * atendimento é a cobrança, na onda 5, pelo mesmo service de `common/`.
     */
    listBlocks: {
      method: "GET",
      path: "/contact-blocks",
      query: z4.object({
        includeArchived: z4.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: {
        200: z4.object({ items: z4.array(contactBlockSchema) }),
        ...defaultErrors
      },
      summary: "Contatos bloqueados \u2014 vale para todas as bases"
    },
    blockContact: {
      method: "POST",
      path: "/contact-blocks",
      body: z4.object({
        tipo: z4.enum(["email", "telefone"]),
        /** cru: quem normaliza é o servidor, e é ele quem recusa o indiscável */
        contact: z4.string().min(1),
        origin: z4.enum(["pedido_do_titular", "retorno_invalido", "operacional"]),
        reason: z4.string().max(500).nullable().optional()
      }),
      responses: { 201: contactBlockSchema, ...defaultErrors },
      summary: "Bloqueia um contato em todas as bases"
    },
    unblockContact: {
      method: "DELETE",
      /*
       * `:id` opaco, e não o contato no caminho. O teste de contrato de
       * `plataforma-37` reprova identificador de pessoa na URL, e telefone e
       * e-mail são exatamente isso — apareceriam em log de proxy e em histórico
       * de navegador.
       */
      path: "/contact-blocks/:id",
      pathParams: z4.object({ id: z4.coerce.number().int() }),
      body: z4.object({}).optional(),
      responses: { 200: z4.object({ ok: z4.literal(true) }), ...defaultErrors },
      summary: "Arquiva o bloqueio \u2014 a opera\xE7\xE3o nunca apaga"
    },
    listUsers: {
      method: "GET",
      path: "/users",
      query: paginationQuery.extend({ search: z4.string().optional() }),
      responses: { 200: paginated(tenantUserSchema), ...defaultErrors },
      summary: "Lista os usu\xE1rios do tenant atual, com o grupo de cada um"
    },
    /**
     * Uma pessoa da base atual — quem não tem vínculo aqui responde 404.
     *
     * Existe para quem chega à pessoa por OUTRO cadastro: a ficha do consultor
     * conhece o id e o nome, e não o e-mail, e editar o e-mail pede saber qual
     * é o de hoje.
     */
    getUser: {
      method: "GET",
      path: "/users/:id",
      pathParams: z4.object({ id: z4.string() }),
      responses: { 200: tenantUserSchema, ...defaultErrors },
      summary: "Uma pessoa da base atual"
    },
    /**
     * Corrige o nome e o e-mail de uma pessoa da base atual (30/10/2026).
     *
     * **É a identidade, e não o vínculo:** `app.users` não tem `tenant_id`, e o
     * nome corrigido aqui vale em todas as bases da pessoa — como o bloqueio.
     * Quem corrige precisa ter vínculo com ela na base atual, senão a rota
     * viraria administração de gente de outra praça.
     *
     * **O e-mail é o login.** Trocá-lo muda com que endereço a pessoa entra a
     * partir do próximo acesso; a senha, as sessões abertas e o convite seguem,
     * porque os três apontam para o id e não para o endereço.
     */
    editUser: {
      method: "PATCH",
      path: "/users/:id",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({
        nome: z4.string().trim().min(2).max(120),
        email: z4.string().trim().email().max(200)
      }),
      responses: {
        /*
         * `inviteResent`: o e-mail mudou com um convite de primeiro acesso
         * ainda aberto, e por isso o convite velho foi arquivado e um novo
         * saiu para o endereço novo. A tela diz isso a quem corrigiu.
         */
        200: tenantUserSchema.extend({ inviteResent: z4.boolean() }),
        ...defaultErrors
      },
      summary: "Corrige o nome e o e-mail de uma pessoa da base atual"
    },
    /**
     * Cria um colaborador e o vincula à base atual — `plataforma-10`.
     *
     * ## O que ela conserta
     *
     * `PlatformService` sabia vincular, trocar de grupo, bloquear e desvincular
     * — e **não sabia criar**. A pessoa tinha de nascer por um `INSERT` à mão
     * ou pelo seed, e "cadastrar um colaborador" continuava sendo dezenove
     * cadastros em dezenove sistemas mais um no banco.
     *
     * ## Criar não convida, e o convite é o passo seguinte
     *
     * A resposta traz a pessoa **sem senha** e sem sessão. O primeiro acesso
     * sai por `POST /platform/users/:id/invite`, que é ato próprio na trilha:
     * separá-los é o que permite cadastrar hoje o time que começa segunda sem
     * mandar, hoje, um link que vence antes.
     *
     * `groupId` anulável é um estado legítimo — entra na base e não pode nada,
     * que é a negação por omissão visível na tela.
     */
    createUser: {
      method: "POST",
      path: "/users",
      body: z4.object({
        nome: z4.string().min(2).max(120),
        email: z4.string().email().max(200),
        /** o mesmo texto-e-não-número de `linkUser`, e pelo mesmo motivo */
        groupId: z4.string().nullable()
      }),
      responses: { 201: userSchema, ...defaultErrors },
      summary: "Cria um colaborador e o vincula \xE0 base atual"
    },
    /**
     * Envia (ou reenvia) o convite de primeiro acesso.
     *
     * Um convite novo **arquiva o anterior**: dois links válidos ao mesmo tempo
     * para a mesma pessoa é um a mais do que precisa existir, e o que sobra é o
     * que alguém encaminhou por engano meses atrás. Mesmo desenho de
     * `members.invite`.
     */
    inviteUser: {
      method: "POST",
      path: "/users/:id/invite",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({}).optional(),
      responses: {
        201: z4.object({
          expiresAt: instantSchema,
          /**
           * O link só volta **fora de produção**. Em produção ele existe no
           * e-mail e em nenhum outro lugar: devolvê-lo na resposta o põe no log
           * do navegador, no histórico da aba e no print que alguém manda.
           */
          link: z4.string().nullable()
        }),
        ...defaultErrors
      },
      summary: "Envia (ou reenvia) o convite de primeiro acesso"
    },
    /**
     * Vincula à base atual, ou troca o grupo de quem já está nela.
     *
     * Um `PUT` e não dois verbos: "está nesta base, neste grupo" é um estado, e
     * declará-lo é idempotente. Duas rotas — uma para vincular, outra para
     * trocar — obrigariam quem chama a saber de antemão em qual dos dois casos
     * está, e a errar quando a resposta mudasse entre a leitura e a escrita.
     */
    linkUser: {
      method: "PUT",
      path: "/users/:id/link",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({
        /*
         * String, e não número: `groups.id` é `bigserial`, e todo id de
         * bigserial viaja como texto nesta casa — `groupSchema.id` já é
         * `z.string()`. Acima de 2^53 o número do JavaScript perde precisão em
         * silêncio, e o dia em que isso importa é tarde demais para descobrir.
         *
         * `null` é vínculo sem grupo: entra na base e não pode nada.
         */
        groupId: z4.string().nullable()
      }),
      responses: { 200: userSchema, ...defaultErrors },
      summary: "Vincula a pessoa \xE0 base atual, ou troca o grupo dela"
    },
    desvincularUser: {
      method: "DELETE",
      path: "/users/:id/link",
      pathParams: z4.object({ id: z4.string() }),
      responses: { 204: z4.null(), ...defaultErrors },
      summary: "Tira a pessoa da base atual"
    },
    /**
     * Substitui a lista INTEIRA de departamentos da pessoa nesta base.
     *
     * A tela manda o estado final, como `editGroup` — calcular a diferença no
     * servidor criaria uma segunda verdade sobre em que filas a pessoa está.
     *
     * Exige `platform.departments:editar`, e não `platform.users:editar`: mudar
     * a fila de alguém é mexer no roteamento das demandas, e quem cadastra
     * pessoa não é necessariamente quem decide qual setor atende o quê.
     */
    setUserDepartments: {
      method: "PUT",
      path: "/users/:id/departments",
      pathParams: z4.object({ id: z4.string() }),
      /**
       * O par, e não a lista de ids — a liderança é atributo do vínculo.
       *
       * Numa chamada à parte existiria o instante em que a pessoa já está no
       * setor e ainda não lidera; e como o `PUT` substitui a lista inteira, a
       * segunda chamada teria de reconstruir o que a primeira decidiu.
       */
      body: z4.object({
        departments: z4.array(z4.object({ id: z4.string(), leads: z4.boolean() }))
      }),
      responses: { 200: tenantUserSchema, ...defaultErrors },
      summary: "Troca os departamentos da pessoa na base atual, com a lideran\xE7a de cada um"
    },
    /**
     * Define, troca ou remove (`status: null`) uma exceção individual.
     *
     * Um verbo só e o estado final de UM par recurso×ação — mesmo idioma de
     * `linkUser` (`groupId: null` é "sem grupo"): `status: null` aqui é "sem
     * exceção", e a pessoa volta a herdar o que o grupo diz.
     */
    setUserPermissionException: {
      method: "PUT",
      path: "/users/:id/permission-exceptions",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({
        resource: z4.string(),
        action: z4.string(),
        status: z4.enum(["permitir", "negar"]).nullable()
      }),
      responses: { 200: tenantUserSchema, ...defaultErrors },
      summary: "Define, troca ou remove uma exce\xE7\xE3o individual de permiss\xE3o"
    },
    /**
     * Bloqueia ou desbloqueia — e, ao bloquear, encerra as sessões da pessoa.
     *
     * Um verbo só com um booleano no corpo, e não `/bloquear` + `/desbloquear`:
     * são o mesmo ato com o sinal trocado, e duas rotas dobrariam a superfície
     * a proteger para não dobrar nada de valor.
     */
    blockUser: {
      method: "POST",
      path: "/users/:id/blocked",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({
        blocked: z4.boolean(),
        /** fica na trilha, não na tela de quem foi bloqueado */
        reason: z4.string().max(500).optional()
      }),
      responses: { 200: userSchema, ...defaultErrors },
      summary: "Bloqueia ou desbloqueia o acesso da pessoa"
    },
    listEvents: {
      method: "GET",
      path: "/events",
      query: auditTrailQuery,
      responses: {
        200: z4.object({
          items: z4.array(eventSchema),
          /** o id a mandar em `beforeOf` para a página seguinte; `null` no fim */
          next: z4.string().nullable()
        }),
        ...defaultErrors
      },
      summary: "A trilha de auditoria da base atual"
    },
    listGroups: {
      method: "GET",
      path: "/groups",
      responses: { 200: z4.array(groupSchema), ...defaultErrors },
      summary: "Lista os grupos de permiss\xE3o do tenant atual"
    },
    createGroup: {
      method: "POST",
      path: "/groups",
      body: editableGroupSchema,
      responses: { 201: groupSchema, ...defaultErrors },
      summary: "Cria um grupo de permiss\xE3o na base atual"
    },
    /**
     * Substitui nome, descrição e a lista INTEIRA de permissões.
     *
     * A tela manda o estado final; calcular a diferença no servidor criaria uma
     * segunda verdade sobre o que o grupo tem.
     */
    editGroup: {
      method: "PUT",
      path: "/groups/:id",
      pathParams: z4.object({ id: z4.string() }),
      body: editableGroupSchema,
      responses: { 200: groupSchema, ...defaultErrors },
      summary: "Edita o grupo e as permiss\xF5es dele"
    },
    /**
     * Arquiva ou reativa o grupo.
     *
     * Rota separada de `editGroup` de propósito: aquela substitui a lista
     * inteira de permissões, e um corpo que carregasse `arquivada` junto faria
     * arquivar exigir mandar as permissões de novo — com o risco de quem
     * arquiva zerar a lista sem querer.
     */
    archiveGroup: {
      method: "POST",
      path: "/groups/:id/archiving",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({ archived: z4.boolean() }),
      responses: { 200: groupSchema.array(), ...defaultErrors },
      summary: "Arquiva ou reativa um grupo de permiss\xE3o"
    },
    /* ------------------------------------------------------------------- *
     * Departamentos
     *
     * O cadastro mora aqui, e não em demandas, pelo motivo escrito no schema:
     * demandas é o primeiro módulo a rotear por setor, não o dono do conceito.
     * ------------------------------------------------------------------- */
    listDepartments: {
      method: "GET",
      path: "/departments",
      query: z4.object({
        /*
         * A tela de administração precisa dos arquivados para reativar.
         *
         * `enum` e não `z.coerce.boolean()`, como em todo `includeArchived` da
         * casa: `Boolean('false')` é `true`, e `?includeArchived=false`
         * devolveria justamente os arquivados que quem perguntou pediu para não
         * ver — sem erro nenhum em lugar nenhum.
         */
        includeArchived: z4.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: z4.array(departmentSchema), ...defaultErrors },
      summary: "Lista os departamentos da base atual"
    },
    createDepartment: {
      method: "POST",
      path: "/departments",
      body: editableDepartmentSchema,
      responses: { 201: departmentSchema, ...defaultErrors },
      summary: "Cria um departamento na base atual"
    },
    editDepartment: {
      method: "PUT",
      path: "/departments/:id",
      pathParams: z4.object({ id: z4.string() }),
      body: editableDepartmentSchema,
      responses: { 200: departmentSchema, ...defaultErrors },
      summary: "Renomeia o departamento ou muda a descri\xE7\xE3o"
    },
    /**
     * Arquiva ou reativa — rota separada de `editDepartment` pelo mesmo motivo
     * de `archiveGroup`: o corpo daquela substitui campos, e carregar o
     * arquivamento junto faria arquivar exigir remandar nome e descrição.
     */
    archiveDepartment: {
      method: "POST",
      path: "/departments/:id/archiving",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({ archived: z4.boolean() }),
      responses: { 200: z4.array(departmentSchema), ...defaultErrors },
      summary: "Arquiva ou reativa um departamento"
    },
    /**
     * As pessoas da base, com a marca de quem está NESTE departamento.
     *
     * Sob `platform.departments:editar`, e não `:ver`: a lista existe para o
     * formulário de pôr e tirar gente, e ela carrega nome e e-mail de toda a
     * base — mais do que a contagem que quem só vê a tela precisa.
     */
    listDepartmentMembers: {
      method: "GET",
      path: "/departments/:id/members",
      pathParams: z4.object({ id: z4.string() }),
      responses: { 200: z4.array(departmentPersonSchema), ...defaultErrors },
      summary: "Lista as pessoas da base, marcando quem est\xE1 no departamento"
    },
    /**
     * Substitui a lista INTEIRA de pessoas do departamento — o espelho de
     * `setUserDepartments`, visto do lado do setor.
     *
     * Mesmo idioma: a tela manda o estado final, com o par pessoa × liderança.
     * As duas rotas gravam a mesma tabela e não guardam nada uma da outra; a
     * última a gravar vence, como em qualquer `PUT`.
     */
    setDepartmentMembers: {
      method: "PUT",
      path: "/departments/:id/members",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.object({
        members: z4.array(z4.object({ userId: z4.string(), leads: z4.boolean() }))
      }),
      responses: { 200: departmentSchema, ...defaultErrors },
      summary: "Troca as pessoas do departamento, com a lideran\xE7a de cada uma"
    },
    /**
     * As etiquetas do setor (07/10/2026) — a lista que quem é dele põe nos
     * cards. Ler é `platform.departments:ver`; criar, renomear, trocar a cor e
     * arquivar é `:editar`, como o resto do cadastro do setor.
     */
    listDepartmentLabels: {
      method: "GET",
      path: "/departments/:id/labels",
      pathParams: z4.object({ id: z4.string() }),
      query: z4.object({ includeArchived: z4.enum(["true"]).optional() }),
      responses: { 200: z4.array(labelSchema), ...defaultErrors },
      summary: "As etiquetas de um departamento"
    },
    /** 409 `nome_repetido` quando o setor já tem uma ativa com esse nome. */
    createDepartmentLabel: {
      method: "POST",
      path: "/departments/:id/labels",
      pathParams: z4.object({ id: z4.string() }),
      body: createLabelSchema,
      responses: { 201: labelSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Cria uma etiqueta no departamento"
    },
    updateDepartmentLabel: {
      method: "PATCH",
      path: "/departments/:id/labels/:labelId",
      pathParams: z4.object({ id: z4.string(), labelId: z4.coerce.number().int() }),
      body: updateLabelSchema,
      responses: { 200: labelSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Renomeia, troca a cor, reordena ou arquiva uma etiqueta do departamento"
    },
    /** catálogo de recurso × ação declarado pelos módulos */
    catalogPermissions: {
      method: "GET",
      path: "/permissions",
      responses: {
        200: z4.array(
          z4.object({
            resource: z4.string(),
            label: z4.string(),
            actions: z4.array(z4.object({ action: z4.string(), label: z4.string() }))
          })
        ),
        ...defaultErrors
      },
      summary: "Cat\xE1logo de permiss\xF5es dispon\xEDvel"
    },
    listMySessions: {
      method: "GET",
      path: "/sessions",
      responses: { 200: z4.array(sessionSchema), ...defaultErrors },
      summary: "Sess\xF5es ativas do pr\xF3prio usu\xE1rio"
    },
    revokeSession: {
      method: "DELETE",
      path: "/sessions/:id",
      pathParams: z4.object({ id: z4.string() }),
      body: z4.void(),
      responses: { 204: z4.null(), ...defaultErrors },
      summary: "Encerra uma sess\xE3o remotamente"
    },
    /**
     * As últimas notificações da PRÓPRIA sessão — sem parâmetro de pessoa,
     * como `listMySessions`. Nunca marca como lida sozinha: o sino do menu
     * PRECISA reler o `readAt` de antes de mostrar a bolinha de "não lida", e
     * uma listagem que marca ao ler apagaria a própria resposta.
     */
    listMyNotifications: {
      method: "GET",
      path: "/notifications",
      query: z4.object({ limit: z4.coerce.number().int().min(1).max(50).default(20) }),
      responses: { 200: z4.array(notificationSchema), ...defaultErrors },
      summary: "\xDAltimas notifica\xE7\xF5es da pr\xF3pria sess\xE3o"
    },
    /**
     * Marca como lidas — chamado quando o menu ABRE, nunca ao listar. Não há
     * rota para marcar uma linha isolada: não existe ação de linha ainda
     * (nenhum clique leva a lugar nenhum, de propósito).
     */
    markNotificationsRead: {
      method: "POST",
      path: "/notifications/read",
      body: z4.void(),
      responses: { 204: z4.null(), ...defaultErrors },
      summary: "Marca as notifica\xE7\xF5es da pr\xF3pria sess\xE3o como lidas"
    },
    /**
     * Troca a própria senha, estando logado (30/10/2026).
     *
     * Exige a atual: sem ela, uma aba esquecida aberta troca a senha e tranca
     * o dono do lado de fora. As OUTRAS sessões caem; a desta aba fica.
     */
    changePassword: {
      method: "POST",
      path: "/password/change",
      body: z4.object({
        passwordCurrent: z4.string().min(1).max(200),
        passwordNew: z4.string().min(MIN_PASSWORD_LENGTH).max(200)
      }),
      responses: { 200: z4.object({ message: z4.string() }), ...defaultErrors },
      summary: "Troca a pr\xF3pria senha"
    },
    /* --- senha esquecida: pública, e as duas respondem igual ------------ */
    /**
     * Manda o link de redefinição ao e-mail ATUAL do cadastro, se houver.
     *
     * **200 sempre, com o mesmo corpo**, exista ou não a pessoa: uma rota que
     * dissesse "não achamos esse e-mail" seria a lista de quem trabalha na casa.
     */
    recoverPassword: {
      method: "POST",
      path: "/password/recover",
      body: z4.object({ email: z4.string().trim().email().max(200) }),
      responses: {
        200: z4.object({ message: z4.string() }),
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Envia o link de redefini\xE7\xE3o de senha do colaborador"
    },
    /** Redefine pelo token do e-mail. Inválido, vencido e usado: a mesma recusa. */
    resetPassword: {
      method: "POST",
      path: "/password/reset",
      body: z4.object({
        token: z4.string().min(16).max(200),
        password: z4.string().min(MIN_PASSWORD_LENGTH).max(200)
      }),
      responses: {
        200: z4.object({ message: z4.string() }),
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Redefine a senha do colaborador pelo link do e-mail"
    },
    /* --- primeiro acesso: público, e sem enumeração --------------------- */
    /**
     * Confere o convite e devolve o que a tela precisa mostrar.
     *
     * **Uma resposta só para todas as recusas.** Expirado, já aceito, arquivado
     * e inexistente devolvem o mesmo 400 — porque a diferença entre eles é uma
     * lista de quem trabalha na casa, consultável por quem tiver um token
     * qualquer. Quem tem o convite legítimo vê o próprio nome; quem não tem, vê
     * sempre a mesma frase.
     */
    validateFirstAccess: {
      method: "POST",
      path: "/first-access/validate",
      body: z4.object({ token: z4.string().min(16).max(200) }),
      responses: {
        200: validUserInviteSchema,
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Confere o convite de primeiro acesso do colaborador"
    },
    /**
     * Define a senha e conclui o primeiro acesso.
     *
     * Devolve o **e-mail inteiro**, e só aqui: quem chega neste ponto acabou de
     * definir a senha com um token válido, demonstrou deter a conta, e o
     * endereço dela deixou de ser informação que se protege dele. É o que
     * permite a tela entrar em seguida em vez de mandar a pessoa digitar, no
     * login, o que ela digitou há um segundo.
     */
    completeFirstAccess: {
      method: "POST",
      path: "/first-access/complete",
      body: z4.object({
        token: z4.string().min(16).max(200),
        password: z4.string().min(MIN_PASSWORD_LENGTH).max(200)
      }),
      responses: {
        200: z4.object({ email: z4.string() }),
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Define a senha e conclui o primeiro acesso do colaborador"
    }
  },
  { pathPrefix: "/platform" }
);

// src/billing.ts
var c3 = initContract3();
var providerSchema = z5.string().min(1).max(40);
var BILLING_CHANNELS = ["email", "whatsapp"];
var billingChannelSchema = z5.enum(BILLING_CHANNELS);
var SENDABLE_CHANNELS = ["email"];
var discardReasonSchema = z5.enum(DISPATCH_DISCARD_REASONS);
var localTimeSchema = z5.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/);
var memberContactSchema = z5.object({
  /** e-mail normalizado ou telefone só com dígitos; nulo é "o espelho não tem" */
  value: z5.string().nullable(),
  /**
   * `true` quando este contato está bloqueado em `app.contact_blocks`.
   *
   * O bloqueio vale para **toda base e todo canal** (regra 15): a tabela não tem
   * `tenant_id`, e a ausência é a decisão. Um contato bloqueado na CE está
   * bloqueado na GO.
   */
  blocked: z5.boolean()
});
var boletoMemberSchema = z5.discriminatedUnion("estado", [
  z5.object({
    /** esta praça nunca sincronizou associados — não é "o associado não existe" */
    estado: z5.literal("sem_espelho"),
    code: z5.string()
  }),
  z5.object({
    /** o espelho desta praça respondeu, e este código não está nele */
    estado: z5.literal("nao_encontrado"),
    code: z5.string()
  }),
  z5.object({
    estado: z5.literal("encontrado"),
    code: z5.string(),
    nome: z5.string().nullable(),
    maskedCpf: z5.string().nullable(),
    telefone: memberContactSchema,
    email: memberContactSchema,
    /** quando o espelho leu isto do ERP — a tela mostra "atualizado há X" */
    syncedAt: instantSchema
  })
]);
var boletoVehicleSchema = z5.discriminatedUnion("estado", [
  z5.object({ estado: z5.literal("sem_espelho"), code: z5.string() }),
  z5.object({ estado: z5.literal("nao_encontrado"), code: z5.string() }),
  z5.object({
    estado: z5.literal("encontrado"),
    code: z5.string(),
    /** maiúscula, sem hífen e sem espaço (`normalizePlate`); nula é veículo sem placa */
    placa: z5.string().nullable(),
    modelo: z5.string().nullable(),
    syncedAt: instantSchema
  })
]);
var boletoStatusSchema = z5.discriminatedUnion("estado", [
  z5.object({
    estado: z5.literal("nao_classificada"),
    code: z5.string(),
    /** a descrição do fornecedor; nula quando o espelho não tem este código */
    descricao: z5.string().nullable()
  }),
  z5.object({
    estado: z5.literal("classificada"),
    code: z5.string(),
    descricao: z5.string().nullable(),
    pago: z5.boolean(),
    canceled: z5.boolean()
  })
]);
var ladderRangeSchema = z5.object({
  step: z5.string(),
  order: z5.number().int(),
  /** negativo é antes do vencimento; o intervalo é fechado nos dois lados */
  minDay: z5.number().int(),
  /** nulo é a faixa final, sem teto */
  maxDay: z5.number().int().nullable(),
  /** vazio é "esta etapa não envia" — é o estado da faixa final do legado */
  channels: z5.array(billingChannelSchema),
  /** hora local da praça — ver `localTimeSchema`; nulo é "sem janela declarada" */
  windowStart: localTimeSchema.nullable(),
  windowEnd: localTimeSchema.nullable()
});
var billingLadderSchema = z5.discriminatedUnion("estado", [
  z5.object({
    /** nenhuma linha em `billing_tiers` nesta base */
    estado: z5.literal("sem_regua")
  }),
  z5.object({
    estado: z5.literal("configurada"),
    tiers: z5.array(ladderRangeSchema)
  })
]);
var deskBoletoSchema = z5.object({
  provider: providerSchema,
  /** o identificador daquele boleto NO provedor; com `provider`, é a identidade */
  idExterno: z5.string(),
  value: moneySchema,
  /** o que entrou; nulo enquanto não entrou nada */
  valuePaid: moneySchema.nullable(),
  /** a data que rege a régua — não muda quando alguém prorroga */
  dueDateOriginal: businessDateSchema,
  /** a corrente; igual à de cima até alguém prorrogar */
  dueDate: businessDateSchema,
  paidAt: businessDateSchema.nullable(),
  canceledAt: instantSchema.nullable(),
  situacao: boletoStatusSchema,
  member: boletoMemberSchema,
  vehicle: boletoVehicleSchema
});
var operatorSchema = z5.object({ id: z5.string(), nome: z5.string() });
var agreementProofSchema = z5.discriminatedUnion("estado", [
  z5.object({ estado: z5.literal("ausente") }),
  z5.object({ estado: z5.literal("guardado"), storedAt: instantSchema })
]);
var proofUrlSchema = z5.object({
  url: z5.string(),
  expiresAtSeconds: z5.number().int()
});
var billingCaseSchema = z5.object({
  id: z5.number().int(),
  /**
   * O boleto deste atendimento. **Nulo é o cartão receptivo**: o associado ligou,
   * a operação não achou o boleto e abriu o cartão pela placa — é o
   * `check (boleto_id ou placa_manual)` do banco visto daqui.
   */
  boleto: deskBoletoSchema.nullable(),
  /** a placa digitada à mão quando não há boleto; normalizada por `@evogard/domain` */
  plateManual: z5.string().nullable(),
  operator: operatorSchema.nullable(),
  assignedAt: instantSchema.nullable(),
  /** texto, não enum — ver acima */
  outcome: z5.string().nullable(),
  note: z5.string().nullable(),
  /** o que ficou combinado; sem `negotiatedAmount` o acordo é palavra */
  negotiatedAmount: moneySchema.nullable(),
  /** a data combinada — `date`, porque dia 10 é dia 10 em Fortaleza e em Goiânia */
  dueAt: businessDateSchema.nullable(),
  receipt: agreementProofSchema,
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});
var deskCardSchema = z5.object({
  serviceCase: billingCaseSchema,
  /** a `etapa` de uma faixa daquela base; nula é "fora da régua" */
  step: z5.string().nullable(),
  /** negativo é antes do vencimento; nulo é cartão sem boleto */
  delayDays: z5.number().int().nullable()
});
var deskGroupSchema = z5.object({
  total: z5.number().int(),
  /** a soma dos boletos do grupo; nula quando o grupo é de cartões sem boleto */
  value: moneySchema.nullable(),
  cards: z5.array(deskCardSchema)
});
var deskColumnSchema = deskGroupSchema.extend({
  /** a `etapa` da faixa, que é a identidade da coluna */
  step: z5.string(),
  order: z5.number().int(),
  minDay: z5.number().int(),
  /**
   * Nulo é a **faixa final, sem teto** — o `> 60` da esteira legada, e a forma
   * normal da última etapa de qualquer régua.
   *
   * Era `z.number().int()` e não podia ser: `billing_tiers.max_days` é anulável
   * de propósito, e `groupDesk` em `billing.service.ts` derrubava a mesa
   * inteira (**500**) ao encontrar a linha sem teto. O defeito era inalcançável
   * enquanto a régua nascia vazia e virou alcançável na primeira régua carregada
   * — o que aconteceu com a massa de `make db-demo` antes de `cobranca-03`
   * chegar, que é a ordem boa para descobrir isso.
   *
   * A tela já sabia: `dayRangeText` imprime "A partir de N dias de atraso"
   * para o nulo desde que existe.
   */
  maxDay: z5.number().int().nullable()
});
var deskSchema = z5.discriminatedUnion("estado", [
  z5.object({
    /** nenhuma faixa nesta base: não há coluna para agrupar */
    estado: z5.literal("sem_regua"),
    ladderOutside: deskGroupSchema,
    withoutBoleto: deskGroupSchema
  }),
  z5.object({
    estado: z5.literal("montada"),
    /** uma coluna por faixa da base, na ordem da régua, **inclusive as vazias** */
    columns: z5.array(deskColumnSchema),
    ladderOutside: deskGroupSchema,
    withoutBoleto: deskGroupSchema
  })
]);
var caseActionSchema = z5.discriminatedUnion("action", [
  z5.object({
    action: z5.literal("registrar_acordo"),
    /** texto na forma da coluna `numeric(12,2)` — ver `moneySchema` */
    negotiatedAmount: moneySchema,
    dueAt: businessDateSchema,
    /**
     * Obrigatório e anulável, sem `default`. Omitir viraria "a casa registrou
     * que não houve observação", que é uma afirmação sobre a negociação.
     */
    note: z5.string().max(2e3).nullable()
  }),
  z5.object({
    action: z5.literal("anotar"),
    note: z5.string().min(1).max(2e3)
  }),
  z5.object({
    action: z5.literal("encerrar"),
    /** texto, não enum — a lista real está no Kanban e não foi lida (regra 14) */
    outcome: z5.string().min(1).max(60),
    note: z5.string().max(2e3).nullable()
  })
]);
var pipelinePauseSchema = z5.discriminatedUnion("estado", [
  z5.object({ estado: z5.literal("ativa") }),
  z5.object({
    estado: z5.literal("pausada"),
    since: z5.string(),
    /** quem pausou; nulo quando foi o sistema */
    by: z5.string().nullable(),
    reason: z5.string()
  })
]);
var lastIntakeSchema = z5.discriminatedUnion("estado", [
  z5.object({ estado: z5.literal("nunca") }),
  z5.object({
    estado: z5.literal("medida"),
    at: z5.string(),
    boletos: z5.number().int()
  })
]);
var discardSchema = z5.object({
  reason: discardReasonSchema,
  count: z5.number().int()
});
var pipelineStageSchema = z5.object({
  step: z5.string(),
  order: z5.number().int(),
  channels: z5.array(billingChannelSchema),
  /** nulas juntas quando a faixa não declarou janela — ver `ladderRangeSchema` */
  windowStart: localTimeSchema.nullable(),
  windowEnd: localTimeSchema.nullable(),
  /** quantos passariam por `canDispatch` agora */
  sendable: z5.number().int(),
  /** uma entrada por motivo que ocorreu — motivo sem ocorrência não aparece */
  discards: z5.array(discardSchema)
});
var pipelinePreviewSchema = z5.discriminatedUnion("estado", [
  z5.object({
    estado: z5.literal("sem_regua"),
    /** todo boleto em aberto, porque não há faixa em que ele caiba */
    ladderOutside: z5.number().int()
  }),
  z5.object({
    estado: z5.literal("montada"),
    /** uma entrada por faixa, na ordem da régua, inclusive as zeradas */
    byStep: z5.array(pipelineStageSchema),
    ladderOutside: z5.number().int()
  })
]);
var pipelineSchema = z5.object({
  pause: pipelinePauseSchema,
  intake: lastIntakeSchema,
  preview: pipelinePreviewSchema
});
var boletoTotalSchema = z5.object({
  amount: z5.number().int(),
  value: moneySchema
});
var billingNumbersSchema = z5.discriminatedUnion("estado", [
  z5.object({
    /** a coleta nunca rodou nesta base — não há o que contar, e zero seria mentira */
    estado: z5.literal("sem_coleta")
  }),
  z5.object({
    estado: z5.literal("medido"),
    /** quando a coleta que produziu estes números rodou */
    collectedAt: instantSchema,
    atOpen: boletoTotalSchema,
    overdue: boletoTotalSchema,
    receivedNoMonth: boletoTotalSchema,
    /**
     * A quebra por etapa — união própria, porque a régua pode não existir e as
     * três medidas acima existem de qualquer jeito.
     */
    byStep: z5.discriminatedUnion("estado", [
      z5.object({ estado: z5.literal("sem_regua") }),
      z5.object({
        estado: z5.literal("medido"),
        steps: z5.array(
          z5.object({
            step: z5.string(),
            order: z5.number().int(),
            amount: z5.number().int(),
            value: moneySchema
          })
        ),
        /** o que não coube em faixa nenhuma — ver `foraDaRegua` na mesa */
        ladderOutside: boletoTotalSchema
      })
    ])
  })
]);
var boletoCopySchema = z5.discriminatedUnion("estado", [
  z5.object({
    /** não temos nem a linha nem o link — a tela diz isso, e não some com o boleto */
    estado: z5.literal("indisponivel")
  }),
  z5.object({
    estado: z5.literal("com_linha"),
    /** a linha digitável, para copiar */
    linhaDigitavel: z5.string(),
    url: z5.string().nullable()
  }),
  z5.object({
    /** o provedor só nos deu um link */
    estado: z5.literal("somente_link"),
    url: z5.string()
  })
]);
var memberBoletoSchema = z5.object({
  /** `BO-000123` — o identificador da casa, e o que leva à ficha */
  code: z5.string(),
  /** competência `MM/AAAA`, como o espelho a guarda; nula quando não veio */
  period: z5.string().nullable(),
  value: moneySchema,
  /** a corrente — é a data que a pessoa precisa cumprir */
  dueDate: businessDateSchema,
  paidAt: businessDateSchema.nullable(),
  valuePaid: moneySchema.nullable(),
  /**
   * O que a casa pode afirmar sobre este boleto.
   *
   * `nao_classificada` é o estado de hoje para todos, e a tela do associado
   * **não pode** traduzi-lo em "em aberto": ela mostra a data e o valor e diz
   * que a confirmação de pagamento pode levar até a próxima atualização. Dizer
   * "em atraso" a quem pagou ontem é a pior mensagem que este portal tem.
   */
  situacao: boletoStatusSchema,
  duplicate: boletoCopySchema,
  /** a placa do veículo daquele boleto, para a pessoa reconhecer qual é */
  placa: z5.string().nullable()
});
var paymentSourceSchema = z5.enum(["coleta", "erp"]);
var boletoVehicleLabelSchema = z5.object({
  placa: z5.string().nullable(),
  modelo: z5.string().nullable()
});
var memberBoletoDetailSchema = z5.object({
  code: z5.string(),
  /** competência `MM/AAAA` */
  period: z5.string().nullable(),
  /** `descricao_tipo_boleto` — mensalidade, taxa, acordo… */
  boletoType: z5.string().nullable(),
  /** o identificador do boleto no banco; é o que a pessoa vê no extrato */
  nossoNumero: z5.string().nullable(),
  value: moneySchema,
  /** `tarifa_cobranca_banco` — o que o banco cobrou para processar */
  bankFee: moneySchema.nullable(),
  issuedOn: businessDateSchema.nullable(),
  /**
   * O vencimento como ele NASCEU, ao lado do corrente.
   *
   * Os dois juntos são a única coisa que a casa sabe dizer sobre alteração
   * deste boleto: o SGA não manda histórico de alteração de boleto — nenhum dos
   * 42 campos da resposta o traz. Quando os dois diferem, houve prorrogação, e
   * são 140.524 assim na CE. A tela diz isso com as duas datas, e não inventa
   * um "alterado em".
   */
  originalDueDate: businessDateSchema,
  dueDate: businessDateSchema,
  paidAt: businessDateSchema.nullable(),
  valuePaid: moneySchema.nullable(),
  /** `null` quando não há pagamento nenhum registrado. Ver `paymentSourceSchema`. */
  paymentSource: paymentSourceSchema.nullable(),
  /** `data_credito_banco` — quando o dinheiro entrou, que não é a data do pagamento */
  bankCreditDate: businessDateSchema.nullable(),
  /** `descricao_forma_pagamento` */
  paymentMethod: z5.string().nullable(),
  /** `descricao_tipo_baixa_boleto` */
  settlementType: z5.string().nullable(),
  /** `descricao_tipo_cobranca_recorrente` */
  recurringType: z5.string().nullable(),
  installmentPaid: z5.string().nullable(),
  installmentCount: z5.string().nullable(),
  bankName: z5.string().nullable(),
  bankCode: z5.string().nullable(),
  agency: z5.string().nullable(),
  account: z5.string().nullable(),
  situacao: boletoStatusSchema,
  duplicate: boletoCopySchema,
  /** os veículos daquele boleto — pode ser mais de um (rateio), e pode ser nenhum (taxa) */
  vehicles: z5.array(boletoVehicleLabelSchema)
});
var memberBoletoDetailResponseSchema = z5.discriminatedUnion("estado", [
  z5.object({ estado: z5.literal("nao_encontrado") }),
  z5.object({ estado: z5.literal("lida"), boleto: memberBoletoDetailSchema })
]);
var memberBoletosSchema = z5.discriminatedUnion("estado", [
  z5.object({ estado: z5.literal("sem_vinculo") }),
  z5.object({ estado: z5.literal("sem_coleta") }),
  z5.object({
    estado: z5.literal("lida"),
    /** do mais recente para o mais antigo; a ordenação é do servidor */
    boletos: z5.array(memberBoletoSchema)
  })
]);
var WEBHOOK_REFUSAL = {
  code: "assinatura_invalida",
  message: "N\xE3o foi poss\xEDvel aceitar este evento."
};
var webhookRefusalSchema = z5.strictObject({
  code: z5.literal(WEBHOOK_REFUSAL.code),
  message: z5.literal(WEBHOOK_REFUSAL.message)
});
var webhookAcceptanceSchema = z5.strictObject({ received: z5.literal(true) });
var billingContract = c3.router(
  {
    /**
     * A mesa: os atendimentos da base, agrupados pela etapa calculada.
     *
     * Ela lista **atendimentos**, não boletos, e essa é a inversão do módulo —
     * ver `billingCaseSchema`.
     *
     * **Nenhuma rota deste contrato CRIA atendimento**, e isso está escrito aqui
     * porque some do plano se não estiver: `POST /billing/service-cases/:id/...`
     * exige um `:id` que precisa ter nascido em algum lugar. Ou a coleta
     * (`cobranca-17`) abre um atendimento por boleto que entra na régua, ou
     * nasce um `POST /billing/service-cases` para o cartão receptivo (o do
     * `manual_plate`, que hoje nenhuma rota consegue criar). Quem escrever
     * `cobranca-15` decide e escreve a decisão ali. O que este arquivo assume é
     * só isto: **quando a mesa mostra um cartão, ele tem `id` de atendimento**.
     */
    desk: {
      method: "GET",
      path: "/desk",
      query: z5.object({
        /** a `etapa` de uma faixa daquela base, nunca um enum do código */
        step: z5.string().min(1).max(60).optional(),
        /**
         * Filtra por quem está com o cartão. "Sem operador" **não** é
         * expressável com um filtro opcional — é o mesmo limite que
         * `assigneeId` tem em `retention.listar` —, e a tela não precisa
         * dele: o cartão já diz quem é o operador, e a marca de "outro operador
         * está nisto" é o que a coluna mostra.
         */
        operatorId: z5.string().optional(),
        /**
         * Quantos cartões cada grupo traz. O quadro não é paginado: paginar um
         * Kanban por página global embaralha as colunas, e por coluna exigiria
         * um cursor por coluna. Cada grupo devolve `total` ao lado dos cartões,
         * então a tela diz "50 de 312" em vez de contar o array.
         */
        byGroup: z5.coerce.number().int().min(1).max(200).default(50),
        /**
         * Encerrar arquiva — é o que solta o boleto para um próximo atendimento.
         * Fora por omissão; a mesa é fila de trabalho, não histórico. Com
         * `true`, é por aqui que se acha o atendimento a reabrir.
         *
         * `z.enum` e não `z.coerce.boolean()`: na query string tudo é string, e
         * `Boolean('false')` é `true`. É a **quinta** ocorrência da mesma
         * armadilha no pacote (`listStages` e `listReasons` em `retention.ts`,
         * `listCategories` em `tickets.ts`, `listClassifications` em
         * `erp.ts`), e continua escrita à mão porque um helper compartilhado é
         * mudança de `common.ts`, que não é desta tarefa.
         */
        includeArchived: z5.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: deskSchema, ...defaultErrors },
      summary: "A mesa de cobran\xE7a da base atual, por etapa calculada"
    },
    /**
     * Pega o cartão, passa adiante ou solta.
     *
     * `null` desatribui — "ninguém pegou" é um estado, não a ausência de um
     * campo. E é este campo que a esteira consulta: **com operador atribuído,
     * `canDispatch` recusa com `atendimento_com_operador`**. É assim que a não
     * colisão deixa de ser acordo entre times e vira consulta.
     *
     * `canDispatch` não devolve booleano: devolve a decisão com o motivo
     * (`DispatchDecision`, em `@evogard/domain/cobranca`), porque a tela da
     * esteira existe para responder POR QUÊ um boleto não saiu — e um booleano
     * responde "não" sem responder isso.
     */
    assign: {
      method: "POST",
      path: "/service-cases/:id/assign",
      pathParams: z5.object({ id: z5.coerce.number().int() }),
      body: z5.object({ operatorId: z5.string().nullable() }),
      responses: { 200: deskCardSchema, ...defaultErrors },
      summary: "Atribui, transfere ou solta o atendimento"
    },
    /** registrar acordo, anotar ou encerrar — ver `caseActionSchema` */
    handle: {
      method: "PATCH",
      path: "/service-cases/:id",
      pathParams: z5.object({ id: z5.coerce.number().int() }),
      body: caseActionSchema,
      responses: { 200: deskCardSchema, ...defaultErrors },
      summary: "Registra acordo, anota ou encerra o atendimento"
    },
    /**
     * Reabre um atendimento encerrado — limpa `archived_at`.
     *
     * ## Por que esta rota existe, se `cobranca-12` não a lista
     *
     * Ela é a décima primeira rota interna, e a lista da tarefa tem dez. Mas
     * `cobranca-13` declara a ação `billing.desk:reabrir` e `cobranca-20` manda
     * o controller declarar `@Permissao('billing.desk','reabrir')` numa linha
     * — e `cobranca-23` percorre "atribuir → tratar → reabrir" no e2e. Sem rota,
     * a ação fica no catálogo sem nada que a exija: a tela de grupos oferece um
     * poder que não faz nada, e `registry.test.ts` reprova (é para isso que o
     * `WITHOUT_ROUTE_YET` dele existe).
     *
     * A alternativa era pôr `reabrir` na união do `PATCH`, e aí `tratar` e
     * `reabrir` teriam a mesma permissão — a distinção que o plano chama de
     * proposital viraria uma linha que nenhum guard aplica.
     *
     * **Pode ser recusada**, e o motivo precisa aparecer na mensagem: se outro
     * atendimento aberto já ocupou aquele boleto, o único parcial
     * `(tenant_id, boleto_id) where arquivado_em is null` recusa a reabertura.
     * É `ErroApi.invalido` com a explicação, nunca um 500 genérico — o 500 faz a
     * pessoa achar que o sistema quebrou quando ele é que está defendendo a
     * regra.
     */
    reopen: {
      method: "POST",
      path: "/service-cases/:id/reopen",
      pathParams: z5.object({ id: z5.coerce.number().int() }),
      body: z5.object({}).optional(),
      responses: { 200: deskCardSchema, ...defaultErrors },
      summary: "Reabre um atendimento encerrado"
    },
    /**
     * O opt-out, registrado onde ele é ouvido: no atendimento.
     *
     * Sem esta rota `app.contact_blocks` nasce vazia e permanece vazia — a
     * plataforma construiu a tabela, o serviço e a tela de manutenção, mas quem
     * encontra o pedido de "não me mande mais mensagem" é o operador, no
     * telefone. Hoje a esteira não tem lista de contato nenhuma e o Kanban não
     * tem nada equivalente: é a assimetria de proteção que morre aqui.
     *
     * **Não recebe `origem`**, e a ausência é a decisão. Pela mesa só existe uma
     * origem possível: `pedido_do_titular`, que é direito da pessoa e não se
     * desfaz por conveniência. `retorno_invalido` é fato do provedor, gravado
     * pelo job que lê o retorno, e "some quando o contato é corrigido" — deixar
     * a mesa escolher entre os dois é deixar um clique transformar um direito em
     * dado operacional.
     *
     * O contato vai **cru** no corpo: quem normaliza é o servidor (`digitsOnly`
     * para telefone, e-mail em minúscula, por `@evogard/domain`), porque uma
     * blacklist que não normaliza não bate com o destinatário que o disparo usa —
     * e aí ela existe, está preenchida, e não protege ninguém.
     *
     * A resposta é o `contactBlockSchema` **da plataforma**, importado e não
     * recriado: a tabela é dela, e duas formas do mesmo registro divergem no dia
     * em que uma das duas ganhar um campo.
     */
    blockContact: {
      method: "POST",
      path: "/service-cases/:id/block-contact",
      pathParams: z5.object({ id: z5.coerce.number().int() }),
      body: z5.object({
        tipo: z5.enum(["email", "telefone"]),
        /** cru: quem normaliza é o servidor, e é ele quem recusa o indiscável */
        contact: z5.string().min(1).max(255),
        reason: z5.string().max(500).nullable()
      }),
      responses: { 201: contactBlockSchema, ...defaultErrors },
      summary: "Registra o pedido de n\xE3o contatar \u2014 vale para todas as bases"
    },
    /**
     * O comprovante do acordo.
     *
     * O mesmo padrão do anexo de demanda e do documento de cancelamento, e por
     * isso o armazenamento é de `common/` e não deste módulo. Só a chave vai para
     * `cobranca_atendimentos.comprovante_chave`; o binário nunca passa pelo
     * banco, e a chave nunca sai em resposta nenhuma.
     *
     * O acordo deixa de ser palavra.
     */
    attachReceipt: {
      method: "POST",
      path: "/service-cases/:id/proof",
      pathParams: z5.object({ id: z5.coerce.number().int() }),
      body: z5.object({
        nome: z5.string().min(1).max(255),
        typeMime: z5.string().min(3).max(160),
        /** o arquivo em base64. O servidor confere tamanho, tipo e assinatura. */
        content: z5.string().min(1)
      }),
      responses: { 201: deskCardSchema, ...defaultErrors },
      summary: "Anexa o comprovante do acordo ao atendimento"
    },
    /**
     * A URL assinada do comprovante, emitida no clique.
     *
     * ## Por que esta rota existe, se `cobranca-12` não a lista
     *
     * É a décima segunda rota interna, e a segunda que a lista da tarefa não
     * tem. Sem ela o comprovante é gravado e **nunca mais é lido**: o schema do
     * atendimento diz `guardado` e não há de onde tirar a URL depois de recarregar
     * a página. Um acordo cujo comprovante ninguém consegue abrir não deixa de
     * ser palavra, que é a entrega inteira de `cobranca-15b` — e a tela teria de
     * mostrar "comprovante guardado" ao lado de nada, que é a família de defeito
     * que este repositório persegue.
     *
     * É também a pendência que o `CLAUDE.md` registra sobre o anexo do portal em
     * demandas, resolvida aqui na declaração em vez de descoberta depois.
     *
     * `/url` no fim, e não o próprio arquivo: a API nunca serve o objeto. A
     * assinatura só é emitida depois de conferir que o atendimento é do tenant
     * do contexto — comprovante de outra base responde **404**, não 403, porque
     * 403 já confirmaria que ele existe.
     *
     * Ler o comprovante é `billing.desk:ver`, e não `tratar`: quem enxerga a
     * mesa enxerga o acordo que foi registrado nela.
     */
    receiptUrl: {
      method: "GET",
      path: "/service-cases/:id/proof/url",
      pathParams: z5.object({ id: z5.coerce.number().int() }),
      responses: { 200: proofUrlSchema, ...defaultErrors },
      summary: "URL assinada de curta validade para o comprovante"
    },
    /**
     * A esteira deixa de ser caixa-preta.
     *
     * O motivo de cada não-disparo é o que hoje ninguém responde sem abrir log
     * de dois sistemas, e é o valor central desta tela. Os motivos são
     * vocabulário fechado, vindo do domínio — ver `DISPATCH_DISCARD_REASONS`.
     */
    pipeline: {
      method: "GET",
      path: "/pipeline",
      responses: { 200: pipelineSchema, ...defaultErrors },
      summary: "O que a esteira vai disparar hoje, e o que n\xE3o vai e por qu\xEA"
    },
    /**
     * Pausa e retoma a esteira.
     *
     * **Retomar mora no mesmo endereço**, e é deliberado: é o mesmo poder (quem
     * pode parar a cobrança da praça pode voltar a ligá-la), então é a mesma
     * permissão, e uma rota `/retomar` separada seria uma permissão a mais no
     * catálogo para a mesma decisão. Uma pausa que só uma rota sabe desfazer é
     * pior do que o verbo na URL.
     *
     * O ato é explícito pela união, pelo mesmo motivo do `PATCH` do atendimento:
     * `{ pausada: false }` e "não mexi nisso" seriam a mesma requisição.
     */
    pausePipeline: {
      method: "POST",
      path: "/pipeline/pause",
      body: z5.discriminatedUnion("action", [
        z5.object({
          action: z5.literal("pausar"),
          /** obrigatório — ver `pipelinePauseSchema` */
          reason: z5.string().min(3).max(300)
        }),
        z5.object({ action: z5.literal("retomar") })
      ]),
      responses: { 200: pipelinePauseSchema, ...defaultErrors },
      summary: "Pausa ou retoma a esteira da base atual"
    },
    tiers: {
      method: "GET",
      path: "/tiers",
      responses: { 200: billingLadderSchema, ...defaultErrors },
      summary: "A r\xE9gua de cobran\xE7a da base atual"
    },
    /**
     * Salva a **régua inteira**, e não faixa a faixa.
     *
     * É o único `PUT` de coleção do contrato da casa que realmente quer dizer
     * "substitua a coleção" — e a razão não é gosto: **sobreposição só se avalia
     * com o conjunto**. Validar pedaço a pedaço é impossível (mover uma faixa
     * para o lugar de outra exige que as duas mudem no mesmo instante), e o
     * `EXCLUDE gist` do banco avalia exatamente o conjunto.
     *
     * Por isso ele é o oposto de `editStage` em `retention.ts` e de
     * `defineClassification` em `erp.ts`, que gravam um item por vez para duas
     * abas não apagarem o trabalho uma da outra. Aqui esse risco existe e é
     * aceito conscientemente: a alternativa é uma régua que o banco recusa por
     * inteiro a cada edição parcial.
     *
     * **`min(1)`**: a régua vazia é o estado de nascença, não um destino. Um
     * `{ faixas: [] }` desligaria a cobrança da praça inteira — e é exatamente o
     * corpo que uma tela envia quando o estado dela ainda não carregou. Apagar a
     * régua não tem rota, de propósito.
     *
     * A resposta é a lista, e não a união de `faixas`: depois de um `PUT` bem
     * sucedido a régua existe, e declarar `sem_regua` aqui faria o tipo prometer
     * um estado que esta rota nunca devolve.
     *
     * **A sobreposição é recusada pelo BANCO**, e a mensagem nomeia as duas
     * etapas. Não reimplemente a validação em TypeScript "para dar um erro
     * melhor": duas verdades divergem, e a do banco é a que vale. A recusa é
     * `ErroApi.invalido` — que é **400**, e não 422: `cobranca-16` pede 422 e a
     * casa não tem esse status em lugar nenhum (`defaultErrors` vai até 404, e
     * `ErroApi.invalido`, que a própria tarefa manda usar, devolve 400).
     * Declarar 422 aqui seria declarar um status que nada produz.
     */
    saveLadder: {
      method: "PUT",
      path: "/tiers",
      body: z5.object({
        tiers: z5.array(
          z5.object({
            step: z5.string().min(1).max(60),
            order: z5.number().int().min(0).max(999),
            /** negativo é antes do vencimento; fechado nos dois lados */
            minDay: z5.number().int().min(-365).max(3650),
            /** nulo é a faixa final, sem teto — ver `ladderRangeSchema` */
            maxDay: z5.number().int().min(-365).max(3650).nullable(),
            /**
             * Pode declarar um canal que a casa ainda não envia — ver
             * `SENDABLE_CHANNELS`. **Vazio é aceito**, e é a etapa que não
             * envia: a faixa final da régua legada é exatamente essa, e o CSV
             * de `cobranca-03` a escreve com a palavra `nenhum` justamente
             * para que ela não se confunda com coluna perdida no export. Um
             * `min(1)` aqui recusaria salvar de volta a régua que a extração
             * trouxe — a coluna é `text[] not null`, e "sem canal" é conteúdo
             * válido dela.
             */
            channels: z5.array(billingChannelSchema),
            /** as duas juntas, ou as duas nulas — quem recusa é o `check` do banco */
            windowStart: localTimeSchema.nullable(),
            windowEnd: localTimeSchema.nullable()
          })
        ).min(1)
      }),
      responses: { 200: z5.array(ladderRangeSchema), ...defaultErrors },
      summary: "Substitui a r\xE9gua de cobran\xE7a da base atual"
    },
    numbers: {
      method: "GET",
      path: "/numbers",
      responses: { 200: billingNumbersSchema, ...defaultErrors },
      summary: "N\xFAmeros de cobran\xE7a para o painel do in\xEDcio"
    }
  },
  { pathPrefix: "/billing" }
);
var billingPortalContract = c3.router(
  {
    boletos: {
      method: "GET",
      path: "/billing/boletos",
      responses: { 200: memberBoletosSchema, ...defaultErrors },
      summary: "Os boletos do associado da sess\xE3o"
    },
    /*
     * A ficha. O `code` no caminho é o da CASA (`BO-000123`), e o escopo é por
     * PESSOA e não só por praça: `(tenant, código do ERP da sessão, code)`. Sem
     * o do meio, qualquer pessoa logada leria o boleto de qualquer outra.
     */
    boleto: {
      method: "GET",
      path: "/billing/boletos/:code",
      pathParams: z5.object({ code: z5.string() }),
      responses: { 200: memberBoletoDetailResponseSchema, ...defaultErrors },
      summary: "A ficha de um boleto do associado da sess\xE3o"
    }
  },
  { pathPrefix: "/portal" }
);
var billingWebhooksContract = c3.router(
  {
    payoff: {
      method: "POST",
      path: "/billing/:provider",
      pathParams: z5.object({ provider: providerSchema }),
      /*
       * `z.unknown()`: o corpo é do provedor, e o provedor não existe ainda.
       * Ver o cabeçalho desta seção — não troque por um schema tipado sem
       * resolver antes a verificação de assinatura sobre o corpo cru.
       */
      body: z5.unknown(),
      responses: {
        200: webhookAcceptanceSchema,
        /** assinatura inválida E provedor desconhecido. O mesmo objeto, sempre. */
        401: webhookRefusalSchema
      },
      summary: "Baixa de boleto por webhook do provedor"
    }
  },
  { pathPrefix: "/webhooks" }
);

// src/billing-desk.ts
import { initContract as initContract4 } from "@ts-rest/core";
import { z as z6 } from "zod";
import {
  DESK_AUTO_IMPORT_STATES,
  DESK_BANDS,
  DESK_FLOWS,
  DESK_IMPORT_DUE_DAYS,
  DESK_IMPORT_ORIGINS,
  DESK_PAID_OUTCOMES,
  LABEL_NAME_MAX as LABEL_NAME_MAX2
} from "@evogard/domain";
var c4 = initContract4();
var deskBandSchema = z6.enum(DESK_BANDS);
var deskFlowSchema = z6.enum(DESK_FLOWS);
var deskPaidOutcomeSchema = z6.enum(DESK_PAID_OUTCOMES);
var tenantParams = z6.object({ tenant: z6.string().min(1).max(40) });
var cardParams = tenantParams.extend({ id: z6.coerce.number().int().positive() });
var labelParams = tenantParams.extend({ id: z6.coerce.number().int().positive() });
var importParams = tenantParams.extend({ id: z6.coerce.number().int().positive() });
var importItemParams = importParams.extend({ itemId: z6.coerce.number().int().positive() });
var shortText = z6.string().trim().max(200);
var plateText = z6.string().trim().min(1).max(10).transform((v) => v.toUpperCase().replace(/[\s-]/g, ""));
var deskProofUploadSchema = z6.object({
  nome: z6.string().min(1).max(255),
  typeMime: z6.string().min(3).max(160),
  content: z6.string().min(1)
});
var deskPersonSchema = z6.object({ id: z6.string(), name: z6.string() });
var deskLabelSchema = z6.object({
  id: z6.number().int(),
  name: z6.string(),
  color: labelColorSchema
});
var deskLabelOptionSchema = deskLabelSchema.extend({ canEdit: z6.boolean() });
var deskLabelWriteSchema = z6.object({
  name: z6.string().trim().min(1, "D\xEA um nome \xE0 etiqueta.").max(LABEL_NAME_MAX2),
  color: labelColorSchema
});
var deskBoardCardSchema = z6.object({
  id: z6.number().int(),
  flow: deskFlowSchema,
  column: z6.string().nullable(),
  band: deskBandSchema.nullable(),
  operator: deskPersonSchema.nullable(),
  assignedAt: instantSchema.nullable(),
  /** Quem cadastrou à mão — o operador pode excluir o card que criou. */
  createdBy: z6.string().nullable(),
  name: z6.string().nullable(),
  document: z6.string().nullable(),
  phone: z6.string().nullable(),
  plates: z6.array(z6.string()),
  amount: moneySchema.nullable(),
  dueDate: businessDateSchema.nullable(),
  consultant: z6.string().nullable(),
  vehicle: z6.object({
    id: z6.number().int(),
    /** `VE-000123` — o identificador ditável da casa. */
    code: z6.string().nullable(),
    /** O veículo deixou de vir do ERP (`erp_missing_at`); o card continua apontando para ele. */
    missing: z6.boolean()
  }).nullable(),
  boleto: z6.object({
    id: z6.number().int(),
    code: z6.string(),
    nossoNumero: z6.string().nullable(),
    status: z6.string().nullable(),
    canceled: z6.boolean(),
    missing: z6.boolean(),
    /** Quando o SGA leu este boleto (`erp_read_at`) — não a hora da projeção. */
    syncedAt: instantSchema.nullable()
  }).nullable(),
  erpPaidOn: businessDateSchema.nullable(),
  conversationRef: z6.string().nullable(),
  note: z6.string().nullable(),
  contactedAt: instantSchema.nullable(),
  contactedToday: z6.boolean(),
  labels: z6.array(deskLabelSchema),
  outcome: z6.string().nullable(),
  closedAt: instantSchema.nullable(),
  hasProof: z6.boolean(),
  createdAt: instantSchema
});
var deskCardsPageSchema = z6.object({
  items: z6.array(deskBoardCardSchema),
  /** Cursor da próxima página; nulo quando acabou. */
  next: z6.string().nullable()
});
var deskEventSchema = z6.object({
  id: z6.string(),
  at: instantSchema,
  source: z6.enum(["mesa", "kanban"]),
  /** O tipo do evento da Mesa, ou `kanban`. */
  type: z6.string(),
  title: z6.string(),
  from: z6.string().nullable(),
  to: z6.string().nullable(),
  details: z6.string().nullable(),
  via: z6.string().nullable(),
  kind: z6.enum(["movimento", "edicao"]),
  actor: z6.string().nullable()
});
var deskCardDetailSchema = z6.object({
  card: deskBoardCardSchema,
  history: z6.array(deskEventSchema)
});
var deskOperatorSchema = z6.object({
  userId: z6.string(),
  name: z6.string(),
  activeFlow: z6.boolean(),
  receptiveFlow: z6.boolean(),
  /** Fora da lista, mas ainda com card aberto — a coluna aparece para quem distribui. */
  listed: z6.boolean()
});
var deskCanSchema = z6.object({
  view: z6.boolean(),
  assign: z6.boolean(),
  handle: z6.boolean(),
  reopen: z6.boolean(),
  create: z6.boolean(),
  receptive: z6.boolean(),
  distribute: z6.boolean(),
  credit: z6.boolean(),
  import: z6.boolean(),
  configure: z6.boolean(),
  /** `acompanhar`: o Aproveitamento e as Movimentações. */
  follow: z6.boolean()
});
var deskSummarySchema = z6.object({
  tenant: z6.object({ id: z6.number().int(), slug: z6.string(), name: z6.string() }),
  me: z6.string(),
  can: deskCanSchema,
  operators: z6.array(deskOperatorSchema),
  columns: z6.array(
    z6.object({ key: z6.string(), count: z6.number().int(), amount: moneySchema })
  ),
  /** O período dos pagos que entrou na contagem. */
  paidFrom: businessDateSchema,
  paidTo: businessDateSchema,
  /**
   * A leitura do SGA mais ANTIGA entre os boletos dos cards abertos (`erp_read_at`)
   * — o que a tela pode afirmar de todos eles. Nulo sem boleto lido.
   */
  syncedAt: instantSchema.nullable(),
  /** Cards cujo dono já expirou e o job ainda não tirou — ver `ownerExpired`. */
  agingPending: z6.number().int()
});
var boardFilters = {
  min: moneySchema.optional(),
  max: moneySchema.optional(),
  paidFrom: businessDateSchema.optional(),
  paidTo: businessDateSchema.optional()
};
var deskPlateLookupSchema = z6.object({
  kind: z6.enum(["cadastro", "fora", "ambigua"]),
  count: z6.number().int(),
  vehicle: z6.object({
    id: z6.number().int(),
    code: z6.string().nullable(),
    plate: z6.string(),
    memberName: z6.string().nullable(),
    consultant: z6.string().nullable()
  }).nullable()
});
var deskCreateCardSchema = z6.object({
  flow: deskFlowSchema,
  /**
   * Obrigatório só quando alguma placa NÃO está no cadastro — a placa que acha
   * o veículo leva o nome do associado de lá, e o digitado não é gravado nela.
   */
  name: z6.string().trim().max(200).nullable(),
  document: z6.string().trim().max(20).nullable(),
  phone: z6.string().trim().min(1).max(40),
  note: z6.string().trim().max(4e3).nullable(),
  conversationRef: shortText.nullable(),
  consultant: shortText.nullable(),
  /** Uma linha por placa — vira um card por placa, como no legado. */
  plates: z6.array(
    z6.object({
      plate: plateText,
      dueDate: businessDateSchema,
      amount: moneySchema
    })
  ).min(1).max(20)
});
var deskEditCardSchema = z6.object({
  note: z6.string().trim().max(4e3).nullable().optional(),
  phone: z6.string().trim().max(40).nullable().optional(),
  conversationRef: shortText.nullable().optional(),
  /* os do card manual — recusados no card com boleto */
  name: z6.string().trim().min(1).max(200).optional(),
  document: z6.string().trim().max(20).nullable().optional(),
  plate: plateText.optional(),
  dueDate: businessDateSchema.optional(),
  amount: moneySchema.optional(),
  consultant: shortText.nullable().optional()
});
var deskDistributeSchema = z6.object({
  /** De onde saem os cards: uma fila, ou a coluna de um operador. */
  column: z6.string().min(1).max(200),
  shares: z6.array(z6.object({ operatorId: z6.string().min(1), count: z6.number().int().min(0) })).min(1).max(50)
});
var deskDistributeResultSchema = z6.object({
  assigned: z6.number().int(),
  byOperator: z6.array(z6.object({ operatorId: z6.string(), count: z6.number().int() }))
});
var deskPaySchema = z6.discriminatedUnion("kind", [
  z6.object({ kind: z6.literal("pago_automatico") }),
  z6.object({
    kind: z6.literal("pago_cobranca"),
    proof: deskProofUploadSchema,
    operatorId: z6.string().min(1).nullable().optional()
  })
]);
var deskCreditSchema = z6.object({
  operatorId: z6.string().min(1),
  proof: deskProofUploadSchema.nullable()
});
var deskProofUrlSchema = z6.object({
  url: z6.string(),
  expiresAtSeconds: z6.number().int()
});
var deskOperatorsConfigSchema = z6.object({
  operators: z6.array(deskOperatorSchema),
  /** As pessoas com vínculo nesta base — quem pode entrar na lista. */
  candidates: z6.array(deskPersonSchema)
});
var deskSaveOperatorsSchema = z6.object({
  operators: z6.array(
    z6.object({
      userId: z6.string().min(1),
      activeFlow: z6.boolean(),
      receptiveFlow: z6.boolean()
    })
  ).max(100)
});
var deskSettingsSchema = z6.object({
  /*
   * Sem uso desde 27/10/2026: era o setor das etiquetas, e a Mesa passou a ter
   * as dela. Os dois campos ficam nesta versão para a interna de antes, que
   * ainda os lê durante o deploy, e saem com a coluna.
   */
  departmentId: z6.number().int().nullable(),
  departments: z6.array(z6.object({ id: z6.number().int(), name: z6.string() })),
  erpCheckEnabled: z6.boolean(),
  erpCheckIntervalHours: z6.number().int().min(1).max(24),
  erpCheckLastRunAt: instantSchema.nullable(),
  importExcludedCooperatives: z6.array(z6.string()),
  /** A faixa de valor do boleto que a importação aceita, as duas pontas inclusive. */
  importMinAmount: moneySchema,
  importMaxAmount: moneySchema,
  /** A importação automática no dia seguinte ao vencimento — nasce desligada. */
  importAutoEnabled: z6.boolean()
});
var deskSaveSettingsSchema = z6.object({
  /** Sem uso desde 27/10/2026 — ver `deskSettingsSchema`. A interna de agora não o manda. */
  departmentId: z6.number().int().nullable().optional(),
  erpCheckEnabled: z6.boolean(),
  erpCheckIntervalHours: z6.number().int().min(1).max(24),
  importExcludedCooperatives: z6.array(z6.string().trim().min(1).max(20)).max(100),
  importMinAmount: moneySchema,
  importMaxAmount: moneySchema,
  importAutoEnabled: z6.boolean()
});
var deskImportSummarySchema = z6.object({
  id: z6.number().int(),
  dueDate: businessDateSchema,
  /** A tela ou o job do dia seguinte ao vencimento. */
  origin: z6.enum(DESK_IMPORT_ORIGINS),
  openCount: z6.number().int(),
  filteredCount: z6.number().int(),
  /** Saíram pela faixa de valor. */
  discardedOutOfRange: z6.number().int(),
  /** Saíram por ter outro boleto em aberto de vencimento original anterior. */
  discardedEarlierOpen: z6.number().int(),
  /** A faixa de valor com que esta prévia foi gerada. */
  minAmount: moneySchema,
  maxAmount: moneySchema,
  eligibleCount: z6.number().int(),
  discardedWithoutVehicle: z6.number().int(),
  discardedVehicleStatus: z6.number().int(),
  discardedCooperative: z6.number().int(),
  committedAt: instantSchema.nullable(),
  committedBy: deskPersonSchema.nullable(),
  committedInserted: z6.number().int().nullable(),
  committedKept: z6.number().int().nullable(),
  createdAt: instantSchema,
  createdBy: deskPersonSchema.nullable()
});
var deskImportItemSchema = z6.object({
  id: z6.number().int(),
  boletoId: z6.number().int(),
  name: z6.string().nullable(),
  nossoNumero: z6.string().nullable(),
  amount: moneySchema.nullable(),
  plate: z6.string().nullable(),
  vehicleStatus: z6.string().nullable(),
  consultant: z6.string().nullable(),
  /** Já há card aberto para este boleto — a confirmação o mantém. */
  alreadyOnDesk: z6.boolean()
});
var deskImportDetailSchema = z6.object({
  import: deskImportSummarySchema,
  items: z6.array(deskImportItemSchema)
});
var deskAutoImportSchema = z6.object({
  state: z6.enum(DESK_AUTO_IMPORT_STATES),
  /** O vencimento que ela busca hoje — o de ontem —, ou nulo. */
  dueDate: businessDateSchema.nullable(),
  /** A releitura mais antiga de um boleto do vencimento: é ela que segura a importação. */
  readAt: instantSchema.nullable(),
  /** A prévia que já cobre o vencimento — a automática, ou a manual confirmada. */
  importId: z6.number().int().nullable()
});
var deskReconcileResultSchema = z6.object({
  closed: z6.number().int(),
  flagged: z6.number().int()
});
var deskTallySchema = z6.object({ count: z6.number().int(), amount: moneySchema });
var deskLoadColumnSchema = z6.union([deskBandSchema, z6.literal("receptiva")]);
var deskMovesQuerySchema = z6.object({
  q: z6.string().trim().max(100).optional(),
  kind: z6.enum(["movimentos", "edicoes", "tudo"]).default("movimentos"),
  from: businessDateSchema.optional(),
  to: businessDateSchema.optional(),
  cursor: z6.string().max(200).optional(),
  limit: z6.coerce.number().int().min(1).max(100).default(50)
});
var deskMoveRowSchema = deskEventSchema.extend({
  card: z6.object({
    id: z6.number().int(),
    name: z6.string().nullable(),
    nossoNumero: z6.string().nullable(),
    plates: z6.array(z6.string())
  })
});
var deskMovesPageSchema = z6.object({
  items: z6.array(deskMoveRowSchema),
  /** Cursor da próxima página; nulo quando acabou. */
  next: z6.string().nullable()
});
var deskDashboardQuerySchema = z6.object({
  from: businessDateSchema,
  to: businessDateSchema
});
var deskDashboardPersonSchema = z6.object({
  userId: z6.string(),
  name: z6.string(),
  /** Tem coluna na Mesa desta base — o "Somente ativos" do legado. */
  listed: z6.boolean(),
  activeFlow: z6.boolean(),
  receptiveFlow: z6.boolean(),
  paidCollection: deskTallySchema,
  paidAutomatic: deskTallySchema,
  /** Dos pagos cobrança, os do fluxo receptivo. */
  receptive: deskTallySchema,
  /** Dos pagos cobrança do fluxo ativo, pela faixa no dia do pagamento (`deskBlock`). */
  blocks: z6.object({
    vencimento: deskTallySchema,
    atraso_6_30: deskTallySchema,
    atraso_31_mais: deskTallySchema
  }),
  /** Cards distintos que a pessoa moveu no período (`DESK_TOUCH_EVENTS`). */
  touched: z6.number().int(),
  /** Dos pagos cobrança creditados a ela, os que ela tocou — o numerador de `deskEfficiency`. */
  paidTouched: z6.number().int(),
  pulled: z6.number().int(),
  /** Cards que a pessoa cadastrou no período. */
  created: z6.number().int(),
  transfersReceived: z6.array(
    z6.object({ fromUserId: z6.string().nullable(), count: z6.number().int() })
  ),
  /** O que está com ela AGORA, por coluna — não depende do período. */
  workload: z6.array(
    z6.object({ column: deskLoadColumnSchema, count: z6.number().int(), amount: moneySchema })
  )
});
var deskDashboardSchema = z6.object({
  tenant: z6.object({ id: z6.number().int(), slug: z6.string(), name: z6.string() }),
  from: businessDateSchema,
  to: businessDateSchema,
  /**
   * O primeiro movimento gravado na Mesa desta base. Antes dele não há rastro:
   * os pagos que vieram do Kanban entram com quem recebeu o crédito, mas sem os
   * toques, os puxados e as transferências.
   */
  trailSince: businessDateSchema.nullable(),
  /** Quem tem coluna, mais quem aparece em qualquer número do período. */
  people: z6.array(deskDashboardPersonSchema),
  /** Os pagos sem ninguém creditado: o automático da fila, e o de quem não tem conta. */
  unattributed: z6.object({ paidCollection: deskTallySchema, paidAutomatic: deskTallySchema }),
  /** Pago cobrança por dia do período, na data do pagamento. */
  recovery: z6.array(
    z6.object({ day: businessDateSchema, count: z6.number().int(), amount: moneySchema })
  ),
  /** Os cards abertos AGORA, por coluna: na fila e com dono. */
  distribution: z6.array(
    z6.object({ column: deskLoadColumnSchema, queue: deskTallySchema, owned: deskTallySchema })
  )
});
var billingDeskContract = c4.router(
  {
    summary: {
      method: "GET",
      path: "/:tenant/summary",
      pathParams: tenantParams,
      query: z6.object(boardFilters),
      responses: { 200: deskSummarySchema, ...defaultErrors },
      summary: "As colunas da base com contagem e valor, os operadores e o que a pessoa pode"
    },
    moves: {
      method: "GET",
      path: "/:tenant/moves",
      pathParams: tenantParams,
      query: deskMovesQuerySchema,
      responses: { 200: deskMovesPageSchema, ...defaultErrors },
      summary: "As movimenta\xE7\xF5es dos cards da base \u2014 as da Mesa e as que vieram do Kanban"
    },
    dashboard: {
      method: "GET",
      path: "/:tenant/dashboard",
      pathParams: tenantParams,
      query: deskDashboardQuerySchema,
      responses: { 200: deskDashboardSchema, ...defaultErrors },
      summary: "O aproveitamento da equipe na base: pagos, tocados, puxados e a carga de agora"
    },
    cards: {
      method: "GET",
      path: "/:tenant/cards",
      pathParams: tenantParams,
      query: z6.object({
        ...boardFilters,
        column: z6.string().min(1).max(200),
        cursor: z6.string().max(200).optional(),
        limit: z6.coerce.number().int().min(1).max(100).default(30)
      }),
      responses: { 200: deskCardsPageSchema, ...defaultErrors },
      summary: "Os cards de uma coluna, p\xE1gina a p\xE1gina"
    },
    search: {
      method: "GET",
      path: "/:tenant/search",
      pathParams: tenantParams,
      query: z6.object({ q: z6.string().trim().min(2).max(120) }),
      responses: { 200: z6.object({ items: z6.array(deskBoardCardSchema) }), ...defaultErrors },
      summary: "Procura por nome, telefone ou placa \u2014 at\xE9 50, inclusive os pagos"
    },
    detail: {
      method: "GET",
      path: "/:tenant/cards/:id",
      pathParams: cardParams,
      responses: { 200: deskCardDetailSchema, ...defaultErrors },
      summary: "O card com o hist\xF3rico"
    },
    lookupPlate: {
      method: "POST",
      path: "/:tenant/plates/lookup",
      pathParams: tenantParams,
      /* a placa no corpo, nunca no caminho — `contract.test.ts` */
      body: z6.object({ flow: deskFlowSchema, plate: plateText }),
      responses: { 200: deskPlateLookupSchema, ...defaultErrors },
      summary: "O que a placa acha no cadastro da base, antes de cadastrar o card"
    },
    create: {
      method: "POST",
      path: "/:tenant/cards",
      pathParams: tenantParams,
      body: deskCreateCardSchema,
      responses: { 201: z6.object({ items: z6.array(deskBoardCardSchema) }), ...defaultErrors },
      summary: "Cadastra um card por placa \u2014 ativo ou receptivo"
    },
    edit: {
      method: "PATCH",
      path: "/:tenant/cards/:id",
      pathParams: cardParams,
      body: deskEditCardSchema,
      responses: { 200: deskBoardCardSchema, ...defaultErrors },
      summary: "Observa\xE7\xE3o, telefone, protocolo e os campos do card manual"
    },
    pull: {
      method: "POST",
      path: "/:tenant/cards/:id/pull",
      pathParams: cardParams,
      body: z6.object({}),
      responses: { 200: deskBoardCardSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Puxa o atendimento de um card da fila de vencimento"
    },
    handOver: {
      method: "POST",
      path: "/:tenant/cards/:id/hand-over",
      pathParams: cardParams,
      body: z6.object({ operatorId: z6.string().min(1).nullable() }),
      responses: { 200: deskBoardCardSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Passa o card a outro operador, ou o devolve \xE0 fila"
    },
    distribute: {
      method: "POST",
      path: "/:tenant/distribute",
      pathParams: tenantParams,
      body: deskDistributeSchema,
      responses: { 200: deskDistributeResultSchema, ...defaultErrors },
      summary: "Distribui uma fila ou redistribui a coluna de um operador, numa vez s\xF3"
    },
    contactToday: {
      method: "POST",
      path: "/:tenant/cards/:id/contact-today",
      pathParams: cardParams,
      body: z6.object({ done: z6.boolean() }),
      responses: { 200: deskBoardCardSchema, ...defaultErrors },
      summary: 'Marca ou desmarca "contato feito hoje"'
    },
    labelOptions: {
      method: "GET",
      path: "/:tenant/labels",
      pathParams: tenantParams,
      responses: {
        /*
         * `configured` é sempre `true` desde 27/10/2026, quando a Mesa passou a
         * ter etiquetas próprias: fica nesta versão para a interna de antes,
         * que ainda o lê durante o deploy.
         */
        200: z6.object({ configured: z6.boolean(), items: z6.array(deskLabelOptionSchema) }),
        ...defaultErrors
      },
      summary: "As etiquetas da Mesa desta base"
    },
    createLabel: {
      method: "POST",
      path: "/:tenant/labels",
      pathParams: tenantParams,
      body: deskLabelWriteSchema,
      responses: { 201: deskLabelOptionSchema, ...defaultErrors },
      summary: "Cria uma etiqueta da Mesa \u2014 qualquer operador"
    },
    editLabel: {
      method: "PATCH",
      path: "/:tenant/labels/:id",
      pathParams: labelParams,
      body: deskLabelWriteSchema.partial(),
      responses: { 200: deskLabelOptionSchema, ...defaultErrors },
      summary: "Renomeia ou troca a cor \u2014 quem criou, ou quem configura a Mesa"
    },
    archiveLabel: {
      method: "DELETE",
      path: "/:tenant/labels/:id",
      pathParams: labelParams,
      body: z6.object({}).optional(),
      responses: { 200: z6.object({ cards: z6.number().int() }), ...defaultErrors },
      summary: "Arquiva a etiqueta e a tira dos cards \u2014 quem criou, ou quem configura a Mesa"
    },
    setLabels: {
      method: "PUT",
      path: "/:tenant/cards/:id/labels",
      pathParams: cardParams,
      body: z6.object({ labelIds: z6.array(z6.number().int()).max(20) }),
      responses: { 200: deskBoardCardSchema, ...defaultErrors },
      summary: "Troca as etiquetas do card"
    },
    pay: {
      method: "POST",
      path: "/:tenant/cards/:id/pay",
      pathParams: cardParams,
      body: deskPaySchema,
      responses: { 200: deskBoardCardSchema, ...defaultErrors },
      summary: "D\xE1 o card como pago \u2014 pago cobran\xE7a exige o comprovante"
    },
    credit: {
      method: "POST",
      path: "/:tenant/cards/:id/credit",
      pathParams: cardParams,
      body: deskCreditSchema,
      responses: { 200: deskBoardCardSchema, ...defaultErrors },
      summary: "Passa um pago autom\xE1tico para pago cobran\xE7a, ou troca o cr\xE9dito de um pago"
    },
    reopen: {
      method: "POST",
      path: "/:tenant/cards/:id/reopen",
      pathParams: cardParams,
      body: z6.object({}),
      responses: { 200: deskBoardCardSchema, ...defaultErrors },
      summary: "Devolve um card fechado ao quadro, sem dono"
    },
    discard: {
      method: "POST",
      path: "/:tenant/cards/:id/discard",
      pathParams: cardParams,
      body: z6.object({}),
      responses: { 200: deskBoardCardSchema, ...defaultErrors },
      summary: "Tira o card do quadro"
    },
    proofUrl: {
      method: "GET",
      path: "/:tenant/cards/:id/proof-url",
      pathParams: cardParams,
      responses: { 200: deskProofUrlSchema, ...defaultErrors },
      summary: "URL assinada de curta validade para o comprovante"
    },
    operators: {
      method: "GET",
      path: "/:tenant/operators",
      pathParams: tenantParams,
      responses: { 200: deskOperatorsConfigSchema, ...defaultErrors },
      summary: "Quem tem coluna na Mesa desta base"
    },
    saveOperators: {
      method: "PUT",
      path: "/:tenant/operators",
      pathParams: tenantParams,
      body: deskSaveOperatorsSchema,
      responses: { 200: deskOperatorsConfigSchema, ...defaultErrors },
      summary: "Substitui a lista de operadores da Mesa"
    },
    settings: {
      method: "GET",
      path: "/:tenant/settings",
      pathParams: tenantParams,
      responses: { 200: deskSettingsSchema, ...defaultErrors },
      summary: "A configura\xE7\xE3o da Mesa desta base"
    },
    saveSettings: {
      method: "PUT",
      path: "/:tenant/settings",
      pathParams: tenantParams,
      body: deskSaveSettingsSchema,
      responses: { 200: deskSettingsSchema, ...defaultErrors },
      summary: "Grava a configura\xE7\xE3o da Mesa"
    },
    reconcile: {
      method: "POST",
      path: "/:tenant/reconcile",
      pathParams: tenantParams,
      body: z6.object({ includeOwned: z6.boolean() }),
      responses: { 200: deskReconcileResultSchema, ...defaultErrors },
      summary: "Confere agora os cards contra o boleto da casa"
    },
    imports: {
      method: "GET",
      path: "/:tenant/imports",
      pathParams: tenantParams,
      responses: {
        200: z6.object({ items: z6.array(deskImportSummarySchema), auto: deskAutoImportSchema }),
        ...defaultErrors
      },
      summary: "As pr\xE9vias recentes da importa\xE7\xE3o, e a importa\xE7\xE3o autom\xE1tica de hoje"
    },
    generateImport: {
      method: "POST",
      path: "/:tenant/imports",
      pathParams: tenantParams,
      body: z6.object({
        day: z6.number().int().refine((d) => DESK_IMPORT_DUE_DAYS.includes(d))
      }),
      responses: { 201: deskImportDetailSchema, ...defaultErrors },
      summary: "Gera a pr\xE9via de um dia de vencimento, a partir do boleto da casa"
    },
    importDetail: {
      method: "GET",
      path: "/:tenant/imports/:id",
      pathParams: importParams,
      responses: { 200: deskImportDetailSchema, ...defaultErrors },
      summary: "A pr\xE9via com os itens"
    },
    removeImportItem: {
      method: "DELETE",
      path: "/:tenant/imports/:id/items/:itemId",
      pathParams: importItemParams,
      body: z6.object({}).optional(),
      responses: { 200: deskImportDetailSchema, ...defaultErrors },
      summary: "Tira um item da pr\xE9via antes de confirmar"
    },
    commitImport: {
      method: "POST",
      path: "/:tenant/imports/:id/commit",
      pathParams: importParams,
      body: z6.object({}),
      responses: { 200: deskImportDetailSchema, ...defaultErrors },
      summary: "P\xF5e os itens da pr\xE9via no quadro"
    }
  },
  { pathPrefix: "/billing/desk" }
);

// src/billing-awards.ts
import { initContract as initContract5 } from "@ts-rest/core";
import { z as z7 } from "zod";
import { BILLING_AWARD_BOLETO_CATEGORIES, BILLING_AWARD_KINDS, BILLING_AWARD_LINK_STATUSES } from "@evogard/domain";
var c5 = initContract5();
var billingAwardKindSchema = z7.enum(BILLING_AWARD_KINDS);
var billingAwardLinkStatusSchema = z7.enum(BILLING_AWARD_LINK_STATUSES);
var billingAwardBoletoCategorySchema = z7.enum(BILLING_AWARD_BOLETO_CATEGORIES);
var billingAwardPeriodSchema = z7.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use o formato AAAA-MM.");
var tenantParams2 = z7.object({ tenant: z7.string().min(1).max(40) });
var linkParams = tenantParams2.extend({ id: z7.coerce.number().int().positive() });
var pageQuery = z7.object({
  period: billingAwardPeriodSchema,
  q: z7.string().trim().max(120).optional(),
  cursor: z7.string().max(80).optional(),
  limit: z7.coerce.number().int().min(1).max(200).default(50)
});
var billingAwardOperatorSchema = z7.object({
  userId: z7.string(),
  name: z7.string(),
  /** O `codigo_usuario` da pessoa no SGA desta base. */
  erpUserCode: z7.string()
});
var billingAwardBoletoTypeSchema = z7.object({
  /** O código do tipo de boleto no SGA desta base — é por ele que o boleto se liga. */
  typeCode: z7.string(),
  /**
   * O nome do tipo, lido de `reference_values` (`tipo_boleto`) — não é
   * gravado na premiação. Nulo quando a lista do SGA não tem aquele código.
   */
  label: z7.string().nullable(),
  category: billingAwardBoletoCategorySchema,
  /** O dono do tipo; nulo é tipo histórico, que não casa. */
  userId: z7.string().nullable(),
  countsForAward: z7.boolean(),
  active: z7.boolean()
});
var billingAwardTierSchema = z7.object({
  kind: billingAwardKindSchema,
  label: z7.string().trim().min(1).max(60),
  minAmount: moneySchema,
  bonus: moneySchema
});
var billingAwardEnabledSchema = z7.object({
  diaria: z7.boolean(),
  semanal: z7.boolean(),
  mensal: z7.boolean(),
  lideranca: z7.boolean()
});
var billingAwardRuleSchema = z7.object({
  enabled: billingAwardEnabledSchema,
  tiers: z7.array(billingAwardTierSchema)
});
var billingAwardConfigSchema = z7.object({
  operators: z7.array(billingAwardOperatorSchema),
  boletoTypes: z7.array(billingAwardBoletoTypeSchema),
  rule: billingAwardRuleSchema,
  /** Os departamentos do SGA de onde o atendimento conta. */
  departmentCodes: z7.array(z7.string()),
  /** Quem tem vínculo com a base — de onde sai quem pode ser operador ou dono de tipo. */
  candidates: z7.array(z7.object({ id: z7.string(), name: z7.string() }))
});
var codeText = z7.string().trim().min(1).max(20);
var billingAwardSaveOperatorsSchema = z7.object({
  operators: z7.array(
    z7.object({ userId: z7.string().min(1), erpUserCode: codeText })
  )
});
var billingAwardSaveBoletoTypesSchema = z7.object({
  boletoTypes: z7.array(
    z7.object({
      typeCode: codeText,
      category: billingAwardBoletoCategorySchema,
      userId: z7.string().min(1).nullable(),
      countsForAward: z7.boolean(),
      active: z7.boolean()
    })
  )
});
var billingAwardSaveRuleSchema = z7.object({
  rule: billingAwardRuleSchema,
  departmentCodes: z7.array(codeText).max(20)
});
var billingAwardCountedLinkSchema = z7.object({
  operatorId: z7.string(),
  period: billingAwardPeriodSchema,
  settledOn: businessDateSchema,
  recovered: moneySchema,
  typeLabel: z7.string()
});
var billingAwardOperatorStatsSchema = z7.object({
  operatorId: z7.string(),
  /** Atendimentos da competência com o código de usuário dele nesta base. */
  records: z7.number().int(),
  byStatus: z7.record(billingAwardLinkStatusSchema, z7.number().int()),
  invalidated: z7.number().int()
});
var billingAwardReachedTierSchema = z7.object({ label: z7.string(), minAmount: moneySchema, bonus: moneySchema });
var billingAwardsResultSchema = z7.object({
  operators: z7.array(
    z7.object({
      operatorId: z7.string(),
      recovered: moneySchema,
      paidCount: z7.number().int(),
      byTenant: z7.array(z7.object({ tenantId: z7.number().int(), count: z7.number().int(), recovered: moneySchema })),
      byType: z7.array(z7.object({ typeLabel: z7.string(), count: z7.number().int(), recovered: moneySchema })),
      days: z7.array(
        z7.object({
          date: businessDateSchema,
          recovered: moneySchema,
          tier: billingAwardReachedTierSchema.nullable(),
          bonus: moneySchema
        })
      ),
      dailyBonus: moneySchema,
      weeks: z7.array(
        z7.object({
          start: businessDateSchema,
          end: businessDateSchema,
          recovered: moneySchema,
          tier: billingAwardReachedTierSchema.nullable(),
          bonus: moneySchema
        })
      ),
      weeklyBonus: moneySchema,
      monthlyTier: billingAwardReachedTierSchema.nullable(),
      monthlyBonus: moneySchema,
      total: moneySchema
    })
  ),
  leadership: z7.object({ recovered: moneySchema, tier: billingAwardReachedTierSchema.nullable(), bonus: moneySchema }).nullable()
});
var billingAwardClosingSchema = z7.object({
  closedAt: instantSchema,
  closedBy: z7.string().nullable(),
  /** `premiacao_legado` quando o mês veio fechado do sistema antigo. */
  source: z7.string().nullable(),
  /** O resultado congelado em valores — só no mês trazido do sistema antigo. */
  result: billingAwardsResultSchema.nullable()
});
var billingAwardPeriodViewSchema = z7.object({
  period: billingAwardPeriodSchema,
  closing: billingAwardClosingSchema.nullable(),
  /** A competência anterior está fechada? A semanal do começo do mês depende dela. */
  previousClosed: z7.boolean(),
  rule: billingAwardRuleSchema,
  operators: z7.array(z7.object({ id: z7.string(), name: z7.string() })),
  links: z7.array(billingAwardCountedLinkSchema),
  stats: z7.array(billingAwardOperatorStatsSchema),
  /** Quantos atendimentos a base tem na competência, e quando o mais novo chegou do ERP. */
  records: z7.object({ total: z7.number().int(), lastSyncedAt: instantSchema.nullable() }),
  lastMatch: z7.object({ at: instantSchema, by: z7.string().nullable() }).nullable(),
  can: z7.object({
    configure: z7.boolean(),
    invalidate: z7.boolean(),
    sync: z7.boolean(),
    close: z7.boolean()
  })
});
var billingAwardLinkRowSchema = z7.object({
  id: z7.number().int(),
  status: billingAwardLinkStatusSchema,
  /** Conta para a premiação: pago e não invalidado. */
  counts: z7.boolean(),
  matchedAt: instantSchema,
  deadlineAt: instantSchema,
  settledOn: businessDateSchema.nullable(),
  recovered: moneySchema.nullable(),
  checkedAt: instantSchema.nullable(),
  operator: z7.object({ id: z7.string(), name: z7.string() }),
  record: z7.object({
    id: z7.number().int(),
    /** `codigo_atendimento` do SGA. Nulo no atendimento que a casa registrasse — hoje nenhum. */
    externalId: z7.string().nullable(),
    servedOn: businessDateSchema.nullable(),
    /** O nome de quem registrou, como o SGA o conhece. */
    erpUserName: z7.string().nullable(),
    typeLabel: z7.string().nullable(),
    protocol: z7.string().nullable()
  }),
  boleto: z7.object({
    id: z7.number().int(),
    code: z7.string(),
    nossoNumero: z7.string().nullable(),
    dueDate: businessDateSchema,
    amount: moneySchema,
    typeLabel: z7.string()
  }),
  memberName: z7.string().nullable(),
  plate: z7.string().nullable(),
  invalidation: z7.object({ reason: z7.string(), by: z7.string().nullable(), at: instantSchema }).nullable()
});
var billingAwardLinksQuerySchema = pageQuery.extend({
  status: billingAwardLinkStatusSchema.optional()
});
var billingAwardRecordRowSchema = z7.object({
  id: z7.number().int(),
  externalId: z7.string().nullable(),
  servedOn: businessDateSchema.nullable(),
  /** `data_cadastro` — o dia em que foi registrado no SGA. */
  registeredOn: businessDateSchema.nullable(),
  erpUserCode: z7.string().nullable(),
  erpUserName: z7.string().nullable(),
  /** O operador da premiação dono daquele código, se houver. */
  operatorName: z7.string().nullable(),
  departmentCode: z7.string().nullable(),
  typeLabel: z7.string().nullable(),
  protocol: z7.string().nullable(),
  plate: z7.string().nullable(),
  memberName: z7.string().nullable(),
  linkStatus: billingAwardLinkStatusSchema.nullable()
});
var billingAwardBoletoRowSchema = z7.object({
  id: z7.number().int(),
  code: z7.string(),
  nossoNumero: z7.string().nullable(),
  dueDate: businessDateSchema,
  amount: moneySchema,
  paid: z7.boolean(),
  paidOn: businessDateSchema.nullable(),
  recovered: moneySchema,
  typeCode: z7.string().nullable(),
  typeLabel: z7.string(),
  ownerName: z7.string().nullable(),
  plate: z7.string().nullable(),
  memberName: z7.string().nullable(),
  linkStatus: billingAwardLinkStatusSchema.nullable()
});
var page = (item) => z7.object({ items: z7.array(item), next: z7.string().nullable() });
var billingAwardLogRowSchema = z7.object({
  id: z7.number().int(),
  at: instantSchema,
  type: z7.string(),
  title: z7.string(),
  details: z7.string().nullable(),
  actor: z7.string().nullable()
});
var billingAwardSyncResultSchema = z7.object({
  period: billingAwardPeriodSchema,
  links: z7.number().int(),
  byStatus: z7.record(billingAwardLinkStatusSchema, z7.number().int())
});
var billingAwardsContract = c5.router(
  {
    period: {
      method: "GET",
      path: "/:tenant/period",
      pathParams: tenantParams2,
      query: z7.object({ period: billingAwardPeriodSchema }),
      responses: { 200: billingAwardPeriodViewSchema, ...defaultErrors },
      summary: "A compet\xEAncia nesta base: os v\xEDnculos que contam, a r\xE9gua e os operadores"
    },
    periods: {
      method: "GET",
      path: "/:tenant/periods",
      pathParams: tenantParams2,
      responses: {
        200: z7.object({
          items: z7.array(
            z7.object({
              period: billingAwardPeriodSchema,
              closed: z7.boolean(),
              /** `premiacao_legado` quando o mês veio fechado do sistema antigo. */
              source: z7.string().nullable()
            })
          )
        }),
        ...defaultErrors
      },
      summary: "As compet\xEAncias com registro nesta base \u2014 fechadas ou com v\xEDnculo \u2014, da mais nova para a mais velha"
    },
    links: {
      method: "GET",
      path: "/:tenant/links",
      pathParams: tenantParams2,
      query: billingAwardLinksQuerySchema,
      responses: { 200: page(billingAwardLinkRowSchema), ...defaultErrors },
      summary: "O monitoramento: os v\xEDnculos da compet\xEAncia, com busca"
    },
    invalidate: {
      method: "POST",
      path: "/:tenant/links/:id/invalidate",
      pathParams: linkParams,
      body: z7.object({ reason: z7.string().trim().min(3).max(500) }),
      responses: { 200: billingAwardLinkRowSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Tira um v\xEDnculo da premia\xE7\xE3o, com motivo"
    },
    revalidate: {
      method: "POST",
      path: "/:tenant/links/:id/revalidate",
      pathParams: linkParams,
      body: z7.object({}),
      responses: { 200: billingAwardLinkRowSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Devolve o v\xEDnculo \xE0 regra autom\xE1tica"
    },
    records: {
      method: "GET",
      path: "/:tenant/records",
      pathParams: tenantParams2,
      query: pageQuery,
      responses: { 200: page(billingAwardRecordRowSchema), ...defaultErrors },
      summary: "Os atendimentos do SGA da compet\xEAncia"
    },
    boletos: {
      method: "GET",
      path: "/:tenant/boletos",
      pathParams: tenantParams2,
      query: pageQuery,
      responses: { 200: page(billingAwardBoletoRowSchema), ...defaultErrors },
      summary: "Os boletos da compet\xEAncia dos tipos da premia\xE7\xE3o"
    },
    requestRecords: {
      method: "POST",
      path: "/:tenant/records/request",
      pathParams: tenantParams2,
      body: z7.object({ period: billingAwardPeriodSchema }),
      responses: { 202: z7.object({ queued: z7.boolean() }), 409: apiErrorSchema, ...defaultErrors },
      summary: "Pede ao ERP os atendimentos do SGA do m\xEAs inteiro da compet\xEAncia"
    },
    sync: {
      method: "POST",
      path: "/:tenant/sync",
      pathParams: tenantParams2,
      body: z7.object({ period: billingAwardPeriodSchema }),
      responses: { 200: billingAwardSyncResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Refaz os v\xEDnculos de uma compet\xEAncia aberta"
    },
    verify: {
      method: "POST",
      path: "/:tenant/verify",
      pathParams: tenantParams2,
      body: z7.object({ period: billingAwardPeriodSchema }),
      responses: { 202: z7.object({ queued: z7.number().int() }), 409: apiErrorSchema, ...defaultErrors },
      summary: "Confere no SGA os boletos dos v\xEDnculos que ainda n\xE3o est\xE3o pagos"
    },
    close: {
      method: "POST",
      path: "/:tenant/close",
      pathParams: tenantParams2,
      body: z7.object({ period: billingAwardPeriodSchema }),
      responses: { 200: billingAwardPeriodViewSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Fecha a compet\xEAncia: congela os v\xEDnculos, a r\xE9gua e os operadores"
    },
    reopen: {
      method: "POST",
      path: "/:tenant/reopen",
      pathParams: tenantParams2,
      body: z7.object({ period: billingAwardPeriodSchema, reason: z7.string().trim().min(3).max(500) }),
      responses: { 200: billingAwardPeriodViewSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Reabre uma compet\xEAncia fechada, com motivo"
    },
    config: {
      method: "GET",
      path: "/:tenant/config",
      pathParams: tenantParams2,
      responses: { 200: billingAwardConfigSchema, ...defaultErrors },
      summary: "Operadores, tipos de boleto, faixas e o que est\xE1 ligado nesta base"
    },
    saveOperators: {
      method: "PUT",
      path: "/:tenant/config/operators",
      pathParams: tenantParams2,
      body: billingAwardSaveOperatorsSchema,
      responses: { 200: billingAwardConfigSchema, ...defaultErrors },
      summary: "Substitui os operadores da premia\xE7\xE3o nesta base"
    },
    saveBoletoTypes: {
      method: "PUT",
      path: "/:tenant/config/boleto-types",
      pathParams: tenantParams2,
      body: billingAwardSaveBoletoTypesSchema,
      responses: { 200: billingAwardConfigSchema, ...defaultErrors },
      summary: "Substitui os tipos de boleto da premia\xE7\xE3o nesta base"
    },
    saveRule: {
      method: "PUT",
      path: "/:tenant/config/rule",
      pathParams: tenantParams2,
      body: billingAwardSaveRuleSchema,
      responses: { 200: billingAwardConfigSchema, ...defaultErrors },
      summary: "Substitui as faixas, o que est\xE1 ligado e os departamentos nesta base"
    },
    log: {
      method: "GET",
      path: "/:tenant/log",
      pathParams: tenantParams2,
      query: z7.object({
        cursor: z7.string().max(80).optional(),
        limit: z7.coerce.number().int().min(1).max(200).default(50)
      }),
      responses: { 200: page(billingAwardLogRowSchema), ...defaultErrors },
      summary: "O que aconteceu na premia\xE7\xE3o desta base"
    }
  },
  { pathPrefix: "/billing/awards" }
);

// src/sales.ts
import { initContract as initContract7 } from "@ts-rest/core";
import {
  BALANCE_PART_SOURCES,
  EXPENSE_PAYMENT_METHODS,
  PLATE_CLASSIFICATIONS,
  RANKING_COUNTINGS,
  RANKING_ORDERS,
  RANKING_SEGMENTS,
  TECHNOLOGY_FEE_LOTS
} from "@evogard/domain";
import { z as z9 } from "zod";

// src/tickets.ts
import { initContract as initContract6 } from "@ts-rest/core";
import { z as z8 } from "zod";
var c6 = initContract6();
var TICKET_STATUSES = [
  "aberta",
  "em_andamento",
  "aguardando_solicitante",
  "concluida",
  "cancelada"
];
var PRIORITIES = ["baixa", "normal", "alta"];
var ticketStatusSchema = z8.enum(TICKET_STATUSES);
var PORTAL_TICKET_VIEWS = ["todas", "abertas", "resolvidas"];
var portalTicketViewSchema = z8.enum(PORTAL_TICKET_VIEWS);
var PORTAL_TICKET_VIEW_STATUSES = {
  todas: TICKET_STATUSES,
  abertas: ["aberta", "em_andamento", "aguardando_solicitante"],
  resolvidas: ["concluida"]
};
var prioritySchema = z8.enum(PRIORITIES);
var categorySchema = z8.object({
  id: z8.number().int(),
  nome: z8.string(),
  /** dias corridos. `null` é "sem prazo" — e a tela diz isso, não inventa 30. */
  deadlineDays: z8.number().int().nullable(),
  /**
   * O setor que atende esta categoria — o padrão da demanda nova.
   *
   * `null` é estado legítimo e a tela precisa marcá-lo: categoria sem setor
   * avisa **todos** que podem responder na base, em vez de ninguém. Silêncio
   * seria o defeito, não o excesso.
   *
   * `departmentId` é texto e `id` acima é número, no mesmo objeto. Não é
   * descuido: o tipo do id é da ENTIDADE, e o departamento é da plataforma, onde
   * todo `bigserial` viaja como texto — ver `departmentSchema` em `platform.ts`.
   */
  departmentId: z8.string().nullable(),
  /** já resolvido: a tela lista categoria e setor juntos sem uma segunda busca */
  departmentName: z8.string().nullable(),
  /**
   * Quem pode ABRIR nesta categoria — o oposto de `departmentId`, que é quem
   * atende. `false` é "qualquer pessoa da base", e é o padrão.
   */
  restricted: z8.boolean(),
  /**
   * Os setores que podem abrir, quando `restricted`. Lista vazia com
   * `restricted` verdadeiro é o estado que a escrita recusa — e a leitura, se
   * encontrá-lo, não libera ninguém.
   */
  requesterDepartmentIds: z8.array(z8.string()),
  /** o associado pode abrir nesta categoria, pelo portal */
  portalEnabled: z8.boolean(),
  order: z8.number().int(),
  archivedAt: instantSchema.nullable()
});
var openingCategorySchema = categorySchema.pick({
  id: true,
  nome: true,
  deadlineDays: true
});
var ticketDepartmentSchema = z8.object({
  id: z8.string(),
  nome: z8.string()
});
var departmentAssigneeSchema = z8.object({
  id: z8.string(),
  nome: z8.string()
});
var personSchema = z8.object({
  tipo: z8.enum(["usuario", "associado", "sistema"]),
  id: z8.string().nullable(),
  nome: z8.string()
});
var ticketVehicleSchema = z8.object({
  code: z8.string(),
  placa: z8.string().nullable(),
  marca: z8.string().nullable(),
  modelo: z8.string().nullable()
});
var attachmentSchema = z8.object({
  id: z8.number().int(),
  nome: z8.string(),
  typeMime: z8.string(),
  sizeBytes: z8.number().int()
  /*
   * A chave do objeto NÃO aparece aqui, de propósito. O que sai é o id, e o
   * download passa por uma rota que assina uma URL curta depois de conferir o
   * tenant do contexto contra o prefixo da chave.
   */
});
var messageSchema = z8.object({
  id: z8.number().int(),
  author: personSchema,
  body: z8.string(),
  /** a nota que nunca sai no portal */
  internal: z8.boolean(),
  /**
   * Quem escreveu era líder do setor **naquele momento** — congelado na escrita.
   *
   * A tela destaca a mensagem com isto. Não é o papel ATUAL de quem escreveu:
   * revogar a liderança não repinta a conversa, e é essa a diferença que a
   * coluna existe para preservar (ver `tickets_messages.from_lead`).
   */
  fromLead: z8.boolean(),
  createdAt: instantSchema,
  attachments: z8.array(attachmentSchema)
});
var ticketSummarySchema = z8.object({
  id: z8.number().int(),
  code: z8.string(),
  title: z8.string(),
  situacao: ticketStatusSchema,
  priority: prioritySchema,
  categoria: categorySchema.pick({ id: true, nome: true }).nullable(),
  /**
   * A fila em que a demanda está AGORA — dela, não da categoria.
   *
   * Vem no resumo porque a mesa filtra por setor, e porque `encaminhar` a muda
   * sem mexer na categoria: ler o setor pela categoria devolveria a fila de hoje
   * para uma demanda transferida meses atrás.
   */
  department: ticketDepartmentSchema.nullable(),
  /** Quem ABRIU — "Aberta por" na tela. É a pessoa de `aguardando_solicitante`. */
  requester: personSchema,
  /**
   * O SOLICITANTE — quem pediu a um colaborador que abrisse (`demandas-45`).
   * Nulo quando quem abriu é o próprio solicitante, que é o caso comum.
   */
  onBehalfOf: personSchema.nullable(),
  vehicle: ticketVehicleSchema.nullable(),
  assignee: personSchema.nullable(),
  dueAt: z8.string().nullable(),
  completedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var ticketSchema = ticketSummarySchema.extend({
  descricao: z8.string(),
  messages: z8.array(messageSchema),
  /** as transições permitidas a partir da situação atual, vindas do domínio */
  transitions: z8.array(ticketStatusSchema)
});
var previewAttachmentSchema = attachmentSchema.extend({
  author: personSchema,
  createdAt: instantSchema
});
var ticketPreviewSchema = ticketSummarySchema.extend({
  descricao: z8.string(),
  attachments: z8.array(previewAttachmentSchema)
});
var reportSliceSchema = z8.object({
  key: z8.string(),
  label: z8.string(),
  total: z8.number().int()
});
var ticketReportSchema = z8.object({
  /** quando esta contagem foi feita — número em documento vem com data */
  generatedAt: instantSchema,
  /** quantas demandas o recorte tem. É o mesmo `total` que a mesa mostra. */
  total: z8.number().int(),
  /**
   * Dentro do recorte, quantas passaram do prazo.
   *
   * A mesma regra de `numbers` e de `isOverdue`: no dia do prazo ainda não
   * venceu. Conta só demanda em aberto — uma concluída com atraso já não é
   * cobrança de ninguém, e somá-la aqui faria o número crescer para sempre.
   */
  overdue: z8.number().int(),
  /** dentro do recorte, quantas estão em aberto e sem ninguém */
  withoutAssignee: z8.number().int(),
  byStatus: z8.array(reportSliceSchema),
  byPriority: z8.array(reportSliceSchema),
  byDepartment: z8.array(reportSliceSchema),
  byCategory: z8.array(reportSliceSchema)
});
var reportAssigneeSchema = z8.object({
  userId: z8.string().nullable(),
  nome: z8.string(),
  open: z8.number().int(),
  inProgress: z8.number().int(),
  waiting: z8.number().int(),
  total: z8.number().int()
});
var reportCategoryOpenSchema = z8.object({
  categoryId: z8.number().int().nullable(),
  nome: z8.string(),
  open: z8.number().int(),
  inProgress: z8.number().int()
});
var ticketDashboardSchema = z8.discriminatedUnion("estado", [
  z8.object({
    estado: z8.literal("sem_setor"),
    generatedAt: instantSchema
  }),
  ticketReportSchema.extend({
    estado: z8.literal("lida"),
    /** os setores que a pessoa pode escolher no seletor */
    departments: z8.array(ticketDepartmentSchema),
    /** o setor contado; nulo é "todos", e só existe com `ver_qualquer` */
    department: ticketDepartmentSchema.nullable(),
    /** dentro do recorte, concluídas nos últimos 30 dias */
    completedLast30Days: z8.number().int(),
    /** idade média, em dias inteiros, das demandas em aberto do recorte; nula sem nenhuma */
    openAverageAgeDays: z8.number().int().nullable(),
    byAssignee: z8.array(reportAssigneeSchema),
    byCategoryOpen: z8.array(reportCategoryOpenSchema)
  })
]);
var reportRowSchema = z8.object({
  code: z8.string(),
  title: z8.string(),
  situacao: ticketStatusSchema,
  priority: prioritySchema,
  categoria: z8.string().nullable(),
  department: z8.string().nullable(),
  /** quem ABRIU */
  openedBy: z8.string(),
  /** o solicitante, quando não é quem abriu */
  onBehalfOf: z8.string().nullable(),
  /** a placa; o código do veículo quando o espelho não o tem */
  placa: z8.string().nullable(),
  assignee: z8.string().nullable(),
  createdAt: instantSchema,
  dueAt: z8.string().nullable(),
  completedAt: instantSchema.nullable()
});
var ticketReportExportSchema = z8.object({
  generatedAt: instantSchema,
  /** o total do RECORTE, que pode ser maior que `rows.length` */
  total: z8.number().int(),
  rows: z8.array(reportRowSchema),
  truncated: z8.boolean()
});
var numbersSchema = z8.object({
  open: z8.number().int(),
  overdue: z8.number().int(),
  my: z8.number().int(),
  withoutAssignee: z8.number().int()
});
var TICKET_OPENED_EVENT = "demanda:aberta";
var ticketOpenedNoticeSchema = z8.object({
  ticketCode: z8.string(),
  department: z8.string(),
  openedBy: z8.string()
});
var TICKET_REPLIED_EVENT = "demanda:respondida";
var ticketRepliedNoticeSchema = z8.object({
  ticketCode: z8.string(),
  title: z8.string(),
  repliedBy: z8.string()
});
var portalMessageSchema = z8.object({
  id: z8.number().int(),
  author: personSchema,
  body: z8.string(),
  /**
   * O associado TAMBÉM vê que a resposta veio da liderança — decisão do dono.
   *
   * É a única informação de estrutura interna da casa que o portal passa a
   * receber, e ela é um rótulo, não um dado sensível: quem lê sabe que o assunto
   * subiu, em vez de ver mais uma resposta igual às outras.
   *
   * Continua declarado **à mão**, e não por `.extend()` do schema interno. Os
   * dois nascem separados de propósito — é assim que `internal` não tem como
   * vazar por tipo —, e `tickets.test.ts` cobra que um não derive do outro.
   */
  fromLead: z8.boolean(),
  createdAt: instantSchema,
  attachments: z8.array(attachmentSchema)
});
var portalTicketSummarySchema = z8.object({
  code: z8.string(),
  title: z8.string(),
  situacao: ticketStatusSchema,
  categoria: z8.string().nullable(),
  /**
   * O veículo, só com o que o dono do carro reconhece. Declarado à mão, como
   * o resto deste lado — ver `portalMessageSchema`.
   */
  vehicle: z8.object({
    code: z8.string(),
    placa: z8.string().nullable(),
    modelo: z8.string().nullable()
  }).nullable(),
  dueAt: z8.string().nullable(),
  completedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var portalTicketSchema = portalTicketSummarySchema.extend({
  descricao: z8.string(),
  /** quem está com ela, pelo nome. O associado não vê id de ninguém. */
  assignee: z8.string().nullable(),
  messages: z8.array(portalMessageSchema)
});
var trackingSchema = portalTicketSchema.omit({ vehicle: true });
var trackingLinkSchema = z8.object({
  url: z8.string().url()
});
function commaList(item) {
  return z8.string().transform((v) => v.split(",")).pipe(z8.array(item).min(1)).optional();
}
var ticketFilterQuery = z8.object({
  situacao: commaList(ticketStatusSchema),
  assigneeId: commaList(z8.string()),
  categoryId: commaList(z8.coerce.number().int()),
  /**
   * A fila. É o filtro que o `ticketgard` tinha e a mesa daqui não —
   * `ConChamado.vue` filtra por departamento, e é como quem atende acha o
   * que é seu num dia com demanda de sete setores.
   *
   * Aqui ele é opcional. No legado o relatório o exigia
   * (`ExportPDFController`: `"departamento" => "required|integer"`), o que
   * impedia a pergunta que a casa mais faz — quantas demandas a base inteira
   * teve no mês. Exigir era limitação da consulta de lá, não regra de negócio.
   */
  departmentId: commaList(z8.string()),
  /** o legado filtra por prioridade e a mesa daqui não filtrava */
  priority: commaList(prioritySchema),
  /**
   * Quem abriu — só entre usuários internos. `ConChamado.vue` também filtra
   * por "aberto por" e a mesa daqui nunca teve o equivalente (`demandas-40`
   * portou departamento, prioridade e período, e parou aí).
   *
   * O associado que abre pelo portal fica de fora deste filtro por decisão: o
   * chamado dele continua existindo e aparecendo na mesa, só não é encontrável
   * por "quem abriu" — a pergunta "e o solicitante associado?" precisaria de
   * uma busca que hoje não existe (`platform.listUsers` e a busca de
   * associados são superfícies diferentes), e é maior do que este filtro.
   */
  requesterUserId: commaList(z8.string()),
  /**
   * Período de ABERTURA, `AAAA-MM-DD`, inclusivo nos dois extremos.
   *
   * `businessDateSchema` e não `z.string()`, pela mesma razão de `auditTrailQuery`
   * — que converte com o MESMO `startOfDay` no servidor. Ele **lança** diante de
   * `abc` ou de `2026-02-30`, e um `throw` no meio de `conditionsOf` chega à tela
   * como 500 "Erro interno" em três rotas (a mesa, o relatório e a exportação),
   * por causa de um parâmetro de filtro. A recusa certa é 400, e quem a dá é o
   * contrato.
   */
  /**
   * O recorte da barra da mesa. "Todas" é a AUSÊNCIA do campo — e desde
   * 23/09/2026 ela tem botão próprio, aceso: era um estado invisível, e quem
   * acendia "Do meu setor" perdia de vista a demanda em que era solicitante.
   *
   * - `comigo`: aderidas a mim — o "Comigo" do Início, agora na mesa;
   * - `meu_setor`: a fila dos meus setores;
   * - `abri`: "Minhas solicitações" — as que eu ABRI ou PEDI (solicitante),
   *   para outros setores.
   *
   * `z.enum` e não `commaList`: são exclusivos, e somados não dão o escopo
   * inteiro (falta a fila comum, sem setor) — por isso "Todas" existe.
   *
   * Os VALORES ficam em português, como `PORTAL_TICKET_VIEWS`: são vocabulário
   * de contrato, lido por gente no endereço da página, e não identificador.
   *
   * Quem resolve os dois é o servidor, em `conditionsOf`. "Que eu abri" precisa
   * EXCLUIR os meus setores, e exclusão não se exprime com os filtros de
   * inclusão que já existem; e mandar a lista dos meus setores pelo endereço
   * faria o cliente declarar o próprio escopo.
   */
  mine: z8.enum(["comigo", "meu_setor", "abri"]).optional(),
  openedFrom: businessDateSchema.optional(),
  openedTo: businessDateSchema.optional(),
  search: z8.string().optional()
});
var ticketsContract = c6.router(
  {
    /* --- categorias: declaradas antes de `/:id` porque `/categories`
     *     e `/:id` disputam o mesmo desenho de caminho, e quem resolve
     *     a disputa é a ordem em que o controller declara os handlers --- */
    listCategories: {
      method: "GET",
      path: "/categories",
      query: z8.object({
        /**
         * Por omissão, só as ativas: categoria arquivada não abre demanda nova.
         *
         * **`z.enum` e não `z.coerce.boolean()`.** Aquele é `Boolean(valor)`, e
         * na query string tudo é string: `?incluirArquivadas=false` virava
         * `true`, e `=0` também — só a string vazia dava `false`. A tela atual
         * escapava por omitir a chave quando não quer as arquivadas, mas a
         * reescrita natural (`{ incluirArquivadas: mostrar }`) devolveria
         * categoria arquivada como opção de abertura de demanda, que é
         * exatamente o que esta linha promete não fazer.
         */
        includeArchived: z8.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: z8.array(categorySchema), ...defaultErrors },
      summary: "Categorias de demanda da base atual"
    },
    /**
     * As categorias em que EU posso abrir uma demanda — o seletor de abertura.
     *
     * Rota à parte, e não um filtro em `listCategories`, porque aquela tem
     * outros três clientes que precisam ver TUDO: a tela que administra as
     * categorias (para editá-las) e os dois filtros da mesa (para filtrar
     * demanda que a pessoa legitimamente lê, mesmo de categoria em que ela não
     * abriria). Um parâmetro só faria a mesma rota responder duas perguntas, e
     * quem acrescentasse a terceira herdaria a confusão.
     *
     * O piso é `tickets.tickets:criar` — "o que eu posso abrir" —, e não
     * `tickets.categories:ver`, que é a permissão de quem administra.
     */
    listOpeningCategories: {
      method: "GET",
      path: "/categories/opening",
      responses: { 200: z8.array(openingCategorySchema), ...defaultErrors },
      summary: "As categorias em que a pessoa da sess\xE3o pode abrir demanda"
    },
    createCategory: {
      method: "POST",
      path: "/categories",
      body: z8.object({
        nome: z8.string().min(2).max(80),
        deadlineDays: z8.number().int().min(0).max(3650).nullable(),
        /**
         * O setor que ATENDE — obrigatório desde 18/09/2026.
         *
         * Era anulável, e `null` mandava o aviso para todos que podem responder
         * na base. Continua sendo o que acontece nas categorias antigas que
         * nasceram assim; o que mudou é que categoria NOVA não nasce mais sem
         * dono. A tela marca as antigas que estão sem, em vez de travar.
         */
        departmentId: z8.string(),
        /** restringe quem pode ABRIR aos setores de `requesterDepartmentIds` */
        restricted: z8.boolean(),
        /** só vale com `restricted`; vazia com `restricted` é recusada */
        requesterDepartmentIds: z8.array(z8.string()),
        /**
         * Sem valor padrão, de propósito: o padrão do BANCO é `true`, para que a
         * implantação não mude o que o associado vê hoje, e o da TELA é
         * desmarcado, para que a exposição ao portal seja uma decisão e não um
         * esquecimento. Um `.optional()` aqui faria os dois padrões brigarem.
         */
        portalEnabled: z8.boolean(),
        order: z8.number().int().min(0).max(999).optional()
      }),
      responses: { 201: categorySchema, ...defaultErrors },
      summary: "Cria uma categoria"
    },
    editCategory: {
      method: "PATCH",
      path: "/categories/:id",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      body: z8.object({
        nome: z8.string().min(2).max(80).optional(),
        deadlineDays: z8.number().int().min(0).max(3650).nullable().optional(),
        /**
         * Trocar o setor que atende — e **não** removê-lo. Deixou de aceitar
         * `null` junto com `createCategory`: a categoria que nasceu com dono não
         * volta a ficar sem. As antigas sem setor continuam como estão até
         * alguém lhes dar um, e é aí que elas ganham a regra nova.
         */
        departmentId: z8.string().optional(),
        /**
         * Os três de "quem abre" andam juntos ao salvar a tela, mas continuam
         * opcionais um a um: a edição em linha desta tela manda só o que mudou
         * (`{ archived }`, `{ nome }`), e exigir o trio ali obrigaria o botão de
         * arquivar a reenviar a régua inteira — que é como uma lista se perde.
         */
        restricted: z8.boolean().optional(),
        requesterDepartmentIds: z8.array(z8.string()).optional(),
        portalEnabled: z8.boolean().optional(),
        order: z8.number().int().min(0).max(999).optional(),
        /** arquivar em vez de apagar: a demanda antiga continua legível */
        archived: z8.boolean().optional()
      }),
      responses: { 200: categorySchema, ...defaultErrors },
      summary: "Renomeia, reordena, restringe ou arquiva uma categoria"
    },
    /** os números do painel do início — o módulo publica os próprios */
    numbers: {
      method: "GET",
      path: "/numbers",
      responses: { 200: numbersSchema, ...defaultErrors },
      summary: "N\xFAmeros de demandas para o painel do in\xEDcio"
    },
    /**
     * O relatório: o recorte da mesa, contado.
     *
     * `/report` e `/report/export` são caminhos FIXOS e por isso vêm antes de
     * `/:id` no controller — `id` é `z.coerce.number()`, e `GET /tickets/report`
     * caindo no handler de `:id` viraria 400 sem que nada pareça errado. É a
     * mesma armadilha de `/tickets/categories`, escrita no cabeçalho de lá.
     */
    report: {
      method: "GET",
      path: "/report",
      query: ticketFilterQuery,
      responses: { 200: ticketDashboardSchema, ...defaultErrors },
      summary: "O dashboard do setor: as demandas do recorte, contadas"
    },
    /**
     * As linhas do recorte, para o arquivo.
     *
     * Rota separada da de cima, e não um campo a mais na resposta dela: a tela
     * de relatório carrega a contagem a cada mudança de filtro, e carregar
     * milhares de linhas junto para mostrar quatro números seria pagar o arquivo
     * inteiro em toda digitação. Esta só é chamada quando alguém clica.
     *
     * **Mesma permissão da mesa, de propósito.** O arquivo não tem uma coluna
     * que a mesa já não mostre, sobre o mesmo recorte que a pessoa já pode
     * filtrar. Uma ação `exportar` que ninguém nega separadamente seria a tela
     * de grupos afirmando uma distinção que o sistema não faz — o mesmo defeito
     * do mapa de `@PermissionByAction` com três valores iguais.
     */
    exportReport: {
      method: "GET",
      path: "/report/export",
      query: ticketFilterQuery,
      responses: { 200: ticketReportExportSchema, ...defaultErrors },
      summary: "As linhas do recorte, para a tela escrever o arquivo"
    },
    /* --- acompanhamento público. POST e não GET: o código, a confirmação e o
     *     token não podem entrar em log de servidor, histórico de navegador nem
     *     cabeçalho `Referer`, que é onde uma query string acaba. O link
     *     `/track/<token>` é bonito porque quem lê o parâmetro é o FRONT; ele
     *     POSTa o token para cá --- */
    track: {
      method: "POST",
      path: "/track",
      /**
       * Dois caminhos para a mesma demanda, e exatamente um por chamada.
       *
       * `token` é o link: 256 bits que não se varrem, e por isso abrem sozinhos.
       * `code` + `confirmation` é o código ditado ao telefone: curto, logo
       * enumerável, e por isso sozinho não abre nada. As duas superfícies
       * respondem a MESMA recusa, montada em `common/tracking.ts`.
       *
       * **Campos opcionais com `.refine()`, e não `z.union`.** É o desenho do
       * check `tickets_tickets_one_requester_ck`: "exatamente um". Um `z.union`
       * que falha emite o `ZodError` das duas ramificações juntas — duas formas
       * de 400 numa superfície cuja regra inteira é ter UMA forma de resposta.
       */
      body: z8.object({
        /** o segredo do link. Nunca deriva do `code` e nunca o contém. */
        token: z8.string().min(32).max(128).optional(),
        code: z8.string().min(3).max(20).optional(),
        /** CPF ou os quatro últimos dígitos do telefone. Um campo, dois formatos. */
        confirmation: z8.string().min(4).max(20).optional()
      }).refine(
        (b) => b.token ? !b.code && !b.confirmation : Boolean(b.code && b.confirmation),
        { message: "Informe o token do link, ou o c\xF3digo com a confirma\xE7\xE3o." }
      ),
      responses: {
        200: trackingSchema,
        400: defaultErrors[400],
        404: defaultErrors[404],
        /** excesso de tentativas — a mesma resposta para os dois caminhos */
        429: defaultErrors[400]
      },
      summary: "Acompanha uma demanda por link ou por c\xF3digo e confirma\xE7\xE3o, sem login"
    },
    /**
     * Quem a pessoa é na hora de abrir — é por esta rota que a tela sabe que
     * seletor de placa mostrar e se o campo de solicitante aparece, sem
     * adivinhar pelo menu. Quem decide é o servidor, e a criação confere de
     * novo: esconder o campo não protege nada.
     */
    openingProfile: {
      method: "GET",
      path: "/opening/profile",
      responses: {
        200: z8.object({
          /** consultor: escolhe entre a carteira dele e é o próprio solicitante */
          profile: z8.enum(["colaborador", "consultor"])
        }),
        ...defaultErrors
      },
      summary: "O perfil de quem abre: que placas escolhe e se informa solicitante"
    },
    /**
     * As placas que a pessoa pode escolher (`demandas-44`).
     *
     * **POST, e não GET**: placa é identificador de pessoa, e não pode ir para
     * log de servidor nem histórico de navegador — `contract.test.ts` cobra.
     * O recorte do consultor é do servidor: o corpo não diz de quem é a
     * carteira.
     *
     * A lista sai sempre desde 27/10/2026: o estado `sem_espelho` (a praça
     * nunca sincronizou veículos) saiu, e vazia é "nenhuma placa encontrada".
     */
    searchVehicles: {
      method: "POST",
      path: "/vehicles/search",
      body: z8.object({
        /** começo da placa, com ou sem hífen, ou um pedaço do nome do associado */
        term: z8.string().trim().min(2).max(60)
      }),
      responses: {
        200: z8.object({
          estado: z8.literal("lida"),
          items: z8.array(
            ticketVehicleSchema.extend({
              anoModelo: z8.string().nullable(),
              situacao: z8.string().nullable(),
              owner: z8.string().nullable()
            })
          )
        }),
        ...defaultErrors
      },
      summary: "Busca as placas que a pessoa pode ligar a uma demanda"
    },
    /**
     * Quem pode ser o solicitante: os usuários ativos desta base (`demandas-45`).
     *
     * Rota daqui, e não `platform.listUsers`, porque aquela pede
     * `platform.users:ver` — quem abre demanda não administra usuário.
     * Devolve nome e e-mail, e nada além: é o que a pessoa precisa para
     * reconhecer quem lhe pediu.
     */
    searchRequesters: {
      method: "GET",
      path: "/requesters",
      query: z8.object({ term: z8.string().trim().min(2).max(80) }),
      responses: {
        200: z8.array(z8.object({ id: z8.string(), nome: z8.string(), email: z8.string() })),
        ...defaultErrors
      },
      summary: "Busca quem pode ser o solicitante de uma demanda"
    },
    list: {
      method: "GET",
      path: "",
      query: paginationQuery.extend(ticketFilterQuery.shape),
      responses: { 200: paginated(ticketSummarySchema), ...defaultErrors },
      summary: "Lista as demandas da base atual"
    },
    create: {
      method: "POST",
      path: "",
      body: z8.object({
        title: z8.string().min(3).max(160),
        descricao: z8.string().min(1).max(1e4),
        categoryId: z8.number().int().nullable(),
        priority: prioritySchema.optional(),
        assigneeId: z8.string().nullable().optional(),
        /** `code` de um item de `searchVehicles` — nunca a placa */
        vehicleCode: z8.string().min(1).max(40).nullable().optional(),
        /** o solicitante; recusado quando quem abre é consultor */
        onBehalfOfUserId: z8.string().nullable().optional()
      }),
      responses: { 201: ticketSchema, ...defaultErrors },
      summary: "Abre uma demanda pela interna"
    },
    /**
     * A conversa inteira — por isso a autorização não é só `tickets.tickets:ver`.
     *
     * `403` para quem não é o solicitante, o responsável, nem tem
     * `tickets.tickets:ver_qualquer` — sem devolver nada da demanda. Quem só
     * precisa do título, da descrição e dos anexos usa `preview`, que continua
     * aberto a quem já vê a mesa.
     */
    get: {
      method: "GET",
      path: "/:id",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      responses: { 200: ticketSchema, ...defaultErrors },
      summary: "Uma demanda, com a linha do tempo"
    },
    preview: {
      method: "GET",
      path: "/:id/preview",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      responses: { 200: ticketPreviewSchema, ...defaultErrors },
      summary: "T\xEDtulo, descri\xE7\xE3o e anexos \u2014 sem as intera\xE7\xF5es"
    },
    /**
     * O link de acompanhamento, montado pelo servidor.
     *
     * ## Por que é rota própria, e não um campo de `ticketSchema`
     *
     * O token é o segredo. Devolvê-lo em toda resposta da mesa o espalharia pelo
     * cache do TanStack Query, pelo devtools de quem abrir e por qualquer log de
     * resposta — e para uma página de cinquenta linhas seriam cinquenta segredos
     * entregues para um clique que talvez não aconteça. Aqui ele sai sob demanda,
     * por demanda.
     *
     * ## Por que a URL vem pronta, e não o token
     *
     * `apps/web` **não conhece a origem do portal**: não há constante nem
     * variável de ambiente com aquele host em lugar nenhum da interna, e é assim
     * de propósito — são duas aplicações e um deploy, mas cada uma só sabe o
     * próprio endereço. Quem sabe é a API, por `EMAIL_LINK_BASE`. Montar no
     * front exigiria uma segunda verdade sobre onde o portal mora.
     *
     * A autorização não é só `tickets.tickets:ver`: o handler aplica
     * `canSeeTicketDetail`, a mesma régua de `get`. Sem ela, quem vê a mesa mas
     * não pode ver a conversa pegaria um link que DÁ a conversa.
     */
    trackingLink: {
      method: "GET",
      path: "/:id/tracking-link",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      responses: { 200: trackingLinkSchema, ...defaultErrors },
      summary: "O link de acompanhamento da demanda, para enviar a quem perguntou"
    },
    /**
     * Atribuir e concluir são rotas separadas, e não um PATCH que decide pelo
     * campo alterado. Uma declaração frouxa cobrindo os dois seria uma
     * permissão só para duas ações que a escala separa: quem responde não
     * necessariamente conclui.
     */
    assign: {
      method: "PATCH",
      path: "/:id/assignee",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      body: z8.object({ assigneeId: z8.string().nullable() }),
      responses: { 200: ticketSchema, ...defaultErrors },
      summary: "Atribui ou desatribui o respons\xE1vel"
    },
    /**
     * Aderir — quem chama vira o responsável, sem escolher ninguém.
     *
     * Mesma permissão de `assign` (`atribuir`): é o mesmo poder, só que sobre a
     * própria pessoa, e por isso não é rota pública nem precisa de
     * `platform.users:ver` — ninguém escolhe um nome numa lista.
     *
     * **Sem corpo.** O `assigneeId` é sempre `ctx.user.id`; aceitar um id no
     * corpo abriria a possibilidade de aderir EM NOME de outra pessoa pela
     * mesma rota que existe para "eu pego esta".
     *
     * A recusa de corrida é dupla, e as telas não atualizam sozinhas:
     * 1. a demanda já tem responsável quando o pedido chega (alguém aderiu ou
     *    foi atribuída antes);
     * 2. a demanda já não está mais numa situação aberta (concluída ou
     *    cancelada enquanto a tela estava parada).
     * As duas viram 400, não 409 — mesma família de erro que `assign` e
     * `transfer` já usam para "o estado mudou embaixo de você".
     */
    claim: {
      method: "PATCH",
      path: "/:id/claim",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      body: z8.object({}).optional(),
      responses: { 200: ticketSchema, ...defaultErrors },
      summary: "Aderir: quem chama assume a demanda sem respons\xE1vel"
    },
    /**
     * Encaminhar — troca a demanda de FILA.
     *
     * Rota própria, e não um campo de `assign`. É o `encaminhar` do
     * `ticketgard`, e lá ele muda departamento **e** responsável no mesmo ato,
     * com uma observação opcional. Aqui os dois atos ficam separados porque a
     * permissão é a mesma (`atribuir`) mas o significado não: desatribuir
     * devolve a demanda à fila em que ela está, e encaminhar a manda para outra
     * fila. Um `PATCH` só, decidindo pelo campo presente, teria uma declaração
     * cobrindo duas mudanças que a tela apresenta como diferentes.
     *
     * `assigneeId` vem junto e é opcional: transferir de setor quase sempre
     * significa que quem estava com ela não está mais, e obrigar duas chamadas
     * deixaria a demanda com responsável do setor errado no meio do caminho.
     */
    /**
     * Para o seletor de responsável do formulário de encaminhar.
     *
     * `/departments/:departmentId/assignees` e não `/:id`: o caminho começa
     * com um segmento fixo, então não disputa com a família `/:id` — nenhuma
     * ordem de declaração no controller resolve nada aqui, ao contrário de
     * `/categories` e `/report`.
     */
    listDepartmentAssignees: {
      method: "GET",
      path: "/departments/:departmentId/assignees",
      pathParams: z8.object({ departmentId: z8.string() }),
      responses: { 200: z8.array(departmentAssigneeSchema), ...defaultErrors },
      summary: "Quem no setor pode aderir a demanda \u2014 para escolher ao encaminhar"
    },
    /**
     * Os setores para onde encaminhar (23/09/2026), com o piso de `transfer`.
     *
     * Rota daqui, e não `platform.listDepartments`, porque aquela pede
     * `platform.departments:ver` — a permissão de quem ADMINISTRA setor — e o
     * encaminhar ficava escondido de quem só atende. Devolve id e nome, e mais
     * nada: é o que o seletor precisa.
     */
    transferDepartments: {
      method: "GET",
      path: "/transfer/departments",
      responses: { 200: z8.array(ticketDepartmentSchema), ...defaultErrors },
      summary: "Os setores ativos para onde a demanda pode ser encaminhada"
    },
    transfer: {
      method: "PATCH",
      path: "/:id/department",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      body: z8.object({
        departmentId: z8.string().nullable(),
        /**
         * Quem passa a ser o responsável no setor novo.
         *
         * Omitir mantém quem está; mandar `null` devolve a demanda à fila. São
         * coisas diferentes, e por isso `optional()` **e** `nullable()`.
         */
        assigneeId: z8.string().nullable().optional(),
        /** a observação da transferência, que entra na linha do tempo */
        note: z8.string().max(2e3).optional()
      }),
      responses: { 200: ticketSchema, ...defaultErrors },
      summary: "Encaminha a demanda para outro departamento"
    },
    changeStatus: {
      method: "PATCH",
      path: "/:id/status",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      body: z8.object({
        situacao: ticketStatusSchema,
        /** o motivo entra na linha do tempo como nota, quando vier */
        note: z8.string().max(2e3).optional()
      }),
      responses: { 200: ticketSchema, ...defaultErrors },
      summary: "Move a demanda para outra situa\xE7\xE3o"
    },
    reply: {
      method: "POST",
      path: "/:id/messages",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      body: z8.object({
        body: z8.string().min(1).max(1e4),
        /** `false` por omissão: o silencioso tem que ser o seguro */
        internal: z8.boolean().default(false)
      }),
      responses: { 201: messageSchema, ...defaultErrors },
      summary: "Responde na demanda, ou deixa nota interna"
    },
    attach: {
      method: "POST",
      path: "/:id/attachments",
      pathParams: z8.object({ id: z8.coerce.number().int() }),
      body: z8.object({
        nome: z8.string().min(1).max(255),
        typeMime: z8.string().min(3).max(160),
        /** o arquivo em base64. O servidor confere tamanho, tipo e assinatura. */
        content: z8.string().min(1),
        messageId: z8.number().int().optional()
      }),
      responses: { 201: attachmentSchema, ...defaultErrors },
      summary: "Anexa um arquivo \xE0 demanda"
    },
    /** devolve uma URL assinada de curta duração; a chave nunca sai na resposta */
    downloadAttachment: {
      method: "GET",
      path: "/:id/attachments/:attachmentId",
      pathParams: z8.object({
        id: z8.coerce.number().int(),
        attachmentId: z8.coerce.number().int()
      }),
      responses: {
        200: z8.object({
          url: z8.string(),
          nome: z8.string(),
          expiresAtSeconds: z8.number()
        }),
        ...defaultErrors
      },
      summary: "URL assinada para baixar um anexo"
    }
  },
  { pathPrefix: "/tickets" }
);
var ticketsPortalContract = c6.router(
  {
    list: {
      method: "GET",
      path: "/tickets",
      /*
       * `view` e não uma lista de situações no query: quem escolhe o que cada
       * aba cobre é `PORTAL_TICKET_VIEW_STATUSES`, no servidor. Aceitar
       * `situacao=aberta&situacao=em_andamento` daria ao cliente a chance de
       * montar um conjunto que a tela não sabe nomear — e de discordar da
       * contagem que ele mesmo mostra.
       */
      query: paginationQuery.extend({ view: portalTicketViewSchema.default("todas") }),
      responses: { 200: paginated(portalTicketSummarySchema), ...defaultErrors },
      summary: "As demandas do associado da sess\xE3o, por aba"
    },
    open: {
      method: "POST",
      path: "/tickets",
      body: z8.object({
        title: z8.string().min(3).max(160),
        descricao: z8.string().min(1).max(1e4),
        categoryId: z8.number().int().nullable(),
        /** `code` de um veículo da carteira do associado (`/portal/erp/wallet`) */
        vehicleCode: z8.string().min(1).max(40).nullable().optional()
      }),
      responses: { 201: portalTicketSchema, ...defaultErrors },
      summary: "O associado abre uma demanda"
    },
    /** as categorias que o associado pode escolher ao abrir */
    categories: {
      method: "GET",
      path: "/tickets/categories",
      responses: {
        200: z8.array(categorySchema.pick({ id: true, nome: true })),
        ...defaultErrors
      },
      summary: "Categorias dispon\xEDveis para o associado"
    },
    get: {
      method: "GET",
      path: "/tickets/:code",
      pathParams: z8.object({ code: z8.string().min(3).max(20) }),
      responses: { 200: portalTicketSchema, ...defaultErrors },
      summary: "Uma demanda do associado da sess\xE3o"
    },
    reply: {
      method: "POST",
      path: "/tickets/:code/messages",
      pathParams: z8.object({ code: z8.string().min(3).max(20) }),
      /** sem `internal`: o corpo do portal não tem de onde tirar o campo */
      body: z8.object({ body: z8.string().min(1).max(1e4) }),
      responses: { 201: portalMessageSchema, ...defaultErrors },
      summary: "O associado responde na pr\xF3pria demanda"
    },
    /**
     * O associado anexa um arquivo à PRÓPRIA mensagem (23/09/2026).
     *
     * `messageId` é obrigatório aqui, ao contrário da interna: anexo sem
     * mensagem não aparece em linha do tempo nenhuma (ver o `CLAUDE.md`), e o
     * portal manda a resposta primeiro e usa o id dela. O servidor confere que
     * a mensagem é DESTE associado, nesta demanda — o escopo é a pessoa, pela
     * sessão, nunca pelo corpo.
     */
    attach: {
      method: "POST",
      path: "/tickets/:code/attachments",
      pathParams: z8.object({ code: z8.string().min(3).max(20) }),
      body: z8.object({
        nome: z8.string().min(1).max(255),
        typeMime: z8.string().min(3).max(160),
        /** o arquivo em base64. O servidor confere tamanho, tipo e assinatura. */
        content: z8.string().min(1),
        messageId: z8.number().int()
      }),
      responses: { 201: attachmentSchema, ...defaultErrors },
      summary: "O associado anexa um arquivo \xE0 pr\xF3pria mensagem"
    },
    /**
     * A URL assinada de um anexo da demanda do associado — o que a Evogard
     * mandou e o que ele mesmo mandou. Nunca o de uma nota interna: o `where`
     * do servidor exclui, como exclui a nota da conversa.
     */
    downloadAttachment: {
      method: "GET",
      path: "/tickets/:code/attachments/:attachmentId",
      pathParams: z8.object({
        code: z8.string().min(3).max(20),
        attachmentId: z8.coerce.number().int()
      }),
      responses: {
        200: z8.object({
          url: z8.string(),
          nome: z8.string(),
          expiresAtSeconds: z8.number()
        }),
        ...defaultErrors
      },
      summary: "URL assinada para o associado baixar um anexo da pr\xF3pria demanda"
    }
  },
  { pathPrefix: "/portal" }
);

// src/sales.ts
var c7 = initContract7();
var BOLETO_PAID_EVENT = "boleto:pago";
var boletoPaidNoticeSchema = z9.object({
  subject: z9.string()
});
var periodSchema = z9.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use o formato AAAA-MM.");
var ENTRY_TYPES = ["entrada", "saida"];
var entryTypeSchema = z9.enum(ENTRY_TYPES);
var TARGET_SCOPES = ["consultor", "time"];
var targetScopeSchema = z9.enum(TARGET_SCOPES);
var REASONS_NOT_TO_SETTLE = [
  /*
   * **`criterio_de_ranking_nao_definido` esteve aqui, e saiu em 09/09.**
   *
   * Era a **P5**: duas contagens rodavam em produção — venda + troca + Bronze
   * (`EvoReport`) ou toda venda (`ranking-comercial`) —, e a mesma pessoa no
   * mesmo mês aparecia como 33 numa tela e 37 na outra (§5.6). A casa escolheu
   * a primeira, e escolheu também que ela **não é configuração por base**: há
   * uma contagem, `RANKING_COUNTINGS` em `@evogard/domain` a nomeia, e não há
   * chave em `app.tenant_settings` para gravar outra.
   *
   * Sem chave não há ausência de chave, e um motivo que nada pode produzir é
   * um vocabulário que a tela precisa traduzir para nunca mostrar. O que
   * continua podendo recusar o ranking é o que também recusa a apuração:
   * `venda_sem_classificacao`, medido.
   */
  /**
   * **A P4 foi respondida em 28/08**: as faixas viraram dado por base e por
   * competência (`app.sales_tiers`, com vigência). O motivo continua, e
   * mudou de natureza — deixou de ser "ninguém decidiu a forma" e virou
   * **medição sobre o dado**: aquela praça não declarou régua para aquela
   * competência, ou declarou parte dela.
   *
   * A tabela nasce **vazia** nas duas praças de propósito (a régua lida é a de
   * um sistema, e se CE e GO divergem segue sem confirmação), então hoje ele é
   * verdadeiro em toda competência — e some sozinho, sem ninguém mexer em
   * código, no dia em que a régua for declarada. Uma **lacuna entre vigências**
   * cai aqui pelo mesmo motivo: pagar zero ali é indistinguível de "todo mundo
   * ficou abaixo do piso".
   */
  "regua_de_comissao_nao_definida",
  /**
   * **A P12 foi respondida em 28/08**, e `app.comercial_vendas.classificacao`
   * nasceu — anulável e sem padrão, porque um `default 'venda'` faria cada linha
   * afirmar uma categoria que ninguém declarou, e a categoria escolhe a faixa.
   *
   * Então este motivo deixou de ser uma **constante sobre o schema** e virou
   * **medição sobre o dado**: ele vale enquanto houver, naquela competência,
   * placa atribuída a consultor com a classificação nula. Quem responde é uma
   * consulta, nunca uma lista fixa — uma constante sobre o schema envelhece
   * calada, e esta já envelheceu uma vez.
   *
   * A recusa é da competência **inteira**, e não o descarte da linha: descartar
   * mudaria a contagem que escolhe a faixa, para menos, que é o lado do qual
   * ninguém reclama.
   */
  "venda_sem_classificacao",
  /*
   * `venda_sem_observacoes` SAIU em 11/09, e é a única remoção deste
   * vocabulário até hoje.
   *
   * Ele dizia: a meta individual do legado tira da conta a venda cadastrada
   * como `venda` cujas observações contenham "troca" e "veículo", salvo se
   * mencionarem "titularidade" (§4.1); sem coluna de observações, o realizado
   * não se reproduz.
   *
   * **A premissa era um andaime.** Escrever "troca de veículo" na observação
   * era como a PLANILHA sinalizava troca, antes de existir coluna de tipo; a
   * heurística entrou na migração dos dados como ponte e foi lida como regra.
   * O dono desfez isso em 11/09: hoje existe a coluna, e observação falando de
   * troca num registro marcado `venda` é registro **errado** — ele é `venda`.
   *
   * Com a premissa desfeita, §5.7 deixou de ser divergência (as duas contagens
   * dão o mesmo número) e o realizado passou a sair. O motivo não virou outro
   * nome: ele deixou de existir, e é por isso que a remoção está escrita aqui
   * em vez de o valor ficar órfão no vocabulário. Um valor que nada produz é
   * uma frase que a tela sabe desenhar e o servidor nunca manda.
   */
  /**
   * **P9** — "quinzena" são duas janelas que quase não se encontram: o bônus
   * paga sobre os dias 1 a 15; o ranking que a operação consulta olha do dia 15
   * ao fim do mês. Um consultor com 52 vendas na segunda quinzena aparece em
   * primeiro lugar na tela e leva R$ 0,00 (§5.5). Enquanto isso não for
   * decidido, `settleBonus` responde `apurada_sem_a_quinzena` e a bonificação
   * **não é um total**.
   */
  "quinzena_nao_definida",
  /**
   * Não existe tabela de bonificação apurada, nem tarefa que a crie: a onda 6
   * tem `sales_commissions` e `sales_entries`, e mais nada de
   * remuneração. Sem lugar onde gravar, não há o que ler — e recalcular na
   * leitura é exatamente o que o `EvoReport` faz hoje (§1.3), que é como
   * transferir uma venda em setembro muda a comissão de março sem nenhuma tela
   * avisar.
   */
  "bonificacao_sem_registro",
  /*
   * `consultor_sem_perfil` SAIU em 22/10/2026.
   *
   * Ele dizia: há placa no mês de consultor cujo cadastro não diz sob qual
   * régua ele é pago (P13) — e recusava a competência INTEIRA, na apuração, na
   * meta e no ranking. O nulo era o estado de nascença da coluna, e a recusa
   * era a guarda contra pagar a base de reativação pela régua padrão.
   *
   * O dono decidiu, em 22/10, que a guarda custava mais que o risco: cinco
   * cadastros incompletos deixavam o ranking da CE sem lista nenhuma. A
   * coluna virou `not null`, toda linha sem perfil virou `externo`, e o
   * cadastro passou a exigir o campo. Sem nulo, o motivo não tem o que medir —
   * e um valor que nada produz é uma frase que a tela sabe desenhar e o
   * servidor nunca manda.
   */
  /**
   * **O espelho do ERP está vencido, e por isso não se paga esta competência.**
   *
   * Ele nasceu em 21/09/2026 de uma trava que **mudou de lugar**, e a mudança é
   * o ponto. A importação de venda pedia espelho de no máximo dois dias com
   * `usage: 'decisao'` e **recusava** — e o efeito medido foi o produto inteiro
   * parado: 15.056 contratos de 2026 no espelho da CE, `app.sales_sales` com
   * zero linha, e as telas de Vendas e Ranking vazias porque são as duas que
   * leem aquela tabela. O dado existia, ninguém o via, e o motivo não aparecia
   * em tela nenhuma — falha de job não tem tela.
   *
   * A trava não pertencia à importação, que só copia contrato e cuja
   * competência é o mês do `data_contrato` — número que a idade do espelho não
   * muda. Ela pertence **aqui**, que é o único lugar onde a casa decide quanto
   * alguém recebe: um contrato cancelado no SGA depois da última varredura
   * ainda conta como venda no espelho, e pagar por ele é o erro caro.
   *
   * Então a importação passou a `usage: 'tela'` — mostra, dizendo de quando é —
   * e a apuração passou a recusar. Ler e pagar deixaram de ter a mesma régua,
   * que é o que a regra 8 pede desde que foi escrita: **quem consulta declara o
   * frescor que precisa**, e consultar e pagar não precisam do mesmo.
   *
   * Como os outros medidos, ele some sozinho: varrida a coleção de veículos, a
   * competência apura sem ninguém mexer em código. E é por isso que a tela
   * aponta para a varredura (`POST /erp/sweeps`) em vez de pedir um chamado.
   */
  "espelho_vencido"
];
var reasonNotToSettleSchema = z9.enum(REASONS_NOT_TO_SETTLE);
var userSummarySchema = z9.object({
  id: z9.string(),
  nome: z9.string()
});
var salesTeamSchema = z9.object({
  id: z9.number().int(),
  nome: z9.string(),
  leader: userSummarySchema.nullable(),
  /** quantos consultores ativos apontam para ele hoje */
  members: z9.number().int(),
  /**
   * Quantos desses membros ainda NÃO respondem ao líder na cadeia de superior.
   *
   * Desde 23/09/2026 a cadeia é derivada do time, e toda troca feita pela tela
   * já a grava. O que sobra é o cadastro de antes: membro no time, sem vínculo
   * com o líder — a premiação de líder não soma essa pessoa. Salvar o time com
   * a data "a partir de" certa zera o número.
   */
  unlinkedMembers: z9.number().int(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var CONSULTANT_PROFILES = ["interno", "externo", "base_reativacao"];
var consultantProfileSchema = z9.enum(CONSULTANT_PROFILES);
var technologyFeeLotSchema = z9.enum(TECHNOLOGY_FEE_LOTS);
var consultantSchema = z9.object({
  id: z9.number().int(),
  user: userSummarySchema,
  /** o código do voluntário NAQUELA base — 394 no CE, 75 no GO, mesma pessoa */
  erpVolunteerCode: z9.string(),
  team: salesTeamSchema.pick({ id: true, nome: true }).nullable(),
  /**
   * Sob qual régua esta pessoa é paga — **obrigatório desde 22/10/2026**.
   *
   * Nasce no `createConsultant` e muda pelo ato `declarar_perfil` de
   * `PATCH /sales/consultants/:id` — ato próprio, e não um campo do `editar`,
   * pelo motivo escrito lá. Ver `REASONS_NOT_TO_SETTLE` sobre o nulo que ele
   * foi até aquele dia.
   */
  profile: consultantProfileSchema,
  /**
   * Em qual lote a taxa de tecnologia cai (05/10/2026). Só pesa para o perfil
   * que paga a taxa — o externo. Quem muda é o ato `definir_taxa_tecnologia`.
   */
  technologyFeeLot: technologyFeeLotSchema,
  /**
   * A foto do consultor (29/10/2026): URL assinada, que vale uma hora, ou
   * nulo sem foto. Muda pelo ato `definir_foto`, ou nasce no `createConsultant`.
   */
  photoUrl: z9.string().nullable(),
  active: z9.boolean(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var consultantPhotoUploadSchema = z9.object({
  nome: z9.string().min(1).max(255),
  typeMime: z9.string().min(3).max(160),
  content: z9.string().min(1)
});
var consultantCodeCorrectionSchema = z9.object({
  consultant: consultantSchema,
  /** o código de antes — a metade "de" do de→para, para a tela dizer o que trocou */
  previousCode: z9.string(),
  /** quantas vendas ficaram sem consultor, arquivadas incluídas */
  salesDetached: z9.number().int(),
  /** as competências com comissão apurada desta pessoa, da mais nova para a mais velha */
  periodsToReassess: z9.array(periodSchema)
});
var consultantSummarySchema = consultantSchema.pick({
  id: true,
  user: true,
  erpVolunteerCode: true
});
var supervisorLinkSchema = z9.object({
  id: z9.number().int(),
  user: userSummarySchema,
  supervisor: userSummarySchema,
  validFrom: businessDateSchema,
  /** último dia em que valeu, **inclusive**. `null` é o vínculo corrente. */
  validUntil: businessDateSchema.nullable(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var plateClassificationSchema = z9.enum(PLATE_CLASSIFICATIONS);
var salesImportSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("importada"),
    period: periodSchema,
    /** contratos que o espelho tem na competência */
    contracts: z9.number().int(),
    /** os que viraram venda — gravados ou atualizados */
    imported: z9.number().int(),
    /** cancelados; não são venda, e o que já era foi arquivado */
    canceled: z9.number().int(),
    /** adesão que o ERP não descreve (a G13) */
    unknownAdhesion: z9.number().int(),
    /**
     * Quantas ficaram **sem consultor casado**. É o número que separa "a
     * importação não funcionou" de "funcionou, e o cadastro de consultores é
     * que está atrás": na CE são 742 voluntários no ERP contra 8 consultores.
     */
    withoutConsultant: z9.number().int(),
    /**
     * De quando é o espelho que produziu estas vendas. `null` quando não veio
     * contrato nenhum.
     *
     * Ele existe porque a importação **deixou de recusar espelho velho** em
     * 21/09/2026 — a trava saiu daqui e foi para a apuração, que é onde se
     * paga. O preço dessa mudança é que o número na tela pode ser de dias
     * atrás, e a tela precisa dizer de quando é: "2.576 contratos" sem data
     * parece o cadastro de agora.
     */
    mirrorSyncedAt: instantSchema.nullable()
  }),
  z9.object({
    estado: z9.literal("recusada"),
    period: periodSchema,
    reason: z9.enum([
      "espelho_vencido",
      "espelho_nao_sincronizado",
      "situacao_nao_classificada"
    ]),
    detail: z9.string()
  })
]);
var saleMemberLabelSchema = z9.object({
  code: z9.string(),
  nome: z9.string().nullable()
});
var saleVehicleLabelSchema = z9.object({
  code: z9.string(),
  /** maiúscula, sem hífen; `null` é veículo sem placa, que existe */
  placa: z9.string().nullable(),
  marca: z9.string().nullable(),
  modelo: z9.string().nullable(),
  anoModelo: z9.string().nullable()
});
var saleSchema = z9.object({
  id: z9.number().int(),
  /** `null` é "nasceu aqui, na tela" — ver o cabeçalho deste schema */
  idExterno: z9.string().nullable(),
  period: periodSchema,
  /** `null` é "a origem não disse quanto" — ver `sales_sales.amount`. */
  value: moneySchema.nullable(),
  /** `null` é "a origem não disse" — texto por regra 14 */
  situacao: z9.string().nullable(),
  /**
   * O que a placa é, para a remuneração. **`null` é "ninguém declarou"**, e é
   * ele que faz a competência inteira recusar apurar — ver o cabeçalho.
   */
  classification: plateClassificationSchema.nullable(),
  /** `null` é "ainda não casou com nenhum consultor", que é fila de trabalho */
  consultant: consultantSummarySchema.nullable(),
  /**
   * `codigo_voluntario` como a origem o mandou, carimbado na importação. `null`
   * é "a origem não disse quem vendeu" — e é o caso da venda digitada na tela.
   *
   * **Ele vem junto com `consultant`, e não no lugar dele.** `consultant` é o
   * vínculo com o cadastro da casa; este é o que o ERP disse. Quando os dois
   * estão preenchidos eles concordam, porque foi por este código que o casamento
   * aconteceu. Quando `consultant` é nulo e este não, a tela tem o que a fila de
   * trabalho precisa dizer: **de quem** é a venda que ninguém cadastrou ainda.
   *
   * Sem nome, pelo motivo do cabeçalho deste schema: o nome mora em
   * `external.sga_voluntarios` e sai pela porta única, não por `join` daqui.
   */
  volunteerCode: z9.string().nullable(),
  /** `external.sga_associados.codigo_associado` daquela base */
  codigoAssociado: z9.string().nullable(),
  /** o código do veículo, **não a placa**: a placa vem em `vehicle` */
  codigoVeiculo: z9.string().nullable(),
  /** o que `codigoAssociado` significa; `null` é "o rótulo não veio" — ver
   *  `saleMemberLabelSchema` */
  member: saleMemberLabelSchema.nullable(),
  /** o que `codigoVeiculo` significa; `null` é "o rótulo não veio" */
  vehicle: saleVehicleLabelSchema.nullable(),
  createdBy: userSummarySchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});
var saleActionSchema = z9.discriminatedUnion("action", [
  z9.object({
    action: z9.literal("corrigir"),
    period: periodSchema,
    /**
     * **Anulável**, e aqui o nulo é enviável — ao contrário da classificação
     * logo abaixo.
     *
     * A diferença é o que cada nulo custa. Sem categoria, a competência inteira
     * fica inapurável, e por isso o ato exige uma. Sem valor, nada trava: a
     * comissão é R$ fixo por placa e o ranking conta placa. Exigir um valor
     * aqui obrigaria quem só quer corrigir a categoria de uma venda importada
     * a **inventar um número** para conseguir salvar.
     */
    value: moneySchema.nullable(),
    situacao: z9.string().max(60).nullable(),
    /**
     * **Obrigatória e NÃO anulável**, ao contrário da coluna e da leitura.
     *
     * A coluna é anulável porque o que já está gravado — e o que a importação
     * ainda vai gravar — pode não ter categoria; a leitura é anulável porque
     * precisa mostrar esse estado. Aqui não: quem corrige tem o registro à
     * frente, e devolver a venda ao estado "ninguém declarou" não conserta nada
     * — deixa a competência inteira inapurável de novo, sem que a tela tenha
     * pedido isso.
     *
     * A consequência precisa ficar dita: **é por este ato que uma venda nascida
     * na tela ganha categoria**, porque `recordSale` ainda não a carrega.
     * Corrigir o valor de uma venda antiga passa a exigir dizer o que ela é — e
     * esse é o efeito procurado, não um custo: é a tela de correção virando o
     * lugar onde a classificação que faltava é declarada.
     */
    classification: plateClassificationSchema,
    codigoAssociado: z9.string().max(64).nullable(),
    codigoVeiculo: z9.string().max(64).nullable()
  }),
  z9.object({
    /**
     * Casa (ou descasa) a venda com um consultor **daquela base** — conferido
     * no servidor contra `ctx.tenant.id`, nunca no cliente.
     *
     * `null` descasa. É o ato que resolve a fila de trabalho das vendas
     * importadas sem dono, e é o único caminho pelo qual uma comissão muda de
     * pessoa. A consequência está medida em §4.7: uma venda de março movida em
     * setembro muda a comissão de março **nos dois consultores**, porque a
     * faixa é escolhida pelo total do mês — e a diferença medida foi de
     * R$ 227,00, seis meses depois. É a **P11**, e enquanto ela não for
     * respondida quem move precisa reapurar a competência à mão e saber que ela
     * mudou.
     */
    action: z9.literal("casar_consultor"),
    consultantId: z9.number().int().nullable()
  })
]);
var targetAchievementSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("nao_apuravel"),
    reasons: z9.array(reasonNotToSettleSchema)
  }),
  z9.object({
    estado: z9.literal("medido"),
    /** vendas que contam para a meta na competência */
    quantity: z9.number().int(),
    /** o valor delas */
    /** `null` é "a origem não disse quanto" — ver `sales_sales.amount`. */
    value: moneySchema.nullable()
  })
]);
var salesTargetSchema = z9.object({
  id: z9.number().int(),
  targetType: targetScopeSchema,
  /** o consultor ou o time, já resolvido em nome */
  target: z9.object({ id: z9.number().int(), nome: z9.string() }),
  period: periodSchema,
  /** meta em dinheiro; `null` é "esta meta não é em dinheiro" */
  value: moneySchema.nullable(),
  /** meta em número de vendas; `null` é "esta meta não é em quantidade" */
  quantity: z9.number().int().nullable(),
  attainment: targetAchievementSchema,
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});
var rankingCountingSchema = z9.enum(RANKING_COUNTINGS);
var rankingOrderSchema = z9.enum(RANKING_ORDERS);
var consultantPositionSchema = z9.object({
  position: z9.number().int(),
  consultant: consultantSummarySchema,
  achieved: z9.number().int(),
  ignored: z9.number().int(),
  /** `null` é "a origem não disse quanto" — ver `sales_sales.amount`. */
  value: moneySchema.nullable(),
  /**
   * O time de quem está nesta posição — `null` é "sem time", e não "sem dado".
   *
   * Ele entra aqui, e não em `consultantSummarySchema`, por alcance: o resumo
   * é produzido por meia dúzia de rotas deste módulo, e acrescentar um campo
   * obrigatório ali obrigaria todas a respondê-lo para servir a **uma** tela.
   * Esta posição é montada num lugar só (`shapeRanking`), que já tem o time na
   * mão.
   *
   * Quem o lê é a régua de `sales.ranking:ver-venda-time`, comparado contra
   * `viewerTeam` do quadro — ver `canSeeRankingSales` em `@evogard/domain`.
   */
  team: salesTeamSchema.pick({ id: true, nome: true }).nullable()
});
var rankingSegmentSchema = z9.enum(RANKING_SEGMENTS);
var rankingSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("nao_apuravel"),
    reasons: z9.array(reasonNotToSettleSchema)
  }),
  z9.object({
    estado: z9.literal("apurado"),
    period: periodSchema,
    /** a regra que produziu estes números — uma, e da casa */
    criterion: rankingCountingSchema,
    /**
     * a coluna que produziu estas POSIÇÕES — a escolhida na tela.
     *
     * Ele volta na resposta, e não só vai na consulta, porque é o que permite a
     * tela afirmar o recorte ao lado da lista. Sem ele, a tela diria o recorte
     * que ela pediu, que não é necessariamente o que ordenou o que está na tela
     * — basta a resposta de uma consulta anterior ainda estar em cache.
     */
    order: rankingOrderSchema,
    consultants: z9.array(consultantPositionSchema),
    /**
     * Os times pelos quais o quadro pode ser filtrado — `null` sem
     * `sales.ranking:filtro_times`.
     *
     * ## É o CADASTRO de times, e não quem pontuou
     *
     * A lista sai de `app.sales_teams`, e não das posições acima. Montá-la a
     * partir de quem pontuou faria o seletor encolher conforme a competência —
     * e a pergunta que se vai fazer com ele é justamente sobre o time que pode
     * não ter pontuado.
     *
     * ## São TODOS os times da base, e não os que pontuaram
     *
     * Um time que não vendeu na competência continua na lista, e filtrar por ele
     * devolve quadro vazio. É a resposta certa: "este time não vendeu em março" é
     * o que a pessoa foi perguntar, e um seletor que escondesse a opção a deixaria
     * sem como fazer a pergunta.
     */
    teamOptions: z9.array(salesTeamSchema.pick({ id: true, nome: true })).nullable(),
    /**
     * O time de **quem está olhando** o quadro — `null` quando ela não é
     * consultora nesta praça, ou é e não tem time.
     *
     * Existe para a tela aplicar a MESMA regra que o servidor aplica, em vez de
     * desenhar um botão que o servidor vai recusar com 403. É a metade de
     * `canSeeRankingSales` que o cliente não tem como descobrir sozinho: o
     * vínculo consultor ↔ usuário da sessão não atravessa o contexto da sessão,
     * e não vai atravessar por causa de uma tela.
     *
     * **Não é permissão, é cadastro.** Quem decide o que a pessoa pode continua
     * sendo `canAccess` sobre as duas ações; este campo só diz de que time ela
     * é. Mandá-lo a quem tem a ação larga não vaza nada — ela já vê todas as
     * posições e o time de cada uma, na lista acima.
     */
    viewerTeam: salesTeamSchema.pick({ id: true, nome: true }).nullable(),
    /**
     * O quadro que esta resposta mostra — o do consultor interno ou o do
     * externo (`comercial-41`). Os dois não se veem: a posição é contada
     * dentro do segmento, e o interno nunca aparece no quadro do externo.
     */
    segment: rankingSegmentSchema,
    /**
     * Os quadros que quem olha pode escolher — `null` quando o segmento é
     * imposto pelo perfil dela (o consultor interno vê o do interno, e o
     * externo o do externo). Quem não é de nenhum dos dois, ou tem
     * `sales.ranking:filtro_times`, escolhe.
     */
    segmentOptions: z9.array(rankingSegmentSchema).nullable()
  })
]);
var rankingSaleSchema = z9.object({
  id: z9.number().int(),
  /** `null` é "nasceu aqui, na tela" — mesmo sentido de `saleSchema.idExterno` */
  idExterno: z9.string().nullable(),
  classification: plateClassificationSchema,
  /** `null` é "a origem não disse quanto" — ver `sales_sales.amount`. */
  value: moneySchema.nullable(),
  /** a situação como a origem a escreveu — texto, pelo motivo de `saleSchema` */
  situacao: z9.string().nullable(),
  /** o código do veículo, **não a placa**: a placa vem em `vehicle` */
  codigoVeiculo: z9.string().nullable(),
  codigoAssociado: z9.string().nullable(),
  /** o que os dois códigos significam — ver `saleMemberLabelSchema` */
  member: saleMemberLabelSchema.nullable(),
  vehicle: saleVehicleLabelSchema.nullable(),
  /** entrou na contagem da P5? Vem de `countsForRanking`, nunca da tela */
  counted: z9.boolean()
});
var rankingConsultantSalesSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("nao_apuravel"),
    reasons: z9.array(reasonNotToSettleSchema)
  }),
  z9.object({
    estado: z9.literal("apurado"),
    period: periodSchema,
    /** a regra que decidiu cada `counted` — uma, e da casa */
    criterion: rankingCountingSchema,
    consultant: consultantSummarySchema,
    achieved: z9.number().int(),
    ignored: z9.number().int(),
    /** `null` é "a origem não disse quanto" — ver `sales_sales.amount`. */
    value: moneySchema.nullable(),
    /**
     * As placas do consultor na competência — as que contaram e as que não.
     * Uma lista só com as contadas não explicaria a diferença que o quadro
     * anuncia, que é a coisa toda.
     *
     * **Paginada, e `achieved`/`ignored`/`value` acima NÃO são.** Os três são
     * sempre da competência inteira: eles existem para ser conferidos contra a
     * lista, e um total que mudasse de valor ao virar a página não conferiria
     * coisa nenhuma — diria que a pessoa vendeu dez.
     *
     * `total` daqui é o número de LINHAS, e bate com `achieved + ignored`. A
     * redundância é de propósito: é ela que deixa a tela dizer "10 de 37" sem
     * somar dois campos que vieram para outra coisa.
     */
    sales: paginated(rankingSaleSchema)
  })
]);
var plateTallySchema = z9.object({
  classification: plateClassificationSchema,
  quantity: z9.number().int()
});
var teamMemberPerformanceSchema = z9.object({
  consultant: consultantSummarySchema,
  profile: consultantProfileSchema,
  /** `false` é "parou de vender": continua dona das placas antigas */
  active: z9.boolean(),
  /** as quatro categorias, sempre, na ordem de `PLATE_CLASSIFICATIONS` */
  plates: z9.array(plateTallySchema),
  /** as que entram na contagem da casa — de `rankingTallyOf`, nunca somadas aqui */
  counted: z9.number().int(),
  /** as que a contagem descarta; hoje é `reativacao`, e os dois números batem */
  ignored: z9.number().int(),
  /** o que as CONTADAS somaram; `null` quando alguma não tem valor declarado */
  value: moneySchema.nullable()
});
var teamPerformanceTotalsSchema = z9.object({
  /** a MESMA lista do integrante, somada — as quatro, na mesma ordem */
  plates: z9.array(plateTallySchema),
  counted: z9.number().int(),
  ignored: z9.number().int(),
  value: moneySchema.nullable(),
  /** quantos consultores ativos apontam para o time HOJE */
  members: z9.number().int(),
  /** quantos deles têm ao menos uma placa na competência */
  scoring: z9.number().int()
});
var teamSubtreeSchema = z9.object({
  team: salesTeamSchema.pick({ id: true, nome: true }),
  leader: userSummarySchema.nullable(),
  totals: teamPerformanceTotalsSchema
});
var teamBoardSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("nao_apuravel"),
    team: salesTeamSchema.pick({ id: true, nome: true }),
    reasons: z9.array(reasonNotToSettleSchema)
  }),
  z9.object({
    estado: z9.literal("apurado"),
    period: periodSchema,
    team: salesTeamSchema.pick({ id: true, nome: true }),
    leader: userSummarySchema.nullable(),
    /** a regra que produziu cada `counted` — uma, e da casa */
    criterion: rankingCountingSchema,
    /**
     * O time INTEIRO, com a árvore abaixo dele: os integrantes e todos os
     * ramos de `subteams`. Decisão do responsável em 23/09/2026 — as vendas do
     * time de baixo são somadas às de cima. Só no painel: a premiação de líder
     * continua somando só quem responde direto ao líder.
     */
    totals: teamPerformanceTotalsSchema,
    /**
     * Os integrantes, do maior `counted` para o menor.
     *
     * **Não há `position`**, e a ausência é a decisão. Posição é o assunto do
     * ranking, que a calcula com desempate estável e a recalcula por recorte;
     * um "1º" aqui dentro de uma equipe de três leria igual ao 1º da praça, que
     * é a confusão que o filtro de time do ranking já paga uma vez. Esta tela
     * ordena para ser legível, e não coroa ninguém.
     *
     * Entra **todo** integrante ativo do time, inclusive quem não vendeu — a
     * linha zerada é informação para quem lidera. Quem já não é do time não
     * entra, mesmo tendo vendido no mês: o agrupamento é pela composição de
     * hoje, porque `sales_consultants.team_id` não tem vigência, e a tela diz
     * isso.
     *
     * **O líder é sempre integrante** do time que lidera (23/09/2026), mesmo
     * sendo membro comum do time de cima.
     */
    members: z9.array(teamMemberPerformanceSchema),
    /** Os times logo abaixo, cada um com o total do ramo dele. */
    subteams: z9.array(teamSubtreeSchema)
  })
]);
var teamDiagramNodeSchema = z9.lazy(
  () => z9.object({
    team: salesTeamSchema.pick({ id: true, nome: true }),
    leader: userSummarySchema.nullable(),
    members: z9.array(
      z9.object({
        consultant: consultantSummarySchema,
        leadsTeamId: z9.number().int().nullable()
      })
    ),
    children: z9.array(teamDiagramNodeSchema)
  })
);
var teamPerformanceSchema = z9.object({
  /** vazia é "o cadastro não põe esta pessoa como líder de time nenhum" */
  options: z9.array(salesTeamSchema.pick({ id: true, nome: true })),
  /** `null` exatamente quando `options` está vazia */
  board: teamBoardSchema.nullable(),
  /**
   * A árvore do time escolhido, para a visão de diagrama. `null` junto com
   * `board` nulo. Ela sai mesmo quando o quadro recusa: a estrutura não
   * depende da categoria das placas.
   */
  diagram: teamDiagramNodeSchema.nullable()
});
var teamOverviewFiguresSchema = z9.discriminatedUnion("estado", [
  z9.object({ estado: z9.literal("apurado"), totals: teamPerformanceTotalsSchema }),
  z9.object({
    estado: z9.literal("nao_apuravel"),
    reasons: z9.array(reasonNotToSettleSchema)
  })
]);
var teamOverviewRowSchema = z9.object({
  team: salesTeamSchema.pick({ id: true, nome: true }),
  leader: userSummarySchema.nullable(),
  parentTeamId: z9.number().int().nullable(),
  /** 0 é nível 1 — o time pendurado direto na raiz da base */
  depth: z9.number().int(),
  figures: teamOverviewFiguresSchema
});
var teamsOverviewSchema = z9.object({
  period: periodSchema,
  tenant: z9.object({ nome: z9.string(), figures: teamOverviewFiguresSchema }),
  /** em pré-ordem: cada time logo seguido dos de baixo, irmãos pelo nome */
  teams: z9.array(teamOverviewRowSchema),
  diagram: z9.array(teamDiagramNodeSchema)
});
var COMMISSION_LADDERS = [
  "comum",
  "interno",
  "externo",
  "base_reativacao",
  "bronze",
  "bronze_interno",
  "bronze_externo"
];
var commissionLadderNameSchema = z9.enum(COMMISSION_LADDERS);
var ladderStepSchema = z9.object({
  id: z9.number().int(),
  ladder: commissionLadderNameSchema,
  /** o piso de placas do degrau; `0` no Bronze, que não é degrau */
  platesMin: z9.number().int(),
  amountPerPlate: moneySchema,
  validFrom: periodSchema,
  /** INCLUSIVE. `null` é o degrau corrente — vale daqui em diante */
  validUntil: periodSchema.nullable()
});
var commissionLadderSchema = z9.discriminatedUnion("estado", [
  z9.object({
    /** nenhuma linha de `sales_tiers` alcança esta competência */
    estado: z9.literal("sem_regua"),
    period: periodSchema
  }),
  z9.object({
    estado: z9.literal("incompleta"),
    period: periodSchema,
    /** quais das três não têm degrau nenhum aqui — nunca "falta alguma" */
    missing: z9.array(commissionLadderNameSchema).min(1),
    /** o que já foi declarado, para a tela não pedir de novo o que existe */
    rungs: z9.array(ladderStepSchema)
  }),
  z9.object({
    estado: z9.literal("configurada"),
    period: periodSchema,
    rungs: z9.array(ladderStepSchema).min(1)
  })
]);
var appliedTierSchema = z9.object({
  id: z9.number().int(),
  /** o piso, INCLUSIVE: a partir de tantas placas no mês cada placa vale isto */
  platesMin: z9.number().int(),
  amountPerPlate: moneySchema
});
var commissionBreakdownSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("nao_gravado"),
    reasons: z9.array(reasonNotToSettleSchema)
  }),
  z9.object({
    estado: z9.literal("gravado"),
    /** `null` é "o total não alcançou piso nenhum" — ver o cabeçalho */
    tier: appliedTierSchema.nullable(),
    /** as placas que a régua daquele perfil contou. **É este número que escolheu
     *  a faixa** — as Bronze incluídas */
    platesCounted: z9.number().int(),
    /** quantas das contadas são Bronze: escolhem a faixa e não são pagas por ela */
    platesBronze: z9.number().int(),
    /** as que a régua daquele perfil descartou; não entram em conta nenhuma */
    platesIgnoradas: z9.number().int(),
    /** `amountPerPlate` da faixa × (`countedPlates` − `bronzePlates`) */
    platesValue: moneySchema,
    /** o valor fixo da placa Bronze × `bronzePlates` — paga inclusive sem faixa */
    bronzeValue: moneySchema
  })
]);
var periodBonusSchema = z9.object({
  estado: z9.literal("nao_apurada"),
  reasons: z9.array(reasonNotToSettleSchema)
});
var commissionPaymentSchema = z9.object({
  estado: z9.literal("nao_sabemos"),
  /**
   * Quem vai responder isso quando alguém responder. Fixo e não texto livre: é
   * a mesma decisão em toda linha, e uma mensagem por linha viraria cinco
   * redações da mesma frase.
   */
  owner: z9.literal("ciclo_da_gestao")
});
var commissionSchema = z9.object({
  consultant: consultantSummarySchema,
  period: periodSchema,
  /** o que a competência inteira paga àquele consultor */
  value: moneySchema,
  /** quando este número foi produzido — não é quando a tela o leu */
  assessedAt: instantSchema,
  breakdown: commissionBreakdownSchema,
  bonus: periodBonusSchema,
  payment: commissionPaymentSchema,
  archivedAt: instantSchema.nullable()
});
var periodCommissionsSchema = z9.discriminatedUnion("estado", [
  z9.object({
    /** nenhuma linha em `sales_commissions` nesta base, em competência nenhuma */
    estado: z9.literal("sem_apuracao"),
    /** por que a apuração ainda não roda — para a tela dizer o que falta */
    reasons: z9.array(reasonNotToSettleSchema)
  }),
  z9.object({
    estado: z9.literal("apurada"),
    period: periodSchema,
    rows: z9.array(commissionSchema),
    /** a soma de `rows` — o custo daquela competência para a base */
    total: moneySchema
  })
]);
var settlementResultSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("nao_apurada"),
    reasons: z9.array(reasonNotToSettleSchema)
  }),
  z9.object({
    estado: z9.literal("apurada"),
    period: periodSchema,
    assessedAt: instantSchema,
    /** quantos consultores ganharam linha nesta passagem */
    consultants: z9.number().int(),
    total: moneySchema
  })
]);
var commissionExportSchema = z9.object({
  de: periodSchema,
  until: periodSchema,
  /** quando esta exportação foi montada — número em documento vem com data */
  generatedAt: instantSchema,
  rows: z9.array(commissionSchema),
  /** a soma de `rows`, atravessando as competências do intervalo */
  total: moneySchema
});
var cashEntrySchema = z9.object({
  id: z9.number().int(),
  tipo: entryTypeSchema,
  categoria: z9.string(),
  /** sempre positivo; a direção é o `tipo` */
  value: moneySchema,
  /** quando o dinheiro se moveu */
  data: businessDateSchema,
  descricao: z9.string(),
  createdBy: userSummarySchema.nullable(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var periodTotalsSchema = z9.object({
  entry: moneySchema,
  exit: moneySchema,
  /** `entrada − saida`; negativo é um resultado legítimo */
  balance: moneySchema
});
var cashBookSchema = paginated(cashEntrySchema).extend({
  de: businessDateSchema,
  until: businessDateSchema,
  /** do período inteiro, nunca da página */
  totals: periodTotalsSchema,
  /** as categorias já usadas nesta base — sugestão, não catálogo */
  categories: z9.array(z9.string())
});
var salesNumbersSchema = z9.discriminatedUnion("estado", [
  z9.object({
    /** nenhuma venda nesta base, em competência nenhuma */
    estado: z9.literal("sem_vendas")
  }),
  z9.object({
    estado: z9.literal("medido"),
    /** a competência corrente da praça, para a tela dizer de qual mês fala */
    period: periodSchema,
    amount: z9.number().int(),
    value: moneySchema,
    /** vendas da competência ainda sem consultor casado — fila de trabalho */
    withoutConsultant: z9.number().int()
  })
]);
var salesPanelConsultantSchema = z9.object({
  consultant: consultantSummarySchema,
  /** o time de HOJE — `sales_consultants.team_id` não tem vigência */
  team: z9.object({ id: z9.number().int(), nome: z9.string() }).nullable(),
  profile: consultantProfileSchema,
  /** as quatro categorias, sempre, na ordem de `PLATE_CLASSIFICATIONS` */
  plates: z9.array(plateTallySchema),
  /** placas sem classificação — não entram em nenhuma das quatro */
  unclassified: z9.number().int(),
  /** venda + troca + Bronze, por `rankingTallyOf` — a contagem do ranking */
  counted: z9.number().int(),
  /** a soma da adesão das vendas que DECLARAM valor */
  adhesion: moneySchema,
  /** vendas sem valor de adesão, fora da soma acima */
  withoutAmount: z9.number().int()
});
var salesPanelFiguresSchema = z9.object({
  plates: z9.array(plateTallySchema),
  unclassified: z9.number().int(),
  counted: z9.number().int(),
  adhesion: moneySchema,
  withoutAmount: z9.number().int()
});
var salesPanelSchema = z9.object({
  period: periodSchema,
  /**
   * Até que dia a competência vai: o último do mês, ou o dia de HOJE quando a
   * competência é a corrente. É o comprimento do gráfico diário e o recorte da
   * comparação com o mês anterior.
   */
  throughDay: z9.number().int(),
  /**
   * O último dia da competência com alguma venda da BASE — sem filtro nenhum.
   * `null` é o mês sem venda com dia. É o frescor que a tela declara: quando ele
   * fica atrás de `throughDay`, o mês parece menor do que é, e o motivo mais
   * provável é a importação ou o espelho do ERP atrasados, não a operação.
   */
  lastSaleDay: z9.number().int().nullable(),
  /**
   * As metas de TIME da competência, cada uma com o próprio atingimento — o
   * mesmo `attainment` da tela de Metas, pela mesma função. **Não segue os
   * filtros de consultor, time e dia**: a meta é do mês e da equipe.
   *
   * A soma delas é a "meta geral" que a tela mostra, e a tela diz que é uma
   * soma: a casa não tem meta da base declarada como tal.
   */
  goals: z9.array(
    z9.object({
      team: z9.object({ id: z9.number().int(), nome: z9.string() }),
      /** `null` quando a meta daquele time é só em dinheiro */
      quantity: z9.number().int().nullable(),
      attainment: targetAchievementSchema
    })
  ),
  /** a competência pedida, dentro dos filtros */
  current: salesPanelFiguresSchema,
  /**
   * A competência anterior, **nos mesmos dias**: os dias filtrados, ou de 1 a
   * `throughDay` quando não há filtro de dia. Comparar um mês pela metade com
   * um mês inteiro diria que a operação caiu todo dia 15.
   */
  previous: salesPanelFiguresSchema.extend({ period: periodSchema }),
  /** um ponto por dia, de 1 a `throughDay`, com os dias fora do filtro zerados */
  daily: z9.array(
    z9.object({
      day: z9.number().int(),
      /** todas as placas do dia, de qualquer categoria */
      plates: z9.number().int(),
      adhesion: moneySchema
    })
  ),
  /** vendas do recorte sem dia conhecido — ver o cabeçalho */
  withoutDay: z9.number().int(),
  /** as placas por tipo de veículo, como o ERP o nomeia; `null` é sem veículo no núcleo */
  vehicleTypes: z9.array(
    z9.object({
      label: z9.string().nullable(),
      plates: z9.number().int(),
      adhesion: moneySchema
    })
  ),
  /** todo consultor ativo do recorte, inclusive quem não vendeu */
  consultants: z9.array(salesPanelConsultantSchema),
  /** as vendas do recorte ainda sem consultor casado — só sem filtro de pessoa */
  withoutConsultant: z9.object({ plates: z9.number().int(), adhesion: moneySchema }),
  /** as opções dos filtros, de dentro da rota — ela não exige `sales.teams:ver` */
  options: z9.object({
    consultants: z9.array(
      z9.object({
        id: z9.number().int(),
        nome: z9.string(),
        teamId: z9.number().int().nullable()
      })
    ),
    teams: z9.array(z9.object({ id: z9.number().int(), nome: z9.string() }))
  })
});
var SALES_COMPARISON_SESSIONS = [
  "geral",
  "venda",
  "troca",
  "reativacao",
  "bronze"
];
var salesComparisonSessionSchema = z9.enum(SALES_COMPARISON_SESSIONS);
var salesComparisonMonthSchema = z9.object({
  period: periodSchema,
  /** até que dia a competência vai — hoje, no mês corrente; o último, nos outros */
  throughDay: z9.number().int(),
  /** o último dia com venda da BASE, sem filtro; `null` é mês sem venda com dia */
  lastSaleDay: z9.number().int().nullable(),
  /** vendas do recorte sem dia conhecido: nos totais, fora da série diária */
  withoutDay: z9.number().int(),
  sessions: z9.array(
    z9.object({
      session: salesComparisonSessionSchema,
      plates: z9.number().int(),
      adhesion: moneySchema,
      withoutAmount: z9.number().int(),
      /** um ponto por dia, de 1 ao último dia do mês, sem acumular */
      daily: z9.array(
        z9.object({
          day: z9.number().int(),
          plates: z9.number().int(),
          adhesion: moneySchema
        })
      ),
      /** as placas da sessão por tipo de veículo; `null` é sem veículo no núcleo */
      vehicleTypes: z9.array(
        z9.object({
          label: z9.string().nullable(),
          plates: z9.number().int(),
          adhesion: moneySchema
        })
      ),
      /**
       * As placas da sessão por consultor — só quem vendeu nela. `null` é a
       * venda sem consultor casado, que só existe sem filtro de pessoa.
       */
      consultants: z9.array(
        z9.object({
          consultantId: z9.number().int().nullable(),
          plates: z9.number().int(),
          adhesion: moneySchema
        })
      )
    })
  )
});
var salesComparisonSchema = z9.object({
  /** o mês de base — `baseline`, e não `base`: aqui "base" é a praça */
  baseline: salesComparisonMonthSchema,
  reference: salesComparisonMonthSchema,
  /**
   * Quem aparece no resumo por consultor: todo consultor ativo do recorte, mais
   * quem vendeu em qualquer um dos dois meses. As placas de cada um estão nas
   * sessões, por `consultantId`.
   */
  consultants: z9.array(
    z9.object({
      consultant: consultantSummarySchema,
      team: z9.object({ id: z9.number().int(), nome: z9.string() }).nullable(),
      profile: consultantProfileSchema
    })
  ),
  options: salesPanelSchema.shape.options
});
var awardFamilySchema = z9.enum([
  "recorrencia",
  "direta",
  "produto",
  "antecipada"
]);
var awardBasisSourceSchema = z9.enum(["proprio", "hierarquia"]);
var awardLineCommon = {
  id: z9.number().int(),
  title: z9.string(),
  /**
   * Presente quando a linha é uma CONCESSÃO RECEBIDA: a parte da premiação de
   * líder de outra pessoa que ela repartiu com quem está sendo olhado.
   *
   * `id` e `title` continuam sendo os da premiação do líder, e `percentage` é
   * o que ESTA pessoa recebe — não o teto. Ausente em toda premiação própria.
   */
  grantedBy: userSummarySchema.optional(),
  /**
   * Presente só na linha de uma premiação de LÍDER — o que o líder vê sobre o
   * time dele naquela competência.
   *
   * `members` é quantos respondiam a ele no último dia do mês; `grants` é com
   * quem ele repartiu, e quanto. Sem isto, a linha de líder e a da carteira
   * própria apareciam com o mesmo título e nada dizia qual era qual, nem que o
   * valor já era o que SOBROU depois da repartição.
   */
  team: z9.object({
    members: z9.number().int(),
    grants: z9.array(z9.object({ member: userSummarySchema, percentage: z9.string() }))
  }).optional(),
  category: z9.enum(["1x", "2x"]),
  family: awardFamilySchema,
  /**
   * Ponto percentual, como texto: `numeric` não cabe em float.
   *
   * **Nulo na família `direta`**, que não paga por percentual. A tela precisa
   * do nulo para não desenhar "null%" — e para saber que o número ao lado do
   * valor é outro: lá é a faixa alcançada.
   */
  percentage: z9.string().nullable(),
  /**
   * Quantos boletos entraram na base.
   *
   * Pagos no lado `garantido`, em aberto no `perdendo` — o que muda é o
   * recorte, e quem diz qual é o `state` da resposta. A contagem não participa
   * da conta desde 21/09/2026, quando a tarifa passou a vir somada do espelho:
   * ela é o que a tela mostra ao lado do valor.
   */
  boletos: z9.number().int(),
  /**
   * Quantas placas ativaram — **só na família `direta`**, nula na outra.
   *
   * É o número que a pessoa confere: "fiz 10 contratos, 5 pagaram no prazo".
   */
  plates: z9.number().int().nullable(),
  /**
   * O piso da faixa alcançada, ou **nulo quando nenhuma foi**.
   *
   * Nulo com `plates` preenchido é "a contagem ficou abaixo do menor degrau" —
   * e é o que permite à tela explicar um R$ 0,00 em vez de deixá-lo parecendo
   * defeito.
   */
  tierReached: z9.number().int().nullable(),
  /**
   * O último dia do ciclo da `direta` — `AAAA-MM-DD`, nulo na `recorrencia`.
   *
   * É o último dia do mês mais o prazo — janeiro com 45 dias fecha em 17/03 —,
   * e ele **não** é o prazo de cada contrato: o de 01/01 vence em 15/02. Este
   * aqui é o dia em que a contagem do mês para de mudar, porque é o prazo do
   * último contrato possível. Antes dele, a contagem é piso.
   *
   * **Também nulo na `direta` sem a regra do prazo declarada.** Sem prazo o
   * primeiro boleto pode ser pago a qualquer momento, então a contagem nunca
   * fecha — e `partial` vem `true` para sempre. Os dois nulos dizem coisas
   * diferentes, e quem separa é `family`: na `recorrencia` não há ciclo; na
   * `direta`, há e não termina.
   */
  closesOn: businessDateSchema.nullable(),
  /**
   * **Só na `direta`**: o mês dos contratos que esta linha conta.
   *
   * Nem sempre é o mês da tela, e a diferença é decisão de 15/10/2026: a
   * direta que vai junto com a recorrência de setembro é a de AGOSTO, porque o
   * ciclo de 45 dias de agosto é o que fecha no mês do pagamento (15/10). Sem
   * este campo a tela diria "contratos de setembro" sobre contratos de agosto.
   */
  competence: periodSchema.optional(),
  /** `true` quando a varredura de boletos não terminou: a base é piso. */
  partial: z9.boolean(),
  /**
   * **Só na família `produto`**: quantas linhas da base são de veículo cujos
   * produtos o SGA ainda não foi perguntado. Elas ficam FORA do valor, e o
   * valor é piso enquanto este número for maior que zero — a tela diz isso. O
   * Extrato, que grava dinheiro, recusa (`produtos_nao_consultados`).
   */
  uncheckedVehicles: z9.number().int().optional(),
  /**
   * **Só nas famílias que contam placa ativada** (`direta`, `antecipada`): a
   * régua inteira da premiação, degrau a degrau.
   *
   * Entrou em 19/10/2026 para o simulador da Carteira — "faltam 3 placas para
   * R$ 630,00" e "com 15 placas, quanto?". A tela lê a régua que PAGA, e não
   * uma cópia: o degrau escolhido continua sendo `reachedTier` do domínio, nos
   * dois lados.
   */
  tiers: z9.array(z9.object({ minPlates: z9.number().int(), amount: moneySchema })).optional()
  /* --- `supersededBy` SAIU em 16/09/2026 ---------------------------------- *
   *
   * Trazia os títulos das premiações nominais que tomavam pedaços do range da
   * geral, e existia para a linha da geral não aparecer menor — ou zerada —
   * sem nada explicando.
   *
   * Sem premiação de escopo `todos`, nenhuma linha é recortada por outra: toda
   * premiação é de uma pessoa e conta o range dela inteiro. O campo passaria a
   * ser sempre `[]`, e uma lista que nunca tem nada é uma pergunta que a tela
   * faz para sempre sem nunca receber resposta.
   * ----------------------------------------------------------------------- */
};
var awardRefusalSchema = z9.enum([
  "tipos_de_boleto_nao_declarados",
  /* Família `produto`: a régua não tem nenhum produto. */
  "produtos_nao_declarados",
  /* Família `produto`: há veículo da base sem produtos consultados. */
  "produtos_nao_consultados",
  "espelho_de_boleto_incompleto",
  "tarifa_do_banco_ausente",
  /*
   * Este é **só do lado perdido**, e ele aponta para a Configuração da
   * base — não para o cadastro da premiação.
   *
   * Os tipos que contam como perda são uma configuração da praça inteira, e
   * ela nasce vazia. Vazia RECUSA: sem filtro entrariam adesão, multa e o
   * que mais o cadastro do ERP tiver, e o número do lado perdido cresceria
   * sem ninguém ter decidido isso (regra 14).
   *
   * Motivo próprio, e não `tipos_de_boleto_nao_declarados`, porque o
   * conserto é em outro lugar: aquele manda editar a premiação, este manda
   * abrir Configuração da base. Uma frase só mandaria metade das pessoas ao
   * lugar errado.
   */
  /*
   * Até 27/10/2026 havia um `situacoes_de_boleto_nao_espelhadas`: a praça
   * que nunca sincronizou a tabela de situações recusava o lado perdido.
   * Saiu com a recusa da porta para a tela — o lado perdido sai zerado e
   * marcado como piso (`partial`).
   */
  "tipos_de_boleto_perdidos_nao_declarados",
  /* ------------------------------------------------------------- *
   * As três da premiação de LÍDER (`basisSource = 'hierarquia'`)
   *
   * Ela apura sobre a base de quem responde diretamente ao dono, e as três
   * coisas que podem faltar são de naturezas diferentes — por isso três
   * nomes, e não um "não deu para apurar o time".
   * ------------------------------------------------------------- */
  /*
   * O ramo resolveu VAZIO na competência: ninguém respondia ao líder
   * naquele mês. É cadastro de `app.hierarchy`, e o conserto é lá — não na
   * premiação, que pode estar perfeita.
   *
   * Recusa em vez de apurar zero porque os dois são indistinguíveis na
   * tela, e mandam a pessoa a lugares opostos: zero apurado diz "o time não
   * vendeu", e isto diz "o time não existe no sistema".
   */
  "time_sem_integrante_na_competencia",
  /*
   * Alguém do time não tem `erp_volunteer_code`, e sem ele não há como
   * perguntar ao espelho quais boletos são dele.
   *
   * Somar o resto e seguir pagaria a MENOS, em silêncio, e a diferença
   * seria exatamente a base de quem ficou de fora — invisível para quem
   * confere, porque o total continua fechando com as linhas que existem.
   */
  "consultor_do_time_sem_codigo_no_erp",
  /*
   * Um boleto entrou na base de DOIS membros do time — ele cobre um veículo
   * de cada um.
   *
   * A base de cada membro o conta inteiro, então o resíduo do líder sobre
   * ele sairia em dobro. Qual é a regra certa (contar uma vez, ratear entre
   * os dois, ou contar nos dois) **ninguém decidiu**, e é regra 14: fica de
   * fora inteira em vez de entrar numa versão aproximada.
   *
   * O preço de recusar foi medido antes de escolher, e é o que torna a
   * recusa barata: na CE, **38 boletos** em 477.879 cobrem veículos de
   * consultores diferentes, e **um só foi pago** — R$ 513,00, em
   * 27/05/2020. Nenhuma competência que alguém apura hoje é alcançada.
   */
  "boleto_de_dois_membros_do_time",
  /*
   * Uma concessão ficou **acima** do percentual da premiação.
   *
   * O cadastro recusa isso na hora de conceder, e mesmo assim o estado é
   * alcançável por um caminho que a operação pediu para existir: o líder
   * baixar o próprio percentual depois de já ter repartido. A tela avisa e
   * oferece editar os dois juntos, e quem não editar deixa a concessão
   * órfã acima do teto.
   *
   * Apurar assim pagaria ao liderado mais do que a premiação inteira e
   * deixaria o líder **negativo**. Recusar é a regra 14, e o nome manda
   * para o conserto certo: a concessão, não a premiação.
   */
  "concessao_acima_do_teto_da_premiacao",
  /*
   * Do lado de quem RECEBE a concessão: no último dia da competência, a
   * pessoa não respondia diretamente ao dono da premiação. A base do líder
   * não a soma naquele mês, então não há parte a receber — e zero apurado
   * diria "o time não vendeu" sobre o que é cadastro de time.
   */
  "liderado_fora_do_time_na_competencia",
  /* As três da família `direta`, e as três são cadastro que falta. */
  "faixas_nao_declaradas",
  "tipos_de_veiculo_nao_declarados",
  "tipos_de_adesao_nao_declarados"
]);
var awardLineSchema = z9.discriminatedUnion("estado", [
  z9.object({
    ...awardLineCommon,
    estado: z9.literal("apurado"),
    basis: moneySchema,
    amount: moneySchema,
    /**
     * O que a TARIFA DO BANCO tirou da base, para a tela explicar o número.
     *
     * É a soma de `tarifa_cobranca_banco` dos boletos da competência, e não
     * uma taxa cadastrada vezes a contagem — ver `AwardBasis.fee` no domínio.
     * Não existe mais o caso "saiu sem desconto": ou a tarifa está no espelho
     * inteiro e a linha apura, ou falta em algum boleto e a linha recusa com
     * `tarifa_do_banco_ausente`.
     */
    feeTotal: moneySchema
  }),
  z9.object({
    ...awardLineCommon,
    estado: z9.literal("recusado"),
    /**
     * Por que esta linha não pôde ser apurada.
     *
     * O primeiro é **cadastro que ninguém preencheu**, e não some sozinho — a
     * tela diz o nome do que falta. Sem tipo declarado não há o que somar: a
     * base seria "todo boleto do cadastro", que ninguém escolheu. O segundo é
     * **estado do espelho**, e some quando a esteira fechar: a de boletos da CE fecha
     * `parcial` (quatro das seis situações batem o teto de chamadas do
     * fornecedor), então nunca houve varredura completa, e apurar dinheiro
     * sobre base reconhecidamente incompleta paga a menos — a diferença só
     * apareceria no mês seguinte, para quem recebeu.
     *
     * O terceiro, `tarifa_do_banco_ausente`, não é nenhum dos dois: é o ERP que
     * não gravou `tarifa_cobranca_banco` naquele boleto. Ninguém o preenche
     * numa tela e a esteira não o conserta — na CE é competência antiga e só
     * ela (2021 tem 57,4% dos pagos com tarifa; de 2024 em diante, 100%).
     * Recusa porque a soma do espelho ignora o nulo: deixar passar descontaria
     * a tarifa de uns e nenhuma dos outros, inflando a base em silêncio.
     */
    reason: awardRefusalSchema
  })
]);
var awardCategorySchema = z9.object({
  category: z9.enum(["1x", "2x"]),
  /** `null` quando alguma linha desta categoria não pôde ser apurada. */
  total: moneySchema.nullable(),
  lines: z9.array(awardLineSchema)
});
var awardStateSchema = z9.enum(["garantido", "perdendo"]);
var managementPanelAwardsSchema = z9.object({
  /** A competência: o mês em que os boletos foram pagos, `AAAA-MM`. */
  month: periodSchema,
  /** De que lado esta leitura é. Ver `awardStateSchema`. */
  state: awardStateSchema,
  /**
   * Quantas premiações da família `direta` ficaram de FORA desta leitura.
   *
   * Sempre `0` no `garantido`, onde nenhuma fica. No `perdendo` é a contagem
   * das diretas do consultor, e elas ficam de fora **inteiras** (regra 14): a
   * direta conta PLACA ativada, não valor de boleto, e "quanto se está
   * perdendo numa premiação por placa" é pergunta que ninguém respondeu — a
   * placa cujo primeiro boleto está em aberto ainda pode ativar dentro do
   * prazo, e chamá-la de perda seria inventar uma régua.
   *
   * O número viaja para a tela DIZER que ficaram de fora. Somem em silêncio e
   * quem lê conclui que a pessoa não tem premiação direta nenhuma.
   */
  omittedDirect: z9.number().int(),
  /**
   * As categorias que têm alguma linha, na ordem `1x` e depois `2x`.
   *
   * Categoria sem nenhuma premiação **não vem** — uma coluna vazia na tela
   * afirmaria que existe uma rodada de pagamento sem nada nela, quando o que há
   * é ausência de cadastro.
   */
  categories: z9.array(awardCategorySchema),
  lines: z9.array(awardLineSchema),
  /**
   * A soma — **`null` quando alguma linha recusou**.
   *
   * Somar as que apuraram e ignorar as recusadas produziria um total que parece
   * o do mês e não é. É o número que alguém confere contra o contracheque, e um
   * total incompleto com cara de completo é o pior desfecho possível aqui.
   */
  total: moneySchema.nullable()
});
var awardPortfolioRowSchema = z9.object({
  /**
   * De qual liderado é a carteira deste boleto — só no relatório de uma
   * premiação de LÍDER, cuja base é a soma das carteiras do time. Ausente nos
   * outros dois, onde a carteira é uma só.
   */
  consultantName: z9.string().nullable().optional(),
  /** O código do liderado no ERP, ao lado de `consultantName` — a agenda dele. */
  consultantCode: z9.string().nullable().optional(),
  /**
   * `codigo_associado` e `codigo_veiculo` do SGA — a placa do relatório leva à
   * ficha do cliente na agenda (`/sales/portfolio/members/$code?vehicle=`,
   * 28/10/2026). Nulos quando o espelho não liga a linha a um veículo.
   */
  memberCode: z9.string().nullable(),
  vehicleCode: z9.string().nullable(),
  /** O nome como o ERP o escreve. Nulo quando o espelho não tem o associado. */
  memberName: z9.string().nullable(),
  /** O celular, já montado com DDD. Nulo é "o cadastro não tem". */
  memberPhone: z9.string().nullable(),
  plate: z9.string().nullable(),
  /** A descrição da situação do veículo no ERP, não o código. */
  vehicleStatus: z9.string().nullable(),
  /** A descrição da situação do boleto no ERP. */
  boletoStatus: z9.string().nullable(),
  contractDate: businessDateSchema.nullable(),
  dueOriginal: businessDateSchema.nullable(),
  dueDate: businessDateSchema.nullable(),
  paidOn: businessDateSchema.nullable(),
  /** `data_contrato` + prazo. **Só na `direta`** — ver o cabeçalho. */
  deadline: businessDateSchema.nullable(),
  /**
   * **Só na `produto`**: os produtos da régua que este veículo tem, separados
   * por " · ", e quanto a linha paga por eles. Nulo onde o veículo não tem
   * nenhum — a linha aparece mesmo assim, com `counted: false`, para a pessoa
   * ver todo veículo com boleto pago e qual ficou de fora.
   */
  products: z9.string().nullable().optional(),
  productAmount: moneySchema.nullable().optional(),
  /** **Só na `produto`**: a redução do boleto pago com desconto foi aplicada. */
  discounted: z9.boolean().optional(),
  /** **Só na `produto`**: os produtos deste veículo nunca foram consultados. */
  unchecked: z9.boolean().optional(),
  /**
   * O dinheiro da linha — ver o cabeçalho.
   *
   * No lado `garantido` é o que o boleto PAGOU (`valor_pagamento`); no
   * `perdendo` é o que está escrito no boleto que ninguém pagou
   * (`valor_boleto`). Quem diz qual dos dois é `state`, na resposta — a linha
   * sozinha não distingue, e a tela que adivinhasse pelo `paidOn` nulo estaria
   * adivinhando.
   *
   * Na `direta` e na `antecipada`, desde 28/10/2026, é o valor do 1º boleto da
   * placa — o pago, quando foi pago, e o do boleto, quando não foi — para o dia
   * a dia do relatório. A tabela delas não mostra esta coluna: a placa não
   * paga por valor, quem paga é a faixa.
   */
  amount: moneySchema.nullable(),
  /**
   * `true` quando esta linha ENTROU na conta.
   *
   * Na direta, a placa cujo primeiro boleto não foi pago no prazo aparece com
   * `false` — e aparecer é o ponto: o demonstrativo diz "30 placas" e a
   * carteira mostra as 35 que existem, com as 5 que ficaram de fora e a data
   * que as deixou. Sem isso, a diferença entre o que a pessoa vendeu e o que
   * ela recebeu não teria onde ser conferida.
   */
  counted: z9.boolean()
});
var awardPortfolioSchema = z9.object({
  award: z9.object({
    id: z9.number().int(),
    title: z9.string(),
    family: awardFamilySchema,
    /**
     * De quem são os boletos listados — e a tela diz isso no cabeçalho.
     *
     * - `propria`: a carteira de quem está sendo olhado;
     * - `time`: as carteiras de quem respondia ao líder no fim do mês — a base
     *   da premiação de líder, e não a carteira dele;
     * - `concessao`: a carteira de quem RECEBEU parte da premiação de líder de
     *   outra pessoa, na janela da concessão.
     */
    basis: z9.enum(["propria", "time", "concessao"]),
    /**
     * O range de `data_contrato` que a premiação considera — a tela diz "de
     * quando até quando" acima do valor. `contractUntil` é FECHADO, e nulo é
     * sem limite: a premiação alcança o contrato que ainda vai ser feito.
     */
    contractFrom: businessDateSchema,
    contractUntil: businessDateSchema.nullable()
  }),
  month: periodSchema,
  /**
   * De que lado esta lista é — o que decide o que cada linha significa.
   *
   * No `garantido`, `paidOn` e `amount` estão preenchidos e a linha é um boleto
   * que entrou. No `perdendo`, `paidOn` é nulo em toda linha por construção, e
   * `amount` é o valor DO BOLETO, que ninguém pagou. A tela troca as colunas
   * com este campo, e não com uma suposição sobre o que veio nulo.
   */
  state: awardStateSchema,
  /** Quantas linhas ENTRARAM na conta — o número que o demonstrativo mostra. */
  counted: z9.number().int(),
  /**
   * **Só na `produto`**: quantas linhas pagaram por CADA produto da régua,
   * sobre a lista inteira e não a página (15/10/2026). Na combinação `soma` o
   * veículo com dois produtos conta nos dois, e a soma passa de `counted`.
   */
  productCounts: z9.array(
    z9.object({
      code: z9.string(),
      label: z9.string().nullable(),
      count: z9.number().int()
    })
  ).optional(),
  items: z9.array(awardPortfolioRowSchema),
  page: z9.number().int(),
  perPage: z9.number().int(),
  total: z9.number().int()
});
var awardDailyRowSchema = z9.object({
  /** As chaves da ficha do cliente na agenda — ver `awardPortfolioRowSchema`. */
  memberCode: z9.string().nullable(),
  vehicleCode: z9.string().nullable(),
  /** O consultor dono da placa, nas premiações de time; nulo na própria. */
  consultantCode: z9.string().nullable(),
  memberName: z9.string().nullable(),
  plate: z9.string().nullable(),
  vehicleStatus: z9.string().nullable(),
  boletoStatus: z9.string().nullable(),
  paidOn: businessDateSchema.nullable(),
  dueDate: businessDateSchema.nullable(),
  /** A coluna do gráfico em que a linha caiu (5 a 30); nula sem data. */
  bucket: z9.number().int().nullable(),
  /** A parte desta linha na premiação — ganha, ou deixada de ganhar. */
  award: moneySchema
});
var awardDailySchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("medido"),
    month: periodSchema,
    /** As seis colunas — dias 5, 10, 15, 20, 25 e 30 —, em ordem, sempre. */
    days: z9.array(
      z9.object({
        day: z9.number().int(),
        paid: moneySchema,
        open: moneySchema
      })
    ),
    /** O que não tinha data — fora do gráfico, e a tela diz quanto. */
    undated: z9.object({
      paid: moneySchema,
      open: moneySchema,
      count: z9.number().int()
    }),
    /**
     * O lado "deixa de ganhar": `apurado`; `nao_se_aplica` na família que conta
     * placa, onde a placa em aberto ainda pode ativar e o painel não chama
     * isso de perda; `recusado` quando o painel não apurou esse lado. Fora do
     * `apurado`, todo `open` é zero e a tela não desenha a série.
     */
    lost: z9.enum(["apurado", "nao_se_aplica", "recusado"]),
    /** O nome da premiação, para o cabeçalho da tela. */
    title: z9.string(),
    /**
     * As linhas da tabela "Dados", cada uma com a SUA parte da premiação — a
     * mesma repartição que montou os dias (28/10/2026, pedido do dono). São a
     * lista inteira, e não uma página: a parte de cada linha só existe sobre
     * todas, e a tela pagina. `gained` são as que geraram o valor ganho;
     * `lost`, as em aberto que geram o que se deixa de ganhar.
     */
    rows: z9.object({
      gained: z9.array(awardDailyRowSchema),
      lost: z9.array(awardDailyRowSchema)
    })
  }),
  /**
   * A premiação não foi apurada no mês. Não há valor a repartir, e um gráfico
   * de zeros diria que nada foi ganho; a tela mostra o motivo.
   */
  z9.object({
    estado: z9.literal("sem_total"),
    month: periodSchema,
    reason: awardRefusalSchema.nullable()
  })
]);
var managementPanelSchema = z9.discriminatedUnion("estado", [
  z9.object({
    /**
     * Quem pediu não é consultor nesta base, e não tem a ação larga.
     *
     * Não é 403: a permissão está concedida e a rota respondeu. O que falta é
     * **cadastro** — ninguém vinculou aquela pessoa a um código do ERP nesta
     * praça —, e a tela diz isso pelo nome, com o que fazer a respeito.
     */
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  /*
   * Até 27/10/2026 havia aqui um `sem_espelho` — "o espelho de veículos daquela
   * praça nunca sincronizou" —, que nenhum código produzia: a porta recusava
   * com 503 e a tela recebia o erro cru. Desde então a porta responde à tela
   * com o que houver, e o painel sai `medido`, com os zeros que a casa tem.
   */
  z9.object({
    estado: z9.literal("medido"),
    consultant: z9.object({
      /**
       * O vínculo da casa (`app.sales_consultants.id`) com este código, ou nulo
       * quando o ERP conhece a pessoa e a plataforma ainda não a vinculou.
       *
       * É o que o botão "Editar premiações" do painel precisa (23/10/2026): a
       * premiação pertence ao vínculo, e o painel é endereçado pelo código do
       * ERP. Nulo desabilita o botão com o motivo, em vez de esconder.
       */
      id: z9.number().int().nullable(),
      /** O código NAQUELA base — 394 no CE e 75 no GO são a mesma pessoa. */
      erpVolunteerCode: z9.string(),
      /**
       * O nome como o ERP o escreve, ou nulo.
       *
       * Nulo não é erro: há código em `sga_veiculos` sem linha em
       * `sga_voluntarios`, porque `/listar/voluntario/ativo` é um recorte e
       * quem saiu não volta nele. Medido na CE em 14/09/2026: 18 códigos assim,
       * carregando 604 veículos. A tela mostra o código e diz que o cadastro do
       * ERP não tem aquela pessoa entre os ativos.
       */
      nome: z9.string().nullable(),
      /** `true` quando o painel é de outra pessoa — exige a ação larga. */
      ofAnotherConsultant: z9.boolean()
    }),
    portfolio: z9.object({
      vehicles: z9.number().int(),
      /**
       * Quantos estão ativos — ou **`null` quando a praça não classificou** as
       * situações do ERP.
       *
       * `app.erp_status_classifications` nasce vazia, e enquanto estiver assim
       * a pergunta não tem resposta. `0` seria uma, errada e indistinguível de
       * uma carteira inteira cancelada; a tela mostra que não sabe e aponta
       * para onde se classifica.
       *
       * Também `null` quando algum veículo da carteira está numa situação que
       * a praça não classificou (`vehiclesUnclassified`): somar só o resto
       * daria um número menor que o real sem dizer que é menor.
       */
      vehiclesActive: z9.number().int().nullable(),
      /** Veículos da carteira em situação ainda não classificada pela praça. */
      vehiclesUnclassified: z9.number().int(),
      members: z9.number().int(),
      boletos: z9.number().int(),
      boletosPaid: z9.number().int(),
      /**
       * `true` quando a última varredura de boletos fechou `parcial`.
       *
       * Na CE de hoje é o caso: quatro das seis situações bateram o orçamento
       * de chamadas do fornecedor, então os dois números de boleto são **piso**,
       * não total. O sinal vem no payload porque quem lê um cartão não lê o
       * rodapé — e um piso desenhado como fato é a tela afirmando o que o código
       * não cumpre.
       */
      boletosPartial: z9.boolean()
    }),
    award: managementPanelAwardsSchema
  })
]);
var walletHistoryMonthSchema = z9.object({
  month: periodSchema,
  amount: moneySchema.nullable(),
  /** Quantos boletos pagos geraram entrada; nulo junto com `amount`. */
  boletos: z9.number().int().nullable(),
  /**
   * O mês ainda não terminou — ou a esteira ainda pode trazer boleto dele.
   * Compará-lo com um mês fechado mede o calendário, e não a carteira.
   */
  partial: z9.boolean()
});
var walletMixSliceSchema = z9.object({
  /** O rótulo como o ERP o dá. Nulo é "o cadastro não diz". */
  label: z9.string().nullable(),
  vehicles: z9.number().int()
});
var walletInsightsSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    month: periodSchema,
    /**
     * Os doze meses que terminam no escolhido, do mais antigo ao mais novo.
     * `recordedSince` é o primeiro mês com Extrato na base — nulo quando a
     * base ainda não tem nenhum.
     */
    history: z9.object({
      recordedSince: periodSchema.nullable(),
      months: z9.array(walletHistoryMonthSchema)
    }),
    /**
     * O DIA A DIA da competência, pelo Extrato gravado (29/10/2026) — a curva
     * da Evolução das Métricas. `days` vai do dia 1 a `throughDay`: o último
     * dia do mês, ou hoje no mês corrente (o dia que ainda não chegou não é
     * zero). `amount` é o que entrou NAQUELE dia, pela data do pagamento; a
     * tela acumula. `undated` é o que o Extrato tem sem dia de pagamento — a
     * faixa e o produto por placa —, fora da curva, e a tela diz quanto.
     */
    daily: z9.object({
      throughDay: z9.number().int(),
      days: z9.array(z9.object({ day: z9.number().int(), amount: moneySchema })),
      undated: moneySchema
    }),
    /**
     * O TICKET MÉDIO da competência — quanto o associado pagou, em média, por
     * boleto (19/10/2026). Sai do Extrato gravado, com cada boleto contado UMA
     * vez mesmo quando entrou em duas premiações. `average` é nulo sem boleto
     * pago: zero diria que o boleto médio custou R$ 0,00.
     */
    ticket: z9.object({
      boletos: z9.number().int(),
      paid: moneySchema,
      average: moneySchema.nullable()
    }),
    /**
     * A BASE MEDIDA da calculadora de planejamento (20/10/2026) — o último mês
     * FECHADO até a competência, pelo Extrato: quantos planos pagaram (cada
     * boleto uma vez), quanto eles pagaram e quanto de recorrência geraram.
     *
     * Os planos são só os das premiações de recorrência PRÓPRIAS: na de líder
     * o Extrato tem os boletos do time, e o plano de um liderado não é plano
     * desta carteira. `plans` zero é "o mês não tem boleto gravado", e a tela
     * não calcula.
     */
    planBaseline: z9.object({
      month: periodSchema,
      plans: z9.number().int(),
      paid: moneySchema,
      /** A recorrência própria — cresce com cada plano pago. */
      recurrence: moneySchema,
      /** A premiação por produto própria (a "Condição Especial") — também. */
      product: moneySchema,
      /**
       * TUDO o que o Extrato do mês tem para a pessoa: recorrência, produto,
       * faixas, Antecipada e a indireta do time. É o "recebido hoje" da
       * calculadora; o que não é recorrência nem produto fica como está.
       */
      total: moneySchema
    }),
    /**
     * Quantos veículos da carteira foram CANCELADOS NO MÊS (29/10/2026) — o
     * card Veículos do BI. Cancelado é a situação de hoje com nome CANCELADO,
     * CANCELAMENTO FROTISTA ou CANCELAMENTO DIRETORIA (pelo NOME: o código
     * muda de praça); o mês é o da ÚLTIMA ALTERAÇÃO do cadastro no SGA, que é
     * aproximação escolhida pelo dono — o núcleo não guarda quando a situação
     * mudou. Ver `SalesWalletService.canceled`.
     */
    canceledVehicles: z9.number().int(),
    /**
     * Os contratos da carteira NO MÊS escolhido, pelo dia do contrato no ERP —
     * agrupados por tipo de veículo, plano e marca. Até 29/10/2026 eram os
     * doze meses que terminam nele; a Distribuição da Carteira passou a seguir
     * o mês das Métricas (pedido do dono).
     *
     * A carteira é a do voluntário no ERP HOJE (`erp_volunteer_code`), e não
     * quem vendeu: um carro transferido conta para quem cuida dele agora.
     */
    mix: z9.object({
      from: businessDateSchema,
      until: businessDateSchema,
      vehicles: z9.number().int(),
      byType: z9.array(walletMixSliceSchema),
      byPlan: z9.array(walletMixSliceSchema),
      /**
       * Pelo DIA do vencimento original (29/10/2026): o dia do mês de
       * `original_due_date` do boleto mais recente de cada veículo, como texto
       * (`'5'`, `'10'`…). Nulo é o veículo que ainda não tem boleto.
       *
       * O vocabulário `vencimento` do SGA não serve: na CE os 31 códigos
       * chegam sem rótulo, e o código sozinho não diz o dia.
       */
      byDueDay: z9.array(walletMixSliceSchema),
      byBrand: z9.array(walletMixSliceSchema)
    })
  })
]);
var walletPendingContractSchema = z9.object({
  plate: z9.string().nullable(),
  memberName: z9.string().nullable(),
  memberPhone: z9.string().nullable(),
  /** De quem é o contrato, na Antecipada (o ramo inteiro); nulo na direta. */
  consultantName: z9.string().nullable(),
  contractDate: businessDateSchema.nullable(),
  /** Até quando o 1º boleto pode ser pago e a placa contar. Nulo: sem prazo. */
  deadline: businessDateSchema.nullable()
});
var walletOpenBoletoSchema = z9.object({
  plate: z9.string().nullable(),
  memberName: z9.string().nullable(),
  memberPhone: z9.string().nullable(),
  dueDate: businessDateSchema.nullable(),
  /** O dia do contrato do veículo — quem é cliente novo e quem é antigo. */
  contractDate: businessDateSchema.nullable(),
  /** O valor do BOLETO, e não o que ele renderia de premiação. */
  amount: moneySchema
});
var walletActionsSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    month: periodSchema,
    climbs: z9.array(
      z9.object({
        awardId: z9.number().int(),
        /** Os primeiros, pela data limite mais próxima. */
        pending: z9.array(walletPendingContractSchema),
        /** Quantos são ao todo — a lista pode vir cortada. */
        pendingTotal: z9.number().int()
      })
    )
  })
]);
var walletOpenBoletosSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    month: periodSchema,
    /** Do maior valor para o menor. */
    items: z9.array(walletOpenBoletoSchema)
  })
]);
var walletVehicleBoletoSchema = z9.object({
  /** `BO-000123` — o código da casa. */
  code: z9.string(),
  plate: z9.string().nullable(),
  /** `descricao_tipo_boleto` — mensalidade, adesão, taxa… */
  type: z9.string().nullable(),
  dueDate: businessDateSchema,
  amount: moneySchema,
  /** A descrição da situação no ERP, sem classificação da casa. */
  status: z9.string().nullable(),
  /** Pela precedência de `boletoPayment`: a coleta, e o ERP no vazio. */
  paidOn: businessDateSchema.nullable(),
  /**
   * O último envio que a COBRANÇA DA CASA registrou para este boleto
   * (`app.billing_contacts.sent_at`). Nulo é "a casa não registrou envio" —
   * o SGA pode ter mandado o boleto por conta própria, e isso não fica aqui.
   */
  lastSentAt: instantSchema.nullable(),
  /** Quantos envios a cobrança da casa registrou para este boleto. */
  sends: z9.number().int()
});
var walletVehicleBoletosSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    plate: z9.string(),
    /** Do vencimento mais novo para o mais velho, limitados. */
    items: z9.array(walletVehicleBoletoSchema),
    /** Quantos boletos o veículo tem ao todo — a lista pode vir cortada. */
    total: z9.number().int()
  })
]);
var walletPaymentRateSchema = z9.object({
  month: periodSchema,
  /** `valor_boleto` dos pagos. */
  paid: moneySchema,
  /** `valor_boleto` dos genuinamente em aberto. */
  open: moneySchema,
  boletos: z9.number().int(),
  /** `paid ÷ (paid + open)`, de 0 a 1. */
  rate: z9.number()
});
var walletForecastSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    month: periodSchema,
    rates: z9.array(walletPaymentRateSchema),
    /** A varredura de boletos não terminou: as taxas são sobre um recorte incompleto. */
    partial: z9.boolean()
  })
]);
var walletCalculatorMonthSchema = z9.object({
  month: periodSchema,
  /** Contratos com data no mês — as "Novas Vendas" do BI. */
  sales: z9.number().int(),
  /** Carros da carteira no primeiro dia do mês: contrato antes dele, e não cancelados até ali. */
  portfolio: z9.number().int(),
  /** Quantos carros de `portfolio` cancelaram no mês, pela última alteração no SGA. */
  canceled: z9.number().int()
});
var walletCalculatorSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    months: z9.array(walletCalculatorMonthSchema),
    salesAverage: z9.number().int(),
    churnPercent: z9.number().nullable()
  })
]);
var walletRowSchema = z9.object({
  consultant: z9.object({
    /** `sales_consultants.id`. */
    id: z9.number().int(),
    /** O nome da pessoa da plataforma vinculada; nulo quando não há. */
    nome: z9.string().nullable(),
    /** O código do voluntário nesta base — é o que abre a carteira dele. */
    erpVolunteerCode: z9.string()
  }),
  total: moneySchema.nullable(),
  boletos: z9.number().int(),
  /** Quantas premiações a carteira tem nesta leitura. */
  awards: z9.number().int(),
  /** Quantas delas recusaram — o motivo está na carteira de cada um. */
  refused: z9.number().int()
});
var walletsSchema = z9.object({
  month: periodSchema,
  state: awardStateSchema,
  /**
   * Quem tem VALOR na carteira: total maior que zero, ou sem total porque
   * alguma premiação recusou. Os que recusaram entram de propósito — tirá-los
   * da tabela diria que eles não têm nada, quando a casa é que não sabe.
   * Ordenado pelo total, do maior para o menor, com os sem total no fim.
   */
  rows: z9.array(walletRowSchema),
  /**
   * Quantos consultores com premiação cadastrada somaram exatamente R$ 0,00 e
   * ficaram FORA da tabela. O número viaja para a tela dizer que eles existem.
   */
  zeroed: z9.number().int()
});
var awardTierSchema = z9.object({
  minPlates: z9.number().int().positive(),
  /** O valor CHEIO da faixa — não é por placa. É a G2, respondida. */
  amount: moneySchema
});
var awardProductSchema = z9.object({
  /** `codigo_produto` do SGA. */
  code: z9.string().min(1).max(40),
  /** A descrição de quando a régua foi gravada — para a tela, não para a conta. */
  label: z9.string().max(200).nullable(),
  amount: moneySchema
});
var awardProductCombinationSchema = z9.enum(["soma", "maior"]);
var awardProductDiscountSchema = z9.object({
  from: businessDateSchema,
  percentage: z9.string()
});
var awardSchema = z9.object({
  id: z9.number().int(),
  title: z9.string(),
  /**
   * De QUEM é esta premiação — o `id` de `sales_consultants`, e nunca o do
   * usuário.
   *
   * Uma premiação pertence a **uma** pessoa desde 16/09/2026. Cinco
   * consultores com a mesma régua são cinco cadastros, e mudar a régua de
   * todos são cinco edições — o preço escolhido para que cada um possa ter
   * período e faixas próprios sem tocar em ninguém.
   */
  consultantId: z9.number().int(),
  family: awardFamilySchema,
  /** De quem são os boletos da base. Ver `awardBasisSourceSchema`. */
  basisSource: awardBasisSourceSchema,
  /**
   * Ponto percentual: `7.5` é 7,5%. Texto porque `numeric` não cabe em float.
   *
   * **Nulo na família `direta`**, que não tem percentual: o que ela paga está
   * em `tiers`, por faixa.
   */
  percentage: z9.string().nullable(),
  /**
   * Quantos dias o primeiro boleto tem para ser pago — o prazo de ativação.
   *
   * **Nulo na família `recorrencia`**, que não tem prazo nenhum, **e também na
   * `direta` que não considera a regra** — a caixa "Considerar regra de 45
   * dias?" do cadastro, aberta em 16/09/2026. Nulo aqui é o que a tela lê para
   * saber se marca a caixa, e o que a apuração lê para decidir se filtra pela
   * data-limite: sem prazo, o primeiro boleto conta pago em qualquer data.
   */
  deadlineDays: z9.number().int().nullable(),
  /**
   * Na `recorrencia`: verdadeiro conta só do 2º boleto válido do veículo em
   * diante (a troca, desde o 1º); falso conta todo boleto pago. Sempre
   * verdadeiro nas outras famílias, que não o leem. Ver `from_second_boleto`.
   */
  fromSecondBoleto: z9.boolean(),
  /**
   * A régua da `direta`, do menor piso para o maior.
   *
   * Vale o **maior piso alcançado**: com a tabela indo 3 · 5 · 10, quem ativou
   * 7 recebe a faixa de 5. Lista vazia recusa a apuração — não paga zero.
   */
  tiers: z9.array(awardTierSchema),
  /** `codigo_tipo` do veículo que a `direta` conta. Vazia recusa. */
  vehicleKinds: z9.array(z9.string()),
  /**
   * `codigo_tipo_adesao` que a `direta` conta — é como o ERP responde "venda
   * nova ou troca". Vazia recusa: sem ela a contagem incluiria a troca.
   */
  adhesionKinds: z9.array(z9.string()),
  /** A régua da `produto`. Vazia recusa. Lista vazia nas outras famílias. */
  products: z9.array(awardProductSchema),
  /** Só na `produto`; nulo nas outras. */
  productCombination: awardProductCombinationSchema.nullable(),
  /** Só na `produto`, e nulo é a regra do desconto desligada. */
  productDiscount: awardProductDiscountSchema.nullable(),
  /** O primeiro dia da janela de `data_contrato`. Sempre preenchido. */
  contractFrom: businessDateSchema,
  /**
   * O último dia, FECHADO — ou **nulo para sem limite**.
   *
   * Nulo não é campo em branco: é a premiação que vale de uma data em diante,
   * inclusive para contrato que ainda não existe. É a assimetria que o
   * responsável pediu em 16/09/2026 — começar sempre se sabe, terminar nem
   * sempre.
   */
  contractUntil: businessDateSchema.nullable(),
  category: z9.enum(["1x", "2x"]),
  /**
   * Os tipos de boleto que esta premiação conta — **do lado que garante**.
   *
   * **Lista vazia não conta tudo — recusa.** Sem filtro entram adesão, multa e
   * o que mais o cadastro do ERP tiver, e a base cresceria sem ninguém ter
   * decidido isso.
   *
   * O lado PERDIDO não sai daqui: ele é uma configuração da praça inteira
   * (`losingBoletoTypesSchema`), porque "quais boletos em aberto contam como
   * perda" é a mesma resposta para todo consultor.
   */
  boletoTypes: z9.array(z9.object({ code: z9.string(), descricao: z9.string().nullable() })),
  /**
   * O dono, já montado — para a lista poder escrever o nome sem uma segunda
   * consulta.
   *
   * Viaja ao lado de `consultantId`, e não no lugar dele: quem grava manda o
   * id, quem desenha lê o nome, e colapsar os dois faria a tela do consultor
   * ter de procurar a si mesma dentro do objeto que ela acabou de pedir.
   */
  consultant: consultantSummarySchema,
  /**
   * De qual MODELO esta premiação nasceu, ou nulo. É só a origem: editar o
   * modelo não mexe nesta premiação, e editar esta não mexe no modelo.
   */
  templateId: z9.number().int().nullable(),
  archivedAt: z9.string().nullable()
});
var awardTemplateSchema = awardSchema.omit({ consultantId: true, consultant: true, templateId: true }).extend({
  /**
   * Quem tem hoje uma premiação ATIVA nascida deste modelo — e qual é ela.
   *
   * `awardId` é a premiação DA PESSOA, e é por ele que a tela de modelos
   * desvincula: desvincular é arquivar aquela premiação (14/10/2026), o
   * mesmo ato do "Arquivar" da ficha, e se desfaz por lá.
   */
  consultants: z9.array(consultantSummarySchema.extend({ awardId: z9.number().int() }))
});
var awardGrantSchema = z9.object({
  id: z9.number().int(),
  /** Quem recebe — `sales_consultants.id`. */
  memberConsultantId: z9.number().int(),
  /** O liderado já montado, para a lista escrever o nome sem outra consulta. */
  member: consultantSummarySchema,
  /**
   * O nome que o líder deu a esta comissão, ou **nulo — herda o título da
   * premiação do líder**. É o que o liderado lê no painel, na Carteira e no
   * Extrato.
   */
  title: z9.string().nullable(),
  /** A fatia da premiação que o liderado recebe, em %. Texto porque `numeric` não cabe em float. */
  percentage: z9.string(),
  /**
   * Até quando o contrato conta — e **nulo é o da premiação**, não "sem
   * limite".
   *
   * É a diferença para `awardSchema.contractUntil`, onde o nulo alcança o
   * contrato que ainda vai ser feito. O responsável pediu que o líder pudesse
   * dizer até quando o contrato conta, e é só essa ponta que ele move: o
   * começo, a categoria, os tipos de boleto e os filtros continuam sendo os da
   * premiação, lidos por referência.
   */
  contractUntil: businessDateSchema.nullable(),
  archivedAt: z9.string().nullable()
});
var awardGrantInputSchema = z9.object({
  memberConsultantId: z9.number().int(),
  /** Nulo ou em branco herda o título da premiação do líder. */
  title: z9.string().max(120).nullable().default(null),
  /**
   * A FATIA do que a premiação do líder paga que fica com o liderado — de 0 a
   * 100, os dois exclusivos. 20 é "ele fica com 20%, eu com 80%".
   *
   * Não é ponto percentual da base desde 23/09/2026: com a premiação de 10%,
   * 20 aqui dá ao liderado 2% da base dele, e continua dando 20% da premiação
   * se o percentual do líder mudar. O `check` do banco repete a faixa.
   */
  percentage: z9.number().gt(0).lt(100),
  /** Nulo herda a ponta da premiação. Preenchido, encurta — nunca estende. */
  contractUntil: businessDateSchema.nullable().default(null)
});
var ownAwardGrantsSchema = z9.object({
  /** `sales_consultants.id` de quem pede, ou nulo — não é consultor nesta praça. */
  consultantId: z9.number().int().nullable(),
  /** Quem responde a UM degrau de quem pede, hoje — `sales_consultants.id`. */
  directMemberIds: z9.array(z9.number().int()),
  /** As premiações de líder de quem pede, com percentual e sem arquivo. */
  awards: z9.array(
    z9.object({
      id: z9.number().int(),
      title: z9.string(),
      /** O teto. Texto porque `numeric` não cabe em float. */
      percentage: z9.string(),
      contractFrom: businessDateSchema,
      contractUntil: businessDateSchema.nullable(),
      /** Todas as concessões dela, inclusive as arquivadas — a unique as inclui. */
      grants: z9.array(awardGrantSchema)
    })
  )
});
var awardInputSchema = z9.object({
  title: z9.string().min(1).max(120),
  /**
   * De quem é — o `id` de `sales_consultants`, obrigatório.
   *
   * Não tem padrão, e é a única forma de a premiação nascer com dono. Antes de
   * 16/09/2026 o campo equivalente era `consultantIds`, uma lista que **nascia
   * vazia** — o padrão que não paga a ninguém. Com um dono obrigatório essa
   * proteção deixa de ser necessária: não existe premiação sem pessoa, e não
   * existe premiação que alcance a base inteira.
   */
  consultantId: z9.number().int(),
  family: awardFamilySchema.default("recorrencia"),
  /**
   * De quem são os boletos da base. O padrão é `proprio` — o comportamento de
   * sempre —, e quem escolhe `hierarquia` está cadastrando a premiação de um
   * líder, que é a única que pode ser repartida.
   */
  basisSource: awardBasisSourceSchema.default("proprio"),
  /**
   * De 0 (exclusivo) a 100, com casas decimais — o responsável pediu que não
   * fosse restrito a 5 ou 10. O `check` do banco repete o intervalo, e é ele
   * que manda. **Nulo na família `direta`.**
   */
  percentage: z9.number().gt(0).lte(100).nullable().default(null),
  /**
   * O prazo de ativação, em dias — e **nulo é uma escolha, não uma omissão**.
   *
   * Nulo na `recorrencia`, que não tem prazo. Na `direta` nulo é a caixa
   * "Considerar regra de 45 dias?" desmarcada: a placa passa a contar com o
   * primeiro boleto pago em **qualquer** data, e a competência deixa de fechar.
   * Marcada, o campo abre em 45 e continua digitável — o número é régua de
   * negócio, e régua que vive no código pede deploy para mudar.
   */
  deadlineDays: z9.number().int().positive().max(365).nullable().default(null),
  /**
   * A regra do 2º boleto da `recorrencia`. O padrão verdadeiro é o
   * comportamento de antes de 23/10/2026 — a tela sempre manda o valor, e
   * quem desmarca está dizendo que a premiação conta desde o 1º boleto.
   */
  fromSecondBoleto: z9.boolean().default(true),
  /** A régua da `direta`. Chega inteira e substitui, como as outras listas. */
  tiers: z9.array(awardTierSchema).default([]),
  vehicleKinds: z9.array(z9.string().min(1).max(40)).default([]),
  adhesionKinds: z9.array(z9.string().min(1).max(40)).default([]),
  /** A régua da `produto`. Chega inteira e substitui, como as outras listas. */
  products: z9.array(awardProductSchema).default([]),
  /** Obrigatória na `produto`, nula nas outras — o serviço recusa com a frase. */
  productCombination: awardProductCombinationSchema.nullable().default(null),
  productDiscount: z9.object({ from: businessDateSchema, percentage: z9.number().gt(0).lte(100) }).nullable().default(null),
  /**
   * A janela de `data_contrato`: o começo é **obrigatório** e o fim é
   * **opcional**.
   *
   * Decisão do responsável em 16/09/2026, e a assimetria é o ponto: começar
   * sempre se sabe, terminar nem sempre. `contractUntil` nulo é **sem limite
   * para a frente** — a premiação alcança o contrato que ainda vai ser feito
   * —, e não um campo que alguém esqueceu.
   *
   * A `category` **não** decide mais nada sobre estas duas. Ela chegou a
   * decidir por algumas horas no mesmo dia (`1x` cobrava o fim, `2x` o
   * começo), e o responsável substituiu a régua: hoje ela volta a ser só o
   * lote de pagamento, que é o que sempre foi.
   */
  contractFrom: businessDateSchema,
  contractUntil: businessDateSchema.nullable().default(null),
  category: z9.enum(["1x", "2x"]),
  boletoTypes: z9.array(
    z9.object({ code: z9.string().min(1).max(40), descricao: z9.string().nullable() })
  ).default([])
});
var awardTemplateInputSchema = awardInputSchema.omit({ consultantId: true });
var losingBoletoTypesSchema = z9.object({
  types: z9.array(z9.object({ code: z9.string(), descricao: z9.string().nullable() }))
});
var losingBoletoTypesInputSchema = z9.object({
  types: z9.array(
    z9.object({ code: z9.string().min(1).max(40), descricao: z9.string().nullable() })
  ).default([])
});
var statementKindSchema = z9.enum(["boleto", "faixa", "produto"]);
var statementEntrySchema = z9.object({
  id: z9.number().int(),
  kind: statementKindSchema,
  award: z9.object({
    id: z9.number().int(),
    title: z9.string(),
    family: awardFamilySchema
  }),
  /** O nome como o ERP o escrevia QUANDO a linha nasceu — ver o schema. */
  memberName: z9.string().nullable(),
  plate: z9.string().nullable(),
  /** `codigo_boleto` do SGA. Nome do fornecedor — não traduz. */
  boletoCode: z9.string().nullable(),
  paidOn: businessDateSchema.nullable(),
  /** O que o boleto pagou. Só na linha de `boleto`. */
  gross: moneySchema.nullable(),
  /** A tarifa que o banco cobrou nele. Só na linha de `boleto`. */
  fee: moneySchema.nullable(),
  /**
   * A ENTRADA — o que esta linha pôs no bolso.
   *
   * **Não é `gross` vezes o percentual.** É a parte que a repartição deu a ela
   * do total que a premiação apurou no mês, e refazer a conta na tela produz
   * divergência de centavos contra a Carteira.
   */
  amount: moneySchema,
  /**
   * Os produtos que pagaram nesta linha, como estavam na apuração. Só na linha
   * de `produto` — uma por veículo em cada boleto pago.
   */
  products: z9.string().nullable().optional(),
  /** Quantas placas a direta contou. Só na linha de `faixa`. */
  plates: z9.number().int().nullable(),
  /**
   * O piso da faixa alcançada. **Nulo com `plates` preenchido** é a contagem
   * que não chegou ao menor degrau: a premiação não paga, e a linha existe para
   * dizer isso em vez de a tela mostrar um R$ 0,00 sem explicação.
   */
  tierFloor: z9.number().int().nullable()
});
var statementDaySchema = z9.object({
  /**
   * A data de pagamento — ou **nulo para o grupo do fecho do mês**, que são as
   * linhas de família `direta`: elas não têm data porque não são um movimento do
   * mês, são o fecho dele.
   */
  date: businessDateSchema.nullable(),
  total: moneySchema,
  /** Quantas entradas o dia tem no período, e não na página. */
  rows: z9.number().int()
});
var agendaMemberSchema = z9.object({
  /** `codigo_associado` do SGA. */
  code: z9.string(),
  name: z9.string().nullable(),
  birthDate: businessDateSchema.nullable(),
  /** O par `ddd` + `telefone` do cadastro, como o ERP guardou. */
  phone: z9.object({ ddd: z9.string().nullable(), number: z9.string() }).nullable(),
  /** A situação do associado no ERP, sem classificação da casa. */
  status: z9.object({ code: z9.string().nullable(), description: z9.string().nullable() }),
  /** A data de contrato mais recente entre os veículos deste consultor. */
  lastContractDate: businessDateSchema.nullable(),
  vehicles: z9.number().int(),
  /**
   * Algum carro dele COM ESTE consultor está em tratativa no sinistro
   * (21/10/2026) — o destaque amarelo da linha. Encerrado e arquivado não
   * contam.
   */
  inClaim: z9.boolean(),
  /** O CPF (ou CNPJ) como o SGA o guarda — a linha da Carteira (28/10/2026). */
  document: z9.string().nullable(),
  email: z9.string().nullable()
});
var agendaSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    consultant: z9.object({
      erpVolunteerCode: z9.string(),
      nome: z9.string().nullable(),
      /** `true` quando é a agenda de outra pessoa — exige `ver_qualquer`. */
      ofAnotherConsultant: z9.boolean()
    }),
    items: z9.array(agendaMemberSchema),
    /** Quantos associados casam o recorte, além da página. */
    total: z9.number().int(),
    page: z9.number().int(),
    perPage: z9.number().int(),
    /** As opções do filtro de situação — da carteira inteira, sem o recorte. */
    situations: z9.array(
      z9.object({
        code: z9.string().nullable(),
        description: z9.string().nullable(),
        members: z9.number().int()
      })
    ),
    /** Os carros dos associados do recorte — somados, e não da página. */
    vehiclesTotal: z9.number().int(),
    /** As opções do filtro de situação do CARRO, da carteira inteira. */
    vehicleSituations: z9.array(
      z9.object({
        code: z9.string(),
        description: z9.string().nullable(),
        vehicles: z9.number().int()
      })
    ),
    /** Os dias de vencimento que os carros da carteira têm, em ordem. */
    dueDays: z9.array(z9.string()),
    /** Quando o cadastro de associados do espelho deu certo pela última vez. */
    syncedAt: z9.string().datetime()
  })
]);
var agendaMemberDetailSchema = z9.discriminatedUnion("estado", [
  z9.object({
    estado: z9.literal("sem_vinculo"),
    message: z9.string()
  }),
  z9.object({
    estado: z9.literal("medido"),
    member: z9.object({
      code: z9.string(),
      name: z9.string().nullable(),
      /** O CPF (ou CNPJ) como o SGA o guarda — o cabeçalho da ficha (28/10/2026). */
      document: z9.string().nullable()
    }),
    vehicles: z9.array(
      z9.object({
        code: z9.string(),
        plate: z9.string().nullable(),
        brand: z9.string().nullable(),
        model: z9.string().nullable(),
        manufactureYear: z9.string().nullable(),
        modelYear: z9.string().nullable(),
        category: z9.string().nullable(),
        /** `codigo_grupo_produto`. Com `plan` nulo, é código que a lista não conhece. */
        planCode: z9.string().nullable(),
        plan: z9.string().nullable(),
        status: z9.object({
          code: z9.string().nullable(),
          description: z9.string().nullable()
        }),
        fipeValue: moneySchema.nullable(),
        protectedValue: moneySchema.nullable(),
        contractDate: businessDateSchema.nullable(),
        /** O dia de vencimento do carro, como o SGA o escreve ("10"). */
        dueDay: z9.string().nullable(),
        /** Quantos atendimentos do SGA o núcleo tem para este carro, de qualquer data. */
        serviceRecords: z9.number().int()
      })
    ),
    boletos: z9.array(
      z9.object({
        code: z9.string(),
        plate: z9.string().nullable(),
        /**
         * Os carros DESTE consultor que o boleto cobre. É uma lista porque o
         * boleto pode cobrir dois carros do cliente — e aparece na ficha de cada
         * um, sem sair duas vezes na conta do cliente.
         */
        vehicleCodes: z9.array(z9.string()),
        dueDate: businessDateSchema.nullable(),
        /** O vencimento antes de qualquer prorrogação. */
        dueOriginal: businessDateSchema.nullable(),
        amount: moneySchema.nullable(),
        status: z9.string().nullable(),
        /** De `sga_situacoes_boleto.pago`. `null` é "não sei", nunca "não pago". */
        paid: z9.boolean().nullable(),
        /**
         * O boleto conta como inadimplência — `considerado_inadimplencia` da
         * situação, e não `!paid`: CANCELADO e BAIXADO C/ DESCONTO também não
         * são pagos (29/10/2026, o "Em atraso" da Carteira). `null` é "não sei".
         */
        delinquent: z9.boolean().nullable(),
        paidOn: businessDateSchema.nullable(),
        amountPaid: moneySchema.nullable()
      })
    ),
    /**
     * Os atendimentos do SGA nos carros DESTE consultor, do mais novo ao mais
     * antigo (21/10/2026). Lidos do núcleo (`app.member_service_records`), sem
     * recorte de data — o histórico é o que a esteira de atendimentos já trouxe.
     * O atendimento sem carro fica de fora: ele é do associado, e a agenda é a
     * carteira deste consultor.
     */
    serviceRecords: z9.array(
      z9.object({
        /** `codigo_atendimento` do SGA. */
        code: z9.string(),
        vehicleCode: z9.string(),
        plate: z9.string().nullable(),
        registeredOn: businessDateSchema.nullable(),
        title: z9.string().nullable(),
        /** Como o SGA guardou — em HTML. A conversão para texto é da tela. */
        description: z9.string().nullable(),
        /** O atendimento sumiu do histórico do SGA; a linha fica, marcada. */
        missing: z9.boolean()
      })
    ),
    /**
     * As demandas que o ASSOCIADO abriu pelo portal sobre os carros DESTE
     * consultor (22/10/2026), da mais nova à mais antiga — dividem a aba de
     * atendimentos com os do SGA. A demanda sem carro fica de fora, pelo mesmo
     * motivo do atendimento sem carro; a aberta pela casa também, porque não é
     * contato do cliente. Só o resumo: a conversa pode ter nota interna.
     */
    tickets: z9.array(
      z9.object({
        /** O número que a operação dita. */
        code: z9.string(),
        vehicleCode: z9.string(),
        /** A do carro na carteira — a demanda guarda o código, não a placa. */
        plate: z9.string().nullable(),
        title: z9.string(),
        status: ticketStatusSchema,
        /** O tipo escolhido na abertura. Nulo: aberta sem tipo. */
        category: z9.string().nullable(),
        openedAt: z9.string().datetime(),
        completedAt: z9.string().datetime().nullable(),
        /** A demanda na mesa — nulo para quem não tem a permissão de abri-la. */
        link: z9.string().nullable()
      })
    ),
    /**
     * Os sinistros EM TRATATIVA dos carros deste consultor (21/10/2026): a
     * fase atual pelo nome, desde quando está nela e quando entrou no
     * sinistro. Encerrado e arquivado ficam fora; o carro sem sinistro aberto
     * não aparece. Do mais novo ao mais antigo.
     */
    claims: z9.array(
      z9.object({
        vehicleCode: z9.string(),
        phase: z9.string(),
        /** A entrada na fase atual. Nulo: o sinistro não tem a passagem gravada. */
        phaseSince: z9.string().datetime().nullable(),
        /** A entrada no sinistro — quando o aviso foi registrado. */
        openedAt: z9.string().datetime(),
        /** O SLA da fase atual; nulo quando a fase não tem prazo. */
        deadlineAt: z9.string().datetime().nullable(),
        /** As passagens do sinistro, da mais nova à mais antiga. */
        phases: z9.array(
          z9.object({ phase: z9.string(), enteredAt: z9.string().datetime() })
        )
      })
    ),
    /** O primeiro dia do recorte dos boletos, por vencimento. */
    dueFrom: businessDateSchema,
    /** A varredura de boletos não fechou: a lista é piso, e pode faltar boleto. */
    boletosPartial: z9.boolean(),
    syncedAt: z9.string().datetime()
  })
]);
var statementEntryServiceSchema = z9.object({
  kind: z9.enum(["demanda", "sinistro", "retencao", "cobranca"]),
  id: z9.string(),
  reference: z9.string().nullable(),
  title: z9.string().nullable(),
  status: z9.string().nullable(),
  openedAt: z9.string().datetime(),
  vehicleCode: z9.string().nullable(),
  link: z9.string().nullable()
});
var statementEntryContextSchema = z9.object({
  /** O código do associado no ERP — a chave da ficha da Agenda. Nulo: o boleto não está no núcleo. */
  memberCode: z9.string().nullable(),
  /** Os veículos (códigos do ERP) que o boleto cobre. */
  vehicleCodes: z9.array(z9.string()),
  services: z9.array(statementEntryServiceSchema)
});
var statementAfterClosureSchema = z9.object({
  /**
   * Quantos boletos pagos da competência **não têm entrada gravada**.
   *
   * É a diferença entre o que o espelho apura HOJE e o que o mês guardou, e o
   * nome disto não é "chegaram depois" — a tela dizia isso e era afirmação a
   * mais. Chegar depois é a causa comum e esperada; as outras são uma régua
   * editada e uma leitura que falhou na consolidação. O que o número afirma é
   * só a diferença.
   */
  boletos: z9.number().int(),
  /** Quanto eles gerariam de entrada, se a competência fosse reaberta. */
  amount: moneySchema
});
var statementClosingSchema = z9.object({
  closedAt: z9.string(),
  /** Nulo quando o usuário que fechou foi removido, ou no fechamento automático. */
  closedBy: z9.string().nullable(),
  /** Fechada pela esteira, na data de `competenceClosingDate` (19/10/2026). */
  automatic: z9.boolean(),
  /**
   * O que chegou depois — ou **nulo quando não há o que dizer**.
   *
   * Nulo cobre dois casos, e nenhum deles é "não chegou nada": ou o espelho
   * estava indisponível para conferir, ou a conferência não pôde ser feita. A
   * tela não afirma nada sobre nulo, porque "zero boletos atrasados" e "não
   * conferimos" são respostas diferentes — e a primeira, dita no lugar da
   * segunda, faria alguém fechar o mês tranquilo.
   */
  afterClosure: statementAfterClosureSchema.nullable()
});
var statementBalancePartSchema = z9.object({
  /** A competência do lote, `AAAA-MM`. */
  period: periodSchema,
  category: z9.enum(["1x", "2x"]),
  /**
   * `competencia_aberta` (ainda cresce), `aguardando_nota` (lote pronto, nota
   * não enviada) ou `no_financeiro` (nota enviada, à espera da baixa).
   */
  source: z9.enum(BALANCE_PART_SOURCES),
  amount: moneySchema,
  /** A despesa, quando a nota já foi enviada. */
  expense: z9.object({
    status: z9.string(),
    dueDate: businessDateSchema.nullable(),
    sentOn: businessDateSchema.nullable()
  }).nullable(),
  /**
   * As premiações que somaram no Extrato deste lote. No `no_financeiro` a soma
   * pode diferir de `amount`: a despesa é o valor da nota, já com a taxa de
   * tecnologia descontada.
   */
  awards: z9.array(
    z9.object({ id: z9.number().int(), title: z9.string(), amount: moneySchema })
  )
});
var statementBalanceSchema = z9.discriminatedUnion("estado", [
  z9.object({ estado: z9.literal("sem_vinculo") }),
  z9.object({
    estado: z9.literal("consultor"),
    balance: moneySchema,
    /** De onde vem o saldo, lote a lote — o que o topo abre ao clicar nele. */
    balanceParts: z9.array(statementBalancePartSchema)
  })
]);
var statementPayoutSchema = z9.object({
  paymentId: z9.number().int(),
  /** A competência do lote, `AAAA-MM` — não a do extrato aberto. */
  lotPeriod: periodSchema,
  category: z9.enum(["1x", "2x"]),
  /** A data do comprovante — a data de negócio da baixa. */
  paidOn: businessDateSchema,
  /** O valor efetivamente pago; o da despesa quando o Financeiro não o mudou. */
  amount: moneySchema,
  paymentMethod: z9.enum(EXPENSE_PAYMENT_METHODS)
});
var statementPayoutDaySchema = z9.object({
  date: businessDateSchema,
  total: moneySchema,
  payouts: z9.array(statementPayoutSchema)
});
var statementSchema = z9.discriminatedUnion("estado", [
  z9.object({
    /**
     * Quem pediu não é consultor nesta base, e não pediu o de outra pessoa.
     *
     * Não é 403 e não é lista vazia, e a distinção é o ponto: a permissão está
     * concedida, a rota respondeu, e o que falta é **cadastro** — ninguém
     * vinculou aquela pessoa a um código do ERP nesta praça. Uma lista vazia no
     * lugar disto faria a pessoa concluir que não recebeu nada no mês.
     *
     * É o mesmo desenho de `managementPanelSchema`, e pelo mesmo motivo.
     */
    estado: z9.literal("sem_vinculo"),
    message: z9.string(),
    /**
     * A competência e o fechamento dela viajam **mesmo sem vínculo**, e é o que
     * permite a quem fecha a praça fechar sem ter carteira própria.
     *
     * O fechamento é um ato por praça e mês — quem administra, quem trabalha no
     * financeiro e quem lidera normalmente **não** são consultores. Sem estes
     * dois campos a tela deles mostraria só "você não tem carteira aqui", e o
     * ato que o recurso `sales.statement:fechar` concede não teria botão em
     * lugar nenhum: a permissão existiria e seria inalcançável.
     */
    month: periodSchema,
    closing: statementClosingSchema.nullable()
  }),
  z9.object({
    estado: z9.literal("medido"),
    month: periodSchema,
    consultant: z9.object({
      erpVolunteerCode: z9.string(),
      nome: z9.string().nullable(),
      /** `true` quando é o extrato de outra pessoa — exige `ver_qualquer`. */
      ofAnotherConsultant: z9.boolean()
    }),
    /**
     * O fechamento da competência, ou **nulo para o mês aberto**.
     *
     * Nulo é o que muda a tela inteira: as linhas ainda podem crescer, as
     * premiações de família `direta` não aparecem, e o total é do que já entrou —
     * nunca do que o mês vai fechar.
     */
    closing: statementClosingSchema.nullable(),
    /**
     * O total das entradas **do período inteiro**, e não da página.
     *
     * Sai de um `sum` sobre o mesmo `where` da listagem, como em `cashBookSchema`
     * e pelo mesmo motivo: somar a página daria outro número, e os dois
     * pareceriam igualmente certos.
     *
     * O filtro de busca **entra** nesta soma. Um total do mês inteiro embaixo de
     * uma lista filtrada seria a tela respondendo outra pergunta.
     */
    total: moneySchema,
    /**
     * Os dias que a competência tem, do mais novo para o mais velho, com o
     * total e a contagem de cada um. Ver `statementDaySchema`.
     *
     * Vem do PERÍODO inteiro, e não da página: é o cabeçalho de grupo do
     * extrato, e um subtotal que muda conforme a página aberta não é subtotal.
     */
    days: z9.array(statementDaySchema),
    entries: z9.array(statementEntrySchema),
    /**
     * As saídas do mês — os lotes pagos, por dia da baixa. Não paginam: são
     * no máximo dois lotes por competência, e a tela as desenha na primeira
     * página, junto dos dias de entrada.
     */
    payouts: z9.array(statementPayoutDaySchema),
    /**
     * O saldo em aberto do consultor, AGORA — a mesma conta do topo
     * (`consultantBalance`). Não é do mês: é quanto a casa deve a ele hoje.
     */
    balance: moneySchema,
    /** De onde vem o saldo, lote a lote — o que a tela abre ao clicar nele. */
    balanceParts: z9.array(statementBalancePartSchema),
    /**
     * Quantas premiações de família `direta` estão **esperando o fechamento**.
     *
     * Elas pagam pelo piso de faixa alcançado no mês, e o número só é final
     * quando o ciclo fecha. Zero linha por causa disso precisa ser dito: sem este
     * campo, quem só tem premiação direta abriria um extrato vazio e concluiria
     * que não recebeu nada.
     */
    awaitingClosure: z9.number().int(),
    /**
     * Quando alguma régua do mês foi editada **depois** de as linhas serem
     * gravadas — ou nulo.
     *
     * A esteira grava com o cadastro de quando passou. Trocar o percentual de uma
     * premiação não reescreve o que já está gravado, e é de propósito: reescrever
     * em silêncio faria a edição da régua deixar de parecer um ato de dinheiro.
     * O que a tela faz é dizer, e oferecer o recálculo a quem pode.
     *
     * **Só no mês aberto.** Competência fechada não recalcula: o caminho é
     * reabrir, que tem nome, dono e motivo.
     */
    rulesChangedAt: z9.string().nullable(),
    page: z9.number().int(),
    perPage: z9.number().int(),
    /** Quantas entradas o período tem, com o filtro aplicado. */
    totalRows: z9.number().int()
  })
]);
var salesContract = c7.router(
  {
    /* --- cadastro: quem é quem, e quem responde a quem -------------------- *
     *
     * Os três recursos abaixo vivem sob **`sales.teams`** — consultores,
     * times e hierarquia —, e a razão está no plano: eles são uma tela só
     * (`comercial-27`). Um recurso separado por lista daria a alguém a metade de
     * cima da tela funcionando e a de baixo devolvendo 403, que é uma tela
     * afirmando o que o código não cumpre. É a lição de `retention.stages`, que
     * cobre também os motivos.
     *
     * A consequência para o catálogo de permissões fica registrada aqui: o
     * rótulo de `sales.teams` precisa nomear as três, senão a tela de grupos
     * concede "times" e a pessoa passa a poder mexer no vínculo que decide **de
     * quem é a comissão**.
     * --------------------------------------------------------------------- */
    listConsultants: {
      method: "GET",
      path: "/consultants",
      query: z9.object({
        teamId: z9.coerce.number().int().optional(),
        /**
         * `ativo = false` é "a pessoa parou de vender", e ela some da lista por
         * omissão — mas continua dona das vendas antigas.
         *
         * `z.enum` e não `z.coerce.boolean()`: na query string tudo é string, e
         * `Boolean('false')` é `true`. É a **sexta** ocorrência da mesma
         * armadilha no pacote (`listStages` e `listReasons` em
         * `retention.ts`, `listCategories` em `tickets.ts`,
         * `listClassifications` em `erp.ts`, `mesa` em `billing.ts`), e
         * continua escrita à mão porque um helper compartilhado é mudança de
         * `common.ts`, que não é desta tarefa.
         */
        includeInactive: z9.enum(["true", "false"]).transform((v) => v === "true").optional(),
        /** vínculo arquivado é o que nunca deveria ter existido — ver `consultantSchema` */
        includeArchived: z9.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: z9.array(consultantSchema), ...defaultErrors },
      summary: "Os consultores da base atual, com o c\xF3digo do ERP"
    },
    /**
     * Vincula um usuário ao código do voluntário **daquela base**.
     *
     * Os dois identificadores vão no corpo, e não no caminho: `contract.test.ts`
     * reprova identificador de pessoa no endereço, e `userId` é um. Fora do
     * caminho ele não entra em log de servidor, histórico de navegador nem
     * cabeçalho `Referer`.
     *
     * O mesmo código em outra base é **aceito**, e é o caso que importa: o mesmo
     * operador é 394 no CE e 75 no GO. O que o banco recusa é o mesmo código
     * ativo duas vezes na mesma base, e a mesma pessoa vinculada duas vezes na
     * mesma base.
     */
    createConsultant: {
      method: "POST",
      path: "/consultants",
      body: z9.object({
        userId: z9.string().min(1),
        /** o código do voluntário NAQUELA base — conferido contra `ctx.tenant.id` */
        erpVolunteerCode: z9.string().min(1).max(40),
        /** `null` é "sem time", que é um estado e não um erro */
        teamId: z9.number().int().nullable(),
        /**
         * Sob qual régua a pessoa é paga — obrigatório desde 22/10/2026. A tela
         * pré-escolhe `externo`, mas quem chama sempre manda o valor: um padrão
         * aqui seria a régua afirmada sem ninguém ter dito.
         */
        profile: consultantProfileSchema,
        /**
         * Desde quando a troca vale — e é o que decide de quais meses a premiação
         * de líder soma esta pessoa. Ausente é hoje; no futuro é recusado.
         */
        effectiveFrom: businessDateSchema.optional(),
        /** A foto, opcional — também se põe depois, pela ficha (`definir_foto`). */
        photo: consultantPhotoUploadSchema.optional()
      }),
      responses: { 201: consultantSchema, ...defaultErrors },
      summary: "Vincula um usu\xE1rio ao c\xF3digo de volunt\xE1rio da base atual"
    },
    /**
     * Edita o vínculo, declara a régua sob a qual a pessoa é paga, ou arquiva.
     *
     * `ativo` e `arquivar` são atos diferentes porque respondem perguntas
     * diferentes (ver `consultantSchema`), e confundi-los apaga a comissão já
     * apurada de quem apenas parou de vender.
     *
     * **Não muda `erpVolunteerCode`.** Ele é a identidade do consultor do lado
     * do ERP: trocá-lo reatribuiria em silêncio todas as vendas já casadas por
     * ele. O caminho de correção é arquivar o vínculo errado e criar o certo,
     * que é o que a chave única parcial da tabela existe para permitir.
     *
     * ## `declarar_perfil` é ato PRÓPRIO, e não um campo do `editar`
     *
     * É a decisão mais consequente desta rota, e ela tem uma frase: **ninguém
     * pode trocar o perfil sem perceber que trocou quanto a pessoa recebe.**
     *
     * Como campo obrigatório do `editar`, toda tela que mexesse no time teria de
     * mandar o perfil junto — e um formulário carregado antes de outra aba
     * declarar o perfil o gravaria de volta ao valor velho, calado. Como campo
     * **opcional**, "não quis mexer" e "apague o perfil" viram a mesma
     * requisição, que é exatamente o defeito que `archiveEntry` evita
     * exigindo o booleano. Ato separado não tem nenhum dos dois: quem o envia
     * está dizendo só isso, e a trilha registra `de → para`.
     *
     * **`perfil` aqui NÃO é anulável**, como em toda a rota desde 22/10/2026.
     * Perfil declarado errado se corrige declarando o certo.
     */
    editConsultant: {
      method: "PATCH",
      path: "/consultants/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.discriminatedUnion("action", [
        z9.object({
          action: z9.literal("editar"),
          /** `null` tira do time */
          teamId: z9.number().int().nullable(),
          /** `false` é "parou de vender": sai do ranking corrente, mantém o passado */
          active: z9.boolean(),
          /**
           * Desde quando a troca vale — e é o que decide de quais meses a premiação
           * de líder soma esta pessoa. Ausente é hoje; no futuro é recusado.
           */
          effectiveFrom: businessDateSchema.optional(),
          /**
           * O ÚLTIMO dia como membro de `teamId` (03/11/2026) — o "Foi membro
           * até" da ficha. Presente, a pessoa respondeu ao líder do time de
           * `effectiveFrom` até este dia, e sai do time: a cadeia fecha nele e
           * o vínculo fica sem time. Pede um time; no futuro, ou antes de
           * `effectiveFrom`, é recusado. Ausente, a pessoa continua no time.
           */
          effectiveUntil: businessDateSchema.optional()
        }),
        z9.object({
          /**
           * Sob qual régua esta pessoa passa a ser paga.
           *
           * Não anulável de propósito — ver o cabeçalho da rota. O ato é
           * idempotente: declarar o mesmo perfil de novo grava o mesmo valor e
           * não registra troca nenhuma na trilha.
           */
          action: z9.literal("declarar_perfil"),
          profile: consultantProfileSchema
        }),
        z9.object({
          /**
           * Em qual lote do mês a taxa de tecnologia é descontada: o maior (o
           * padrão), ou sempre o 1x, ou sempre o 2x. Vale para os meses cuja
           * primeira despesa ainda não nasceu — o que já começou a ser pago
           * tem a taxa gravada no lote.
           */
          action: z9.literal("definir_taxa_tecnologia"),
          technologyFeeLot: technologyFeeLotSchema
        }),
        z9.object({
          /** Põe ou troca a foto; `null` tira. Não mexe em mais nada do vínculo. */
          action: z9.literal("definir_foto"),
          photo: consultantPhotoUploadSchema.nullable()
        }),
        z9.object({
          /** este vínculo nunca deveria ter existido — sai de tudo, inclusive do passado */
          action: z9.literal("arquivar")
        })
      ]),
      responses: { 200: consultantSchema, ...defaultErrors },
      summary: "Edita, declara a r\xE9gua de pagamento ou arquiva o v\xEDnculo de um consultor"
    },
    /**
     * Corrige o código de voluntário de um vínculo — o caminho que o `editar`
     * não tem, e a decisão que reverteu o bloqueio de 28/08.
     *
     * ## O bloqueio estava certo sobre o risco e errado sobre o remédio
     *
     * O risco é real e continua sendo: `sales_sales.consultant_id` é **congelado
     * na importação**, e trocar o código depois não refaz o casamento — as
     * vendas que entraram pelo código velho continuariam penduradas na pessoa,
     * agora afirmando ser do código novo. Por isso o campo nunca entrou no
     * `editar`, e continua fora dele.
     *
     * O que estava errado era mandar **arquivar e recriar** no lugar, e a conta
     * do que aquilo custa não estava escrita em lugar nenhum:
     *
     *   · o vínculo arquivado **não sai** da apuração — `consultantOf` e
     *     `tenantConsultants` declaram a ausência do filtro `archived_at` como
     *     deliberada —, então as placas do vínculo errado continuam contando
     *     para ele;
     *   · premiação (`sales_awards`) e meta (`sales_targets`) apontam para o
     *     **id** do vínculo. Recriar perde as duas; corrigir o código as mantém,
     *     que é o desfecho certo — elas são da pessoa, não do código;
     *   · e o casamento das vendas **não** se conserta sozinho, porque a busca
     *     da importação nunca filtrou `archived_at`: com o código repetido entre
     *     um vínculo arquivado e um ativo, o mapa `código → id` ficava com o
     *     último do `select`, sem ordem declarada.
     *
     * Então a troca deixou de ser "proibir" e passou a ser **fazer o que o
     * arquivar-e-recriar só fingia fazer**: soltar as vendas do código velho e
     * dizer, em voz alta, quais competências precisam ser reapuradas.
     *
     * ## Rota própria, e não um quarto ato do `PATCH`
     *
     * Porque a resposta é outra — ver `consultantCodeCorrectionSchema`. E porque
     * a ação do catálogo é outra: `sales.teams:corrigir_codigo`, separada de
     * `:editar`. Quem monta times não solta venda de ninguém.
     *
     * ## O que ela NÃO faz
     *
     * Não reapura, e a ausência é deliberada: reapurar é `sales.commissions:apurar`,
     * é sobre a competência inteira da praça e não sobre uma pessoa, e fazê-la
     * de carona aqui esconderia dentro de uma correção de cadastro a reescrita
     * do valor de todo mundo naquele mês. A resposta nomeia as competências; o
     * ato de reapurar continua sendo de quem tem a ação para isso.
     *
     * Não conserta vínculo arquivado: ali o código não casa mais nada, e mexer
     * nele seria dado que ninguém lê.
     */
    correctConsultantCode: {
      method: "POST",
      path: "/consultants/:id/code",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.object({
        /** o código certo NAQUELA base — mesmo formato do `createConsultant` */
        erpVolunteerCode: z9.string().min(1).max(40)
      }),
      responses: { 200: consultantCodeCorrectionSchema, ...defaultErrors },
      summary: "Corrige o c\xF3digo de volunt\xE1rio de um v\xEDnculo e solta as vendas do c\xF3digo velho"
    },
    listTeams: {
      method: "GET",
      path: "/teams",
      query: z9.object({
        includeArchived: z9.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: z9.array(salesTeamSchema), ...defaultErrors },
      summary: "Os times da base atual"
    },
    createTeam: {
      method: "POST",
      path: "/teams",
      body: z9.object({
        nome: z9.string().min(1).max(120),
        /**
         * O líder **corrente** — um consultor desta base, e `null` é "sem
         * líder". O líder sem time entra neste; o que já é membro de outro fica
         * lá, e este time nasce abaixo daquele. Quem era o líder em março
         * responde a hierarquia, que as gravações de time escrevem.
         */
        leaderUserId: z9.string().nullable(),
        /**
         * Desde quando a troca vale — e é o que decide de quais meses a premiação
         * de líder soma esta pessoa. Ausente é hoje; no futuro é recusado.
         */
        effectiveFrom: businessDateSchema.optional()
      }),
      responses: { 201: salesTeamSchema, ...defaultErrors },
      summary: "Cria um time na base atual"
    },
    editTeam: {
      method: "PATCH",
      path: "/teams/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.discriminatedUnion("action", [
        z9.object({
          action: z9.literal("editar"),
          nome: z9.string().min(1).max(120),
          leaderUserId: z9.string().nullable(),
          /**
           * Desde quando a troca vale — e é o que decide de quais meses a premiação
           * de líder soma esta pessoa. Ausente é hoje; no futuro é recusado.
           */
          effectiveFrom: businessDateSchema.optional()
        }),
        z9.object({
          /** arquivar em vez de apagar: as vendas antigas continuam apontando para ele */
          action: z9.literal("arquivar")
        })
      ]),
      responses: { 200: salesTeamSchema, ...defaultErrors },
      summary: "Edita ou arquiva um time da base atual"
    },
    /**
     * A cadeia de superiores da base, com vigência — SÓ LEITURA desde 23/09/2026.
     *
     * Não há mais rota que abra, encerre ou arquive um vínculo. A cadeia é
     * derivada do time: entrar num time é responder ao líder dele, e quem
     * escreve em `app.hierarchy` são as gravações de time e de consultor, com a
     * data "a partir de" de cada troca. Duas fontes da mesma relação era o que
     * deixava o time dizer uma coisa e a cadeia outra.
     *
     * `em` filtra pela data: é o que responde "quem respondia a quem em março",
     * e é a pergunta que a apuração de uma competência antiga faz. Sem ela, a
     * tela mostraria a cadeia de hoje e alguém concluiria que março estava
     * errado.
     */
    listHierarchy: {
      method: "GET",
      path: "/hierarchy",
      query: z9.object({
        /** a cadeia vigente NAQUELE dia; ausente, a corrente */
        at: businessDateSchema.optional(),
        /** os vínculos de uma pessoa; no corpo da query, nunca no caminho */
        userId: z9.string().optional(),
        /** linha arquivada é a que nunca deveria ter existido — fora por omissão */
        includeArchived: z9.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: z9.array(supervisorLinkSchema), ...defaultErrors },
      summary: "A cadeia de superiores da base atual, por data"
    },
    /* --- venda ------------------------------------------------------------ */
    listSales: {
      method: "GET",
      path: "/sales",
      query: paginationQuery.extend({
        period: periodSchema.optional(),
        consultantId: z9.coerce.number().int().optional(),
        /**
         * A situação como a origem a escreveu — texto, e não enum, pelo motivo
         * do cabeçalho de `saleSchema`.
         */
        situacao: z9.string().min(1).max(60).optional(),
        /**
         * As categorias de placa que a lista traz — uma ou várias.
         *
         * ## Lista separada por vírgula, e não chave repetida
         *
         * `?classification=venda,troca` e nunca
         * `?classification=venda&classification=troca`. A segunda forma tem uma
         * armadilha que não aparece em teste de caminho feliz: com **um** valor
         * selecionado o Express entrega uma `string`, com **dois** entrega um
         * `array`, e o schema que aceitasse os dois calaria a diferença até
         * alguém escrever `.map` sobre a string. A vírgula tem uma forma só.
         *
         * ## `min(1)`: a lista vazia é a ausência do filtro, e não um recorte
         *
         * Nenhuma categoria marcada quer dizer "todas", e quem chama manda o
         * campo **ausente**. Uma lista vazia que chegasse até aqui viraria um
         * `in ()` — recorte que não casa com nada —, e a tela mostraria zero
         * linhas dizendo que o filtro está desligado.
         *
         * ## A venda SEM categoria fica fora de qualquer seleção
         *
         * `classification` é anulável desde a P12, e nulo é "ninguém declarou" —
         * o estado que faz a apuração recusar a competência inteira. Ele **não**
         * é uma das quatro, então marcar as quatro traz menos linhas do que não
         * marcar nenhuma. É consequência de `in` sobre coluna anulável, é o que
         * a operação espera de um filtro por categoria, e a tela precisa dizer
         * isso em voz alta — quem quiser a fila dos não declarados tem a coluna
         * `Classificação` e o selo dela.
         */
        classification: z9.string().transform((v) => v.split(",")).pipe(z9.array(plateClassificationSchema).min(1)).optional(),
        /**
         * As vendas **sem consultor casado**: a fila de trabalho das importadas
         * com um código que ainda não tem vínculo. Filtro próprio porque
         * `consultantId` não consegue expressar "nenhum" — é o mesmo limite que
         * `assigneeId` tem em `retention.listar`, e aqui ele custa caro: essas
         * vendas não entram em apuração nenhuma.
         */
        withoutConsultant: z9.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: paginated(saleSchema), ...defaultErrors },
      summary: "As vendas da base atual"
    },
    /**
     * Registra uma venda à mão.
     *
     * Convive com a importada: a identidade é `(tenant_id, id_externo)`, e
     * reenviar o mesmo `id_externo` **atualiza** a mesma linha em vez de criar a
     * segunda. `idExterno: null` é a venda que nasceu aqui — e dois nulos não
     * colidem em índice único, que é o comportamento certo: duas vendas
     * digitadas à mão são dois fatos, e a tela não tem como saber que são a
     * mesma.
     *
     * Nenhum campo é `optional`, e os anuláveis são obrigatórios e anuláveis:
     * omitir não pode virar `null` por descuido de quem chama. É o mesmo cuidado
     * de `reasonId` em `retention.criar`.
     *
     * ## Não há `classification` AQUI, e é dívida declarada — não desenho
     *
     * A P12 fez a coluna nascer e este contrato passou a transportá-la na
     * leitura e no ato `corrigir`. Nesta rota ela **falta**, e a consequência é
     * exata: a venda registrada pela tela nasce sem categoria, e a competência
     * dela recusa apurar até alguém corrigi-la. A tela precisa dizer isso; ela
     * não pode oferecer um cadastro que parece completo.
     *
     * Ela não entrou junto porque acrescentar um campo obrigatório aqui inverte
     * um caso que hoje mede exatamente essa consequência
     * (`apps/api/test/sales.e2e.test.ts`, "a venda registrada pela ROTA
     * nasce sem classificação"), e mais dois corpos de requisição do mesmo
     * arquivo. Quem fechar isto muda os três **junto** com esta linha: um teste
     * verde afirmando o contrário da decisão é pior do que teste nenhum, porque
     * faz o conserto seguinte parecer regressão.
     */
    /**
     * O botão "importar do ERP" da tela de Vendas.
     *
     * `200` e não `202` — ver `salesImportSchema`: isto roda aqui, porque lê o
     * espelho e não o fornecedor.
     *
     * Sem `period`, a competência CORRENTE. O cron faz a corrente e a anterior
     * (o contrato chega ao espelho depois de ser feito); um clique é sobre o
     * mês que a pessoa está olhando, e a tela manda o que ela escolheu.
     */
    importSales: {
      method: "POST",
      path: "/sales/import",
      body: z9.object({ period: periodSchema.optional() }),
      responses: { 200: salesImportSchema, ...defaultErrors },
      summary: "Importa as vendas de uma compet\xEAncia a partir do espelho do ERP"
    },
    recordSale: {
      method: "POST",
      path: "/sales",
      body: z9.object({
        /** `null` é "nasceu aqui"; preenchido, reenviar atualiza em vez de duplicar */
        idExterno: z9.string().min(1).max(120).nullable(),
        period: periodSchema,
        value: moneySchema,
        situacao: z9.string().max(60).nullable(),
        /** `null` deixa a venda na fila de trabalho, sem dono — e é honesto */
        consultantId: z9.number().int().nullable(),
        codigoAssociado: z9.string().max(64).nullable(),
        codigoVeiculo: z9.string().max(64).nullable()
      }),
      responses: { 201: saleSchema, ...defaultErrors },
      summary: "Registra ou atualiza uma venda da base atual"
    },
    /** corrigir ou casar com um consultor — ver `saleActionSchema` */
    editSale: {
      method: "PATCH",
      path: "/sales/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: saleActionSchema,
      responses: { 200: saleSchema, ...defaultErrors },
      summary: "Corrige uma venda ou a casa com um consultor"
    },
    /* --- meta ------------------------------------------------------------- */
    listTargets: {
      method: "GET",
      path: "/targets",
      query: z9.object({
        /** obrigatória: meta sem competência é a coluna que o legado zera na
         *  virada do mês, e é como um mês fechado muda de valor sozinho (§5.3) */
        period: periodSchema,
        targetType: targetScopeSchema.optional()
      }),
      responses: { 200: z9.array(salesTargetSchema), ...defaultErrors },
      summary: "As metas da compet\xEAncia, na base atual"
    },
    /**
     * As últimas competências de **quem pergunta** — a série do comparativo.
     *
     * ## Ela é individual por construção, e não por permissão
     *
     * Não existe parâmetro de alvo, e o serviço resolve o consultor contra
     * `ctx.user.id` **sempre** — inclusive para quem tem `ver_qualquer`. É a
     * diferença entre uma rota que *pode* ser usada por outro escopo e uma que
     * *não tem como*: sem id de alvo na entrada, não há o que trocar por um
     * dígito. Quem não é consultor nesta praça recebe `[]`.
     *
     * Por isso o gráfico some para quem conduz a praça: a comparação
     * "minha meta × minha venda" não tem leitura coletiva, e uma versão
     * agregada dela seria outro número com outro nome — não este, maior.
     *
     * ## Devolve `SalesTarget[]`, e não uma forma própria
     *
     * Uma linha por competência **que tem meta declarada**, com o mesmo
     * `attainment` da listagem. Um schema próprio para o gráfico faria a mesma
     * pergunta ter duas respostas, e a primeira divergência apareceria como uma
     * linha que discorda do cartão logo acima dela.
     *
     * Mês sem meta declarada **não vem**: a série mostra o que foi declarado, e
     * inventar zero ali desenharia uma meta que ninguém digitou — o defeito de
     * §5.3 com outra forma.
     */
    targetHistory: {
      method: "GET",
      path: "/targets/history",
      query: z9.object({
        /** a última competência da série, inclusive */
        until: periodSchema,
        /**
         * Quantas competências para trás, contando com `ate`. Sem `.default()`
         * pela mesma disciplina de `order` no ranking: quem pede a série declara
         * o tamanho dela, e não recebe um recorte que a tela não escolheu.
         */
        months: z9.coerce.number().int().min(2).max(24)
      }),
      responses: { 200: z9.array(salesTargetSchema), ...defaultErrors },
      summary: "As \xFAltimas compet\xEAncias da pr\xF3pria meta, para o comparativo"
    },
    /**
     * Define a meta de um alvo numa competência — `upsert` na chave
     * `(tenant, alvo_tipo, alvo_id, competencia)`.
     *
     * `PUT` num caminho de coleção aqui quer dizer "esta meta passa a ser esta",
     * e não "substitua a coleção": o corpo nomeia **um** alvo. Duas telas abertas
     * na mesma meta gravam a última, que é o comportamento de um `upsert` — e é
     * o que `comercial-17` pede ("PUT duas vezes na mesma competência mantém uma
     * linha com o último valor").
     *
     * `valor` e `quantity` são obrigatórios e anuláveis, sem `default`: omitir
     * os dois é um corpo malformado, e o banco recusa a meta que não declara
     * nenhum (`sales_targets_declared_ck`). A checagem não é repetida aqui —
     * item 6 do cabeçalho.
     *
     * O alvo é resolvido contra `ctx.tenant.id` **antes** de gravar: a
     * estrangeira é polimórfica e o Postgres não a garante, então meta apontando
     * para consultor de outra base é erro de negócio, não 500.
     */
    defineTarget: {
      method: "PUT",
      path: "/targets",
      body: z9.object({
        targetType: targetScopeSchema,
        targetId: z9.number().int(),
        period: periodSchema,
        /** meta em dinheiro; `null` é "esta meta não é em dinheiro" */
        value: moneySchema.nullable(),
        /** meta em número de vendas; `null` é "esta meta não é em quantidade" */
        quantity: z9.number().int().min(0).nullable()
      }),
      responses: { 200: salesTargetSchema, ...defaultErrors },
      summary: "Define a meta de um consultor ou time numa compet\xEAncia"
    },
    /**
     * Exclui a meta — **arquiva**, e não apaga (convenção 9). Decidido em
     * 30/09, revogando o "arquivar meta não existe" de 10/09.
     *
     * A linha fica com `archived_at`, some da listagem e do histórico, e o ato
     * vai para a trilha com os valores que ela tinha. Definir de novo o mesmo
     * alvo na mesma competência **reativa a mesma linha** — a chave do `upsert`
     * é cheia, então não nasce uma segunda meta do mesmo mês.
     *
     * Meta já excluída, de outra base ou inexistente respondem 404.
     */
    archiveTarget: {
      method: "DELETE",
      path: "/targets/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.void(),
      responses: { 204: z9.null(), ...defaultErrors },
      summary: "Exclui (arquiva) a meta de um consultor ou time"
    },
    /* --- ranking ---------------------------------------------------------- */
    /**
     * O ranking da competência.
     *
     * **`order` é obrigatório, e não tem `.default()`.** Um padrão no schema
     * faria a consulta sem o campo devolver silenciosamente a ordem por
     * quantidade — e quem esquecesse de passá-lo receberia um primeiro lugar
     * diferente do que a tela está afirmando, sem erro em lugar nenhum. É a
     * mesma disciplina de `bonificacaoDe`, onde `default = []` fez a mesma
     * pessoa ter três valores. Quem pede o quadro declara sob qual recorte.
     */
    ranking: {
      method: "GET",
      path: "/ranking",
      query: z9.object({
        period: periodSchema,
        order: rankingOrderSchema,
        /**
         * Restringe o quadro de consultores a um time — exige
         * `sales.ranking:filtro_times`, e **403 sem ela**: a ação existe para
         * ser concedida, e uma rota que aceitasse o filtro de quem não a tem
         * faria a tela de grupos anunciar um poder que não muda nada.
         *
         * ## A posição é RECALCULADA dentro do time
         *
         * Filtrado, o primeiro do time é `1º` — e não o 3º da praça com saltos
         * até o 12º. É decisão de produto, tomada em 11/09, e ela tem um preço
         * que a tela precisa dizer: "posição" passa a significar duas coisas na
         * mesma tela conforme o filtro, e o 1º de um time de três lê igual ao 1º
         * da praça.
         *
         * ## A RECUSA continua sendo da competência inteira
         *
         * `venda_sem_classificacao` é medido sobre **todas** as placas do mês,
         * antes de o filtro entrar. Sem isso, uma
         * placa sem categoria num time faria o quadro recusar sem filtro e
         * responder normalmente com ele — e quem quisesse escapar da recusa
         * escaparia escolhendo um time.
         */
        teamId: z9.coerce.number().int().optional(),
        /**
         * O quadro — interno ou externo (`comercial-41`). Sem ele, o do perfil
         * de quem olha; quem não é de nenhum dos dois cai no externo. Pedir o
         * quadro do OUTRO segmento sem poder escolher é recusado, e não
         * ignorado — pelo mesmo motivo do `teamId`.
         */
        segment: rankingSegmentSchema.optional()
      }),
      responses: { 200: rankingSchema, ...defaultErrors },
      summary: "O ranking de vendas da compet\xEAncia, calculado no servidor"
    },
    /**
     * As vendas por trás da posição de um consultor — o detalhe do quadro.
     *
     * ## Ela tem ação PRÓPRIA, e não é zelo: é o que se estaria concedendo
     *
     * `sales.ranking:ver` libera o quadro — nomes, posições e totais. Esta
     * rota libera as **placas**: código do veículo, código do associado, valor
     * e situação de cada venda, uma a uma. São coisas diferentes, e quem
     * concede precisa poder conceder uma sem a outra — por isso
     * `sales.ranking:ver_vendas` existe em vez de esta rota pegar carona no
     * `ver` do quadro.
     *
     * A alternativa que parecia mais barata era o detalhe reusar
     * `GET /sales?consultantId=` sob `sales.sales:ver`. Ela custa o oposto do
     * que promete: quem só precisa conferir o número do ranking passaria a ter
     * a mesa de vendas inteira da base **e** o painel do início, que é o que
     * aquele recurso concede (ver o rótulo dele no catálogo). Uma permissão
     * larga concedida para destravar um botão estreito é como um sistema perde
     * a conta de quem pode o quê.
     *
     * ## Ela pagina, e o resumo não
     *
     * A lista usa `paginationQuery` como as outras leituras da casa. O que ela
     * **não** pagina são `achieved`, `ignored` e `value`: eles são o total da
     * competência, existem para ser conferidos contra as linhas, e recontá-los
     * por página transformaria o número que confere no número que precisa ser
     * conferido.
     *
     * Um consultor com 37 placas já fazia o modal medir 1817px de altura numa
     * janela de 800 — ver o docblock de `Modal` em `packages/ui`. A altura foi
     * consertada lá, na peça; a paginação é a outra metade, e é a que impede
     * uma competência de 200 placas de trafegar inteira para caber numa caixa
     * de dez linhas.
     *
     * ## Não recebe `order`, e a ausência é a decisão
     *
     * Nada nesta resposta depende do recorte: `achieved`, `ignored`, `value` e
     * cada `counted` são idênticos nas duas ordens — a ordem muda `position`, e
     * `position` não viaja aqui. Aceitar um `order` que não muda resposta
     * nenhuma faria a rota parecer ter um recorte que ela não tem, e o primeiro
     * a acreditar nisso seria quem escrevesse a segunda tela.
     */
    rankingSales: {
      method: "GET",
      path: "/ranking/sales",
      query: paginationQuery.extend({
        period: periodSchema,
        consultantId: z9.coerce.number().int()
      }),
      responses: { 200: rankingConsultantSalesSchema, ...defaultErrors },
      summary: "As vendas que comp\xF5em a posi\xE7\xE3o de um consultor na compet\xEAncia"
    },
    /* --- o painel do time ------------------------------------------------- */
    /**
     * O desempenho de um time na competência — a tela do líder.
     *
     * ## `teamId` é opcional, e a ausência NÃO é "todos"
     *
     * Ausente quer dizer **"o time que eu lidero"**, e o servidor responde o
     * primeiro de `options`. É o contrário de `ranking.teamId`, onde ausente é
     * a praça inteira — e a diferença é que lá o time é um filtro sobre um
     * quadro que existe sem ele, e aqui o time é o assunto: um painel "de todos
     * os times" somaria equipes diferentes numa linha só, que é o quadro que
     * saiu do ranking nesta mesma fatia.
     *
     * Um `teamId` que não está em `options` responde **403**, e não uma lista
     * vazia: quem manda o id de um time que não lidera está pedindo o
     * desempenho nominal de gente de outra equipe, e a resposta certa a isso é
     * dizer não. Lista vazia faria a tela afirmar que aquele time não vendeu.
     *
     * ## A competência é obrigatória, e não tem `.default()`
     *
     * Mesma disciplina de `ranking.order`: um padrão no schema faria a consulta
     * sem o campo devolver silenciosamente o mês de hoje, e quem esquecesse de
     * passá-lo receberia um painel de outro mês sem erro em lugar nenhum.
     */
    teamPerformance: {
      method: "GET",
      path: "/teams/performance",
      query: z9.object({
        period: periodSchema,
        /** ausente é "o time que eu lidero" — ver o docblock acima */
        teamId: z9.coerce.number().int().optional()
      }),
      responses: { 200: teamPerformanceSchema, ...defaultErrors },
      summary: "O desempenho de um time na compet\xEAncia, por integrante"
    },
    /**
     * A visão geral — todos os times da base, uma linha por time, e o diagrama
     * geral com a base na raiz (28/09/2026).
     *
     * Exige `sales.team_performance:ver_visao_geral` no decorador, e não dentro
     * do serviço: aqui não há recorte a alargar, a resposta é a base inteira ou
     * nada. Ver o catálogo.
     */
    teamsOverview: {
      method: "GET",
      path: "/teams/performance/overview",
      query: z9.object({ period: periodSchema }),
      responses: { 200: teamsOverviewSchema, ...defaultErrors },
      summary: "Todos os times da base na compet\xEAncia, com o total de cada ramo"
    },
    /**
     * As vendas de um integrante do time — o relatório venda a venda.
     *
     * ## Ela responde o MESMO schema de `rankingSales`, e é de propósito
     *
     * A pergunta é a mesma — *quais placas desta pessoa nesta competência, e
     * quais contaram* — e a resposta sai da **mesma função pura**
     * (`shapeConsultantSales`) sobre a **mesma leitura**. Um schema próprio
     * seria a segunda forma de dizer a mesma coisa, e o dia em que uma das duas
     * ganhasse um campo seria o dia em que as duas telas passariam a explicar o
     * mesmo número de jeitos diferentes.
     *
     * O que muda entre as duas rotas não é o payload: é **quem pode chamar**.
     * Lá o alcance é `sales.ranking:ver_vendas` ou o time de quem olha; aqui é
     * `sales.team_performance:ver_vendas`, e o recorte é o time que a pessoa
     * lidera. São concessões diferentes porque são plateias diferentes — o
     * líder não precisa do quadro da praça para conferir a própria equipe.
     *
     * ## Ela pagina, e o resumo não
     *
     * Pelo motivo de `rankingSales`: `achieved`, `ignored` e `value` são da
     * competência inteira e existem para ser conferidos contra as linhas. Um
     * total que encolhesse ao virar a página diria que a pessoa vendeu dez.
     */
    teamMemberSales: {
      method: "GET",
      path: "/teams/performance/sales",
      query: paginationQuery.extend({
        period: periodSchema,
        consultantId: z9.coerce.number().int()
      }),
      responses: { 200: rankingConsultantSalesSchema, ...defaultErrors },
      summary: "As vendas de um integrante do time na compet\xEAncia, placa a placa"
    },
    /* --- a régua da comissão ---------------------------------------------- *
     *
     * A régua é o **insumo** da apuração, e por isso vem antes dela aqui.
     * Recurso próprio no catálogo (`sales.tiers`), pela lição que a
     * cobrança escreveu: *quem opera a mesa não muda a régua da casa*.
     *
     * `/tiers` é o mesmo nome que a cobrança já dá a este conceito (convenção
     * 7, um nome por conceito) — os prefixos separam os dois, e quem procurar a
     * régua de um módulo acha a do outro pelo mesmo caminho.
     * --------------------------------------------------------------------- */
    /**
     * A régua de comissão da base, **na competência pedida**.
     *
     * `period` é obrigatória, e não é comodidade de filtro: os degraus têm
     * vigência, e uma leitura "da régua atual" devolveria hoje o mesmo que
     * devolveu em março até a primeira troca de régua — e aí um mês fechado
     * mudaria de valor sem ninguém mexer em nada (§5.3). Reapurar março tem de
     * poder olhar a régua de março.
     */
    tiers: {
      method: "GET",
      path: "/tiers",
      query: z9.object({ period: periodSchema }),
      responses: { 200: commissionLadderSchema, ...defaultErrors },
      summary: "A r\xE9gua de comiss\xE3o da base atual numa compet\xEAncia"
    },
    /**
     * Abre uma vigência da régua — a base passa a pagar por esta a partir de
     * `period`.
     *
     * ## O corpo é a régua INTEIRA, e o motivo é o mesmo da cobrança
     *
     * **Sobreposição só se avalia com o conjunto.** Validar degrau a degrau é
     * impossível (mover um piso para o lugar de outro exige que os dois mudem no
     * mesmo instante), e quem julga é o `EXCLUDE USING gist`
     * `sales_tiers_no_overlap`, que avalia exatamente o conjunto.
     * A recusa dele é traduzida em pt-BR pelo serviço, nomeando o degrau e as
     * duas vigências que se encavalaram. **Não reimplemente essa checagem
     * aqui** — item 6 do cabeçalho: duas verdades divergem, e a do banco é a que
     * vale, porque é ela que também defende a semeadura, o `psql` e a importação
     * que ainda vai nascer.
     *
     * É por isso que `minPlates` não tem `min(0)` neste corpo, embora
     * `sales_tiers_non_negative_ck` recuse negativo: com o zero barrado
     * aqui, aquela tradução — que existe, tem teste e diz *"um sinal a mais numa
     * digitação sairia verde na tela e pagaria ao contrário"* — deixaria de ser
     * alcançável por esta rota, e uma tradução que ninguém consegue ver é uma
     * tradução que apodrece.
     *
     * ## `PUT` que NÃO substitui a coleção — e aqui ele diverge da cobrança
     *
     * `PUT /billing/tiers` apaga a régua e reinsere. Aqui isso seria estrago,
     * por duas razões que o cabeçalho de `sales_tiers` escreve: a comissão
     * já apurada aponta para o degrau por estrangeira composta (apagar deixaria
     * um valor sem explicação, ou faria toda edição falhar com `23503` depois da
     * primeira apuração), e a régua de março **precisa continuar existindo**
     * depois de a de abril entrar.
     *
     * Então o serviço arquiva a reedição da mesma competência, fecha a vigência
     * anterior no mês de antes e insere a nova valendo daqui em diante. Não há
     * rota que apague régua, e `min(1)` recusa `{ degraus: [] }` — que é o corpo
     * que uma tela manda quando o estado dela ainda não carregou, e que deixaria
     * a praça sem régua daquela competência em diante: a comissão pararia sem
     * ninguém ter pedido.
     *
     * ## A resposta é a UNIÃO, e não a lista — ao contrário da cobrança
     *
     * Salvar duas das três réguas é um corpo perfeitamente válido, e deixa a
     * base **incompleta**. Devolver a lista, ou prometer `configurada`, faria a
     * tela dizer "salvo" sobre uma régua com que a apuração se recusa a pagar.
     * A resposta é relida do banco: traz os `id` que a comissão vai gravar e os
     * valores já normalizados pelo `numeric(12,2)`.
     *
     * `validFrom`/`validUntil` não entram no corpo: a vigência é derivada de
     * `period`, e deixá-la digitável abriria a régua que começa no meio do
     * mês — o que `sales_tiers_period_ck` recusa com uma mensagem de
     * banco.
     */
    saveLadder: {
      method: "PUT",
      path: "/tiers",
      body: z9.object({
        /** a régua passa a valer deste mês em diante; a anterior é fechada no de antes */
        period: periodSchema,
        rungs: z9.array(
          z9.object({
            ladder: commissionLadderNameSchema,
            /** o piso; `0` no Bronze, que não é degrau. Quem recusa o resto é o `check` */
            platesMin: z9.number().int(),
            amountPerPlate: moneySchema
          })
        ).min(1)
      }),
      responses: { 200: commissionLadderSchema, ...defaultErrors },
      summary: "Abre uma vig\xEAncia da r\xE9gua de comiss\xE3o da base atual"
    },
    /* --- comissão --------------------------------------------------------- *
     *
     * `/commissions/export` e `/commissions/assess` não disputam desenho com
     * caminho nenhum — não há `/commissions/:id` neste contrato, porque a comissão
     * não é consultada uma a uma: a unidade é a competência.
     * --------------------------------------------------------------------- */
    listCommissions: {
      method: "GET",
      path: "/commissions",
      query: z9.object({ period: periodSchema }),
      responses: { 200: periodCommissionsSchema, ...defaultErrors },
      summary: "As comiss\xF5es apuradas de uma compet\xEAncia, na base atual"
    },
    exportCommissions: {
      method: "GET",
      path: "/commissions/export",
      query: z9.object({
        de: periodSchema,
        until: periodSchema
      }),
      responses: { 200: commissionExportSchema, ...defaultErrors },
      summary: "Exporta as comiss\xF5es de um intervalo de compet\xEAncias"
    },
    /**
     * Manda apurar a competência — ver `settlementResultSchema`.
     *
     * `POST` e não `PUT`: é um ato, e ele grava. E é **idempotente por chave**,
     * não por verbo: reapurar março atualiza a mesma linha de cada consultor.
     */
    assessCommissions: {
      method: "POST",
      path: "/commissions/assess",
      body: z9.object({ period: periodSchema }),
      responses: { 200: settlementResultSchema, ...defaultErrors },
      summary: "Apura a comiss\xE3o de uma compet\xEAncia na base atual"
    },
    /* --- livro-caixa ------------------------------------------------------ */
    /**
     * O livro-caixa de um período.
     *
     * `de` e `ate` são **obrigatórios**: sem período não há total de período, e
     * uma listagem sem recorte devolveria o caixa inteiro da base junto de um
     * total que ninguém pediu.
     */
    livroCashbook: {
      method: "GET",
      path: "/finance",
      query: paginationQuery.extend({
        de: businessDateSchema,
        until: businessDateSchema,
        tipo: entryTypeSchema.optional(),
        categoria: z9.string().min(1).max(80).optional(),
        /** arquivado some da listagem padrão e continua na tabela */
        includeArchived: z9.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: cashBookSchema, ...defaultErrors },
      summary: "Os lan\xE7amentos e os totais de um per\xEDodo, na base atual"
    },
    post: {
      method: "POST",
      path: "/finance/entries",
      body: z9.object({
        tipo: entryTypeSchema,
        categoria: z9.string().min(1).max(80),
        /** sempre positivo; a direção é o `tipo`. Quem recusa é o `check`. */
        value: moneySchema,
        data: businessDateSchema,
        descricao: z9.string().min(1).max(400)
      }),
      responses: { 201: cashEntrySchema, ...defaultErrors },
      summary: "Lan\xE7a uma entrada ou uma sa\xEDda no livro-caixa da base atual"
    },
    /**
     * Arquiva (ou desarquiva) um lançamento.
     *
     * **Lançamento não se apaga**, e o estorno é um lançamento novo de tipo
     * contrário — o kanban antigo apagava o card e perdia o histórico. Arquivar
     * é para a linha que nunca deveria ter existido, e desarquivar existe porque
     * arquivar errado não pode ser irreversível.
     *
     * `arquivado` é um booleano **obrigatório**: um campo opcional faria
     * "arquive" e "não quis mexer" serem a mesma requisição.
     */
    archiveEntry: {
      method: "PATCH",
      path: "/finance/entries/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.object({ archived: z9.boolean() }),
      responses: { 200: cashEntrySchema, ...defaultErrors },
      summary: "Arquiva ou desarquiva um lan\xE7amento do livro-caixa"
    },
    /* --- painel do início -------------------------------------------------- *
     *
     * `/numbers`, e não `/painel` como o plano do módulo escreve. É o nome que
     * `retention` e `billing` já dão a **este** conceito — o cartão do início
     * publicado pelo próprio módulo —, e a convenção 7 pede um nome por
     * conceito. Duas rotas com o mesmo papel e nomes diferentes é como alguém
     * procura `/gestao/painel` daqui a dois meses e conclui que a gestão não tem
     * painel.
     * --------------------------------------------------------------------- */
    numbers: {
      method: "GET",
      path: "/numbers",
      responses: { 200: salesNumbersSchema, ...defaultErrors },
      summary: "N\xFAmeros de vendas da compet\xEAncia para o painel do in\xEDcio"
    },
    /**
     * O Painel do Relatório de Vendas — ver `salesPanelSchema`.
     *
     * Sob `sales.reports:ver`, que é a base inteira ou nada: o relatório existe
     * para quem conduz o comercial, e não tem leitura estreita.
     *
     * `days` é lista separada por vírgula, pelo motivo de `classification` em
     * `listSales`. A semana é da tela: ela traduz a semana nos dias dela, e a rota
     * recebe dias.
     */
    salesPanel: {
      method: "GET",
      path: "/reports/panel",
      query: z9.object({
        period: periodSchema,
        consultantId: z9.coerce.number().int().optional(),
        teamId: z9.coerce.number().int().optional(),
        days: z9.string().transform((v) => v.split(",").map(Number)).pipe(z9.array(z9.number().int().min(1).max(31)).min(1)).optional()
      }),
      responses: { 200: salesPanelSchema, ...defaultErrors },
      summary: "O painel do Relat\xF3rio de Vendas: a compet\xEAncia da base inteira"
    },
    /**
     * A Comparação de meses — ver `salesComparisonSchema`. Mesmo recurso do
     * painel, e os mesmos filtros de pessoa e de dia; os dias valem para os
     * dois meses (o dia 31 simplesmente não existe no mês que não o tem).
     */
    salesComparison: {
      method: "GET",
      path: "/reports/comparison",
      query: z9.object({
        baseline: periodSchema,
        reference: periodSchema,
        consultantId: z9.coerce.number().int().optional(),
        teamId: z9.coerce.number().int().optional(),
        days: z9.string().transform((v) => v.split(",").map(Number)).pipe(z9.array(z9.number().int().min(1).max(31)).min(1)).optional()
      }),
      responses: { 200: salesComparisonSchema, ...defaultErrors },
      summary: "Duas compet\xEAncias lado a lado, no mesmo recorte"
    },
    /* --- Painel de Gestão --------------------------------------------------- *
     *
     * `GET /sales/management` — o painel de UM consultor.
     *
     * A unidade é a pessoa, e não a base, porque é a pessoa que o painel
     * responde: "o que há na minha carteira". A lista de todos os voluntários
     * do cadastro continua sendo outra rota, com outro payload e outra pergunta
     * — quem o ERP conhece e a plataforma ainda não.
     *
     * `consultant` é o código do voluntário NAQUELA base, e só quem tem
     * `sales.management:ver_qualquer` pode mandá-lo. Sem a ação larga o
     * parâmetro é recusado em vez de ignorado: ignorar faria a tela pedir o
     * painel de outra pessoa, receber 200 com o próprio e mostrar o nome errado
     * em cima dos números certos.
     * --------------------------------------------------------------------- */
    /* --- a régua da bonificação por recorrência --------------------------- *
     *
     * Sob `sales.commissions:configurar_premiacao`, e não sob um recurso
     * próprio: a pedido do responsável, a caixa aparece na tela de grupos
     * dentro de "Comissões", logo abaixo de "Exportar comissões".
     * --------------------------------------------------------------------- */
    listAwards: {
      method: "GET",
      path: "/awards",
      /*
       * O filtro por consultor é opcional, e a rota sem ele devolve a base
       * inteira — é o que a tela de um consultor pede com o id dele, e o que
       * qualquer leitura da base inteira pede sem.
       *
       * **Filtro, e não escopo.** Quem pode ler premiação pode ler as de
       * todos: a permissão é `sales.commissions:configurar_premiacao`, e o
       * isolamento que importa é o da praça, que vem da sessão.
       */
      query: z9.object({
        consultant: z9.coerce.number().int().optional()
      }),
      responses: { 200: z9.array(awardSchema), ...defaultErrors },
      summary: "As premia\xE7\xF5es cadastradas na base atual, de um consultor ou de todos"
    },
    /**
     * Cria uma premiação **de um consultor**.
     *
     * ## Nenhuma trava de sobreposição, e nenhuma resposta traduzida
     *
     * O cadastro não recusa período ocupado: duas premiações do mesmo
     * consultor cobrindo os mesmos contratos pagam as duas (decisão de
     * 16/09/2026). Quem cadastra é quem decide, e o preço está registrado no
     * schema.
     *
     * ## O consultor é do CORPO, e não do caminho
     *
     * A rota podia ser `/consultants/:id/awards`, e não é: `editAward` faz
     * `awardInputSchema.extend({ action })` e precisa do mesmo corpo nos dois
     * verbos. Com o dono no caminho, editar teria de repetir o id em dois
     * lugares — e a pergunta "qual deles vale quando divergirem" nasceria com
     * a rota.
     */
    createAward: {
      method: "POST",
      path: "/awards",
      body: awardInputSchema,
      responses: { 201: awardSchema, ...defaultErrors },
      summary: "Cadastra uma premia\xE7\xE3o para um consultor da base atual"
    },
    /**
     * Copia uma premiação para outros consultores — **uma nova por pessoa**.
     *
     * ## Por que ela existe, e por que não é uma premiação compartilhada
     *
     * A premiação pertence a **uma** pessoa desde 16/09/2026, e o preço estava
     * previsto no cabeçalho de `salesAwards`: cinco consultores com a mesma
     * régua são cinco cadastros. O que não estava previsto é o tamanho — as
     * duas tabelas que o responsável entregou têm **11 faixas cada**, e dez
     * consultores com as duas são **220 linhas digitadas à mão**. A divergência
     * nasce na primeira que alguém errar, e ela não dá erro: dá uma faixa
     * diferente num consultor só, descoberta pelo contracheque.
     *
     * Esta rota não desfaz a decisão de 16/09 — ela a torna praticável. O que
     * sai daqui são premiações **independentes**: editar a de Maria não toca na
     * de Pedro, que é exatamente o que a operação pediu ao inverter o cadastro.
     *
     * ## O que é copiado, e o que não é
     *
     * Vai tudo o que decide dinheiro: família, percentual, prazo, janela de
     * contrato, categoria, as faixas, os filtros de veículo e de adesão, e os
     * tipos de boleto. **O título vai junto e sem sufixo** — a premiação já é
     * identificada pelo dono na tela, e "Premiação Direta Moto (cópia)" seria
     * ruído no demonstrativo de quem recebe.
     *
     * **Não** vai `archivedAt`: a cópia de uma premiação arquivada nasce ativa.
     * Copiar é escolher aquela régua, e ninguém escolhe uma régua para já
     * nascer fora da apuração.
     *
     * ## Tudo ou nada
     *
     * Uma transação para a lista inteira. Metade dos consultores com a régua e
     * metade sem é o estado que ninguém consegue ver na tela — as premiações
     * aparecem cada uma na ficha do seu dono, e a que faltou só apareceria como
     * um valor menor no fim do mês.
     */
    copyAward: {
      method: "POST",
      path: "/awards/:id/copy",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.object({
        /**
         * Para quem copiar — `sales_consultants.id`, os da praça da sessão.
         *
         * O teto de 50 não é medo do banco: é o tamanho a partir do qual "copiei
         * sem querer para a base inteira" deixa de ser desfazível numa tarde. A
         * praça com mais gente tem dezenas de consultores, não centenas.
         */
        consultantIds: z9.array(z9.number().int()).min(1).max(50)
      }),
      responses: { 201: z9.array(awardSchema), ...defaultErrors },
      summary: "Copia uma premia\xE7\xE3o para outros consultores, uma nova para cada"
    },
    /**
     * Edita uma premiação, ou a arquiva.
     *
     * Os dois atos exigem a **mesma** ação do catálogo
     * (`sales.commissions:configurar_premiacao`), então a declaração é a
     * estática — `@PermissionByAction` com um mapa de valores iguais não
     * distingue nada e faria `registry.test.ts` afirmar que distingue.
     *
     * `boletoTypes`, `tiers` e os filtros de veículo chegam **inteiros** e
     * substituem a lista: é o desenho de `PUT /billing/tiers`. Um `PATCH` que
     * acrescenta sem remover obrigaria a tela a mandar remoções separadas, e
     * duas chamadas para salvar uma tela é como metade de um cadastro fica
     * gravada.
     *
     * **`consultantId` pode ser editado**, e isso é mover a premiação de
     * pessoa. É o ato de corrigir quem foi escolhido errado; quem quer as duas
     * pessoas com a mesma régua cadastra duas premiações, que é o que o modelo
     * de 16/09 passou a pedir.
     */
    editAward: {
      method: "PATCH",
      path: "/awards/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.discriminatedUnion("action", [
        awardInputSchema.extend({ action: z9.literal("editar") }),
        z9.object({ action: z9.literal("arquivar"), archived: z9.boolean() })
      ]),
      responses: { 200: awardSchema, ...defaultErrors },
      summary: "Edita ou arquiva uma premia\xE7\xE3o de recorr\xEAncia"
    },
    /**
     * Apaga uma premiação — **só a que nunca gerou extrato** (15/10/2026).
     *
     * É a exceção à convenção 9, e o limite é o que a mantém de pé: a
     * premiação com linha em `sales_statement_entries` explicou um valor já
     * mostrado a alguém, e essa continua só arquivável — a rota recusa e diz
     * para arquivar. A que tem concessão também: a concessão é a razão de um
     * número no extrato de outra pessoa. O que sobra é o cadastro errado que
     * nunca pagou nada, e arquivá-lo só deixaria lixo na lista de arquivados.
     */
    deleteAward: {
      method: "DELETE",
      path: "/awards/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.void(),
      responses: { 204: z9.null(), ...defaultErrors },
      summary: "Apaga uma premia\xE7\xE3o que nunca gerou extrato"
    },
    /* ===================================================================
     * As concessões — o líder repartindo a própria premiação
     *
     * Todas sob `sales.commissions:conceder_premiacao_apenas_a_membros_do_time`,
     * e **não** sob `configurar_premiacao`: conceder é um ato de quem lidera, e
     * a ação que cadastra premiação abre a régua da base inteira. Quem tem as
     * duas faz as duas; quem tem só esta mexe apenas nas próprias.
     * =================================================================== */
    /**
     * As concessões de uma premiação.
     *
     * ## Aninhada na premiação, enquanto `createAward` põe o dono no corpo
     *
     * A divergência é deliberada. Lá o dono está no corpo porque `editAward`
     * reusa `awardInputSchema` e teria de repetir o id em dois lugares. Aqui a
     * premiação **não** é um campo editável da concessão: mudar de premiação é
     * outro teto, e seria uma concessão diferente. O que não se edita pertence
     * ao caminho.
     */
    listAwardGrants: {
      method: "GET",
      path: "/awards/:id/grants",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      responses: { 200: z9.array(awardGrantSchema), ...defaultErrors },
      summary: "As concess\xF5es de uma premia\xE7\xE3o de l\xEDder"
    },
    /**
     * Concede parte da premiação a um liderado.
     *
     * As três recusas que só o serviço consegue dar, e as três são cadastro e
     * não erro: a premiação não é de líder (`basisSource` é `proprio`), o
     * percentual alcança ou passa o do líder, e o liderado não está **um
     * degrau** abaixo dele na cadeia da data de hoje.
     */
    createAwardGrant: {
      method: "POST",
      path: "/awards/:id/grants",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: awardGrantInputSchema,
      responses: { 201: awardGrantSchema, ...defaultErrors },
      summary: "Concede parte de uma premia\xE7\xE3o de l\xEDder a um liderado direto"
    },
    /**
     * Edita ou arquiva uma concessão.
     *
     * Os dois braços exigem a mesma ação, então a declaração é a estática —
     * `@PermissionByAction` com um mapa de valores iguais não distingue nada.
     *
     * **Arquivar e não apagar** (convenção 9): a concessão explicou um valor
     * que já apareceu no extrato de duas pessoas, e apagá-la tiraria do banco a
     * razão do número que o líder recebeu a menos.
     */
    editAwardGrant: {
      method: "PATCH",
      path: "/awards/:id/grants/:grant",
      pathParams: z9.object({
        id: z9.coerce.number().int(),
        grant: z9.coerce.number().int()
      }),
      body: z9.discriminatedUnion("action", [
        awardGrantInputSchema.extend({ action: z9.literal("editar") }),
        z9.object({ action: z9.literal("arquivar"), archived: z9.boolean() })
      ]),
      responses: { 200: awardGrantSchema, ...defaultErrors },
      summary: "Edita ou arquiva uma concess\xE3o"
    },
    /**
     * As premiações de líder de quem pede, e quem elas alcançam.
     *
     * **Fora de `/awards/`**, de propósito: `/awards/mine/grants` casaria com
     * `/awards/:id/grants` e dependeria da ordem de registro das rotas para não
     * virar um 400 de `id` inválido.
     */
    ownAwardGrants: {
      method: "GET",
      path: "/award-grants/mine",
      responses: { 200: ownAwardGrantsSchema, ...defaultErrors },
      summary: "As premia\xE7\xF5es de l\xEDder de quem pede, com as concess\xF5es e o alcance"
    },
    /* ===================================================================
     * Os MODELOS de premiação — 23/09/2026
     *
     * Todas sob `sales.commissions:configurar_premiacao`, a mesma ação que
     * cadastra premiação: um modelo é uma premiação sem dono, e vinculá-lo é
     * criar premiações. Uma ação nova permitiria criar premiação por uma
     * porta que a ação de cadastrar não controla.
     * =================================================================== */
    listAwardTemplates: {
      method: "GET",
      path: "/award-templates",
      query: z9.object({
        includeArchived: z9.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: z9.array(awardTemplateSchema), ...defaultErrors },
      summary: "Os modelos de premia\xE7\xE3o da base atual, com quem est\xE1 vinculado a cada um"
    },
    createAwardTemplate: {
      method: "POST",
      path: "/award-templates",
      body: awardTemplateInputSchema,
      responses: { 201: awardTemplateSchema, ...defaultErrors },
      summary: "Cadastra um modelo de premia\xE7\xE3o na base atual"
    },
    /**
     * Edita ou arquiva um modelo.
     *
     * Editar não mexe em quem já foi vinculado, **a menos que a tela diga em
     * quem**: `applyTo` são as premiações (`consultants[].awardId`) que recebem
     * a régua nova junto, na mesma transação — pedido de 15/10/2026. Vazio ou
     * ausente é o comportamento de 23/09: só as vinculações seguintes. A
     * escolha é por pessoa porque a premiação de alguém pode ter sido ajustada
     * só para ela, e sobrescrevê-la sem perguntar apagaria o ajuste.
     */
    editAwardTemplate: {
      method: "PATCH",
      path: "/award-templates/:id",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.discriminatedUnion("action", [
        awardTemplateInputSchema.extend({
          action: z9.literal("editar"),
          applyTo: z9.array(z9.number().int()).max(50).optional()
        }),
        z9.object({ action: z9.literal("arquivar"), archived: z9.boolean() })
      ]),
      responses: { 200: awardTemplateSchema, ...defaultErrors },
      summary: "Edita ou arquiva um modelo de premia\xE7\xE3o"
    },
    /**
     * Vincula o modelo a consultores — uma premiação nova e independente para
     * cada um, numa transação só.
     *
     * Recusa quem já tem uma premiação ATIVA deste modelo: duas iguais na
     * mesma pessoa pagariam duas vezes sobre os mesmos contratos. O teto de 50
     * é o de `copyAward`, pelo mesmo motivo.
     */
    applyAwardTemplate: {
      method: "POST",
      path: "/award-templates/:id/apply",
      pathParams: z9.object({ id: z9.coerce.number().int() }),
      body: z9.object({ consultantIds: z9.array(z9.number().int()).min(1).max(50) }),
      responses: { 201: z9.array(awardSchema), ...defaultErrors },
      summary: "Vincula um modelo de premia\xE7\xE3o a consultores, criando uma para cada"
    },
    /* --- `setAwardFee` e `listAwardFees` SAÍRAM em 21/09/2026 ----------- *
     *
     * As duas serviam a tela que declarava a taxa física por boleto. A tarifa
     * passou a vir do espelho, somada por boleto na mesma consulta que soma o
     * pagamento — ninguém a digita, então não há o que cadastrar nem o que
     * listar.
     *
     * Ficam registradas porque a pergunta volta: se um dia a casa precisar
     * descontar algo que o banco NÃO cobra — uma taxa de tecnologia, por
     * exemplo —, esse valor é outro conceito e merece nome próprio, não o
     * reaproveitamento deste. Foi exatamente a confusão entre os dois que fez a
     * tarifa do banco ficar de fora por uma semana.
     * ------------------------------------------------------------------- */
    /* --- `setAwardVisibility` SAIU em 16/09/2026 ------------------------- *
     *
     * A rota definia quais premiações um grupo enxergava, com uma caixa
     * marcável na tela de grupos. Ela existia porque a premiação era um
     * registro solto da base, e "quem vê esta régua" não tinha outra resposta.
     *
     * Com a premiação pertencendo a um consultor, a pergunta passou a ter
     * dono: **quem enxerga o consultor enxerga as premiações dele**. Uma
     * segunda régua de visibilidade por cima seria duas respostas para a mesma
     * pergunta, e a mais estreita venceria sem ninguém perceber — que foi a
     * decisão do responsável ao tirá-la.
     * --------------------------------------------------------------------- */
    /**
     * A CARTEIRA de uma premiação na competência — o que gerou o valor.
     *
     * Mesma permissão do painel, e o mesmo estreitamento: quem tem só
     * `sales.management:ver` vê a própria, e pedir a de outro exige
     * `ver_qualquer`. A régua vive no serviço, como no painel — se ela vivesse
     * aqui, seriam duas.
     *
     * PAGINADA porque a lista é grande e cresce: o voluntário 117 da CE tem
     * 100 boletos pagos numa competência comum, e os maiores da praça têm
     * 4.625 veículos. A porta única devolve contagem em todo o resto justamente
     * para não trazer linha; aqui a linha é o ponto da tela, e o teto é a
     * página.
     */
    managementPortfolio: {
      method: "GET",
      path: "/management/portfolio",
      query: z9.object({
        award: z9.coerce.number().int(),
        month: periodSchema,
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /**
         * O lado da competência, e ele precisa ser o MESMO que a tela de origem
         * usou: a lista existe para conferir um número, e uma lista de boletos
         * pagos sob um total de boletos em aberto não confere nada.
         */
        state: awardStateSchema.optional(),
        page: z9.coerce.number().int().min(1).default(1),
        perPage: z9.coerce.number().int().min(1).max(200).default(50)
      }),
      responses: { 200: awardPortfolioSchema, ...defaultErrors },
      summary: "As linhas que geraram o valor de uma premia\xE7\xE3o na compet\xEAncia"
    },
    /**
     * O dia a dia da mesma carteira, pelos dois lados — o gráfico do relatório
     * aberto pelo Início do Comercial (28/10/2026). Sem lado e sem página: o
     * gráfico é o mês inteiro, pago e em aberto juntos.
     */
    managementPortfolioDaily: {
      method: "GET",
      path: "/management/portfolio/daily",
      query: z9.object({
        award: z9.coerce.number().int(),
        month: periodSchema,
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional()
      }),
      responses: { 200: awardDailySchema, ...defaultErrors },
      summary: "O pago e o em aberto de uma premia\xE7\xE3o, dia a dia, na compet\xEAncia"
    },
    /**
     * A configuração da praça: quais tipos de boleto em aberto contam como
     * perda.
     *
     * ## O recurso é o do cadastro de premiação, e não um novo
     *
     * `sales.commissions:configurar_premiacao` é quem decide **quanto cada
     * placa paga**; esta lista decide **o que a Carteira chama de perda**. São a
     * mesma concessão — mexer na régua do comercial —, e um recurso próprio
     * faria a tela de grupos conceder duas vezes a mesma coisa com dois
     * rótulos, que é como uma casa perde a conta de quem pode o quê.
     *
     * A LEITURA também exige a mesma ação, e é diferente do resto do módulo: a
     * lista não aparece em tela de consultor nenhum, só na Configuração da
     * base. Quem não pode editar não tem onde a ver.
     */
    losingBoletoTypes: {
      method: "GET",
      path: "/losing-boleto-types",
      responses: { 200: losingBoletoTypesSchema, ...defaultErrors },
      summary: "Os tipos de boleto que esta base considera perda"
    },
    setLosingBoletoTypes: {
      method: "PUT",
      path: "/losing-boleto-types",
      body: losingBoletoTypesInputSchema,
      responses: { 200: losingBoletoTypesSchema, ...defaultErrors },
      summary: "Substitui os tipos de boleto que esta base considera perda"
    },
    managementPanel: {
      method: "GET",
      path: "/management",
      query: z9.object({
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /**
         * A competência das bonificações — o mês em que o boleto foi pago.
         * Omitida, é o mês corrente.
         */
        month: periodSchema.optional(),
        /**
         * O lado da competência. Omitido é `garantido`, que é o painel de
         * sempre — ver `awardStateSchema`.
         */
        state: awardStateSchema.optional()
      }),
      responses: { 200: managementPanelSchema, ...defaultErrors },
      summary: "O painel de gest\xE3o de um consultor, na base atual"
    },
    /**
     * O PANORAMA da Carteira: a evolução gravada no Extrato e o mix de
     * contratos do cadastro, ao redor da competência.
     *
     * Mesma régua do painel: `sales.management:ver` lê a própria; pedir a de
     * outro exige `ver_qualquer` ou `ver_carteiras`, lida no serviço.
     */
    walletInsights: {
      method: "GET",
      path: "/wallet/insights",
      query: z9.object({
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /** O último mês da janela. Omitido, é o mês corrente. */
        month: periodSchema.optional()
      }),
      responses: { 200: walletInsightsSchema, ...defaultErrors },
      summary: "A evolu\xE7\xE3o e o mix da carteira de um consultor, na base atual"
    },
    /**
     * As AÇÕES da Carteira na competência: os contratos que ainda podem ativar
     * e os boletos em aberto que mais pesam. Mesma régua do painel.
     */
    walletActions: {
      method: "GET",
      path: "/wallet/actions",
      query: z9.object({
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /** A competência. Omitida, é o mês corrente. */
        month: periodSchema.optional()
      }),
      responses: { 200: walletActionsSchema, ...defaultErrors },
      summary: "O que fazer para a carteira crescer na compet\xEAncia"
    },
    /**
     * Os boletos em aberto da carteira na competência — a lista inteira, para a
     * tabela de recuperar recorrência. Mesma régua do painel.
     */
    walletOpenBoletos: {
      method: "GET",
      path: "/wallet/open-boletos",
      query: z9.object({
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /** A competência. Omitida, é o mês corrente. */
        month: periodSchema.optional()
      }),
      responses: { 200: walletOpenBoletosSchema, ...defaultErrors },
      summary: "Os boletos em aberto da carteira na compet\xEAncia"
    },
    /**
     * O ciclo dos boletos de UM veículo da carteira — o modal da placa na
     * tabela de recuperar recorrência. `POST` numa leitura porque a placa é
     * identificador de pessoa e vai no corpo (`contract.test.ts`).
     */
    walletVehicleBoletos: {
      method: "POST",
      path: "/wallet/vehicle-boletos",
      query: z9.object({
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional()
      }),
      body: z9.object({ plate: z9.string().min(1).max(10) }),
      responses: { 200: walletVehicleBoletosSchema, ...defaultErrors },
      summary: "Os boletos mais recentes de um ve\xEDculo da carteira, com o \xFAltimo envio"
    },
    /**
     * A taxa de pagamento da carteira nos meses fechados até a competência — a
     * matéria dos cenários da projeção. Mesma régua do painel.
     */
    walletForecast: {
      method: "GET",
      path: "/wallet/forecast",
      query: z9.object({
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /** O último mês da janela. Omitida, é o mês corrente. */
        month: periodSchema.optional()
      }),
      responses: { 200: walletForecastSchema, ...defaultErrors },
      summary: "A taxa de pagamento da carteira, para os cen\xE1rios da proje\xE7\xE3o"
    },
    /**
     * A média de vendas e o churn dos três meses fechados — os padrões de
     * "Vendas por mês" e "Churn mensal" na Calculadora. Só a PRÓPRIA: a
     * calculadora planeja a carteira de quem a abre, e não há tela que peça a
     * de outro.
     */
    walletCalculator: {
      method: "GET",
      path: "/wallet/calculator",
      query: z9.object({
        /** O último mês da janela. Omitido, é o último mês fechado. */
        month: periodSchema.optional()
      }),
      responses: { 200: walletCalculatorSchema, ...defaultErrors },
      summary: "A m\xE9dia de vendas e o churn do consultor nos tr\xEAs meses fechados"
    },
    /**
     * TODAS AS CARTEIRAS da base na competência — uma linha por consultor.
     *
     * Exige `sales.management:ver_carteiras`, na rota: esta leitura não tem
     * versão estreita — a própria carteira é `managementPanel`.
     */
    wallets: {
      method: "GET",
      path: "/wallets",
      query: z9.object({
        /** A competência. Omitida, é o mês corrente. */
        month: periodSchema.optional(),
        /** O lado. Omitido é `garantido`, como no painel. */
        state: awardStateSchema.optional()
      }),
      responses: { 200: walletsSchema, ...defaultErrors },
      summary: "A carteira de cada consultor da base, na compet\xEAncia"
    },
    /**
     * O EXTRATO do consultor numa competência — a movimentação, linha a linha.
     *
     * ## Recurso próprio, e não `sales.management`
     *
     * A Carteira e o Extrato mostram o mesmo dinheiro, mas conceder um não é
     * conceder o outro: o Extrato é o documento pessoal de quem recebeu, com
     * nome de associado e placa em cada linha, e `sales.statement:fechar`
     * é um ato que a Carteira não tem. Um recurso só faria "ver o total do
     * consultor" e "fechar a competência da praça" caberem na mesma caixa da
     * tela de grupos.
     *
     * ## O estreitamento é o mesmo do resto do módulo
     *
     * Quem tem só `ver` lê o próprio, e a régua vive no serviço — não aqui, nem
     * no guard. Pedir o de outro exige `ver_qualquer`, e sem ela a rota
     * responde 403 com o nome da ação que falta, nunca o extrato alheio.
     *
     * ## PAGINADO, e a busca desce até aqui
     *
     * A lista é grande por construção: um consultor com carteira média tem uma
     * centena de boletos pagos por mês, e cada premiação que os alcança gera a
     * própria linha. Buscar no cliente só acharia dentro da página aberta — e a
     * tela pareceria dizer que a placa não existe.
     */
    statement: {
      method: "GET",
      path: "/statement",
      query: z9.object({
        /** A competência. Omitida, é o mês corrente. */
        month: periodSchema.optional(),
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /**
         * Procura na PLACA e no TÍTULO DA PREMIAÇÃO.
         *
         * A placa é normalizada antes de comparar (convenção 6): quem digitar
         * `abc-1d23` acha `ABC1D23`. Sem isso, o hífen que a pessoa lê no
         * documento do carro não acharia nada, e ela concluiria que a venda
         * dela não entrou.
         */
        search: z9.string().min(1).max(60).optional(),
        page: z9.coerce.number().int().min(1).default(1),
        perPage: z9.coerce.number().int().min(1).max(200).default(50)
      }),
      responses: { 200: statementSchema, ...defaultErrors },
      summary: "O extrato de um consultor na compet\xEAncia"
    },
    /**
     * Os consultores com ENTRADA no extrato da competência — as opções do
     * seletor de quem tem `sales.statement:ver_qualquer`.
     *
     * É lista própria, e não `listConsultants`, porque aquela pede
     * `sales.teams:ver`: quem só recebeu o extrato de todo mundo não ganha o
     * cadastro de times de carona. Vem do que foi GRAVADO no mês, então quem
     * não teve entrada não aparece — o extrato dele diria "nenhuma entrada".
     */
    /**
     * O saldo da PRÓPRIA pessoa — nunca aceita consultor pelo cliente. Sob
     * `sales.statement:ver`, a permissão do próprio Extrato: é a mesma
     * informação, somada.
     */
    statementBalance: {
      method: "GET",
      path: "/statement/balance",
      responses: { 200: statementBalanceSchema, ...defaultErrors },
      summary: "O saldo de premia\xE7\xE3o em aberto do pr\xF3prio consultor"
    },
    /**
     * O que está por trás de UMA entrada do Extrato — o associado, os veículos
     * do boleto e os atendimentos deles (19/10/2026).
     *
     * O associado e os veículos completos vêm da Agenda (`agendaMember`), que já
     * recorta pela carteira; esta rota dá o caminho até lá e os atendimentos.
     * Ela só responde para um boleto que está no Extrato de quem pergunta — ou
     * do consultor pedido, com `ver_qualquer` —, para não virar uma consulta de
     * boleto qualquer da praça.
     */
    statementEntryContext: {
      method: "GET",
      path: "/statement/entries/:boletoCode/context",
      pathParams: z9.object({ boletoCode: z9.string().min(1).max(40) }),
      query: z9.object({
        /** O código do voluntário, como no Extrato. Exige `ver_qualquer`. */
        consultant: z9.string().min(1).max(40).optional()
      }),
      responses: { 200: statementEntryContextSchema, ...defaultErrors },
      summary: "O associado, os ve\xEDculos e os atendimentos de uma entrada do Extrato"
    },
    /**
     * A AGENDA — os associados da carteira do consultor, com telefone,
     * nascimento, situação e data de contrato.
     *
     * `sales.agenda:ver` é a própria; `consultant` pede a de outra pessoa e
     * exige `ver_qualquer`, lida no service — o desenho do Painel de Gestão.
     */
    agenda: {
      method: "GET",
      path: "/agenda",
      query: z9.object({
        /** O código do voluntário na base atual. Exige a ação larga. */
        consultant: z9.string().min(1).max(40).optional(),
        /**
         * Busca livre (21/10/2026): o código do associado, exato; o nome, em
         * qualquer ordem e sem acento; a placa de um carro dele com este
         * consultor; o CPF ou CNPJ; o telefone; o e-mail. Ver `agendaSearch`.
         */
        search: z9.string().min(1).max(60).optional(),
        /**
         * A DESCRIÇÃO da situação do associado, como o ERP a escreve. Não o
         * código: `codigo_situacao` vem vazio no espelho da CE.
         *
         * Desde 29/10/2026 os três recortes de lista (este, `vehicleSituation`
         * e `dueDay`) aceitam VÁRIOS valores, separados por vírgula — o
         * desenho de `classification` em `vendas`. Nenhuma descrição de
         * situação das duas praças tem vírgula (medido no mesmo dia).
         */
        situation: z9.string().transform((v) => v.split(",")).pipe(z9.array(z9.string().min(1).max(80)).min(1).max(50)).optional(),
        /**
         * Só os associados com carro em tratativa no sinistro (21/10/2026).
         * VALOR em pt-BR, e não booleano: `"false"` na busca do endereço
         * coagiria para verdadeiro.
         */
        claim: z9.enum(["em_tratativa"]).optional(),
        /**
         * Os recortes por CARRO (28/10/2026, a Carteira): o associado entra
         * quando um carro dele com este consultor casa todos — a situação do
         * carro (código), o contrato no intervalo e o dia de vencimento.
         */
        vehicleSituation: z9.string().transform((v) => v.split(",")).pipe(z9.array(z9.string().min(1).max(20)).min(1).max(50)).optional(),
        contractFrom: businessDateSchema.optional(),
        contractTo: businessDateSchema.optional(),
        dueDay: z9.string().transform((v) => v.split(",")).pipe(
          z9.array(z9.string().regex(/^\d{1,2}$/)).min(1).max(31)
        ).optional(),
        page: z9.coerce.number().int().min(1).default(1),
        /* Dez, como toda tabela da casa (decisão de 15/09; a agenda passou a seguir em 21/10/2026). */
        perPage: z9.coerce.number().int().min(1).max(200).default(10)
      }),
      responses: { 200: agendaSchema, ...defaultErrors },
      summary: "Os associados da carteira de um consultor"
    },
    /**
     * Os consultores da base, para quem tem `sales.agenda:ver_qualquer`
     * escolher de quem é a agenda. A larga fica NA ROTA: esta lista não tem
     * versão estreita.
     */
    /**
     * Os veículos e os boletos de um cliente da agenda. O mesmo escopo de
     * `agenda`: `consultant` exige `ver_qualquer`, e o associado precisa ser da
     * carteira pedida — senão é 404, e não uma lista vazia.
     */
    agendaMember: {
      method: "GET",
      path: "/agenda/members/:code",
      pathParams: z9.object({ code: z9.string().min(1).max(40) }),
      query: z9.object({
        consultant: z9.string().min(1).max(40).optional()
      }),
      responses: { 200: agendaMemberDetailSchema, ...defaultErrors },
      summary: "Os ve\xEDculos e os boletos de um cliente da agenda"
    },
    agendaConsultants: {
      method: "GET",
      path: "/agenda/consultants",
      responses: {
        200: z9.object({
          consultants: z9.array(
            z9.object({
              erpVolunteerCode: z9.string(),
              nome: z9.string().nullable()
            })
          )
        }),
        ...defaultErrors
      },
      summary: "Os consultores da base, para escolher a agenda"
    },
    statementConsultants: {
      method: "GET",
      path: "/statement/consultants",
      query: z9.object({ month: periodSchema.optional() }),
      responses: {
        200: z9.object({
          month: periodSchema,
          consultants: z9.array(
            z9.object({
              erpVolunteerCode: z9.string(),
              nome: z9.string().nullable(),
              /** A soma das entradas gravadas — a mesma do extrato dele, sem busca. */
              total: moneySchema,
              /** Quantas entradas ele tem na competência. */
              entries: z9.number().int()
            })
          )
        }),
        ...defaultErrors
      },
      summary: "Quem tem entrada no extrato da compet\xEAncia, nesta base"
    },
    /**
     * O ESTADO do fechamento de uma competência — aberta ou fechada, e o que
     * chegou depois.
     *
     * Existe para o modal de Pagamentos, onde o fechamento mora desde
     * 25/09/2026: quem fecha a praça não precisa ter extrato próprio, e ler o
     * estado pelo `statement` pediria `sales.statement:ver` a quem só recebeu
     * `fechar`. Exige a mesma ação dos três atos.
     */
    statementClosing: {
      method: "GET",
      path: "/statement/closing",
      query: z9.object({ month: periodSchema }),
      responses: {
        200: z9.object({
          month: periodSchema,
          closing: statementClosingSchema.nullable()
        }),
        ...defaultErrors
      },
      summary: "O estado do fechamento da compet\xEAncia nesta base"
    },
    /**
     * FECHA a competência da praça — o ato que trava o mês e libera a direta.
     *
     * É por praça e mês, nunca por consultor: fechar para uns e não para outros
     * daria ao demonstrativo da base uma data por pessoa.
     *
     * Fechar duas vezes a mesma competência é recusado pela chave única parcial
     * do banco, e a rota traduz a recusa em vez de devolver SQLSTATE.
     */
    closeStatement: {
      method: "POST",
      path: "/statement/closings",
      body: z9.object({ month: periodSchema }),
      responses: { 200: statementClosingSchema, ...defaultErrors },
      summary: "Fecha a compet\xEAncia do extrato nesta base"
    },
    /**
     * REABRE a competência, e o motivo é obrigatório.
     *
     * Reabrir uma competência já paga é o ato mais caro desta tela: ele
     * descongela linhas que alguém já conferiu, e some com as entradas de
     * família `direta` até o próximo fechamento. Sem o motivo gravado, ninguém
     * consegue auditar seis meses depois por que o extrato de setembro mudou —
     * e o `check` `sales_statement_closings_reopen_ck` cobra o mesmo no banco.
     */
    reopenStatement: {
      method: "POST",
      path: "/statement/closings/reopen",
      body: z9.object({
        month: periodSchema,
        reason: z9.string().min(5).max(500)
      }),
      responses: { 200: z9.object({ ok: z9.literal(true) }), ...defaultErrors },
      summary: "Reabre a compet\xEAncia do extrato nesta base"
    },
    /**
     * RECALCULA o mês aberto — reescreve as linhas com o cadastro de agora.
     *
     * Existe porque a esteira grava com a régua de quando passou, e editar a
     * régua depois não reescreve o que já está gravado (ver `rulesChangedAt`).
     * O recálculo é o ato que o faz, com nome e com dono.
     *
     * **Competência fechada não recalcula.** O caminho é reabrir, e é
     * deliberado: um recálculo silencioso sobre um mês fechado mudaria um
     * número que já foi pago, sem nada na tela dizendo que mudou.
     */
    recalculateStatement: {
      method: "POST",
      path: "/statement/recalculate",
      body: z9.object({ month: periodSchema }),
      responses: {
        200: z9.object({
          entries: z9.number().int(),
          /**
           * As premiações que o espelho não respondeu, e o motivo de cada uma.
           *
           * Sem elas, `entries: 0` tinha dois significados e a tela mostrava o
           * mesmo "0 entradas regravadas" para "o mês não teve boleto" e para "a
           * casa não conseguiu ler nada" — que são respostas opostas.
           */
          refused: z9.array(
            z9.object({
              /** O título da premiação, como o cadastro o escreve. */
              award: z9.string(),
              /** `espelho_vencido`, `espelho_nao_sincronizado`, … */
              refusal: z9.string()
            })
          )
        }),
        ...defaultErrors
      },
      summary: "Regrava as entradas da compet\xEAncia aberta com o cadastro atual"
    },
    /**
     * RECALCULA a competência de UM consultor — inclusive FECHADA (26/10/2026).
     *
     * O recálculo e a reabertura acima são da praça inteira; este reescreve só
     * as linhas de uma pessoa, e o mês continua fechado para as outras. No mês
     * fechado ele exige o `reason` (400 sem ele) e recusa (403) se algum lote
     * daquele consultor na competência já tiver nota fiscal enviada — a mesma
     * trava da reabertura, recortada pela pessoa.
     */
    recalculateConsultantStatement: {
      method: "POST",
      path: "/statement/consultant/recalculate",
      body: z9.object({
        month: periodSchema,
        /** O código do voluntário na base atual. */
        consultant: z9.string().min(1).max(40),
        /** Obrigatório quando a competência está fechada. */
        reason: z9.string().min(5).max(500).optional()
      }),
      responses: {
        200: z9.object({
          entries: z9.number().int(),
          refused: z9.array(z9.object({ award: z9.string(), refusal: z9.string() }))
        }),
        ...defaultErrors
      },
      summary: "Regrava as entradas de um consultor na compet\xEAncia, aberta ou fechada"
    }
  },
  { pathPrefix: "/sales" }
);

// src/management.ts
import { initContract as initContract9 } from "@ts-rest/core";
import { z as z11 } from "zod";
import {
  BOARD_STATUSES,
  EXPENSE_PAYMENT_METHODS as EXPENSE_PAYMENT_METHODS2,
  PAYMENT_STAGE_EVENT_TYPES,
  PAYMENT_STAGE_NAME_MAX,
  PAYMENT_STATUSES,
  STAGE_COLORS
} from "@evogard/domain";

// src/retention.ts
import { initContract as initContract8 } from "@ts-rest/core";
import { z as z10 } from "zod";
import {
  LABEL_NAME_MAX as LABEL_NAME_MAX3,
  RETENTION_FINAL_PHASES,
  RETENTION_PHASES
} from "@evogard/domain";
var c8 = initContract8();
var retentionPhaseSchema = z10.enum(RETENTION_PHASES);
var retentionFinalPhaseSchema = z10.enum(RETENTION_FINAL_PHASES);
var retentionChannelSchema = z10.enum(["portal", "interno", "telefone", "chat"]);
var tenantParams3 = z10.object({ tenant: z10.string().min(1).max(40) });
var requestParams = tenantParams3.extend({ id: z10.coerce.number().int().positive() });
var labelParams2 = tenantParams3.extend({ id: z10.coerce.number().int().positive() });
var attachmentParams = tenantParams3.extend({ id: z10.coerce.number().int().positive() });
var person = z10.object({ id: z10.string(), name: z10.string() });
var attachmentUrlSchema = z10.object({
  url: z10.string(),
  nome: z10.string(),
  expiresAtSeconds: z10.number().int()
});
var retentionLabelSchema = z10.object({
  id: z10.number().int(),
  name: z10.string(),
  color: labelColorSchema
});
var retentionLabelWriteSchema = z10.object({
  name: z10.string().trim().min(1, "D\xEA um nome \xE0 etiqueta.").max(LABEL_NAME_MAX3),
  color: labelColorSchema
});
var retentionCardSchema = z10.object({
  id: z10.number().int(),
  protocol: z10.string(),
  phase: retentionPhaseSchema,
  phaseSince: instantSchema,
  /** `retentionColumnKey`; nulo só para o cartão excluído. */
  column: z10.string().nullable(),
  operator: person.nullable(),
  memberName: z10.string().nullable(),
  plate: z10.string().nullable(),
  model: z10.string().nullable(),
  fixedAmount: moneySchema.nullable(),
  reasonText: z10.string().nullable(),
  labels: z10.array(retentionLabelSchema),
  retainedIn72h: z10.boolean(),
  hasBoleto: z10.boolean(),
  createdAt: instantSchema,
  completedAt: instantSchema.nullable()
});
var retentionHistoryEntrySchema = z10.object({
  id: z10.number().int(),
  kind: z10.enum(["movimentacao", "edicao", "transferencia", "email"]),
  fromPhase: z10.string().nullable(),
  toPhase: z10.string().nullable(),
  actorName: z10.string(),
  details: z10.string().nullable(),
  occurredAt: instantSchema,
  /** veio do `esteira-cancelamento` na migração */
  legacy: z10.boolean()
});
var retentionAttachmentSchema = z10.object({
  id: z10.number().int(),
  type: z10.enum(["selfie_documento", "documento_aberto", "boleto_pdf"]),
  name: z10.string(),
  mimeType: z10.string(),
  sizeBytes: z10.number().int(),
  createdAt: instantSchema
});
var retentionCardCanSchema = z10.object({
  edit: z10.boolean(),
  finish: z10.boolean(),
  retain: z10.boolean(),
  pull: z10.boolean(),
  transfer: z10.boolean(),
  remove: z10.boolean(),
  labels: z10.boolean()
});
var retentionCardDetailSchema = z10.object({
  card: retentionCardSchema,
  channel: retentionChannelSchema,
  /**
   * A pessoa. `email` e `phone` são os do CADASTRO, agora; `requestEmail` e
   * `requestPhone` são o que ela digitou no pedido, quando diferente — é para
   * onde os e-mails deste pedido vão.
   */
  member: z10.object({
    id: z10.number().int().nullable(),
    name: z10.string().nullable(),
    document: z10.string().nullable(),
    email: z10.string().nullable(),
    phone: z10.string().nullable(),
    requestEmail: z10.string().nullable(),
    requestPhone: z10.string().nullable()
  }),
  vehicle: z10.object({
    id: z10.number().int().nullable(),
    plate: z10.string().nullable(),
    model: z10.string().nullable(),
    chassis: z10.string().nullable(),
    /** a situação no cadastro, agora */
    statusNow: z10.string().nullable(),
    /** a situação no dia do pedido */
    statusAtRequest: z10.string().nullable()
  }),
  consultant: z10.object({
    name: z10.string().nullable(),
    email: z10.string().nullable(),
    phone: z10.string().nullable()
  }).nullable(),
  fields: z10.object({
    ourNumber: z10.string().nullable(),
    chatProtocol: z10.string().nullable(),
    handling: z10.string().nullable(),
    notes: z10.string().nullable(),
    lastContactOn: businessDateSchema.nullable()
  }),
  /** O boleto da casa, quando o nosso número casou — com link e linha digitável. */
  boleto: z10.object({
    id: z10.number().int(),
    ourNumber: z10.string().nullable(),
    dueDate: businessDateSchema.nullable(),
    amount: moneySchema.nullable(),
    digitableLine: z10.string().nullable(),
    link: z10.string().nullable()
  }).nullable(),
  firstOperator: person.nullable(),
  handledBy: person.nullable(),
  fellInto72hAt: instantSchema.nullable(),
  boletoAt72h: z10.boolean().nullable(),
  /** As outras placas do mesmo envio — o legado as mandava no mesmo e-mail. */
  siblings: z10.array(
    z10.object({
      id: z10.number().int(),
      protocol: z10.string(),
      plate: z10.string().nullable()
    })
  ),
  attachments: z10.array(retentionAttachmentSchema),
  history: z10.array(retentionHistoryEntrySchema),
  can: retentionCardCanSchema
});
var retentionOperatorSchema = z10.object({
  userId: z10.string(),
  name: z10.string(),
  rotationEnabled: z10.boolean(),
  /** fora da escala, mas ainda com cartão — a coluna não some */
  listed: z10.boolean()
});
var retentionSummarySchema = z10.object({
  tenant: z10.object({ id: z10.number().int(), slug: z10.string(), name: z10.string() }),
  me: z10.string(),
  can: z10.object({
    view: z10.boolean(),
    handle: z10.boolean(),
    retain: z10.boolean(),
    distribute: z10.boolean(),
    remove: z10.boolean(),
    configure: z10.boolean(),
    export: z10.boolean()
  }),
  /** Na ordem do rodízio. */
  operators: z10.array(retentionOperatorSchema),
  columns: z10.array(z10.object({ key: z10.string(), count: z10.number().int() }))
});
var boardFilters2 = {
  /** nome, CPF, placa ou protocolo */
  q: z10.string().trim().max(120).optional(),
  /** dia de ENTRADA no quadro, inclusive — `AAAA-MM-DD`, hora de Fortaleza */
  from: businessDateSchema.optional(),
  to: businessDateSchema.optional()
};
var finishFields = z10.object({
  ourNumber: z10.string().trim().max(60),
  chatProtocol: z10.string().trim().max(200),
  handling: z10.string().trim().max(4e3),
  lastContactOn: businessDateSchema
});
var retentionReportRowSchema = z10.object({
  protocol: z10.string(),
  memberName: z10.string().nullable(),
  document: z10.string().nullable(),
  email: z10.string().nullable(),
  phone: z10.string().nullable(),
  plate: z10.string().nullable(),
  tenant: z10.string(),
  vehicleStatus: z10.string().nullable(),
  fixedAmount: moneySchema.nullable(),
  reasonText: z10.string().nullable(),
  consultantName: z10.string().nullable(),
  consultantEmail: z10.string().nullable(),
  consultantPhone: z10.string().nullable(),
  phase: retentionPhaseSchema,
  operator: z10.string().nullable(),
  firstOperator: z10.string().nullable(),
  handledBy: z10.string().nullable(),
  ourNumber: z10.string().nullable(),
  chatProtocol: z10.string().nullable(),
  handling: z10.string().nullable(),
  lastContactOn: businessDateSchema.nullable(),
  notes: z10.string().nullable(),
  labels: z10.string(),
  createdAt: instantSchema,
  phaseSince: instantSchema,
  retainedIn72h: z10.boolean(),
  boletoLink: z10.string().nullable(),
  attachments: z10.string()
});
var retentionContract = c8.router(
  {
    summary: {
      method: "GET",
      path: "/:tenant/summary",
      pathParams: tenantParams3,
      query: z10.object(boardFilters2),
      responses: { 200: retentionSummarySchema, ...defaultErrors },
      summary: "As colunas da base com a contagem, os operadores e o que a pessoa pode"
    },
    cards: {
      method: "GET",
      path: "/:tenant/cards",
      pathParams: tenantParams3,
      query: z10.object({
        ...boardFilters2,
        column: z10.string().min(1).max(200),
        cursor: z10.string().max(200).optional(),
        limit: z10.coerce.number().int().min(1).max(100).default(30)
      }),
      responses: {
        200: z10.object({
          items: z10.array(retentionCardSchema),
          nextCursor: z10.string().nullable()
        }),
        ...defaultErrors
      },
      summary: "Os cart\xF5es de uma coluna, p\xE1gina a p\xE1gina"
    },
    detail: {
      method: "GET",
      path: "/:tenant/requests/:id",
      pathParams: requestParams,
      responses: { 200: retentionCardDetailSchema, ...defaultErrors },
      summary: "O cart\xE3o com a pessoa, o ve\xEDculo, o boleto, as fotos e o hist\xF3rico"
    },
    edit: {
      method: "PATCH",
      path: "/:tenant/requests/:id",
      pathParams: requestParams,
      body: z10.object({
        ourNumber: z10.string().trim().max(60).nullable().optional(),
        chatProtocol: z10.string().trim().max(200).nullable().optional(),
        handling: z10.string().trim().max(4e3).nullable().optional(),
        notes: z10.string().trim().max(4e3).nullable().optional(),
        lastContactOn: businessDateSchema.nullable().optional()
      }),
      responses: { 200: retentionCardDetailSchema, ...defaultErrors },
      summary: "Os campos da opera\xE7\xE3o: nosso n\xFAmero, protocolo, tratativa, observa\xE7\xE3o e contato"
    },
    finish: {
      method: "POST",
      path: "/:tenant/requests/:id/finish",
      pathParams: requestParams,
      body: z10.object({ phase: retentionFinalPhaseSchema }),
      responses: {
        200: retentionCardDetailSchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Finaliza como retido, cancelado ou diverg\xEAncia de documenta\xE7\xE3o"
    },
    retain: {
      method: "POST",
      path: "/:tenant/requests/:id/retain",
      pathParams: requestParams,
      body: finishFields,
      responses: {
        200: retentionCardDetailSchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Ret\xE9m o cliente a partir do 72h \u2014 o cart\xE3o passa a ser de quem reteve"
    },
    pull: {
      method: "POST",
      path: "/:tenant/requests/:id/pull",
      pathParams: requestParams,
      body: z10.object({}),
      responses: {
        200: retentionCardDetailSchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Puxa para si o cart\xE3o de outro operador"
    },
    transfer: {
      method: "POST",
      path: "/:tenant/requests/:id/transfer",
      pathParams: requestParams,
      body: z10.object({
        operatorId: z10.string().min(1),
        /** leva junto os cartões do mesmo associado que estão na mesma coluna */
        sameMember: z10.boolean().default(false)
      }),
      responses: {
        200: z10.object({ moved: z10.number().int() }),
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Transfere o cart\xE3o a outro operador, sem zerar o rel\xF3gio"
    },
    transferColumn: {
      method: "POST",
      path: "/:tenant/transfer-column",
      pathParams: tenantParams3,
      body: z10.object({
        fromOperatorId: z10.string().min(1),
        phase: z10.enum(["em_atendimento", "atendimento_48h"]),
        toOperatorId: z10.string().min(1)
      }),
      responses: {
        200: z10.object({ moved: z10.number().int() }),
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Transfere a coluna inteira de um operador para outro"
    },
    remove: {
      method: "DELETE",
      path: "/:tenant/requests/:id",
      pathParams: requestParams,
      body: z10.object({}).optional(),
      responses: { 204: z10.null(), ...defaultErrors },
      summary: "Exclui o cart\xE3o (arquiva)"
    },
    setLabels: {
      method: "PUT",
      path: "/:tenant/requests/:id/labels",
      pathParams: requestParams,
      body: z10.object({ labelIds: z10.array(z10.number().int().positive()).max(30) }),
      responses: {
        200: z10.object({ labels: z10.array(retentionLabelSchema) }),
        ...defaultErrors
      },
      summary: "As etiquetas do cart\xE3o"
    },
    labels: {
      method: "GET",
      path: "/:tenant/labels",
      pathParams: tenantParams3,
      responses: {
        200: z10.object({ items: z10.array(retentionLabelSchema) }),
        ...defaultErrors
      },
      summary: "O cat\xE1logo de etiquetas da base"
    },
    createLabel: {
      method: "POST",
      path: "/:tenant/labels",
      pathParams: tenantParams3,
      body: retentionLabelWriteSchema,
      responses: { 201: retentionLabelSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Cria uma etiqueta"
    },
    editLabel: {
      method: "PATCH",
      path: "/:tenant/labels/:id",
      pathParams: labelParams2,
      body: retentionLabelWriteSchema,
      responses: { 200: retentionLabelSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Renomeia ou troca a cor de uma etiqueta"
    },
    archiveLabel: {
      method: "DELETE",
      path: "/:tenant/labels/:id",
      pathParams: labelParams2,
      body: z10.object({}).optional(),
      responses: { 204: z10.null(), ...defaultErrors },
      summary: "Apaga a etiqueta \u2014 e a tira de todos os cart\xF5es"
    },
    operators: {
      method: "GET",
      path: "/:tenant/operators",
      pathParams: tenantParams3,
      responses: {
        200: z10.object({
          operators: z10.array(retentionOperatorSchema),
          /** quem pode entrar na escala: tem `retention.requests:tratar` nesta base */
          candidates: z10.array(person)
        }),
        ...defaultErrors
      },
      summary: "A escala da base, na ordem do rod\xEDzio, e quem pode entrar nela"
    },
    saveOperators: {
      method: "PUT",
      path: "/:tenant/operators",
      pathParams: tenantParams3,
      body: z10.object({
        /** a ordem da lista é a ordem do rodízio */
        operators: z10.array(z10.object({ userId: z10.string().min(1), rotationEnabled: z10.boolean() })).max(100)
      }),
      responses: {
        200: z10.object({ operators: z10.array(retentionOperatorSchema) }),
        ...defaultErrors
      },
      summary: "Grava a escala da base"
    },
    attachmentUrl: {
      method: "GET",
      path: "/:tenant/attachments/:id/url",
      pathParams: attachmentParams,
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de curta validade para a foto ou o boleto"
    },
    report: {
      method: "GET",
      path: "/:tenant/report",
      pathParams: tenantParams3,
      query: z10.object({ from: businessDateSchema, to: businessDateSchema }),
      responses: {
        200: z10.object({ items: z10.array(retentionReportRowSchema) }),
        ...defaultErrors
      },
      summary: "Os cart\xF5es que entraram no per\xEDodo, com as colunas do relat\xF3rio do legado"
    }
  },
  { pathPrefix: "/retention" }
);
var memberTenantSchema = z10.object({ slug: z10.string(), name: z10.string() });
var retentionPortalVehicleSchema = z10.object({
  tenant: memberTenantSchema,
  /** `VE-000123` — o identificador da casa, nunca o `id` da linha */
  code: z10.string(),
  plate: z10.string().nullable(),
  model: z10.string().nullable(),
  fixedAmount: moneySchema.nullable(),
  /** já tem pedido em andamento — aparece travado, como no legado */
  busy: z10.boolean()
});
var retentionPortalStartSchema = z10.discriminatedUnion("state", [
  z10.object({ state: z10.literal("sem_vinculo") }),
  z10.object({
    state: z10.literal("lida"),
    name: z10.string(),
    document: z10.string().nullable(),
    /** o contato do cadastro, que a pessoa confirma ou corrige */
    phone: z10.string().nullable(),
    email: z10.string().nullable(),
    vehicles: z10.array(retentionPortalVehicleSchema)
  })
]);
var retentionPortalFileSchema = z10.object({
  fileName: z10.string().min(1).max(255),
  mimeType: z10.string().min(3).max(160),
  content: z10.string().min(1)
});
var retentionPortalUploadSchema = z10.object({
  token: z10.string(),
  fileName: z10.string(),
  sizeBytes: z10.number().int()
});
var uploadToken = z10.string().min(16).max(4e3);
var retentionPortalOpenSchema = z10.object({
  vehicles: z10.array(
    z10.object({ tenant: z10.string().min(1).max(40), code: z10.string().min(1).max(40) })
  ).min(1).max(50),
  phone: z10.string().trim().min(1).max(40),
  email: z10.string().trim().min(3).max(254),
  reasonText: z10.string().trim().min(1).max(4e3),
  /** a selfie segurando o documento */
  selfie: uploadToken,
  /** o documento aberto, frente e verso */
  document: uploadToken
});
var openedVehicle = z10.object({
  tenant: memberTenantSchema,
  plate: z10.string().nullable(),
  model: z10.string().nullable()
});
var retentionPortalOpenedSchema = z10.object({
  created: z10.array(openedVehicle.extend({ protocol: z10.string() })),
  /** já tinham pedido em andamento, e não foram reenviados */
  duplicated: z10.array(openedVehicle)
});
var retentionMemberRequestSchema = z10.object({
  protocol: z10.string(),
  tenant: memberTenantSchema,
  plate: z10.string().nullable(),
  model: z10.string().nullable(),
  phase: retentionPhaseSchema,
  createdAt: instantSchema,
  completedAt: instantSchema.nullable()
});
var retentionPortalContract = c8.router(
  {
    start: {
      method: "GET",
      path: "/retention/start",
      responses: { 200: retentionPortalStartSchema, ...defaultErrors },
      summary: "O contato do cadastro e os ve\xEDculos que o associado da sess\xE3o pode cancelar"
    },
    upload: {
      method: "POST",
      path: "/retention/attachments",
      body: retentionPortalFileSchema,
      responses: { 201: retentionPortalUploadSchema, ...defaultErrors },
      summary: "Envia uma das duas fotos do pedido, antes de abrir"
    },
    open: {
      method: "POST",
      path: "/retention/requests",
      body: retentionPortalOpenSchema,
      responses: {
        201: retentionPortalOpenedSchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "O associado da sess\xE3o pede o cancelamento de um ou mais ve\xEDculos"
    },
    list: {
      method: "GET",
      path: "/retention/requests",
      responses: {
        200: z10.object({ items: z10.array(retentionMemberRequestSchema) }),
        ...defaultErrors
      },
      summary: "Os pedidos de cancelamento do associado da sess\xE3o"
    }
  },
  { pathPrefix: "/portal" }
);

// src/management.ts
var c9 = initContract9();
var paymentStatusSchema = z11.enum(PAYMENT_STATUSES);
var paymentMethodSchema = z11.enum(EXPENSE_PAYMENT_METHODS2);
var paymentCategorySchema = z11.enum(["1x", "2x"]);
var fileUploadSchema = z11.object({
  nome: z11.string().min(1).max(255),
  typeMime: z11.string().min(3).max(160),
  content: z11.string().min(1)
});
var paymentFileSchema = z11.object({
  id: z11.number().int(),
  kind: z11.enum(["nota_fiscal", "comprovante"]),
  nome: z11.string(),
  typeMime: z11.string(),
  sizeBytes: z11.number().int(),
  sha256: z11.string(),
  createdAt: instantSchema,
  /** Quem enviou. Nulo não acontece hoje — todo arquivo é ato de alguém. */
  createdBy: z11.string().nullable()
});
var windowStateSchema = z11.discriminatedUnion("estado", [
  z11.object({ estado: z11.literal("sem_janela") }),
  z11.object({
    estado: z11.enum(["antes", "aberta", "encerrada"]),
    opensOn: businessDateSchema,
    closesOn: businessDateSchema
  })
]);
var paymentSchema = z11.object({
  id: z11.number().int(),
  /** A competência, `AAAA-MM`. A nota é lançada neste mesmo mês. */
  period: periodSchema,
  category: paymentCategorySchema,
  status: paymentStatusSchema,
  consultant: z11.object({
    id: z11.number().int(),
    nome: z11.string(),
    erpVolunteerCode: z11.string()
  }),
  /**
   * O valor do lote: o da despesa quando ela existe; antes disso, o que o
   * Extrato fechado apurou — é nesse valor que a nota deve ser emitida.
   */
  amount: moneySchema,
  /**
   * A taxa de tecnologia deste lote (05/10/2026) — `amount` já vem com ela
   * descontada. `aplicada` traz o valor (zero no lote que não a carrega);
   * `nao_se_aplica` é o perfil que não paga (o interno); `faixas_nao_definidas`
   * é a praça sem faixa cadastrada — o envio da nota fica recusado, e `amount`
   * é o apurado, sem desconto.
   */
  technologyFee: z11.object({
    state: z11.enum(["aplicada", "nao_se_aplica", "faixas_nao_definidas"]),
    amount: moneySchema.nullable()
  }),
  /** A despesa do Financeiro. Nula antes do primeiro envio. */
  expenseId: z11.number().int().nullable(),
  /**
   * Para onde pagar — o beneficiário da despesa (`app.beneficiaries`, ramo
   * `consultor`). Nulo antes do primeiro envio. Só o id: os dados bancários
   * saem de `GET /platform/beneficiaries/:id`, sob
   * `platform.beneficiaries:ver_dados_bancarios` e com a consulta na trilha —
   * repeti-los aqui abriria a conta inteira a quem só abre o cartão.
   */
  beneficiaryId: z11.number().int().nullable(),
  /**
   * A primeira nota chegou depois da janela — "Premiação lançada fora do
   * período". Não trava nada: é a etiqueta do cartão.
   */
  sentLate: z11.boolean(),
  /**
   * O lote é pago contra nota fiscal? Falso é o consultor interno
   * (`comercial-41`): ele não envia nota, e a despesa nasce sozinha no dia 11.
   * Gravado quando o lote nasceu — trocar o perfil depois não muda este lote.
   */
  invoiceRequired: z11.boolean(),
  /** O vencimento da despesa (`paymentDueOn`). Nulo antes do primeiro envio. */
  dueDate: businessDateSchema.nullable(),
  /** Quando a nota vigente chegou. Nulo antes do primeiro envio. */
  invoiceSentAt: instantSchema.nullable(),
  /** Por que a nota voltou ao consultor — só em "Pendente". */
  correctionReason: z11.string().nullable(),
  /** Por que o Financeiro recusou o pagamento — só em "Pagamento Recusado". */
  refusalReason: z11.string().nullable(),
  /** A forma de pagamento da despesa. Nasce PIX; o Financeiro confirma ao pagar. */
  paymentMethod: paymentMethodSchema.nullable(),
  paidAmount: moneySchema.nullable(),
  /** A data do comprovante — a data de negócio de `financial_expenses.paid_at`. */
  paidAt: businessDateSchema.nullable(),
  /**
   * A competência está fechada agora no comercial? Falso é "reaberta": o valor
   * apurado pode mudar, e a nota não sai até ela fechar de novo.
   */
  closed: z11.boolean(),
  updatedAt: instantSchema
});
var paymentTransitionSchema = z11.object({
  from: paymentStatusSchema.nullable(),
  to: paymentStatusSchema,
  note: z11.string().nullable(),
  at: instantSchema,
  /** Nome de quem agiu. Nulo é o sistema — o fechamento da competência. */
  by: z11.string().nullable()
});
var paymentNoteSchema = z11.object({
  id: z11.number().int(),
  kind: z11.enum(["observacao", "edicao"]),
  body: z11.string(),
  at: instantSchema,
  /** Nome de quem escreveu. */
  by: z11.string().nullable()
});
var paymentContaAzulSchema = z11.object({
  eventCode: z11.string().nullable(),
  settled: z11.boolean(),
  steps: z11.array(
    z11.object({
      kind: z11.enum([
        "enviando",
        "lancada",
        "baixada",
        "fornecedor_criado",
        "recusada",
        "sem_resposta"
      ]),
      at: instantSchema,
      message: z11.string()
    })
  )
});
var paymentDetailSchema = paymentSchema.extend({
  files: z11.array(paymentFileSchema),
  transitions: z11.array(paymentTransitionSchema),
  notes: z11.array(paymentNoteSchema),
  contaAzul: paymentContaAzulSchema.nullable()
});
var myPaymentSchema = paymentSchema.extend({
  window: windowStateSchema,
  sendRefusal: z11.string().nullable(),
  /**
   * O lançamento do lote sem nota (consultor interno): o dia em que a despesa
   * nasce e, enquanto ela não nasceu, por que ainda não — nula quando nada a
   * impede. `null` inteiro no lote com nota.
   */
  launch: z11.object({ on: businessDateSchema, refusal: z11.string().nullable() }).nullable(),
  files: z11.array(paymentFileSchema)
});
var myPaymentDetailSchema = paymentDetailSchema.extend({
  window: windowStateSchema,
  sendRefusal: z11.string().nullable()
});
var myPaymentsSchema = z11.discriminatedUnion("estado", [
  /** A pessoa não é consultora nesta base — não há nota a enviar. */
  z11.object({ estado: z11.literal("sem_vinculo"), message: z11.string() }),
  z11.object({
    estado: z11.literal("consultor"),
    /** Os lotes da competência pedida. */
    items: z11.array(myPaymentSchema),
    /**
     * Os lotes de meses ANTERIORES ao pedido que esperam o consultor — a
     * primeira nota ou a correção. Sem esta lista, o painel aberto noutro mês
     * esconderia a nota atrasada que ainda pode ser enviada. A competência
     * seguinte não entra: ela tem a própria aba no filtro do painel.
     */
    pending: z11.array(myPaymentSchema)
  })
]);
var boardQuery = {
  status: paymentStatusSchema.optional(),
  month: periodSchema.optional(),
  category: paymentCategorySchema.optional(),
  cursor: z11.string().regex(/^\d+$/).optional(),
  perPage: z11.coerce.number().int().min(1).max(200).default(50)
};
var paymentsPageSchema = z11.object({
  items: z11.array(paymentSchema),
  next: z11.string().nullable(),
  total: z11.number().int()
});
var paymentActionSchema = z11.discriminatedUnion("action", [
  z11.object({
    action: z11.literal("aprovar"),
    /** A observação, se houver. O consultor lê junto com o aviso de aprovação. */
    note: z11.string().trim().max(2e3).optional()
  }),
  z11.object({
    action: z11.literal("solicitar_correcao"),
    reason: z11.string().trim().min(3).max(2e3)
  }),
  z11.object({
    action: z11.literal("pagar"),
    /** A data do comprovante. Não pode estar no futuro. */
    paidAt: businessDateSchema,
    /** Uma das formas fixas. A despesa nasce PIX; o Financeiro confirma ou troca aqui. */
    paymentMethod: paymentMethodSchema,
    receipt: fileUploadSchema
  }),
  /** Recusa o pagamento. É final, e o motivo é o que o consultor lê. */
  z11.object({
    action: z11.literal("recusar"),
    reason: z11.string().trim().min(3).max(2e3)
  }),
  /**
   * Tira da pendência o lote SEM nota, depois que o Financeiro corrigiu os
   * dados — no lote com nota, quem sai da pendência é o consultor, reenviando.
   */
  z11.object({
    action: z11.literal("retomar"),
    note: z11.string().trim().max(2e3).optional()
  })
]);
var invoiceWindowSchema = z11.object({
  category: paymentCategorySchema,
  opensDay: z11.number().int().min(1).max(31),
  closesDay: z11.number().int().min(1).max(31),
  /** O dia do pagamento do lote. Nulo é "ainda não definido". */
  payDay: z11.number().int().min(1).max(31).nullable()
});
var technologyFeeTierSchema = z11.object({
  fromAmount: moneySchema,
  fee: moneySchema
});
var boardStatusSchema = z11.enum(
  BOARD_STATUSES
);
var paymentStageEventSchema = z11.object({
  id: z11.number().int(),
  type: z11.enum(PAYMENT_STAGE_EVENT_TYPES),
  accountId: z11.number().int(),
  accountName: z11.string(),
  accountArchived: z11.boolean()
});
var paymentStageSchema = z11.object({
  status: boardStatusSchema,
  name: z11.string(),
  color: z11.enum(STAGE_COLORS),
  /** `false` é o nome e a cor de nascença — a praça nunca salvou esta fase. */
  customized: z11.boolean(),
  events: z11.array(paymentStageEventSchema)
});
var paymentStagesSchema = z11.object({
  stages: z11.array(paymentStageSchema),
  accounts: z11.array(z11.object({ id: z11.number().int(), name: z11.string() }))
});
var myPaymentStagesSchema = z11.object({
  stages: z11.array(paymentStageSchema.pick({ status: true, name: true, color: true }))
});
var savePaymentStageSchema = z11.object({
  name: z11.string().trim().min(1, "Informe o nome da fase.").max(PAYMENT_STAGE_NAME_MAX),
  color: z11.enum(STAGE_COLORS),
  /** A lista inteira da fase: o que não vem sai. */
  events: z11.array(
    z11.object({
      type: z11.enum(PAYMENT_STAGE_EVENT_TYPES),
      accountId: z11.number().int().positive()
    })
  ).max(20)
});
var PAYMENT_UPDATED_EVENT = "pagamento:atualizado";
var paymentUpdatedNoticeSchema = z11.object({
  paymentId: z11.number().int(),
  /** O `type` do aviso: `correcao_solicitada`, `nf_aprovada`, `premiacao_paga`, `pagamento_recusado`… */
  type: z11.string(),
  subject: z11.string()
});
var managementContract = c9.router(
  {
    myPayments: {
      method: "GET",
      path: "/payments/mine",
      query: z11.object({ month: periodSchema }),
      responses: { 200: myPaymentsSchema, ...defaultErrors },
      summary: "Os lotes da pr\xF3pria pessoa numa compet\xEAncia, com a janela de envio"
    },
    myFileUrl: {
      method: "GET",
      path: "/payments/mine/:id/files/:fileId/url",
      pathParams: z11.object({
        id: z11.coerce.number().int(),
        fileId: z11.coerce.number().int()
      }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de um arquivo do PR\xD3PRIO pagamento"
    },
    /*
     * O quadro do PRÓPRIO consultor (`:ver_meus`). Debaixo de `/mine/board`, e
     * não reaproveitando `/mine/:id/files`, porque a rota do painel é de
     * `:enviar_nf`: uma rota pede uma ação só, e quem tem só uma das duas
     * precisa abrir o próprio arquivo pela tela que tem.
     */
    myBoard: {
      method: "GET",
      path: "/payments/mine/board",
      query: z11.object(boardQuery),
      responses: { 200: paymentsPageSchema, ...defaultErrors },
      summary: "Uma p\xE1gina dos PR\xD3PRIOS pagamentos \u2014 uma coluna do quadro do consultor"
    },
    myBoardDetail: {
      method: "GET",
      path: "/payments/mine/board/:id",
      pathParams: z11.object({ id: z11.coerce.number().int() }),
      responses: { 200: myPaymentDetailSchema, ...defaultErrors },
      summary: "O PR\xD3PRIO cart\xE3o aberto, com as observa\xE7\xF5es e a janela de envio"
    },
    myBoardFileUrl: {
      method: "GET",
      path: "/payments/mine/board/:id/files/:fileId/url",
      pathParams: z11.object({
        id: z11.coerce.number().int(),
        fileId: z11.coerce.number().int()
      }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de um arquivo do PR\xD3PRIO pagamento, pelo quadro"
    },
    sendInvoice: {
      method: "POST",
      path: "/payments/:id/invoice",
      pathParams: z11.object({ id: z11.coerce.number().int() }),
      /*
       * Só o arquivo (24/09/2026): o valor é o apurado, e o primeiro envio faz
       * nascer a despesa do Financeiro. Não há número nem valor da nota.
       */
      body: z11.object({ file: fileUploadSchema }),
      responses: { 201: myPaymentSchema, ...defaultErrors },
      summary: "Envia (ou reenvia) o arquivo da nota fiscal do pr\xF3prio lote"
    },
    list: {
      method: "GET",
      path: "/payments",
      query: z11.object({ ...boardQuery, search: z11.string().trim().max(120).optional() }),
      responses: { 200: paymentsPageSchema, ...defaultErrors },
      summary: "Uma p\xE1gina dos pagamentos da pra\xE7a \u2014 uma coluna do quadro, ou todas"
    },
    detail: {
      method: "GET",
      path: "/payments/:id",
      pathParams: z11.object({ id: z11.coerce.number().int() }),
      responses: { 200: paymentDetailSchema, ...defaultErrors },
      summary: "O cart\xE3o aberto, com os arquivos e a linha do tempo"
    },
    act: {
      method: "PATCH",
      path: "/payments/:id",
      pathParams: z11.object({ id: z11.coerce.number().int() }),
      body: paymentActionSchema,
      responses: { 200: paymentDetailSchema, ...defaultErrors },
      summary: "Aprova, devolve para corre\xE7\xE3o, marca como pago ou recusa o pagamento"
    },
    addNote: {
      method: "POST",
      path: "/payments/:id/notes",
      pathParams: z11.object({ id: z11.coerce.number().int() }),
      body: z11.object({ body: z11.string().trim().min(1).max(2e3) }),
      responses: { 201: paymentDetailSchema, ...defaultErrors },
      summary: "Adiciona uma observa\xE7\xE3o ao cart\xE3o \u2014 o consultor l\xEA e \xE9 avisado"
    },
    fileUrl: {
      method: "GET",
      path: "/payments/:id/files/:fileId/url",
      pathParams: z11.object({
        id: z11.coerce.number().int(),
        fileId: z11.coerce.number().int()
      }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de curta validade para um arquivo do pagamento"
    },
    windows: {
      method: "GET",
      path: "/invoice-windows",
      responses: { 200: z11.array(invoiceWindowSchema), ...defaultErrors },
      summary: "A janela de envio da nota, por lote, nesta pra\xE7a"
    },
    saveWindows: {
      method: "PUT",
      path: "/invoice-windows",
      /**
       * A lista inteira: o lote ausente fica sem janela. É o mesmo "salvar
       * substitui" de `billing_tiers`.
       */
      body: z11.object({ windows: z11.array(invoiceWindowSchema).max(2) }),
      responses: { 200: z11.array(invoiceWindowSchema), ...defaultErrors },
      summary: "Substitui a janela de envio da nota nesta pra\xE7a"
    },
    technologyFeeTiers: {
      method: "GET",
      path: "/technology-fee-tiers",
      responses: { 200: z11.array(technologyFeeTierSchema), ...defaultErrors },
      summary: "As faixas da taxa de tecnologia da premia\xE7\xE3o nesta pra\xE7a"
    },
    saveTechnologyFeeTiers: {
      method: "PUT",
      path: "/technology-fee-tiers",
      /** A lista inteira — salvar substitui, como a janela. */
      body: z11.object({ tiers: z11.array(technologyFeeTierSchema).min(1).max(30) }),
      responses: { 200: z11.array(technologyFeeTierSchema), ...defaultErrors },
      summary: "Substitui as faixas da taxa de tecnologia nesta pra\xE7a \u2014 vale para os meses ainda n\xE3o pagos"
    },
    paymentStages: {
      method: "GET",
      path: "/payment-stages",
      responses: { 200: paymentStagesSchema, ...defaultErrors },
      summary: "As fases do quadro de pagamentos desta pra\xE7a \u2014 nome, cor e eventos"
    },
    myPaymentStages: {
      method: "GET",
      path: "/payment-stages/mine",
      responses: { 200: myPaymentStagesSchema, ...defaultErrors },
      summary: "O nome e a cor das fases, para o quadro do consultor"
    },
    savePaymentStage: {
      method: "PUT",
      path: "/payment-stages/:status",
      pathParams: z11.object({ status: boardStatusSchema }),
      body: savePaymentStageSchema,
      responses: { 200: paymentStagesSchema, ...defaultErrors },
      summary: "Substitui o nome, a cor e os eventos de uma fase nesta pra\xE7a"
    }
  },
  { pathPrefix: "/management" }
);

// src/sale-expenses.ts
import { initContract as initContract10 } from "@ts-rest/core";
import { z as z12 } from "zod";
import {
  SALE_EXPENSE_REASON_MAX,
  SALE_EXPENSE_STATUSES,
  SALE_EXPENSE_TEXT_MAX,
  SALE_EXPENSE_TYPES,
  STAGE_COLORS as STAGE_COLORS2
} from "@evogard/domain";
var c10 = initContract10();
var saleExpenseTypeSchema = z12.enum(SALE_EXPENSE_TYPES);
var saleExpenseStatusSchema = z12.enum(SALE_EXPENSE_STATUSES);
var fileUploadSchema2 = z12.object({
  nome: z12.string().min(1).max(255),
  typeMime: z12.string().min(3).max(160),
  content: z12.string().min(1)
});
var text = z12.string().trim().max(SALE_EXPENSE_TEXT_MAX);
var formFields = {
  referenceDate: businessDateSchema,
  plate: z12.string().trim().min(1).max(10),
  personName: text,
  amount: moneySchema,
  pixKey: text.nullable(),
  /** O código de compensação, três dígitos. */
  pixBankCode: z12.string().trim().max(3).nullable(),
  contact: text.nullable(),
  cancellationReason: z12.string().trim().max(SALE_EXPENSE_REASON_MAX).nullable()
};
var createSaleExpenseSchema = z12.object({
  saleId: z12.number().int().positive(),
  type: saleExpenseTypeSchema,
  ...formFields,
  /** Obrigatório em todo tipo (02/10/2026). */
  attachment: fileUploadSchema2
});
var correctSaleExpenseSchema = z12.object({
  ...formFields,
  attachment: fileUploadSchema2.optional()
});
var saleExpenseFileSchema = z12.object({
  id: z12.number().int(),
  kind: z12.enum(["comprovante", "pagamento"]),
  nome: z12.string(),
  typeMime: z12.string(),
  sizeBytes: z12.number().int(),
  createdAt: instantSchema
});
var saleExpenseSchema = z12.object({
  id: z12.number().int(),
  type: saleExpenseTypeSchema,
  status: saleExpenseStatusSchema,
  sale: z12.object({
    id: z12.number().int(),
    period: periodSchema,
    /** `null` é "nasceu aqui, na tela". */
    idExterno: z12.string().nullable(),
    value: moneySchema.nullable()
  }),
  member: z12.object({ code: z12.string(), nome: z12.string().nullable() }).nullable(),
  vehicle: z12.object({
    placa: z12.string().nullable(),
    marca: z12.string().nullable(),
    modelo: z12.string().nullable()
  }).nullable(),
  /**
   * O consultor DA VENDA — o dono da despesa: é ele quem a vê em "Minhas
   * despesas", quem corrige e quem é avisado. `id` é o usuário dele.
   */
  consultant: z12.object({ id: z12.string(), nome: z12.string() }),
  /** Quem lançou — o próprio consultor, ou alguém que lançou pela venda dele. */
  launchedBy: z12.object({ id: z12.string(), nome: z12.string() }),
  referenceDate: businessDateSchema,
  plate: z12.string(),
  personName: z12.string(),
  contact: z12.string().nullable(),
  amount: moneySchema,
  pixKey: z12.string().nullable(),
  pixBankCode: z12.string().nullable(),
  cancellationReason: z12.string().nullable(),
  /** Por que voltou ao consultor — só em "Pendente de Correção". */
  correctionReason: z12.string().nullable(),
  /** A data do comprovante de pagamento — só em "Pago". */
  paidOn: businessDateSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});
var saleExpenseTransitionSchema = z12.object({
  from: saleExpenseStatusSchema.nullable(),
  to: saleExpenseStatusSchema,
  note: z12.string().nullable(),
  at: instantSchema,
  by: z12.string().nullable()
});
var saleExpenseDetailSchema = saleExpenseSchema.extend({
  files: z12.array(saleExpenseFileSchema),
  transitions: z12.array(saleExpenseTransitionSchema)
});
var saleExpensesPageSchema = z12.object({
  items: z12.array(saleExpenseSchema),
  next: z12.string().nullable(),
  total: z12.number().int()
});
var boardQuery2 = {
  status: saleExpenseStatusSchema.optional(),
  /**
   * Um ou mais tipos, separados por vírgula — o mesmo formato de
   * `classification` na lista de vendas. Ausente é todos.
   */
  types: z12.string().transform((v) => v.split(",")).pipe(z12.array(saleExpenseTypeSchema).min(1)).optional(),
  cursor: z12.string().regex(/^\d+$/).optional(),
  perPage: z12.coerce.number().int().min(1).max(200).default(50)
};
var saleExpenseActionSchema = z12.discriminatedUnion("action", [
  z12.object({ action: z12.literal("conferir") }),
  z12.object({ action: z12.literal("aprovar") }),
  z12.object({
    action: z12.literal("devolver"),
    reason: z12.string().trim().min(3).max(SALE_EXPENSE_REASON_MAX)
  }),
  z12.object({
    action: z12.literal("pagar"),
    /** A data do comprovante. Não pode estar no futuro. */
    paidOn: businessDateSchema,
    receipt: fileUploadSchema2
  })
]);
var saleExpenseStageSchema = z12.object({
  status: saleExpenseStatusSchema,
  name: z12.string(),
  color: z12.enum(STAGE_COLORS2),
  /** `false` é o nome e a cor de nascença — a praça nunca salvou esta fase. */
  customized: z12.boolean(),
  events: z12.array(paymentStageEventSchema)
});
var saleExpenseStagesSchema = z12.object({
  stages: z12.array(saleExpenseStageSchema),
  accounts: z12.array(z12.object({ id: z12.number().int(), name: z12.string() }))
});
var mySaleExpenseStagesSchema = z12.object({
  stages: z12.array(saleExpenseStageSchema.pick({ status: true, name: true, color: true }))
});
var saleExpenseLaunchAccessSchema = z12.object({
  allowed: z12.boolean(),
  reason: z12.string().nullable()
});
var idParams = z12.object({ id: z12.coerce.number().int() });
var fileParams = z12.object({
  id: z12.coerce.number().int(),
  fileId: z12.coerce.number().int()
});
var saleExpensesContract = c10.router(
  {
    create: {
      method: "POST",
      path: "",
      body: createSaleExpenseSchema,
      responses: { 201: saleExpenseDetailSchema, ...defaultErrors },
      summary: 'Lan\xE7a uma despesa a partir de uma venda \u2014 nasce em "Despesas Recebidas"'
    },
    launchAccess: {
      method: "GET",
      path: "/launch-access",
      responses: { 200: saleExpenseLaunchAccessSchema, ...defaultErrors },
      summary: "Se a pessoa da sess\xE3o pode lan\xE7ar despesa \u2014 consultor do time interno, ou com a exce\xE7\xE3o"
    },
    /* As fases. Caminhos fixos antes de `/:id` — ver o controller. */
    stages: {
      method: "GET",
      path: "/stages",
      responses: { 200: saleExpenseStagesSchema, ...defaultErrors },
      summary: "As fases do quadro de despesas desta pra\xE7a \u2014 nome, cor e eventos"
    },
    myStages: {
      method: "GET",
      path: "/stages/mine",
      responses: { 200: mySaleExpenseStagesSchema, ...defaultErrors },
      summary: "O nome e a cor das fases, para o quadro do consultor"
    },
    saveStage: {
      method: "PUT",
      path: "/stages/:status",
      pathParams: z12.object({ status: saleExpenseStatusSchema }),
      body: savePaymentStageSchema,
      responses: { 200: saleExpenseStagesSchema, ...defaultErrors },
      summary: "Substitui o nome, a cor e os eventos de uma fase nesta pra\xE7a"
    },
    /* O consultor. Caminhos fixos antes de `/:id`. */
    mine: {
      method: "GET",
      path: "/mine",
      query: z12.object(boardQuery2),
      responses: { 200: saleExpensesPageSchema, ...defaultErrors },
      summary: "Uma p\xE1gina das despesas que a PR\xD3PRIA pessoa lan\xE7ou"
    },
    myDetail: {
      method: "GET",
      path: "/mine/:id",
      pathParams: idParams,
      responses: { 200: saleExpenseDetailSchema, ...defaultErrors },
      summary: "A PR\xD3PRIA despesa aberta"
    },
    myFileUrl: {
      method: "GET",
      path: "/mine/:id/files/:fileId/url",
      pathParams: fileParams,
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de um arquivo da PR\xD3PRIA despesa"
    },
    correct: {
      method: "PUT",
      path: "/:id/correction",
      pathParams: idParams,
      body: correctSaleExpenseSchema,
      responses: { 200: saleExpenseDetailSchema, ...defaultErrors },
      summary: 'Corrige a PR\xD3PRIA despesa devolvida \u2014 ela volta para "Em Confer\xEAncia"'
    },
    /* O Financeiro — a praça. */
    list: {
      method: "GET",
      path: "",
      query: z12.object({ ...boardQuery2, search: z12.string().trim().max(120).optional() }),
      responses: { 200: saleExpensesPageSchema, ...defaultErrors },
      summary: "Uma p\xE1gina das despesas de venda da pra\xE7a \u2014 uma coluna do quadro, ou todas"
    },
    detail: {
      method: "GET",
      path: "/:id",
      pathParams: idParams,
      responses: { 200: saleExpenseDetailSchema, ...defaultErrors },
      summary: "A despesa aberta, com os arquivos e a linha do tempo"
    },
    act: {
      method: "PATCH",
      path: "/:id",
      pathParams: idParams,
      body: saleExpenseActionSchema,
      responses: { 200: saleExpenseDetailSchema, ...defaultErrors },
      summary: "Recebe para confer\xEAncia, aprova, devolve ao consultor ou marca como pago"
    },
    fileUrl: {
      method: "GET",
      path: "/:id/files/:fileId/url",
      pathParams: fileParams,
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de curta validade para um arquivo da despesa"
    }
  },
  { pathPrefix: "/sales/expenses" }
);

// src/erp.ts
import { initContract as initContract11 } from "@ts-rest/core";
import { z as z13 } from "zod";
var c11 = initContract11();
var MIRRORED_ENTITIES = [
  "associado",
  "veiculo",
  "boleto",
  "voluntario",
  "venda",
  "situacao_boleto",
  "situacao_veiculo",
  "situacao_motivo",
  "referencia",
  "veiculo_produto",
  "atendimento",
  "alteracao",
  "associado_detalhe"
];
var mirroredEntitySchema = z13.enum(MIRRORED_ENTITIES);
var CORE_ENTITIES = [
  "associado",
  "voluntario",
  "veiculo",
  "boleto",
  "boleto_veiculo",
  "veiculo_produto",
  "referencia",
  "situacao_veiculo",
  "situacao_boleto",
  "situacao_motivo",
  /* o histórico do SGA (14/10/2026) */
  "atendimento",
  "alteracao"
];
var coreEntitySchema = z13.enum(CORE_ENTITIES);
var ERP_CORE_CHANGED_EVENT = "erp:nucleo_alterado";
var erpCoreChangedNoticeSchema = z13.object({
  entity: coreEntitySchema,
  codes: z13.array(z13.string()).max(50).nullable(),
  /** O início da passagem que gravou — o mesmo `erp_synced_at` das linhas. */
  syncedAt: instantSchema
});
var ERP_PIPELINE_EVENT = "erp:esteira";
var erpPipelineNoticeSchema = z13.object({
  kind: z13.enum(["sincronizacao", "projecao"]),
  entity: z13.string(),
  result: z13.enum(["iniciada", "sucesso", "parcial", "falha"])
});
var RECORD_ENTITIES = [
  "associado",
  "veiculo",
  "boleto"
];
var recordEntitySchema = z13.enum(RECORD_ENTITIES);
var entitySyncSchema = z13.discriminatedUnion("estado", [
  z13.object({
    entity: mirroredEntitySchema,
    /** nunca sincronizou nesta praça. Não é "sincronizou e veio vazio". */
    estado: z13.literal("nunca")
  }),
  z13.object({
    entity: mirroredEntitySchema,
    estado: z13.literal("em_andamento"),
    startedAt: instantSchema,
    /**
     * Onde a passagem está, desde 28/10/2026. Nulo quando ela não contou nada
     * — passagem de uma chamada só, ou worker de uma versão que não conta.
     */
    progress: z13.object({
      /** nulo quando o total não é conhecido: a tela não fabrica porcentagem */
      percent: z13.number().int().min(0).max(99).nullable(),
      /** "janela 3 de 9 · situação 2 de 6 · página 40 de ~120" */
      label: z13.string(),
      /** registros recebidos do SGA nesta passagem; nulo antes do primeiro */
      records: z13.number().int().nullable(),
      /** o último tique — velho demais é passagem parada */
      at: instantSchema
    }).nullable()
  }),
  z13.object({
    entity: mirroredEntitySchema,
    estado: z13.literal("concluida"),
    /** `falha` também **concluiu**: a passagem terminou, e terminou mal. */
    result: z13.enum(["sucesso", "parcial", "falha"]),
    startedAt: instantSchema,
    completedAt: instantSchema,
    /**
     * Nulo é "não sei quantos". Zero registros e falha antes da primeira página
     * são a mesma tela com significados opostos — a coluna do banco já nasceu
     * anulável por isso, e a anulabilidade tem que atravessar até aqui.
     */
    records: z13.number().int().nullable(),
    /** o erro, quando houve. É o texto que a tela de saúde mostra. */
    detail: z13.string().nullable(),
    /**
     * A passagem parou antes do fim E guardou de onde continuar: a esteira
     * retoma sozinha. É o que separa "concluída em parte" — acabou, e faltou —
     * de "interrompida": não acabou, e vai acabar sem ninguém fazer nada.
     */
    resuming: z13.boolean()
  })
]);
var BREAKER_STATES = ["sem_falhas", "fechado", "aberto", "meio_aberto"];
var breakerStateSchema = z13.enum(BREAKER_STATES);
var breakerSchema = z13.object({
  estado: breakerStateSchema,
  /** quantas falhas estão dentro da janela agora. Zero medido, não zero suposto. */
  failuresNaWindow: z13.number().int(),
  /** quantas falhas na janela abrem o disjuntor */
  failuresLimit: z13.number().int(),
  /** o tamanho da janela deslizante */
  windowMinutes: z13.number().int(),
  /**
   * Até quando a espera vai. Preenchido em `aberto` (ainda recusa) e em
   * `meio_aberto` (a espera venceu e a próxima chamada mede); nulo nos outros
   * dois, onde não há espera nenhuma correndo.
   */
  openUntil: z13.string().nullable(),
  /** a falha mais recente dentro da janela; nulo quando não há nenhuma */
  lastFailureAt: instantSchema.nullable()
});
var dailyUsageSchema = z13.discriminatedUnion("estado", [
  z13.object({ estado: z13.literal("nao_medido") }),
  z13.object({
    estado: z13.literal("medido"),
    /** o começo do dia contado, com fuso — a virada do dia é decisão do servidor */
    since: z13.string(),
    calls: z13.number().int(),
    failures: z13.number().int(),
    dayCap: z13.number().int()
  })
]);
var coreProjectionSchema = z13.discriminatedUnion("estado", [
  z13.object({ entity: coreEntitySchema, estado: z13.literal("nunca") }),
  z13.object({
    entity: coreEntitySchema,
    estado: z13.literal("em_andamento"),
    startedAt: instantSchema,
    lastSuccessAt: instantSchema.nullable()
  }),
  z13.object({
    entity: coreEntitySchema,
    estado: z13.literal("concluida"),
    result: z13.enum(["sucesso", "falha"]),
    startedAt: instantSchema,
    completedAt: instantSchema,
    lastSuccessAt: instantSchema.nullable(),
    /** nulos na falha: a passagem não chegou a contar */
    records: z13.number().int().nullable(),
    inserted: z13.number().int().nullable(),
    updated: z13.number().int().nullable(),
    missing: z13.number().int().nullable(),
    detail: z13.string().nullable()
  })
]);
var erpQueueStateSchema = z13.object({
  waiting: z13.object({
    /** pedidos de tela ("atualizar do ERP"), prioridade 1 */
    screen: z13.number().int(),
    /** retomadas de varredura, prioridade 5 */
    resume: z13.number().int(),
    /** esteiras e agendamentos, prioridade 10 */
    background: z13.number().int()
  }),
  /** desde quando o pedido de tela mais antigo desta base espera; nulo sem nenhum */
  oldestScreenSince: instantSchema.nullable(),
  running: z13.object({
    /** `true` quando o job em execução é desta base */
    ours: z13.boolean(),
    /** `true` quando é do relógio, sem base: ele roda todas, esta inclusive */
    allTenants: z13.boolean(),
    /** o nome do job, quando é desta base ou de todas */
    job: z13.string().nullable(),
    since: instantSchema.nullable()
  }).nullable()
});
var erpFailedJobSchema = z13.object({
  queue: z13.enum(["sga", "projecao"]),
  job: z13.string(),
  entity: z13.string().nullable(),
  reason: z13.string(),
  attempts: z13.number().int(),
  failedAt: instantSchema.nullable()
});
var erpWorkerSchema = z13.discriminatedUnion("estado", [
  z13.object({ estado: z13.literal("nao_medido") }),
  z13.object({
    estado: z13.literal("de_pe"),
    beatAt: instantSchema,
    readyAt: instantSchema,
    /** o último job da fila do SGA que começou ou terminou; nulo desde a partida */
    lastJobAt: instantSchema.nullable()
  }),
  /** a partida em curso, dentro do prazo — `step` é o passo em que ela está */
  z13.object({
    estado: z13.literal("subindo"),
    since: instantSchema,
    step: z13.string().nullable()
  }),
  /** sem tique desde `since` */
  z13.object({ estado: z13.literal("parado"), since: instantSchema }),
  /** a partida começou em `since` e não terminou no prazo; `step` é onde parou */
  z13.object({
    estado: z13.literal("travado_na_partida"),
    since: instantSchema,
    step: z13.string().nullable()
  }),
  /** de pé, mas a fila do SGA não anda: job esperando desde `since`, nada rodando */
  z13.object({
    estado: z13.literal("fila_parada"),
    since: instantSchema,
    lastJobAt: instantSchema.nullable()
  })
]);
var erpHealthSchema = z13.object({
  tenant: z13.object({
    id: z13.number().int(),
    slug: z13.string(),
    nome: z13.string()
  }),
  /**
   * Uma entrada para **cada** entidade de `MIRRORED_ENTITIES`, sempre —
   * inclusive as que nunca sincronizaram, que vêm com `estado: 'nunca'`. Mandar
   * só as que têm linha no banco faria "ausente da lista" significar as duas
   * coisas ao mesmo tempo: nunca rodou, ou o servidor esqueceu de mandar.
   *
   * A quantidade não é escrita aqui de propósito: a lista cresceu de sete para
   * nove quando o espelho ganhou os motivos e as listas de referência, e todo
   * número copiado em prosa envelheceu junto — inclusive um caso de e2e, que
   * cobrava sete e reprovou.
   */
  syncs: z13.array(entitySyncSchema),
  breaker: breakerSchema,
  dayconsumo: dailyUsageSchema,
  /** uma entrada para cada entidade do núcleo, sempre — `nunca` incluído */
  projections: z13.array(coreProjectionSchema),
  queue: erpQueueStateSchema,
  /** as últimas, mais recentes primeiro, desta base */
  failures: z13.array(erpFailedJobSchema),
  worker: erpWorkerSchema
});
var sweepResultSchema = z13.discriminatedUnion("estado", [
  z13.object({
    estado: z13.literal("enfileirada"),
    entity: mirroredEntitySchema,
    pedidaAt: instantSchema
  }),
  /** Já havia uma varredura desta entidade de pé. É sucesso, como em `sync`. */
  z13.object({
    estado: z13.literal("ja_pedida"),
    entity: mirroredEntitySchema,
    pedidaAt: instantSchema
  }),
  /** Esta entidade não tem varredura em massa — ver o cabeçalho. */
  z13.object({
    estado: z13.literal("sem_esteira"),
    entity: mirroredEntitySchema
  })
]);
var refreshCountsSchema = z13.object({
  members: z13.number().int().nonnegative(),
  vehicles: z13.number().int().nonnegative(),
  products: z13.number().int().nonnegative(),
  /** quantos boletos o SGA listou nas janelas pedidas */
  boletosListed: z13.number().int().nonnegative(),
  /** quantos boletos foram gravados */
  boletos: z13.number().int().nonnegative(),
  /** quantas janelas de emissão de 200 dias foram pedidas — o histórico inteiro são várias */
  boletoWindows: z13.number().int().nonnegative(),
  /** quantos atendimentos o histórico do associado trouxe */
  serviceRecords: z13.number().int().nonnegative()
});
var syncDoneSchema = z13.discriminatedUnion("estado", [
  z13.object({
    estado: z13.literal("atualizado"),
    entity: recordEntitySchema,
    counts: refreshCountsSchema,
    /**
     * O "deu certo, menos isto": os produtos não vieram, uma janela de boletos
     * não respondeu. Vazio quando tudo veio — a tela mostra cada um.
     */
    warnings: z13.array(z13.string()),
    completedAt: instantSchema
  }),
  /** O SGA respondeu que não tem este registro. É resposta, e não falha. */
  z13.object({
    estado: z13.literal("inexistente"),
    entity: recordEntitySchema
  })
]);
var syncPendingSchema = z13.discriminatedUnion("estado", [
  z13.object({
    estado: z13.literal("em_andamento"),
    /** quando o pedido entrou na fila */
    pedidaAt: instantSchema
  }),
  z13.object({
    estado: z13.literal("adiada"),
    /** até quando o disjuntor desta praça recusa chamadas */
    until: z13.string()
  })
]);
var syncResultSchema = z13.union([syncDoneSchema, syncPendingSchema]);
var STATUS_DOMAINS = ["boleto", "veiculo"];
var statusDomainSchema = z13.enum(STATUS_DOMAINS);
var classificationSchema = z13.object({
  id: z13.number().int(),
  domain: statusDomainSchema,
  /**
   * O código como o ERP o devolve, em texto. Não é número: ninguém mediu se
   * vem `9` ou `09`, e tratar os dois como o mesmo antes de saber é a suposição
   * que a regra 14 proíbe.
   */
  code: z13.string(),
  canceled: z13.boolean(),
  deleted: z13.boolean(),
  blocksCancellation: z13.boolean(),
  /** Troca: a recorrência conta o veículo desde o 1º boleto. Só em `veiculo`. */
  vehicleSwap: z13.boolean(),
  /** Entrada de troca: a premiação direta não conta o veículo. Só em `veiculo`. */
  swapEntry: z13.boolean(),
  /**
   * O nome que o ERP dá ao código — `ATIVO`, `CANCELADO` —, do vocabulário do
   * núcleo. A classificação da casa não guarda a descrição (ela muda no SGA e
   * a decisão não), mas o filtro que lista situações precisa dela: sem isto a
   * tela mostrava `1`, `6`, `20` no lugar do nome. Nula quando o código não
   * está no vocabulário da praça.
   */
  descricao: z13.string().nullable(),
  /** nula enquanto a classificação vale; a data de quando alguém a arquivou */
  archivedAt: instantSchema.nullable(),
  updatedAt: instantSchema
});
var unclassifiedStatusSchema = z13.object({
  domain: statusDomainSchema,
  code: z13.string(),
  descricao: z13.string().nullable()
});
var classificationStateSchema = z13.object({
  classifications: z13.array(classificationSchema),
  notClassified: z13.array(unclassifiedStatusSchema),
  /**
   * A decisão da casa que está no arquivo da praça
   * (`erp-classification-<praça>.csv`) e ainda não está no banco.
   *
   * `available` é `false` quando a praça não tem decisão em arquivo — a GO,
   * hoje. `pending` é o que `POST /classifications/decided` gravaria: só os
   * códigos sem classificação vigente, porque o que alguém classificou pela
   * tela vence o arquivo.
   */
  decided: z13.object({
    available: z13.boolean(),
    pending: z13.array(
      z13.object({
        domain: statusDomainSchema,
        code: z13.string(),
        canceled: z13.boolean(),
        deleted: z13.boolean(),
        blocksCancellation: z13.boolean(),
        vehicleSwap: z13.boolean(),
        swapEntry: z13.boolean(),
        descricao: z13.string().nullable()
      })
    )
  }),
  mirror: z13.array(entitySyncSchema)
});
var newClassificationSchema = z13.object({
  domain: statusDomainSchema,
  code: z13.string().min(1).max(20),
  canceled: z13.boolean(),
  deleted: z13.boolean(),
  blocksCancellation: z13.boolean(),
  vehicleSwap: z13.boolean(),
  swapEntry: z13.boolean()
});
var vehicleStatusSchema = z13.discriminatedUnion("estado", [
  z13.object({
    /** o código existe e a casa ainda não disse o que ele significa nesta praça */
    estado: z13.literal("nao_classificada"),
    code: z13.string(),
    /** a descrição do fornecedor; nula quando o espelho não tem este código */
    descricao: z13.string().nullable()
  }),
  z13.object({
    estado: z13.literal("classificada"),
    code: z13.string(),
    descricao: z13.string().nullable(),
    blocksCancellation: z13.boolean()
  })
]);
var portfolioVehicleSchema = z13.object({
  /** o código no ERP — é ele que a abertura de cancelamento recebe no corpo */
  code: z13.string(),
  /** maiúscula, sem hífen e sem espaço; nula é veículo sem placa, que existe */
  placa: z13.string().nullable(),
  marca: z13.string().nullable(),
  modelo: z13.string().nullable(),
  anoModelo: z13.string().nullable(),
  situacao: vehicleStatusSchema,
  /** quando o espelho leu isto do ERP — a tela mostra "atualizado há X" */
  syncedAt: instantSchema
});
var portfolioVehicleDetailSchema = z13.object({
  /** `VE-000123` — o identificador da casa */
  code: z13.string(),
  /** `codigo_veiculo` do SGA — o que a carteira mostra e o que vai no caminho */
  erpCode: z13.string().nullable(),
  placa: z13.string().nullable(),
  chassi: z13.string().nullable(),
  renavam: z13.string().nullable(),
  marca: z13.string().nullable(),
  modelo: z13.string().nullable(),
  /** os dois anos vêm como texto: 2.083 e 2.003 linhas da CE não têm quatro dígitos */
  anoFabricacao: z13.string().nullable(),
  anoModelo: z13.string().nullable(),
  tipo: z13.string().nullable(),
  categoria: z13.string().nullable(),
  /**
   * Anulável porque `erp_status_code` é anulável no núcleo — hoje são zero de
   * 78.037 na CE, e a tela precisa poder dizer "não sei" em vez de inventar um
   * código vazio no dia em que aparecer um.
   */
  situacao: vehicleStatusSchema.nullable(),
  /** valores do contrato, como o ERP os guarda */
  valorFipe: moneySchema.nullable(),
  valorProtegido: moneySchema.nullable(),
  valorFixo: moneySchema.nullable(),
  valorAdesao: moneySchema.nullable(),
  dataContrato: businessDateSchema.nullable(),
  dataFimContrato: businessDateSchema.nullable(),
  dataReativacao: businessDateSchema.nullable(),
  dataCadastro: businessDateSchema.nullable(),
  /** `mes_referente` e `mes_fim_carne`, como o espelho os guarda */
  mesReferencia: z13.string().nullable(),
  mesFimCarne: z13.string().nullable(),
  /**
   * `boleto_fisico` do SGA, **como veio**: é `"Y"`/`"N"`, e não um booleano. A
   * conversão não foi medida (a casa tem DOIS alfabetos booleanos no mesmo
   * fornecedor), então quem traduz é a tela, que sabe o que está escrevendo.
   */
  boletoFisico: z13.string().nullable(),
  /** quando a projeção escreveu esta linha a partir do espelho */
  syncedAt: instantSchema.nullable()
});
var portfolioVehicleDetailResponseSchema = z13.discriminatedUnion("estado", [
  z13.object({ estado: z13.literal("nao_encontrado") }),
  z13.object({ estado: z13.literal("lida"), vehicle: portfolioVehicleDetailSchema })
]);
var memberPortfolioSchema = z13.discriminatedUnion("estado", [
  z13.object({ estado: z13.literal("sem_vinculo") }),
  z13.object({
    estado: z13.literal("lida"),
    vehicles: z13.array(portfolioVehicleSchema)
  })
]);
var catalogStatusSchema = z13.discriminatedUnion("estado", [
  z13.object({
    estado: z13.literal("nao_classificada"),
    code: z13.string(),
    /** a descrição do fornecedor; nula quando o espelho tem o código sem ela */
    descricao: z13.string().nullable()
  }),
  z13.object({
    estado: z13.literal("classificada"),
    code: z13.string(),
    descricao: z13.string().nullable(),
    canceled: z13.boolean(),
    deleted: z13.boolean(),
    blocksCancellation: z13.boolean()
  })
]);
var catalogOwnerSchema = z13.object({
  code: z13.string(),
  nome: z13.string().nullable()
});
var catalogOwnerDetailSchema = catalogOwnerSchema.extend({
  /**
   * `AS-000123`, o endereço da ficha do associado. Nulo quando o dono não está
   * no núcleo — o link não é desenhado, em vez de levar a um 404.
   */
  houseCode: z13.string().nullable(),
  documento: z13.string().nullable(),
  documentRevealed: z13.boolean(),
  /** o tipo de pessoa que o ERP declara; o documento de 14 dígitos é o jurídico */
  tipoPessoa: z13.string().nullable(),
  /**
   * A situação com o julgamento da casa — o mesmo selo de veículo e boleto.
   * O SGA a manda só por descrição, e ela é julgada pela classificação de
   * VEÍCULO de mesma descrição (decisão de 20/10/2026, `memberStatus`). Nula
   * quando o SGA não disse situação nenhuma.
   */
  situacao: catalogStatusSchema.nullable(),
  email: z13.string().nullable(),
  /** todos os que o espelho tem, já formatados e sem os vazios */
  phones: z13.array(z13.string()),
  address: z13.string().nullable()
});
var catalogBoletoSchema = z13.object({
  /**
   * `BO-000123` — o identificador da CASA, gerado pelo banco a partir do `id`.
   *
   * Substituiu o `id` do espelho em 05/10/2026, e a troca conserta um defeito
   * medido: `external.sga_*` é refeito por `replaceSlice`, que apaga a fatia da
   * praça e reinsere — a sequência dá números novos, e **todo `id` do espelho
   * muda a cada varredura completa**. Na CE os ids de `sga_veiculos` iam de
   * 76.749 a 154.785 para 78.037 linhas: os 76.748 primeiros tinham sido
   * consumidos e apagados. Um endereço construído sobre ele apontava para
   * outro registro no dia seguinte.
   *
   * O do núcleo não muda: a projeção faz upsert e nunca apaga.
   */
  code: z13.string(),
  /** `codigo_boleto` do SGA — procedência, e o que se dita ao fornecedor. */
  erpCode: z13.string().nullable(),
  member: catalogOwnerSchema.nullable(),
  value: moneySchema.nullable(),
  valuePaid: moneySchema.nullable(),
  dueDateOriginal: businessDateSchema.nullable(),
  dueDate: businessDateSchema.nullable(),
  paidAt: businessDateSchema.nullable(),
  /**
   * De onde vêm `paidAt` e `valuePaid` acima — e é o campo que impede a tela
   * de chamar as duas coisas pelo mesmo nome.
   *
   * `app.boletos` tem dois pares de colunas de pagamento de propósito: o da
   * COLETA (`paid_at`/`paid_amount`), que é a baixa do gateway, e o do ERP
   * (`erp_paid_at`/`erp_paid_amount`), que é o que o SGA diz. Enquanto o
   * gateway não existir, a coleta está preenchida em 3.198 linhas de 1.249.074
   * — e uma tela que lesse só ela diria "sem pagamento" em 852.348 boletos que
   * o fornecedor considera pagos. `boletoPayment`, em `@evogard/domain`,
   * resolve os dois numa ordem só; isto diz qual ganhou.
   */
  paymentSource: z13.enum(["coleta", "erp"]).nullable(),
  /** `nosso_numero` — o número impresso no papel. Dado, não identidade. */
  nossoNumero: z13.string().nullable(),
  /** a competência, como o núcleo a guarda (`AAAA-MM`); nula é boleto sem ela */
  period: z13.string().nullable(),
  situacao: catalogStatusSchema,
  /** o que o FORNECEDOR diz sobre o pagamento; nulo é "ele não disse" */
  pago: z13.boolean().nullable(),
  /**
   * Quando a PROJEÇÃO escreveu esta linha a partir do espelho
   * (`app.boletos.erp_synced_at`) — e nulo porque o núcleo tem linha que não
   * veio do espelho: a coluna só é escrita pela projeção.
   */
  syncedAt: instantSchema.nullable()
});
var catalogVehicleSchema = z13.object({
  /** `VE-000123` — o identificador da casa. Ver `catalogBoletoSchema.code`. */
  code: z13.string(),
  /** `codigo_veiculo` do SGA — é o que a sincronização de um registro recebe. */
  erpCode: z13.string().nullable(),
  placa: z13.string().nullable(),
  marca: z13.string().nullable(),
  modelo: z13.string().nullable(),
  anoModelo: z13.string().nullable(),
  categoria: z13.string().nullable(),
  situacao: catalogStatusSchema,
  owner: catalogOwnerSchema.nullable(),
  syncedAt: instantSchema.nullable()
});
var catalogBoletoErpSchema = z13.object({
  regionalCode: z13.string().nullable(),
  typeCode: z13.string().nullable(),
  typeLabel: z13.string().nullable(),
  issuedOn: businessDateSchema.nullable(),
  paidAt: businessDateSchema.nullable(),
  paidAmount: moneySchema.nullable(),
  bankCreditDate: businessDateSchema.nullable(),
  bankFee: moneySchema.nullable(),
  paymentMethodCode: z13.string().nullable(),
  paymentMethodLabel: z13.string().nullable(),
  mgfPaymentMethodCode: z13.string().nullable(),
  settlementTypeLabel: z13.string().nullable(),
  recurringBillingTypeLabel: z13.string().nullable(),
  installmentPaid: z13.string().nullable(),
  installmentCount: z13.string().nullable(),
  accountCode: z13.string().nullable(),
  bankCode: z13.string().nullable(),
  bankName: z13.string().nullable(),
  agency: z13.string().nullable(),
  account: z13.string().nullable(),
  digitableLine: z13.string().nullable(),
  /** o endereço do boleto no fornecedor; a tela só o abre se for `http(s)` */
  link: z13.string().nullable()
});
var catalogBoletoDetailSchema = catalogBoletoSchema.extend({
  vehicles: z13.array(catalogVehicleSchema),
  erp: catalogBoletoErpSchema,
  /** o que mudou neste boleto no SGA (vencimento, valor, situação, pagamento) */
  changes: z13.array(z13.lazy(() => catalogChangeSchema))
});
var catalogVehicleProductSchema = z13.object({
  /** `codigo_produto` do SGA */
  erpCode: z13.string(),
  label: z13.string().nullable(),
  /** o texto impresso no boleto — às vezes truncado pelo SGA */
  boletoLabel: z13.string().nullable(),
  /** `R$` ou `%`, como o SGA escreve */
  chargeFormat: z13.string().nullable(),
  amount: moneySchema.nullable(),
  /** ponto percentual, como texto: `numeric` não cabe em float */
  percentage: z13.string().nullable(),
  /** `ATIVO`/`INATIVO`, como o SGA diz */
  erpStatus: z13.string().nullable(),
  showOnBoleto: z13.boolean().nullable()
});
var catalogProductSchema = z13.object({
  /** `codigo_produto` do SGA */
  erpCode: z13.string(),
  label: z13.string().nullable(),
  /** em quantos veículos consultados o produto está AGORA */
  vehicles: z13.number().int(),
  /** desses, em quantos o SGA o diz `ATIVO` */
  vehiclesActive: z13.number().int(),
  amountMin: moneySchema.nullable(),
  amountMax: moneySchema.nullable(),
  /** ponto percentual, como texto — ver `catalogVehicleProductSchema` */
  percentageMin: z13.string().nullable(),
  percentageMax: z13.string().nullable()
});
var catalogBoletoTypeOptionSchema = z13.object({
  /** `codigo_tipo_boleto` do SGA. */
  code: z13.string(),
  label: z13.string().nullable(),
  /** Quantos boletos da base têm este tipo. */
  boletos: z13.number().int()
});
var catalogBoletoTypeSearchSchema = z13.object({
  term: z13.string().trim().min(1).max(64).optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogBoletoTypeResultSchema = z13.object({
  items: z13.array(catalogBoletoTypeOptionSchema),
  total: z13.number().int(),
  page: z13.number().int(),
  perPage: z13.number().int()
});
var catalogPlanSchema = z13.object({
  code: z13.string(),
  label: z13.string().nullable(),
  /** carros da base com este plano, inclusive os que o ERP deixou de ter */
  vehicles: z13.number().int(),
  inList: z13.boolean()
});
var catalogPlanSearchSchema = z13.object({
  term: z13.string().trim().min(1).max(64).optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogPlanResultSchema = z13.object({
  /**
   * A lista de planos do ERP já chegou a esta base? Sem ela, todo código vem
   * `inList: false` — e "fora da lista" sobre uma lista que não existe é
   * afirmação falsa. A tela troca o selo de cada linha por um aviso só.
   */
  listSynced: z13.boolean(),
  items: z13.array(catalogPlanSchema),
  total: z13.number().int(),
  page: z13.number().int(),
  perPage: z13.number().int()
});
var catalogProductSearchSchema = z13.object({
  term: z13.string().trim().min(2).max(64).optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogProductResultSchema = z13.object({
  items: z13.array(catalogProductSchema),
  total: z13.number().int(),
  page: z13.number().int(),
  perPage: z13.number().int(),
  vehiclesChecked: z13.number().int(),
  vehiclesTotal: z13.number().int(),
  /** a consulta mais recente entre os veículos da base; nulo é "nunca" */
  lastCheckedAt: instantSchema.nullable()
});
var catalogProductVehicleSchema = catalogVehicleSchema.extend({
  amount: moneySchema.nullable(),
  /** ponto percentual, como texto — ver `catalogVehicleProductSchema` */
  percentage: z13.string().nullable(),
  /** `ATIVO`/`INATIVO`: a situação do PRODUTO neste carro, e não a do veículo */
  productStatus: z13.string().nullable()
});
var catalogProductVehicleSearchSchema = z13.object({
  /** placa, código do veículo, marca, modelo ou nome do dono — o que a tela desenha */
  term: z13.string().trim().min(2).max(64).optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogProductVehicleResultSchema = z13.object({
  product: z13.object({ erpCode: z13.string(), label: z13.string().nullable() }),
  items: z13.array(catalogProductVehicleSchema),
  total: z13.number().int(),
  page: z13.number().int(),
  perPage: z13.number().int()
});
var catalogVehicleDetailSchema = catalogVehicleSchema.extend({
  /**
   * O que o ERP traz no campo da placa e NÃO é placa (`OOOOOO`, `NAOEXIS`),
   * como veio. Só existe quando `placa` é nula por esse motivo — e é o que
   * separa "o ERP não informou placa" de "o ERP informou algo que não é placa".
   */
  invalidPlate: z13.string().nullable(),
  chassi: z13.string().nullable(),
  renavam: z13.string().nullable(),
  tipo: z13.string().nullable(),
  anoFabricacao: z13.string().nullable(),
  valorFipe: moneySchema.nullable(),
  dataContrato: businessDateSchema.nullable(),
  dataCadastro: businessDateSchema.nullable(),
  owner: catalogOwnerDetailSchema.nullable(),
  /** os mais recentes primeiro, limitados — `boletosTotal` diz quantos existem */
  boletos: z13.array(catalogBoletoSchema),
  /**
   * Quantos boletos este veículo tem ao todo.
   *
   * Separado da lista porque ela é limitada: um veículo da praça CE tem
   * **1.255** boletos, e despejar todos numa ficha é uma tela que ninguém lê e
   * um navegador que engasga. Sem este número a lista curta pareceria completa.
   */
  boletosTotal: z13.number().int(),
  /**
   * Pode faltar boleto nesta lista, e por dois motivos diferentes.
   *
   * A ligação boleto × veículo nasceu em 10/09 e não cobre o que foi
   * sincronizado antes dela; e uma varredura que fecha `parcial` deixa boletos
   * por ler. Em qualquer um dos casos a tela precisa dizer, porque lista curta
   * sem aviso é lida como "não deve nada".
   */
  boletosIncompletos: z13.boolean(),
  /**
   * Os produtos que o SGA diz que este carro tem AGORA — os que saíram do
   * contrato ficam no núcleo com `erp_missing_at` e não vêm aqui.
   */
  products: z13.array(catalogVehicleProductSchema),
  /**
   * Quando os produtos foram perguntados ao SGA. Nulo é "nunca", e é o que
   * separa `products: []` de "não sabemos": a esteira de produtos consulta a
   * base aos poucos, e um carro pode esperar dias pela vez dele — o "Atualizar
   * do ERP" da ficha consulta na hora.
   */
  productsSyncedAt: instantSchema.nullable(),
  /**
   * O plano do carro — o GRUPO DE PRODUTO do ERP (ver `catalogPlanSchema`).
   * Nulo quando o ERP não aponta plano nenhum; `label` nulo quando o código não
   * está na lista de planos espelhada, e a tela diz o código em vez de esconder.
   */
  plan: z13.object({ code: z13.string(), label: z13.string().nullable() }).nullable(),
  /** o que mudou neste carro no SGA — as últimas, da mais nova para a mais velha */
  changes: z13.array(z13.lazy(() => catalogChangeSchema))
});
var catalogVehicleSearchSchema = z13.object({
  /** placa, chassi, renavam, documento do dono, ou parte do nome dele */
  term: z13.string().trim().min(2).max(64).optional(),
  /** códigos de situação do ERP; vazio é "todas" */
  situacao: z13.array(z13.string().max(20)).max(60).optional(),
  /**
   * O código do veículo no SGA, EXATO — é como o sinistro guarda o carro
   * (`claims.vehicle_code`). Separado de `term` de propósito: um código curto
   * ("2") procurado como texto casaria qualquer chassi ou renavam com um 2, e
   * o carro certo não viria entre os primeiros.
   */
  code: z13.string().trim().min(1).max(20).optional(),
  /** o código do plano (grupo de produto do ERP), exato — é o filtro de `/erp/planos` */
  plan: z13.string().trim().min(1).max(20).optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogBoletoSearchSchema = z13.object({
  /** código do boleto, o número impresso nele, ou o documento do associado */
  term: z13.string().trim().min(2).max(64).optional(),
  situacao: z13.array(z13.string().max(20)).max(20).optional(),
  /** o que o FORNECEDOR diz; omitir é "tanto faz" e nunca "os não pagos" */
  pago: z13.boolean().optional(),
  dueFrom: businessDateSchema.optional(),
  dueUntil: businessDateSchema.optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
function catalogSearchResult(item) {
  return z13.object({
    estado: z13.literal("lida"),
    items: z13.array(item),
    total: z13.number().int(),
    page: z13.number().int(),
    perPage: z13.number().int(),
    /** quando a última passagem que gravou alguma coisa terminou; nulo se nenhuma gravou */
    syncedAt: instantSchema.nullable(),
    /**
     * A última varredura não cobriu tudo — e a tela precisa DIZER isso.
     *
     * Medido em 12/09 na praça CE: quatro das seis situações de boleto
     * pararam exatamente em 120.000 registros, que é o orçamento de chamadas
     * da esteira. Há mais boletos lá, e eles não foram lidos.
     *
     * Sem este campo a tela teria duas saídas, e as duas erradas: esconder
     * 488 mil linhas porque a varredura não foi completa, ou mostrá-las como
     * se fossem o cadastro inteiro. A primeira foi o defeito que este campo
     * conserta — `last_success_at` só avança no sucesso, então uma passagem
     * parcial deixava a busca respondendo "nunca sincronizou" com o espelho
     * cheio.
     */
    incomplete: z13.boolean(),
    /** o que a esteira escreveu; nomeia as situações que ficaram pela metade */
    incompleteDetail: z13.string().nullable()
  });
}
var catalogVehicleResultSchema = catalogSearchResult(catalogVehicleSchema);
var catalogMemberSchema = z13.object({
  /** o `AS-000123` da casa — o endereço da ficha */
  code: z13.string(),
  erpCode: z13.string().nullable(),
  nome: z13.string().nullable(),
  documento: z13.string().nullable(),
  documentRevealed: z13.boolean(),
  /** `FÍSICA`/`JURÍDICA` — o do SGA, ou o do documento quando o SGA não mandou */
  tipoPessoa: z13.string().nullable(),
  /**
   * A situação com o julgamento da casa — o mesmo selo de veículo e boleto.
   * O SGA a manda só por descrição, e ela é julgada pela classificação de
   * VEÍCULO de mesma descrição (decisão de 20/10/2026, `memberStatus`). Nula
   * quando o SGA não disse situação nenhuma.
   */
  situacao: catalogStatusSchema.nullable(),
  cidade: z13.string().nullable(),
  estado: z13.string().nullable(),
  /** quantos veículos do cadastro são dele */
  vehicles: z13.number().int(),
  syncedAt: instantSchema.nullable()
});
var catalogMemberSearchSchema = z13.object({
  /** nome, documento, ou código (`AS-` ou do ERP) */
  term: z13.string().trim().min(2).max(64).optional(),
  /** o NOME da situação — o código vem vazio na maior parte do cadastro */
  situacao: z13.string().max(80).optional(),
  tipoPessoa: z13.string().max(20).optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogMemberResultSchema = catalogSearchResult(catalogMemberSchema);
var memberFiltersSchema = z13.object({
  statuses: z13.array(z13.object({ label: z13.string(), total: z13.number().int() })),
  personTypes: z13.array(z13.object({ label: z13.string(), total: z13.number().int() }))
});
var catalogServiceRecordSchema = z13.object({
  /** `codigo_atendimento` no SGA */
  erpCode: z13.string().nullable(),
  registeredOn: businessDateSchema.nullable(),
  servedOn: businessDateSchema.nullable(),
  title: z13.string().nullable(),
  description: z13.string().nullable(),
  typeCode: z13.string().nullable(),
  typeLabel: z13.string().nullable(),
  statusCode: z13.string().nullable(),
  statusLabel: z13.string().nullable(),
  departmentCode: z13.string().nullable(),
  departmentLabel: z13.string().nullable(),
  amount: moneySchema.nullable(),
  /** quem registrou, pelo nome que o SGA conhece */
  author: z13.string().nullable(),
  /** o associado — o `AS-` da casa e o nome */
  member: catalogOwnerSchema.nullable(),
  /** o código do associado no ERP — é por ele que o "Atualizar do ERP" pergunta */
  memberErpCode: z13.string().nullable(),
  /** o carro, quando o SGA o liga a um */
  vehicle: z13.object({ code: z13.string(), placa: z13.string().nullable() }).nullable(),
  /** o atendimento sumiu do histórico do SGA (a linha fica) */
  missing: z13.boolean()
});
var catalogServiceRecordSearchSchema = z13.object({
  /** nome, documento ou código (`AS-`) do associado, ou parte do título */
  term: z13.string().trim().min(2).max(64).optional(),
  from: businessDateSchema.optional(),
  until: businessDateSchema.optional(),
  typeCode: z13.string().max(20).optional(),
  statusCode: z13.string().max(20).optional(),
  departmentCode: z13.string().max(20).optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogServiceRecordResultSchema = catalogSearchResult(
  catalogServiceRecordSchema
);
var vocabularyEntrySchema = z13.object({
  code: z13.string(),
  label: z13.string().nullable()
});
var serviceRecordFiltersSchema = z13.object({
  types: z13.array(vocabularyEntrySchema),
  statuses: z13.array(vocabularyEntrySchema),
  departments: z13.array(vocabularyEntrySchema)
});
var CHANGE_SUBJECTS = ["boleto", "associado", "veiculo"];
var changeSubjectSchema = z13.enum(CHANGE_SUBJECTS);
var catalogChangeSchema = z13.object({
  subject: changeSubjectSchema,
  /** `codigo_alteracao` no SGA — o endereço da página da alteração, junto do assunto */
  erpCode: z13.string(),
  /**
   * O código do registro no ERP pelo qual ele é ATUALIZADO — o `codigo_boleto`
   * do boleto (a lista de alteração só traz o nosso número), o código do
   * associado ou do veículo. Nulo quando o núcleo ainda não conhece o boleto.
   */
  refreshCode: z13.string().nullable(),
  /** o `nosso_numero` do boleto, ou o código do associado ou do veículo no ERP */
  erpRecordCode: z13.string(),
  /** o `BO-`/`AS-`/`VE-` da casa, quando o núcleo conhece o registro */
  recordCode: z13.string().nullable(),
  field: z13.string().nullable(),
  oldValue: z13.string().nullable(),
  newValue: z13.string().nullable(),
  /**
   * O NOME de `oldValue`/`newValue` quando o campo é um código que o
   * vocabulário da praça conhece — a situação (`4` → `INADIMPLENTE`). Nulo
   * quando o campo não é código ou o código não está na lista.
   */
  oldLabel: z13.string().nullable(),
  newLabel: z13.string().nullable(),
  changedOn: businessDateSchema.nullable(),
  author: z13.string().nullable(),
  note: z13.string().nullable()
});
var catalogChangeDetailSchema = catalogChangeSchema.extend({
  history: z13.array(catalogChangeSchema)
});
var HISTORY_ENTITIES = ["atendimento", "alteracao"];
var historyEntitySchema = z13.enum(HISTORY_ENTITIES);
var historyRefreshDoneSchema = z13.object({
  estado: z13.literal("atualizado"),
  entity: historyEntitySchema,
  /** quantas linhas a passagem trouxe do SGA */
  records: z13.number().int().nonnegative(),
  /** o que a passagem diz de si — os dias lidos, o que ficou parcial */
  detail: z13.string(),
  completedAt: instantSchema
});
var catalogChangeSearchSchema = z13.object({
  subject: changeSubjectSchema.optional(),
  /** `nome_campo_tabela`, exato */
  field: z13.string().max(64).optional(),
  /** o código do registro (nosso número, código do ERP, `BO-`/`AS-`/`VE-`) ou o autor */
  term: z13.string().trim().min(2).max(64).optional(),
  from: businessDateSchema.optional(),
  until: businessDateSchema.optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogChangeResultSchema = catalogSearchResult(catalogChangeSchema);
var catalogBoletoTypeSchema = z13.object({
  code: z13.string(),
  label: z13.string().nullable(),
  /** `ATIVO`/`INATIVO`, como o SGA diz */
  situacao: z13.string().nullable(),
  /** `boleto_adesao` — o tipo é o boleto da adesão */
  adhesion: z13.boolean().nullable(),
  /** o tipo está na lista do SGA; `false` é tipo que só os boletos trazem */
  inList: z13.boolean(),
  /** quantos boletos do núcleo têm este tipo */
  boletos: z13.number().int(),
  /** quantos deles o ERP considera pagos */
  paid: z13.number().int()
});
var catalogBoletoTypesSchema = z13.object({
  estado: z13.literal("lida"),
  items: z13.array(catalogBoletoTypeSchema),
  syncedAt: instantSchema.nullable()
});
var memberRegistrySchema = z13.object({
  gender: z13.string().nullable(),
  birthDate: businessDateSchema.nullable(),
  motherName: z13.string().nullable(),
  fatherName: z13.string().nullable(),
  rg: z13.string().nullable(),
  rgIssuer: z13.string().nullable(),
  rgIssuedOn: businessDateSchema.nullable(),
  driverLicense: z13.string().nullable(),
  driverLicenseCategory: z13.string().nullable(),
  driverLicenseExpiresOn: businessDateSchema.nullable(),
  children: z13.number().int().nullable(),
  maritalStatusCode: z13.string().nullable(),
  professionCode: z13.string().nullable(),
  classificationCode: z13.string().nullable(),
  registeredOn: businessDateSchema.nullable(),
  contractDate: businessDateSchema.nullable(),
  regionalCode: z13.string().nullable(),
  cooperativeCode: z13.string().nullable(),
  volunteerCode: z13.string().nullable(),
  points: z13.string().nullable(),
  dueDay: z13.string().nullable(),
  externalCode: z13.string().nullable(),
  /** o SGA diz se o associado está no SPC/Serasa */
  creditBureau: z13.boolean().nullable(),
  beneficiaryCode: z13.string().nullable(),
  recurringBillingType: z13.string().nullable(),
  /** `opcoes_notificacao` do SGA — a preferência da CASA é outra (regra 15) */
  notify: z13.object({
    email: z13.boolean().nullable(),
    sms: z13.boolean().nullable(),
    whatsapp: z13.boolean().nullable()
  })
});
var memberBeneficiarySchema = z13.object({
  erpCode: z13.string().nullable(),
  situacao: z13.string().nullable(),
  dueDay: z13.string().nullable(),
  recurringBillingType: z13.string().nullable(),
  cooperativeCode: z13.string().nullable(),
  volunteerCode: z13.string().nullable(),
  points: z13.string().nullable(),
  /** saiu da lista do associado no SGA (a linha fica) */
  missing: z13.boolean()
});
var catalogMemberDetailSchema = catalogOwnerDetailSchema.extend({
  registry: memberRegistrySchema,
  beneficiaries: z13.array(memberBeneficiarySchema),
  erpCode: z13.string().nullable(),
  syncedAt: instantSchema.nullable(),
  vehicles: z13.array(catalogVehicleSchema),
  boletos: z13.array(catalogBoletoSchema),
  boletosTotal: z13.number().int(),
  serviceRecords: z13.array(catalogServiceRecordSchema),
  /** quando o histórico de atendimento foi perguntado ao SGA; nulo é "ninguém perguntou" */
  serviceRecordsSyncedAt: instantSchema.nullable(),
  changes: z13.array(catalogChangeSchema)
});
var catalogBoletoResultSchema = z13.object({
  estado: z13.literal("lida"),
  items: z13.array(catalogBoletoSchema),
  total: z13.number().int(),
  page: z13.number().int(),
  perPage: z13.number().int(),
  /** ver `syncedAt` em `catalogSearchResult` — nulo quando nenhuma passagem gravou */
  syncedAt: instantSchema.nullable(),
  /** ver `incomplete` em `catalogSearchResult` — a mesma coisa, pelo mesmo motivo */
  incomplete: z13.boolean(),
  incompleteDetail: z13.string().nullable(),
  /** a soma de `value` de toda a seleção, e não só da página */
  totalValue: moneySchema,
  /** a soma de `valuePaid`; o que entrou, na mesma seleção */
  totalPaid: moneySchema
});
var catalogVolunteerSchema = z13.object({
  /**
   * `VO-000123` — o identificador da CASA. Ver `catalogBoletoSchema.code`.
   *
   * Substituiu o `id` do espelho em 05/10/2026, junto com veículo e boleto.
   * Aqui ele nem endereçava tela — esta lista não tem ficha —, mas atravessava
   * o contrato como identidade de linha, e `external.sga_*` é renumerado por
   * `replaceSlice` a cada varredura completa: o número mudava de dono entre
   * duas aberturas da mesma tela.
   */
  code: z13.string(),
  /**
   * `codigo_voluntario` do SGA — procedência, e **o que liga veículo a
   * consultor**: é ele que `app.vehicles.erp_volunteer_code` guarda, e é por
   * ele que as duas contagens abaixo agrupam.
   *
   * Anulável porque o núcleo aceita voluntário nascido na casa, e esse não tem
   * código no fornecedor. Hoje os 742 da CE têm.
   */
  erpCode: z13.string().nullable(),
  nome: z13.string().nullable(),
  email: z13.string().nullable(),
  /** já formatado; nulo quando nenhum dos três campos tem dígito */
  telefone: z13.string().nullable(),
  /** o que o SGA chama de situação do voluntário */
  situacao: z13.string().nullable(),
  /** quantos veículos do NÚCLEO apontam para este `codigo_voluntario` */
  vehicles: z13.number().int(),
  /**
   * Quantos boletos DISTINTOS estão ligados aos veículos deste voluntário.
   *
   * Distintos, e não vínculos: um boleto que aponta dois veículos do mesmo
   * voluntário é um boleto. Medido na CE em 14/09/2026 — 12 dos 328 voluntários
   * com veículo mostrariam um número maior se a contagem fosse de vínculos.
   */
  boletos: z13.number().int(),
  /** quando a PROJEÇÃO escreveu esta linha; nulo é linha que não veio do espelho */
  syncedAt: instantSchema.nullable()
});
var countProvenanceSchema = z13.object({
  /** o espelho daquela entidade está incompleto — o número é PISO, não total */
  incomplete: z13.boolean(),
  /** o que a esteira escreveu, quando há o que dizer */
  detail: z13.string().nullable(),
  /** quando aquele espelho foi datado; nulo se nunca concluiu */
  syncedAt: instantSchema.nullable()
});
var catalogVolunteerResultSchema = z13.object({
  estado: z13.literal("lida"),
  items: z13.array(catalogVolunteerSchema),
  total: z13.number().int(),
  page: z13.number().int(),
  perPage: z13.number().int(),
  /** quando a última passagem que gravou alguma coisa terminou; nulo se nenhuma gravou */
  syncedAt: instantSchema.nullable(),
  incomplete: z13.boolean(),
  incompleteDetail: z13.string().nullable(),
  /** a procedência da coluna "Veículos" */
  vehicles: countProvenanceSchema,
  /** a procedência da coluna "Boletos" — hoje incompleta na CE */
  boletos: countProvenanceSchema
});
var volunteerLinkSchema = z13.enum(["vinculados", "sem_vinculo"]);
var catalogVolunteerSearchSchema = z13.object({
  /** parte do nome, do código do fornecedor, do e-mail ou do telefone */
  term: z13.string().trim().min(2).max(64).optional(),
  /** ausente é todos; ver `volunteerLinkSchema` */
  link: volunteerLinkSchema.optional(),
  page: z13.number().int().min(1).default(1),
  perPage: z13.number().int().min(1).max(200).default(10)
});
var catalogVehicleFilterSchema = z13.object({
  code: z13.string(),
  descricao: z13.string().nullable(),
  /** Quantos veículos da base estão neste código. */
  vehicles: z13.number().int()
});
var catalogVehicleFiltersSchema = z13.object({
  /** `sga_veiculos.codigo_tipo` — automóvel, moto, camionete/SUV. */
  vehicleKinds: z13.array(catalogVehicleFilterSchema),
  /** `sga_veiculos.codigo_tipo_adesao` — nova cotação, troca, reativação. */
  adhesionKinds: z13.array(catalogVehicleFilterSchema)
});
var erpContract = c11.router(
  {
    /**
     * O botão "atualizar agora" de uma linha de tela.
     *
     * É o mesmo caminho de uma leitura com frescor zero, e não um recurso
     * separado — quem decide *quão fresco* precisa é quem pergunta; quem decide
     * *como buscar* é a porta única.
     *
     * Desde 14/10/2026 a rota ESPERA: `200` quando o job terminou — e aí a
     * ficha relida já é a nova —, `202` quando a espera acabou antes dele ou o
     * disjuntor está aberto. Ver `syncDoneSchema` e `syncPendingSchema`.
     */
    /**
     * O botão "atualizar a coleção agora" da tela de saúde.
     *
     * `202` pelo mesmo motivo de `sync`: quem fala com o SGA é a fila. A
     * diferença é o tamanho — isto varre a entidade inteira da praça, e na CE
     * são 76 mil veículos em 78 chamadas. Por isso partilha o recurso
     * `erp.sync`, que existe justamente porque pedir atualização gasta o teto
     * do fornecedor.
     */
    sweep: {
      method: "POST",
      path: "/sweeps",
      body: z13.object({ entity: mirroredEntitySchema }),
      responses: { 202: sweepResultSchema, ...defaultErrors },
      summary: "Varre a cole\xE7\xE3o inteira de uma entidade nesta pra\xE7a"
    },
    sync: {
      method: "POST",
      path: "/sync",
      body: z13.object({
        entity: recordEntitySchema,
        /**
         * A chave do registro na origem — código do associado, do veículo, do
         * boleto, do voluntário, ou o identificador externo da venda. É a chave
         * natural que o espelho já usa, e nunca CPF. Com `by: 'placa'` (só no
         * veículo), é a PLACA.
         *
         * Texto porque é assim que a origem devolve; convertê-lo em número
         * perderia zeros à esquerda que ninguém mediu se existem.
         *
         * Vai no **corpo**, e não no caminho: `packages/contract/src/contract.test.ts`
         * reprova identificador de pessoa em endereço, e o código do associado é
         * um. Fora do endereço ele não entra em log de servidor, histórico de
         * navegador nem cabeçalho `Referer`.
         */
        id: z13.string().min(1).max(64),
        /**
         * Por qual campo perguntar. `placa` só no veículo, e só para o
         * veículo sem código do ERP: a ficha manda o `codigo_veiculo` sempre
         * que tem. Ausente é o código, como sempre foi.
         */
        by: z13.enum(["codigo", "placa"]).optional()
      }),
      responses: {
        200: syncDoneSchema,
        202: syncPendingSchema,
        ...defaultErrors,
        /** o job falhou — o SGA não respondeu, a credencial não vale. Nunca "atualizado". */
        502: apiErrorSchema
      },
      summary: "Atualiza um registro do ERP agora, e o que vem com ele"
    },
    /**
     * A tela de saúde da integração: o que sincronizou e quando, como está o
     * disjuntor e quanto do teto do dia já foi.
     */
    health: {
      method: "GET",
      path: "/health",
      responses: { 200: erpHealthSchema, ...defaultErrors },
      summary: "Estado da integra\xE7\xE3o na base atual"
    },
    listClassifications: {
      method: "GET",
      path: "/classifications",
      query: z13.object({
        /**
         * Arquivada é o histórico: a classificação que valia e alguém tirou do
         * ar. Fora por omissão.
         *
         * `z.enum` e não `z.coerce.boolean()` pelo mesmo motivo que
         * `listCategories` em `tickets.ts`: na query string tudo é string, e
         * `Boolean('false')` é `true`.
         */
        includeArchived: z13.enum(["true", "false"]).transform((v) => v === "true").optional()
      }),
      responses: { 200: classificationStateSchema, ...defaultErrors },
      summary: "A classifica\xE7\xE3o de situa\xE7\xF5es da base atual"
    },
    /**
     * Classifica **um** código — cria se não existe, atualiza a vigente se
     * existe.
     *
     * `PUT` num caminho de coleção normalmente quer dizer "substitua a coleção
     * inteira", e aqui **não** quer: duas abas abertas na mesma tela apagariam
     * o trabalho uma da outra em silêncio, e o que some é a decisão que faz um
     * veículo aceitar ou não cancelamento. É o mesmo desenho de
     * `PUT /platform/tenant-settings`, que grava uma chave por vez.
     *
     * A resposta é a tela inteira de novo — a lista com o código recém-saído de
     * `notClassified` — para a página não precisar de uma segunda consulta
     * para mostrar o efeito do que a pessoa acabou de fazer.
     */
    defineClassification: {
      method: "PUT",
      path: "/classifications",
      body: newClassificationSchema,
      responses: { 200: classificationStateSchema, ...defaultErrors },
      summary: "Classifica um c\xF3digo de situa\xE7\xE3o na base atual"
    },
    /**
     * Grava, de uma vez, a decisão da casa que está no arquivo da praça e
     * falta no banco — a lista de `decided.pending`.
     *
     * Sem corpo: a lista é relida no servidor, e não recebida. O que outra aba
     * classificou entre a pessoa abrir a confirmação e clicar fica como está,
     * em vez de ser sobrescrito por uma lista velha.
     */
    applyDecidedClassifications: {
      method: "POST",
      path: "/classifications/decided",
      body: z13.object({}),
      responses: { 200: classificationStateSchema, ...defaultErrors },
      summary: "Grava a classifica\xE7\xE3o decidida pela casa que falta nesta base"
    },
    /**
     * Arquiva uma classificação — a operação nunca apaga (convenção 9).
     *
     * O código volta a ser **não classificado**, que é um estado visível na
     * tela, e não "não bloqueia cancelamento". Quem arquiva por engano vê a
     * esteira parar de decidir por aquele código; quem zerasse
     * `blocksCancellation` no lugar disso veria o veículo bloqueado voltar a
     * aceitar cancelamento sem nada dizendo isso.
     *
     * Não é uma ação nova de permissão: arquivar é editar. Está aqui porque sem
     * ela a tela de `erp-24` classifica e nunca desclassifica — e quem errasse
     * o código 15 numa praça só sairia disso por SQL.
     */
    archiveClassification: {
      method: "DELETE",
      path: "/classifications/:id",
      pathParams: z13.object({ id: z13.coerce.number().int() }),
      responses: { 200: classificationStateSchema, ...defaultErrors },
      summary: "Arquiva uma classifica\xE7\xE3o de situa\xE7\xE3o da base atual"
    },
    /**
     * A busca de veículos do cadastro — `erp-35`.
     *
     * `POST` numa leitura, e é deliberado: o que ela recebe é placa, chassi,
     * renavam ou documento, e `contract.test.ts` proíbe os quatro no caminho.
     * Uma query string não resolve — ela vaza pelos mesmos três lugares. O
     * docblock daquela régua já previa esta rota: *"o caminho de busca por
     * placa, quando existir, é POST com o valor no corpo"*.
     */
    searchVehicles: {
      method: "POST",
      path: "/vehicles/search",
      body: catalogVehicleSearchSchema,
      responses: { 200: catalogVehicleResultSchema, ...defaultErrors },
      summary: "Procura ve\xEDculos no cadastro espelhado da base atual"
    },
    /**
     * Um veículo, com o dono e os boletos dele — `erp-38`.
     *
     * O `:code` é o **código da casa** (`VE-000123`), e não a placa nem o
     * código do fornecedor. Placa no caminho é o que a régua proíbe; o código
     * do fornecedor não é proibido, mas amarraria o endereço da tela à chave de
     * quem está do outro lado da integração — e a regra 1 existe para que essa
     * amarra fique num arquivo só.
     */
    vehicle: {
      method: "GET",
      path: "/vehicles/:code",
      /*
       * O código da CASA (`VE-000123`), e não o `id` do espelho: aquele muda a
       * cada varredura completa — ver `catalogBoletoSchema.code`. Era o endereço
       * desta ficha até 05/10/2026, e um link salvo apontava para outro veículo
       * no dia seguinte.
       */
      pathParams: z13.object({ code: z13.string() }),
      responses: { 200: catalogVehicleDetailSchema, ...defaultErrors },
      summary: "Um ve\xEDculo do cadastro, com o dono e os boletos dele"
    },
    /**
     * A busca de boletos — `erp-39`.
     *
     * `POST` pelo mesmo motivo: o `term` aceita o documento do associado.
     */
    /**
     * Os voluntários do cadastro, com quantos veículos e quantos boletos cada
     * um tem — e a busca por um deles.
     *
     * **Era `GET /volunteers`, e virou `POST` em 22/09/2026.** O bloco que
     * estava aqui explicava o `GET` pela ausência de filtro e escrevia, na
     * mesma frase, a condição da troca: *"o dia em que a busca entrar, ela é
     * `POST` como as outras — o `term` aceitaria o nome de uma pessoa"*. A
     * busca entrou, e o `term` aceita. Nome de gente em query string acaba em
     * log de servidor, histórico de navegador e cabeçalho `Referer` — é a mesma
     * régua que já põe placa, chassi e documento no corpo nas duas buscas
     * vizinhas.
     *
     * O que ela DEVOLVE continua nominal, e isso segue sendo resposta e não
     * endereço.
     */
    searchVolunteers: {
      method: "POST",
      path: "/volunteers/search",
      body: catalogVolunteerSearchSchema,
      responses: { 200: catalogVolunteerResultSchema, ...defaultErrors },
      summary: "Procura volunt\xE1rios no cadastro espelhado, com ve\xEDculos e boletos de cada um"
    },
    /**
     * Os códigos de TIPO DE VEÍCULO e de TIPO DE ADESÃO que a base tem, com
     * quantos veículos em cada um — o que a premiação direta filtra.
     *
     * ## Por que ela existe, em vez de um campo de texto
     *
     * Os tipos de boleto do cadastro de premiação são digitados à mão, separados
     * por vírgula, porque não há lista para oferecer. Aqui há: os dois códigos
     * estão no espelho, e um deles **não** tem lista de referência no
     * fornecedor — `codigo_tipo` é denormalizado em `sga_veiculos`. Pedir que
     * alguém digite "1, 6" de cabeça é pedir que erre, e o erro paga a pessoa
     * errada.
     *
     * ## A contagem vem junto, e ela é o aviso
     *
     * Medido na CE em 15/09/2026, o tipo de adesão `0` tem **20.035 veículos** e
     * nenhuma descrição na lista do fornecedor. O número ao lado de cada opção é
     * o que faz quem cadastra ver o tamanho do que está marcando — ou deixando
     * de marcar. É a **G13** aparecendo na tela onde ela é decidida.
     *
     * `GET` e sem identificador de pessoa nenhum: é catálogo da base.
     */
    vehicleFilters: {
      method: "GET",
      path: "/vehicle-filters",
      responses: { 200: catalogVehicleFiltersSchema, ...defaultErrors },
      summary: "Os tipos de ve\xEDculo e de ades\xE3o do espelho, com quantos ve\xEDculos em cada"
    },
    searchBoletos: {
      method: "POST",
      path: "/boletos/search",
      body: catalogBoletoSearchSchema,
      responses: { 200: catalogBoletoResultSchema, ...defaultErrors },
      summary: "Procura boletos no cadastro espelhado da base atual"
    },
    /**
     * A ficha de um boleto. O `:code` é o da CASA (`BO-000123`), pelo motivo de
     * `/vehicles/:code`: o `id` do espelho muda a cada varredura, e o número
     * impresso é dado, nunca endereço (regra 7).
     */
    boleto: {
      method: "GET",
      path: "/boletos/:code",
      pathParams: z13.object({ code: z13.string() }),
      responses: { 200: catalogBoletoDetailSchema, ...defaultErrors },
      summary: "Um boleto do cadastro, com o associado e os ve\xEDculos que ele cobre"
    },
    /**
     * A ficha do associado — `AS-000123` é o `code` da casa. O cadastro, os
     * carros, os boletos mais recentes, o histórico de atendimento e as
     * alterações, numa resposta (14/10/2026).
     */
    member: {
      method: "GET",
      path: "/members/:code",
      pathParams: z13.object({ code: z13.string() }),
      responses: { 200: catalogMemberDetailSchema, ...defaultErrors },
      summary: "A ficha de um associado no cadastro espelhado"
    },
    /** O histórico de atendimento da praça, com filtro — tudo no corpo, pelo motivo de `searchVehicles`. */
    searchServiceRecords: {
      method: "POST",
      path: "/service-records/search",
      body: catalogServiceRecordSearchSchema,
      responses: { 200: catalogServiceRecordResultSchema, ...defaultErrors },
      summary: "Busca atendimentos registrados no SGA"
    },
    /**
     * UM atendimento, pelo `codigo_atendimento` do SGA — a página que o botão
     * "Ver" da lista abre, com a descrição inteira.
     */
    serviceRecord: {
      method: "GET",
      path: "/service-records/:code",
      pathParams: z13.object({ code: z13.string() }),
      responses: { 200: catalogServiceRecordSchema, ...defaultErrors },
      summary: "Um atendimento registrado no SGA"
    },
    /** Os tipos, status e departamentos de atendimento — as opções dos filtros. */
    serviceRecordFilters: {
      method: "GET",
      path: "/service-record-filters",
      responses: { 200: serviceRecordFiltersSchema, ...defaultErrors },
      summary: "O vocabul\xE1rio que filtra os atendimentos"
    },
    /**
     * O "Atualizar do ERP" das listas de histórico (atendimentos, alterações):
     * roda a passagem daquela lista NA HORA, fora da fila das esteiras, e
     * espera — como `sync`. `202` quando a espera acaba antes dela.
     */
    refreshHistory: {
      method: "POST",
      path: "/history-refresh",
      body: z13.object({ entity: historyEntitySchema }),
      responses: {
        200: historyRefreshDoneSchema,
        202: syncPendingSchema,
        ...defaultErrors,
        502: apiErrorSchema
      },
      summary: "Traz agora o hist\xF3rico mais recente do SGA"
    },
    /** A lista de associados, com filtro — no corpo, pelo motivo de `searchVehicles`. */
    searchMembers: {
      method: "POST",
      path: "/members/search",
      body: catalogMemberSearchSchema,
      responses: { 200: catalogMemberResultSchema, ...defaultErrors },
      summary: "Busca associados no cadastro espelhado"
    },
    /** As situações e os tipos de pessoa que a praça tem — as opções dos filtros. */
    memberFilters: {
      method: "GET",
      path: "/member-filters",
      responses: { 200: memberFiltersSchema, ...defaultErrors },
      summary: "As op\xE7\xF5es dos filtros da lista de associados"
    },
    /** UMA alteração, com o histórico do mesmo registro — a página do "Ver". */
    change: {
      method: "GET",
      path: "/changes/:subject/:code",
      pathParams: z13.object({ subject: changeSubjectSchema, code: z13.string() }),
      responses: { 200: catalogChangeDetailSchema, ...defaultErrors },
      summary: "Uma altera\xE7\xE3o do SGA e as outras do mesmo registro"
    },
    /** As alterações do SGA — boleto, associado e veículo —, com filtro. */
    searchChanges: {
      method: "POST",
      path: "/changes/search",
      body: catalogChangeSearchSchema,
      responses: { 200: catalogChangeResultSchema, ...defaultErrors },
      summary: "Busca o que mudou no SGA, campo a campo"
    },
    /** Os tipos de boleto da praça, com quantos boletos cada um tem. */
    boletoTypes: {
      method: "GET",
      path: "/boleto-types",
      responses: { 200: catalogBoletoTypesSchema, ...defaultErrors },
      summary: "Os tipos de boleto do SGA"
    },
    /**
     * Os produtos da base, somados sobre os veículos já consultados.
     *
     * `POST` pela forma das buscas vizinhas, e não por sigilo: o `term` aqui é
     * descrição de produto, sem dado de pessoa.
     */
    /**
     * Os tipos de boleto da base, com a contagem de cada um — a lista que a
     * régua de uma premiação marca (recorrência e por produto).
     */
    searchBoletoTypes: {
      method: "POST",
      path: "/boleto-types/search",
      body: catalogBoletoTypeSearchSchema,
      responses: { 200: catalogBoletoTypeResultSchema, ...defaultErrors },
      summary: "Os tipos de boleto da base atual, com quantos boletos cada um tem"
    },
    /** Os planos da base — a lista do ERP e os códigos que os carros apontam. */
    searchPlans: {
      method: "POST",
      path: "/plans/search",
      body: catalogPlanSearchSchema,
      responses: { 200: catalogPlanResultSchema, ...defaultErrors },
      summary: "Os planos (grupos de produto) da base atual, com quantos carros cada um tem"
    },
    searchProducts: {
      method: "POST",
      path: "/products/search",
      body: catalogProductSearchSchema,
      responses: { 200: catalogProductResultSchema, ...defaultErrors },
      summary: "Os produtos dos ve\xEDculos j\xE1 consultados no ERP, na base atual"
    },
    /**
     * Os veículos que têm um produto. O `:erpCode` é o `codigo_produto` do
     * SGA — não há código da casa para produto, porque o fornecedor nega o
     * catálogo dele —, e não identifica pessoa nenhuma.
     */
    searchProductVehicles: {
      method: "POST",
      path: "/products/:erpCode/vehicles/search",
      pathParams: z13.object({ erpCode: z13.string().min(1).max(20) }),
      body: catalogProductVehicleSearchSchema,
      responses: { 200: catalogProductVehicleResultSchema, ...defaultErrors },
      summary: "Os ve\xEDculos j\xE1 consultados que t\xEAm um produto, na base atual"
    }
  },
  { pathPrefix: "/erp" }
);
var erpPortalContract = c11.router(
  {
    wallet: {
      method: "GET",
      path: "/erp/wallet",
      /*
       * Sem `query` e sem `pathParams`: nada aqui vem do cliente. O associado é
       * o da sessão, a praça é a dele, e o frescor é do servidor — ver o
       * cabeçalho da seção da carteira.
       */
      responses: { 200: memberPortfolioSchema, ...defaultErrors },
      summary: "Os ve\xEDculos do associado da sess\xE3o"
    },
    /*
     * A ficha. O caminho recebe o código do **ERP** (`codigo_veiculo`), e não o
     * da casa — é o que `portfolioVehicleSchema` carrega, porque a carteira é
     * lida pela porta única e a porta fala o vocabulário do espelho. Chamá-lo
     * de `code` no caminho faria a ficha procurar por `VE-000123` um valor que
     * o chamador nunca teve.
     *
     * A resposta devolve os DOIS: `code` (o da casa, `VE-000123`) e o que veio
     * no caminho. O dia em que a carteira passar a ler o núcleo, o caminho
     * troca — e `erpCode` no nome é o que torna essa troca visível.
     *
     * O escopo é por PESSOA: `(praça, associado da sessão, erpCode)`.
     */
    vehicle: {
      method: "GET",
      path: "/erp/vehicles/:erpCode",
      pathParams: z13.object({ erpCode: z13.string() }),
      responses: { 200: portfolioVehicleDetailResponseSchema, ...defaultErrors },
      summary: "A ficha de um ve\xEDculo do associado da sess\xE3o"
    }
  },
  { pathPrefix: "/portal" }
);

// src/portal.ts
import { initContract as initContract12 } from "@ts-rest/core";
import { z as z14 } from "zod";
var c12 = initContract12();
var memberSchema = z14.object({
  id: z14.string(),
  nome: z14.string(),
  email: z14.string(),
  cpf: z14.string().nullable(),
  telefone: z14.string().nullable()
});
var portalContextSchema = z14.object({
  member: memberSchema,
  /** a base do associado; o portal não troca de base */
  tenant: tenantSchema,
  /** já existe cadastro correspondente no ERP? o espelho entra na Onda 3 */
  linkedToErp: z14.boolean()
});
var memberSessionSchema = z14.object({
  id: z14.string(),
  createdAt: instantSchema,
  expiresAt: instantSchema,
  ip: z14.string().nullable(),
  userAgent: z14.string().nullable(),
  current: z14.boolean()
});
var memberPreferencesSchema = z14.object({
  theme: themeSchema,
  channels: channelsSchema
});
var receivedSchema = z14.object({
  /** o texto que a tela mostra. Igual nos dois desfechos, por construção. */
  message: z14.string()
});
var validInviteSchema = z14.object({
  nome: z14.string(),
  /** mascarado: a tela confirma o cadastro sem expor o documento inteiro */
  emailMasked: z14.string(),
  /** os termos que precisam de aceite. Vazia enquanto o negócio não publicar. */
  terms: z14.array(z14.object({ term: z14.string(), version: z14.string(), title: z14.string() }))
});
var portalContract = c12.router(
  {
    context: {
      method: "GET",
      path: "/context",
      responses: { 200: portalContextSchema, ...defaultErrors },
      summary: "Contexto da sess\xE3o do associado"
    },
    /* --- primeiro acesso: público, e sem enumeração --------------------- */
    /**
     * Valida o token e devolve o que a tela precisa mostrar.
     *
     * Devolve 200 com os dados ou **um 400 idêntico** para expirado, já aceito,
     * arquivado e inexistente. Quem tem o token legítimo vê o próprio nome; quem
     * não tem, vê sempre a mesma frase.
     */
    validateFirstAccess: {
      method: "POST",
      path: "/first-access/validate",
      body: z14.object({ token: z14.string().min(16).max(200) }),
      responses: {
        200: validInviteSchema,
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Confere o convite de primeiro acesso"
    },
    completeFirstAccess: {
      method: "POST",
      path: "/first-access/complete",
      body: z14.object({
        token: z14.string().min(16).max(200),
        password: z14.string().min(MIN_PASSWORD_LENGTH).max(200),
        /** os termos aceitos, com a versão. Vazia quando não há termo publicado. */
        acceptances: z14.array(z14.object({ term: z14.string(), version: z14.string() })).default([])
      }),
      responses: {
        /*
         * Devolve o e-mail — e só aqui, não na validação.
         *
         * Quem chega neste ponto acabou de **definir a senha** com um token de
         * convite válido: ele demonstrou deter a conta, e o e-mail dela deixou
         * de ser informação que se protege dele. É o que permite a tela entrar
         * em seguida em vez de mandar a pessoa digitar, no login, o que ela
         * digitou há um segundo — a fricção que faz desistir no último passo.
         *
         * `validateFirstAccess`, que roda ANTES da senha, continua devolvendo
         * só o mascarado: lá quem chama apenas tem um token, e um token pode ter
         * sido encaminhado.
         */
        200: receivedSchema.extend({ email: z14.string() }),
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Define a senha e conclui o primeiro acesso"
    },
    /* --- senha: as duas públicas respondem igual ------------------------ */
    recoverPassword: {
      method: "POST",
      path: "/password/recover",
      body: z14.object({ email: z14.string().email() }),
      // 200 sempre, exista ou não o cadastro. É a resposta que não vira lista.
      responses: {
        200: receivedSchema,
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Envia o link de redefini\xE7\xE3o, se houver cadastro"
    },
    resetPassword: {
      method: "POST",
      path: "/password/reset",
      body: z14.object({
        token: z14.string().min(16).max(200),
        password: z14.string().min(MIN_PASSWORD_LENGTH).max(200)
      }),
      responses: {
        200: receivedSchema,
        400: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Redefine a senha pelo token recebido por e-mail"
    },
    switchPassword: {
      method: "POST",
      path: "/password/change",
      body: z14.object({
        passwordCurrent: z14.string().min(1).max(200),
        passwordNew: z14.string().min(MIN_PASSWORD_LENGTH).max(200)
      }),
      responses: { 200: receivedSchema, ...defaultErrors },
      summary: "Troca a pr\xF3pria senha, com a atual"
    },
    /* --- meus acessos e preferências ------------------------------------ */
    listSessions: {
      method: "GET",
      path: "/sessions",
      responses: { 200: z14.array(memberSessionSchema), ...defaultErrors },
      summary: "Onde o associado est\xE1 logado"
    },
    endSession: {
      method: "DELETE",
      path: "/sessions/:id",
      pathParams: z14.object({ id: z14.string() }),
      body: z14.void(),
      responses: { 204: z14.null(), ...defaultErrors },
      summary: "Derruba um dispositivo"
    },
    getPreferences: {
      method: "GET",
      path: "/preferences",
      responses: { 200: memberPreferencesSchema, ...defaultErrors },
      summary: "Tema e canais do associado"
    },
    savePreferences: {
      method: "PUT",
      path: "/preferences",
      body: memberPreferencesSchema.partial(),
      responses: { 200: memberPreferencesSchema, ...defaultErrors },
      summary: "Salva tema e canais"
    }
  },
  { pathPrefix: "/portal" }
);

// src/claims.ts
import { initContract as initContract13 } from "@ts-rest/core";
import { z as z15 } from "zod";
import {
  isBrazilianState as isBrazilianState2,
  CLAIM_DEPARTMENTS,
  CLAIM_TYPES,
  MAX_DEADLINE_HOURS,
  ROTATION_DEPARTMENTS,
  ROTATION_NAME_MAX,
  SLA_DAY_KINDS,
  SLA_DEPARTMENTS,
  COMPANY_CATEGORIES,
  COMPANY_TYPES,
  REPAIR_PART_ORIGINS,
  REPAIR_PART_SUPPLIERS,
  REPAIR_QUOTE_COVERAGES,
  REPAIR_QUOTE_ITEM_KINDS,
  REPAIR_QUOTE_SOURCES,
  PROVIDER_PAYMENT_MODES
} from "@evogard/domain";
var c13 = initContract13();
var searchTerm = z15.string().trim().min(1).max(120).optional();
var thirdPartySchema = z15.object({
  id: z15.number().int(),
  name: z15.string(),
  phone: z15.string(),
  email: z15.string().nullable(),
  /** CPF ou CNPJ, só dígitos (item 11). Opcional: nem todo terceiro o informa. */
  document: z15.string().nullable(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var thirdPartyVehicleSchema = z15.object({
  id: z15.number().int(),
  thirdPartyId: z15.number().int(),
  plate: z15.string(),
  model: z15.string(),
  manufactureYear: z15.number().int(),
  modelYear: z15.number().int(),
  /**
   * O carro do núcleo, quando o terceiro também é associado (item 12). Nulo é
   * carro de fora. O aviso continua sendo de `terceiro` mesmo com o vínculo.
   */
  vehicleId: z15.number().int().nullable(),
  /** Arquivado não aparece para escolher num sinistro novo, e continua no histórico. */
  archivedAt: instantSchema.nullable()
});
var coreVehicleMatchSchema = z15.object({
  id: z15.number().int(),
  /** `VE-000123` — o identificador ditável da casa. */
  code: z15.string(),
  plate: z15.string(),
  model: z15.string().nullable(),
  modelYear: z15.string().nullable(),
  memberName: z15.string().nullable()
});
var thirdPartyWithVehiclesSchema = thirdPartySchema.extend({
  vehicles: z15.array(thirdPartyVehicleSchema)
});
var thirdPartyWriteSchema = z15.object({
  name: z15.string().trim().min(1),
  phone: phoneSchema,
  email: z15.string().trim().email().nullable().optional(),
  /** CPF ou CNPJ, com ou sem pontuação; chega ao serviço só com os dígitos, conferidos. */
  document: documentSchema.nullable().optional()
});
var createThirdPartyVehicleSchema = z15.object({
  plate: z15.string().trim().min(7).max(10),
  model: z15.string().trim().min(1),
  manufactureYear: z15.number().int().min(1900),
  modelYear: z15.number().int().min(1900),
  /** O carro do núcleo com a mesma placa, quando quem cadastra confirma o vínculo. */
  vehicleId: z15.number().int().nullable().optional()
});
var companyTypeSchema = z15.enum(COMPANY_TYPES);
var companyCategorySchema = z15.enum(COMPANY_CATEGORIES);
var companyDocumentTypeSchema = z15.enum(["cpf", "cnpj"]);
var companySchema = z15.object({
  id: z15.number().int(),
  type: companyTypeSchema,
  name: z15.string(),
  documentType: companyDocumentTypeSchema,
  document: z15.string(),
  email: z15.string().nullable(),
  phone: z15.string().nullable(),
  /** O WhatsApp da empresa — para onde a assistência manda o script (`assistencia-04`). */
  whatsapp: z15.string().nullable(),
  /** O endereço do prestador — opcional; instituição não tem (07/10/2026). */
  address: addressSchema.nullable(),
  /**
   * Como a Assistência 24h paga esta empresa (30/10/2026). Preenchida, ela é
   * prestador da assistência, e o `id` daqui é o `providerId` de lá. Nula:
   * fora da assistência. Editada pela rota da assistência, nunca por esta.
   */
  assistancePaymentMode: z15.enum(PROVIDER_PAYMENT_MODES).nullable(),
  /** Retirada da assistência — continua no cadastro. */
  assistanceArchivedAt: instantSchema.nullable(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var companyWriteSchema = z15.object({
  type: companyTypeSchema,
  name: z15.string().min(1),
  documentType: companyDocumentTypeSchema,
  /** Com ou sem pontuação; chega ao serviço só com os dígitos, conferidos. */
  document: documentSchema,
  email: z15.string().email().nullable().optional(),
  phone: phoneSchema.nullable().optional(),
  whatsapp: phoneSchema.nullable().optional(),
  /** Nulo tira o endereço; sem o campo, ele fica como está. Só prestador guarda. */
  address: addressWriteSchema.nullable().optional()
});
var claimTypeSchema = z15.enum(CLAIM_TYPES);
var occurrenceAddressWriteSchema = addressWriteSchema.extend({
  state: z15.string().trim().toUpperCase().refine((v) => isBrazilianState2(v), "Escolha uma das 27 UFs."),
  city: z15.string().trim().min(1).max(300),
  street: z15.string().trim().min(1).max(300)
});
var towDestinationTypeSchema = z15.enum([
  "base_do_prestador",
  "residencia_do_associado",
  "oficina_credenciada",
  "oficina_indicada_pelo_cliente",
  "outro_local_indicado_pelo_associado"
]);
var claimDepartmentSchema = z15.enum(CLAIM_DEPARTMENTS);
var claimDriverFields = {
  driverIsMember: z15.enum(["sim", "nao"]),
  driverDocument: cpfSchema.nullable().optional(),
  driverName: z15.string().nullable().optional(),
  driverPhone: phoneSchema.nullable().optional()
};
var claimCommonFields = {
  opaSuiteProtocol: z15.string().min(1),
  direction: z15.string().nullable().optional(),
  occurredAt: instantSchema,
  notifiedAt: instantSchema,
  claimType: z15.string().min(1),
  /** O local do ocorrido — vira uma linha de `addresses` (item 16). */
  occurrenceAddress: occurrenceAddressWriteSchema,
  occurrenceHasCameras: z15.enum(["sim", "nao", "nao_foi_possivel_verificar"]).nullable().optional(),
  causedBy: z15.enum(["associado", "terceiro", "outro"]),
  hadRightOfWayViolation: z15.enum(["sim", "nao"]),
  trafficAuthorityNotified: z15.enum(["sim", "nao"]),
  trafficAuthorityDescription: z15.string().nullable().optional(),
  memberRequestedTow: z15.enum(["sim", "nao"]),
  towDestinationType: towDestinationTypeSchema,
  towDestinationAddress: z15.string().nullable().optional(),
  description: z15.string().min(1)
};
var claimMemberThirdPartyFields = {
  reportedThirdParties: z15.enum(["sim", "nao", "sim_mas_nao_identificado"])
};
var claimThirdPartyFields = {
  reportedThirdPartyVehicle: z15.enum(["sim", "nao"]),
  /** Só com veículo — `thirdPartyQuestions` em `@evogard/domain`. */
  thirdPartyHasInsurance: z15.enum(["sim", "nao"]).nullable().optional(),
  /**
   * Só SEM veículo (muro, portão, poste…). Opcional aqui porque depende da
   * resposta de cima: quem cobra é o serviço, pela mesma `thirdPartyQuestions`
   * que esconde a pergunta na tela.
   */
  thirdPartyDamageType: z15.string().min(1).nullable().optional()
};
var createClaimSchema = z15.discriminatedUnion("type", [
  z15.object({
    type: z15.literal("associado"),
    vehicleCode: z15.string().min(1),
    ...claimDriverFields,
    ...claimMemberThirdPartyFields,
    ...claimCommonFields
  }),
  z15.object({
    type: z15.literal("terceiro"),
    parentClaimId: z15.number().int(),
    /** A pessoa terceira — o aviso aponta para ela, com ou sem carro. */
    thirdPartyId: z15.number().int(),
    /** O carro dela, só quando `reportedThirdPartyVehicle` é `sim`. */
    thirdPartyVehicleId: z15.number().int().nullable().optional(),
    ...claimThirdPartyFields,
    ...claimCommonFields
  })
]);
var claimFieldsSchema = z15.object({
  ...claimDriverFields,
  ...claimMemberThirdPartyFields,
  ...claimThirdPartyFields,
  ...claimCommonFields
});
var claimSummarySchema = z15.object({
  id: z15.number().int(),
  type: claimTypeSchema,
  involvesThirdParty: z15.boolean(),
  currentDepartment: claimDepartmentSchema,
  /** Uma fase de `currentDepartment` — `ANALYSIS_STAGES` ou `REPAIR_STAGES`. */
  currentPhase: z15.string(),
  /**
   * A situação dentro da etapa (`ANALYSIS_SITUATIONS`), lida da passagem
   * aberta do departamento atual. Nulo = só a etapa.
   */
  situation: z15.string().nullable(),
  /** A parada (`ANALYSIS_HOLDS`), lida da mesma passagem. Nulo = não está parado. */
  hold: z15.string().nullable(),
  /** O resultado da CAF (`CAF_STATUSES`, 16/10/2026) — o selo do quadro. Nulo = sem resultado. */
  cafStatus: z15.string().nullable(),
  vehicleCode: z15.string().nullable(),
  parentClaimId: z15.number().int().nullable(),
  /** No do terceiro: a pessoa. `null` no do associado. */
  thirdPartyId: z15.number().int().nullable(),
  /** No do terceiro com carro: o carro. `null` no terceiro sem carro (o muro). */
  thirdPartyVehicleId: z15.number().int().nullable(),
  /**
   * No do terceiro: quem é e qual é o carro — lido do cadastro de terceiros por
   * join, para a tela não fazer uma consulta por linha. `null` no do associado,
   * cujo carro é do SGA (`vehicleCode`).
   */
  thirdParty: z15.object({
    id: z15.number().int(),
    name: z15.string(),
    phone: z15.string(),
    /** `null` no terceiro sem carro. */
    plate: z15.string().nullable(),
    model: z15.string().nullable()
  }).nullable(),
  /** O carro do núcleo (`app.vehicles`) — só no do associado. */
  vehicleId: z15.number().int().nullable(),
  /**
   * No do associado: o carro e o dono, lidos do núcleo por `join` — nada é
   * copiado para o sinistro.
   *
   * `null` no do terceiro, e `null` também quando o código do SGA ainda não
   * foi projetado no núcleo (`vehicleId` nulo). `memberName` nulo com
   * `vehicle` presente é outro fato: o núcleo tem o associado, e o nome dele
   * está em branco.
   */
  vehicle: z15.object({
    id: z15.number().int(),
    /** `VE-000123`. */
    code: z15.string(),
    plate: z15.string().nullable(),
    brand: z15.string().nullable(),
    model: z15.string().nullable(),
    modelYear: z15.string().nullable(),
    memberId: z15.number().int(),
    memberName: z15.string().nullable()
  }).nullable(),
  claimType: z15.string(),
  /**
   * O protocolo do atendimento que abriu o aviso — o do primeiro contato do
   * sinistro em `claims_contacts`, que nasce na mesma transação que ele.
   */
  opaSuiteProtocol: z15.string().nullable(),
  /**
   * O responsável do card no departamento onde ele está (`claims_assignments`,
   * 07/10/2026) — na Análise, o Analista Responsável. Nulo = ninguém foi
   * escolhido. O nome é o da conta, lido por join.
   */
  assignee: z15.object({ id: z15.string(), name: z15.string() }).nullable(),
  /**
   * As etiquetas que o card mostra A QUEM PERGUNTA (`claims_labels`,
   * 07/10/2026): as dos setores da pessoa, na ordem do setor e da etiqueta.
   * As de outro setor continuam gravadas e não vêm.
   */
  labels: z15.array(appliedLabelSchema),
  /**
   * O vencimento do card NO DEPARTAMENTO onde ele está (`claims_due_dates`,
   * 07/10/2026). O de outro departamento continua gravado e não vem. Nulo =
   * sem vencimento. "Vencido" é comparação com agora: `claimDueState`.
   */
  dueAt: instantSchema.nullable(),
  /**
   * O SLA da fase onde ele está: quando entrou na fase e o prazo copiado na
   * entrada (`claims_phase_passages.deadline_at`). `phaseDeadlineAt` nulo =
   * a fase não tem SLA. "Expirado" é comparação com agora: `phaseSlaState`.
   */
  phaseEnteredAt: instantSchema.nullable(),
  phaseDeadlineAt: instantSchema.nullable(),
  /**
   * O que a fila do supervisor do Reparo mostra para reconhecer o sinistro de
   * cara (29/10/2026) — só com `supervisionStages=somente`; nas outras listas
   * não vem. O relato, onde aconteceu, de quem foi a culpa, a FIPE e, na
   * Aprovação Supervisor, a oficina e o valor bruto do orçamento. Dinheiro é
   * "1234.56", como as colunas saem.
   */
  supervisionQueue: z15.object({
    description: z15.string(),
    city: z15.string().nullable(),
    state: z15.string().nullable(),
    causedBy: z15.string(),
    fipeValue: z15.string().nullable(),
    workshop: z15.string().nullable(),
    grossTotal: z15.string().nullable()
  }).optional(),
  occurredAt: instantSchema,
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var claimAssigneeOptionSchema = z15.object({ id: z15.string(), name: z15.string() });
var setAnalysisAssigneeSchema = z15.object({ userId: z15.string().nullable() });
var claimDocumentSchema = z15.object({
  id: z15.number().int(),
  /** O que o arquivo É para o sinistro — `anexo_aviso`, `cnh`, `crlv`… */
  kind: z15.string(),
  /** A fase em que o arquivo entrou. */
  phase: z15.string(),
  fileName: z15.string(),
  /** O tipo confirmado pelo armazenamento: é por ele que a tela separa foto de vídeo. */
  mimeType: z15.string(),
  sizeBytes: z15.number().int(),
  createdAt: instantSchema,
  /** Quem enviou. Nulo quando quem enviou não é usuário da interna. */
  createdByName: z15.string().nullable()
});
var attachClaimDocumentSchema = z15.object({
  fileName: z15.string().min(1).max(255),
  mimeType: z15.string().min(3).max(160),
  /** O arquivo em base64. */
  content: z15.string().min(1)
});
var claimSchema = claimSummarySchema.extend({
  thirdPartyClaims: z15.array(claimSummarySchema),
  /** Nulo no do terceiro, que não pergunta o condutor. */
  driverIsMember: z15.enum(["sim", "nao"]).nullable(),
  driverDocument: z15.string().nullable(),
  driverName: z15.string().nullable(),
  driverPhone: z15.string().nullable(),
  direction: z15.string().nullable(),
  notifiedAt: instantSchema,
  /** O local do ocorrido, de `addresses`. Nulo só em sinistro sem local gravado. */
  occurrenceAddress: addressSchema.nullable(),
  occurrenceHasCameras: z15.enum(["sim", "nao", "nao_foi_possivel_verificar"]).nullable(),
  causedBy: z15.enum(["associado", "terceiro", "outro"]),
  hadRightOfWayViolation: z15.enum(["sim", "nao"]),
  trafficAuthorityNotified: z15.enum(["sim", "nao"]),
  trafficAuthorityDescription: z15.string().nullable(),
  memberRequestedTow: z15.enum(["sim", "nao"]),
  towDestinationType: towDestinationTypeSchema,
  towDestinationAddress: z15.string().nullable(),
  description: z15.string(),
  /**
   * O que o aviso trouxe — foto, vídeo ou qualquer arquivo —, do mais antigo
   * para o mais novo, sem os arquivados. É `claims_documents` com
   * `kind = anexo_aviso`; os documentos das fases ficam de fora.
   */
  noticeAttachments: z15.array(claimDocumentSchema),
  /** Do terceiro. Nulo no do associado aberto desde 05/10. */
  reportedThirdPartyVehicle: z15.enum(["sim", "nao"]).nullable(),
  /** Do associado. Nulo no do terceiro. */
  reportedThirdParties: z15.enum(["sim", "nao", "sim_mas_nao_identificado"]).nullable(),
  thirdPartyHasInsurance: z15.enum(["sim", "nao"]).nullable(),
  thirdPartyDamageType: z15.string().nullable()
});
var updateClaimSchema = claimFieldsSchema.partial().extend({
  vehicleCode: z15.string().trim().min(1).optional(),
  /** Trocar a pessoa do terceiro. Vai junto com o carro dela, ou com `null`. */
  thirdPartyId: z15.number().int().optional(),
  /** `null` tira o carro — o terceiro passou a ser sem veículo. */
  thirdPartyVehicleId: z15.number().int().nullable().optional(),
  archived: z15.boolean().optional()
});
var claimPassageSchema = z15.object({
  id: z15.number().int(),
  department: z15.string(),
  phase: z15.string(),
  /** De onde veio; nulo na primeira entrada. */
  fromPhase: z15.string().nullable(),
  enteredAt: instantSchema,
  /** Nulo = é onde o sinistro está. */
  leftAt: instantSchema.nullable(),
  /** A situação dentro da etapa nesta linha (F1). */
  status: z15.string().nullable(),
  /** A parada nesta linha (F4). */
  holdType: z15.string().nullable(),
  /** Quem moveu o sinistro para cá. */
  createdBy: z15.string().nullable(),
  createdByName: z15.string().nullable()
});
var registerClaimContactSchema = z15.object({
  fromPhase: z15.string().min(1),
  /* Sem "chat": o chat do atendimento É o WhatsApp (decisão do dono em 06/10/2026). */
  channel: z15.enum(["ligacao", "whatsapp", "email"]),
  answered: z15.boolean(),
  phone: z15.string().trim().max(40).optional(),
  email: z15.string().trim().max(200).optional(),
  protocol: z15.string().trim().max(120).optional(),
  notes: z15.string().trim().max(2e3).optional(),
  /**
   * A empresa com quem foi a tentativa — só no Reparo, e só a oficina do
   * reparo (07/10/2026). Sem ela, a tentativa é com o associado ou o terceiro.
   */
  companyId: z15.coerce.number().int().positive().optional(),
  /**
   * A evidência do contato — o print do chat ou da ligação —, obrigatória
   * desde 21/10/2026 (opcional de 15/10 a 21/10). No lugar do campo "Print
   * do chat ou tentativa de ligação" da Entrada. Vira um `claims_documents`
   * `print_contato` preso à tentativa.
   */
  attachment: attachClaimDocumentSchema
}).superRefine((v, ctx) => {
  const missing = (path, message) => ctx.addIssue({ code: "custom", path: [path], message });
  if (v.channel === "email") {
    if (!v.email) missing("email", "Informe o e-mail para onde a tentativa foi.");
  } else if (!v.phone) {
    missing("phone", "Informe o n\xFAmero tentado.");
  }
  if (v.channel === "whatsapp" && !v.protocol) {
    missing("protocol", "Informe o protocolo do atendimento.");
  }
});
var holdClaimSchema = z15.object({
  fromPhase: z15.string().min(1),
  type: z15.enum(["pendencia", "pre_desistencia", "negado_com_posse", "juridico"]),
  text: z15.string().trim().min(1).max(4e3).optional(),
  /**
   * O tipo da pendência, onde o departamento os tem — na Indenização,
   * `COMPENSATION_PENDENCY_KINDS` (I2, 09/10/2026), e lá é obrigatório. Na
   * Análise e no Reparo a pendência não tem tipo, e o campo é recusado.
   */
  pendencyKind: z15.string().min(1).optional()
}).superRefine((data, ctx) => {
  if (data.type === "negado_com_posse") {
    if (data.text !== void 0) {
      ctx.addIssue({
        code: "custom",
        path: ["text"],
        message: "O negado com posse n\xE3o leva texto ao parar."
      });
    }
  } else if (data.text === void 0) {
    ctx.addIssue({ code: "custom", path: ["text"], message: "Informe o texto." });
  }
});
var resumeClaimSchema = z15.object({
  fromPhase: z15.string().min(1),
  note: z15.string().trim().max(4e3).optional()
});
var moveClaimSchema = z15.object({
  fromPhase: z15.string().min(1),
  toPhase: z15.string().min(1)
});
var undoClaimMoveSchema = z15.object({
  passageId: z15.number().int()
});
var claimMoveResultSchema = z15.object({
  claimId: z15.number().int(),
  /** Onde o sinistro ficou: `analise`, ou o departamento para onde foi enviado. */
  currentDepartment: claimDepartmentSchema,
  currentPhase: z15.string(),
  passage: claimPassageSchema
});
var claimContactAttemptSchema = z15.object({
  id: z15.number().int(),
  happenedAt: instantSchema,
  channel: z15.string().nullable(),
  phone: z15.string().nullable(),
  email: z15.string().nullable(),
  protocol: z15.string().nullable(),
  answered: z15.boolean(),
  notes: z15.string().nullable(),
  /** A empresa com quem foi a tentativa — a oficina, no Reparo. */
  companyName: z15.string().nullable(),
  createdByName: z15.string().nullable(),
  files: z15.array(claimDocumentSchema)
});
var claimCommentSchema = z15.object({
  id: z15.number().int(),
  body: z15.string(),
  department: z15.string().nullable(),
  phase: z15.string(),
  /**
   * O campo do formulário que o pediu (20/10/2026) e o rótulo dele no
   * catálogo — o porquê do comentário. Nulos no escrito solto, na Atividade.
   */
  field: z15.string().nullable(),
  fieldLabel: z15.string().nullable(),
  authorId: z15.string().nullable(),
  authorName: z15.string().nullable(),
  createdAt: instantSchema,
  /** Nulo = nunca editado. */
  editedAt: instantSchema.nullable(),
  /** Remover é arquivar: o texto fica, e a tela diz "removido". */
  archivedAt: instantSchema.nullable()
});
var analysisFormSchema = z15.object({
  phase: z15.string(),
  editable: z15.boolean(),
  lockedFields: z15.array(z15.string()),
  values: z15.record(z15.string(), z15.string().nullable()),
  /**
   * O que conta e não é valor: quantos comentários cada campo de observação
   * tem na fase — a observação é comentário, e o obrigatório dela conta
   * quantos há (07/10/2026). Desde 20/10/2026 conta só os DAQUELE campo.
   */
  counts: z15.record(z15.string(), z15.number().int()),
  /**
   * Os comentários de cada campo de observação NESTA fase, do mais antigo
   * para o mais novo, sem os removidos (20/10/2026) — a lista embaixo do
   * campo, como as tentativas de contato.
   */
  comments: z15.record(z15.string(), z15.array(claimCommentSchema)),
  files: z15.record(z15.string(), z15.array(claimDocumentSchema)),
  assignees: z15.array(z15.object({ id: z15.string(), name: z15.string() })),
  /** As oficinas da base, para o campo da oficina credenciada. Vazio na fase que não tem um. */
  workshops: z15.array(z15.object({ id: z15.string(), name: z15.string() })),
  /** Quem pode fornecer as peças — os fornecedores de peças e as oficinas da base —, para a Compra de Peças (21/10/2026). Vazio na fase que não tem o campo. */
  suppliers: z15.array(z15.object({ id: z15.string(), name: z15.string() })),
  /**
   * Os telefones (só dígitos, com DDD) e e-mails do cadastro de quem é o
   * sinistro — o associado pelo núcleo, o terceiro pelo cadastro dele —, para
   * a tentativa de contato já abrir preenchida (06/10/2026). Vazio quando o
   * cadastro não tem.
   */
  contactSuggestions: z15.object({
    phones: z15.array(z15.string()),
    emails: z15.array(z15.string()),
    /** No Reparo, a oficina do reparo — a tentativa pode ser com ela. Nulo na Análise. */
    workshop: z15.object({
      id: z15.number().int(),
      name: z15.string(),
      phone: z15.string().nullable(),
      email: z15.string().nullable()
    }).nullable()
  }),
  /**
   * As tentativas de contato registradas NESTA fase deste departamento, da
   * mais nova para a mais antiga (15/10/2026).
   */
  contacts: z15.array(claimContactAttemptSchema),
  /** A situação da passagem aberta — só na fase onde o sinistro está. */
  situation: z15.string().nullable(),
  /** A parada da passagem aberta, com o texto e desde quando — idem. */
  hold: z15.object({
    type: z15.string(),
    text: z15.string().nullable(),
    since: instantSchema,
    /** O tipo da pendência, na Indenização (I2); nulo onde a pendência não tem tipo. */
    pendencyKind: z15.string().nullable()
  }).nullable()
});
var repairSummarySchema = z15.object({
  workshop: z15.object({
    id: z15.number().int(),
    name: z15.string(),
    phone: z15.string().nullable(),
    email: z15.string().nullable(),
    /** "Av. Beira Mar, 1000 · Meireles" — do cadastro da empresa, quando tem. */
    address: z15.string().nullable(),
    /** "Fortaleza/CE". */
    city: z15.string().nullable()
  }).nullable(),
  /** O Prazo de Orçamentação que o supervisor definiu — não é copiado (Q1). */
  quoteDueAt: instantSchema.nullable(),
  scheduling: z15.object({
    /** A situação de Aguardando Parada — o Status do Agendamento. */
    status: z15.string().nullable(),
    /** A data marcada para a parada do orçamento. */
    scheduledFor: z15.string().nullable(),
    /** A data marcada para a parada do reparo. */
    repairScheduledFor: z15.string().nullable()
  }),
  quote: z15.object({
    kind: z15.string().nullable(),
    ciliaNumber: z15.string().nullable(),
    /** "1234.56", como as colunas de dinheiro saem. */
    grossTotal: z15.string().nullable(),
    approvalResult: z15.string().nullable(),
    vehicleSituation: z15.string().nullable(),
    participationPayment: z15.string().nullable()
  }).nullable(),
  parts: z15.object({
    supplyKind: z15.string().nullable(),
    requestedOn: z15.string().nullable(),
    forecastOn: z15.string().nullable(),
    arrivedOn: z15.string().nullable()
  }).nullable(),
  dates: z15.object({
    /** O aviso, e a chegada ao Reparo — o começo dos marcos (27/10/2026). */
    notifiedOn: z15.string().nullable(),
    repairEnteredOn: z15.string().nullable(),
    /** A parada real para orçamento (Aguardando Parada, 27/10/2026). */
    vehicleStoppedForQuoteOn: z15.string().nullable(),
    /** A parada real para o reparo (Reparo - Oficina). */
    vehicleStoppedOn: z15.string().nullable(),
    quoteSentOn: z15.string().nullable(),
    quoteApprovedOn: z15.string().nullable(),
    partsArrivedOn: z15.string().nullable(),
    repairStartedOn: z15.string().nullable(),
    deliveryForecastOn: z15.string().nullable(),
    vehicleReadyOn: z15.string().nullable(),
    /** O pagamento da PI — só o associado paga (21/10/2026, saiu do Reparo - Oficina). */
    participationPaidOn: z15.string().nullable(),
    inspectedOn: z15.string().nullable(),
    deliveredAt: instantSchema.nullable()
  }),
  /** As fotos de antes (orçamento) e de depois (vistoria de entrega). */
  photos: z15.object({
    quote: z15.array(claimDocumentSchema),
    inspection: z15.array(claimDocumentSchema)
  })
});
var hoursSchema = z15.string().regex(/^\d{1,4}(\.\d{1,2})?$/);
var repairQuoteItemSchema = z15.object({
  impactArea: z15.string().trim().min(1).max(120),
  kind: z15.enum(REPAIR_QUOTE_ITEM_KINDS),
  replaceHours: hoursSchema.nullable(),
  removeInstallHours: hoursSchema.nullable(),
  repairHours: hoursSchema.nullable(),
  paintHours: hoursSchema.nullable(),
  quantity: z15.string().regex(/^\d{1,6}(\.\d{1,2})?$/),
  partCode: z15.string().trim().max(60).nullable(),
  partOrigin: z15.enum(REPAIR_PART_ORIGINS).nullable(),
  description: z15.string().trim().min(1).max(300),
  supplier: z15.enum(REPAIR_PART_SUPPLIERS),
  unitPrice: moneySchema.nullable(),
  discountAmount: moneySchema.nullable(),
  coverage: z15.enum(REPAIR_QUOTE_COVERAGES)
});
var repairQuoteRatesSchema = z15.object({
  labor: moneySchema.nullable(),
  repair: moneySchema.nullable(),
  paint: moneySchema.nullable()
});
var repairQuoteSchema = z15.object({
  rates: repairQuoteRatesSchema,
  items: z15.array(repairQuoteItemSchema),
  /** Quando foi salvo pela última vez, e por quem; nulo se nunca foi. */
  savedAt: instantSchema.nullable(),
  savedByName: z15.string().nullable()
});
var repairQuotesSchema = z15.object({
  /** Só na Orçamentação, com o sinistro ali, ativo e com permissão de mover. */
  editable: z15.boolean(),
  /** A participação apurada (PI); nula no terceiro, que não paga (R7). */
  participation: moneySchema.nullable(),
  /** O orçamento que a Aprovação Supervisor escolheu (A3); nulo antes dela. */
  approved: z15.enum(REPAIR_QUOTE_SOURCES).nullable(),
  /** O valor FIPE do sinistro, para o orçamento ser lido contra ele. */
  fipeValue: moneySchema.nullable(),
  quotes: z15.object({ proprio: repairQuoteSchema, cilia: repairQuoteSchema })
});
var saveRepairQuoteSchema = z15.object({
  rates: repairQuoteRatesSchema,
  items: z15.array(repairQuoteItemSchema).max(500)
});
var saveAnalysisFormSchema = z15.object({
  values: z15.record(z15.string(), z15.string().trim().max(4e3).nullable())
});
var claimFileSchema = claimDocumentSchema.extend({
  department: z15.string().nullable()
});
var claimDeadlineFilterSchema = z15.enum([
  "sla_expirado",
  "sla_expira_em_breve",
  "vencido",
  "vence_em_breve",
  "sem_vencimento"
]);
var writeClaimCommentSchema = z15.object({
  body: z15.string().trim().min(1, "Escreva o coment\xE1rio.").max(4e3)
});
var activityChangeSchema = z15.object({
  field: z15.string(),
  label: z15.string(),
  before: z15.unknown(),
  after: z15.unknown()
});
var claimActivitySchema = z15.discriminatedUnion("kind", [
  z15.object({
    kind: z15.literal("comentario"),
    key: z15.string(),
    at: instantSchema,
    comment: claimCommentSchema
  }),
  z15.object({
    kind: z15.literal("alteracao"),
    key: z15.string(),
    at: instantSchema,
    type: z15.string(),
    actorName: z15.string().nullable(),
    /**
     * O departamento do evento, e o de chegada quando ele foi um envio — a
     * Entrada da Análise e a do Reparo têm o mesmo código (07/10/2026).
     */
    department: z15.string().nullable(),
    toDepartment: z15.string().nullable(),
    phase: z15.string().nullable(),
    fromPhase: z15.string().nullable(),
    toPhase: z15.string().nullable(),
    fileName: z15.string().nullable(),
    /**
     * O texto do registro (06/10/2026): o canal e a resposta da tentativa de
     * contato, a mensagem da pendência, a justificativa do pedido de
     * desistência, a nota da retomada, a situação nova.
     */
    detail: z15.string().nullable(),
    /** A parada do evento `parado`/`retomado` (`ANALYSIS_HOLDS`). */
    holdType: z15.string().nullable(),
    changes: z15.array(activityChangeSchema)
  })
]);
var claimMessageSchema = z15.object({
  department: claimDepartmentSchema,
  kind: z15.string(),
  label: z15.string(),
  /** De onde ela nasce — "o link da CAF". */
  source: z15.string(),
  /** O texto. Nulo enquanto o link dela não foi preenchido. */
  body: z15.string().nullable()
});
var claimCafLinkSchema = z15.object({
  link: z15.string().nullable(),
  generatedAt: z15.string().nullable(),
  availableAt: z15.string().nullable(),
  /** O transaction ID — o `uuid` da chamada do webhook. Nulo até a CAF chamar. */
  transactionId: z15.string().nullable(),
  /**
   * O resultado da CAF (`CAF_STATUSES`) e as regras dele, do último resultado
   * registrado. Nulo enquanto a CAF não tiver dado resultado.
   */
  result: z15.object({
    status: z15.string(),
    at: z15.string(),
    rules: z15.array(
      z15.object({ code: z15.string(), label: z15.string(), result: z15.string().nullable() })
    ),
    /** Quantos documentos entraram nos anexos, e os que falharam, pelo nome. */
    documents: z15.number().int(),
    failed: z15.array(z15.string())
  }).nullable()
});
var claimCafTrackingSchema = claimCafLinkSchema.extend({
  /** Os arquivos que a CAF mandou, do mais novo para o mais velho. */
  documents: z15.array(
    z15.object({
      id: z15.number().int(),
      /** O nome que o arquivo tinha na CAF — "CNH — frente", "Comprovante de residência". */
      label: z15.string(),
      /** O campo da Documentação onde ele entrou. Nulo = foi para os Anexos. */
      field: z15.string().nullable(),
      fileName: z15.string(),
      mimeType: z15.string(),
      receivedAt: instantSchema,
      /** Tirado do campo por alguém da casa. */
      archived: z15.boolean()
    })
  ),
  /** O CAF JSON da transação atual — o arquivo, para abrir. Nulo sem resultado. */
  json: z15.object({ id: z15.number().int(), fileName: z15.string() }).nullable(),
  /** O que a mesa mostra do CAF JSON. Nulo sem ele, ou quando não deu para ler. */
  facts: z15.object({
    documentType: z15.string().nullable(),
    startedAt: z15.string().nullable(),
    lastEventAt: z15.string().nullable(),
    fraud: z15.boolean().nullable(),
    fraudScore: z15.number().nullable(),
    license: z15.object({ category: z15.string().nullable(), expiresOn: z15.string().nullable() }).nullable()
  }).nullable()
});
var phaseDeadlineSchema = z15.object({
  department: z15.enum(SLA_DEPARTMENTS),
  phase: z15.string(),
  deadlineHours: z15.number().int().nullable(),
  /** Como o SLA conta: dias corridos ou dias úteis, com os feriados da praça (15/10/2026). */
  dayKind: z15.enum(SLA_DAY_KINDS),
  updatedAt: instantSchema.nullable()
});
var claimRotationSchema = z15.object({
  id: z15.number().int(),
  department: z15.enum(ROTATION_DEPARTMENTS),
  name: z15.string(),
  phases: z15.array(z15.string()),
  members: z15.array(
    z15.object({
      userId: z15.string(),
      name: z15.string(),
      /** Fora da vez — férias, afastamento. Continua na lista. */
      paused: z15.boolean(),
      /** Pessoa ativa da base. Inativa ou bloqueada fica na lista e não recebe. */
      active: z15.boolean()
    })
  ),
  nextUserId: z15.string().nullable(),
  updatedAt: instantSchema
});
var setClaimRotationsSchema = z15.object({
  department: z15.enum(ROTATION_DEPARTMENTS),
  rotations: z15.array(
    z15.object({
      id: z15.number().int().nullable(),
      name: z15.string().trim().min(1, "D\xEA um nome ao rod\xEDzio.").max(ROTATION_NAME_MAX),
      phases: z15.array(z15.string().min(1)).max(50),
      members: z15.array(z15.object({ userId: z15.string().min(1), paused: z15.boolean() })).max(100)
    })
  ).max(30)
});
var claimsContract = c13.router(
  {
    createThirdParty: {
      method: "POST",
      path: "/third-parties",
      body: thirdPartyWriteSchema,
      responses: { 201: thirdPartySchema, ...defaultErrors },
      summary: "Cadastra a pessoa terceira de um sinistro"
    },
    listThirdParties: {
      method: "GET",
      path: "/third-parties",
      /*
       * `search` é UM campo, como a busca de veículos do ERP: quem atende tem
       * na mão o que a pessoa soube dizer — o nome, o telefone ou a placa do
       * carro dela — e não precisa escolher em qual campo digitar.
       */
      query: paginationQuery.extend({
        search: z15.string().trim().max(120).optional(),
        /**
         * `sim`: só quem tem veículo ativo — é quem já pode entrar num aviso.
         * `nao`: só quem não tem, que é a fila de quem falta completar.
         */
        withVehicle: z15.enum(["sim", "nao"]).optional()
      }),
      responses: { 200: paginated(thirdPartyWithVehiclesSchema), ...defaultErrors },
      summary: "Busca terceiros por nome, telefone ou placa, com os ve\xEDculos ativos de cada um"
    },
    getThirdParty: {
      method: "GET",
      path: "/third-parties/:id",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: thirdPartyWithVehiclesSchema, ...defaultErrors },
      summary: "Um terceiro e os ve\xEDculos dele"
    },
    updateThirdParty: {
      method: "PATCH",
      path: "/third-parties/:id",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: thirdPartyWriteSchema.partial(),
      responses: { 200: thirdPartySchema, ...defaultErrors },
      summary: "Corrige nome, telefone ou e-mail de um terceiro"
    },
    createThirdPartyVehicle: {
      method: "POST",
      path: "/third-parties/:id/vehicles",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: createThirdPartyVehicleSchema,
      responses: { 201: thirdPartyVehicleSchema, ...defaultErrors },
      summary: "Cadastra o ve\xEDculo de um terceiro"
    },
    updateThirdPartyVehicle: {
      method: "PATCH",
      path: "/third-parties/:id/vehicles/:vehicleId",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        vehicleId: z15.coerce.number().int()
      }),
      /*
       * `vehicleId` liga ou desfaz a ligação com o carro do núcleo; `null`
       * desfaz. Os dois juntos valem: é um ato de correção do cadastro.
       */
      body: z15.object({
        archived: z15.boolean().optional(),
        vehicleId: z15.number().int().nullable().optional()
      }).refine((b) => b.archived !== void 0 || b.vehicleId !== void 0, {
        message: "Informe o que muda: archived ou vehicleId."
      }),
      responses: { 200: thirdPartyVehicleSchema, ...defaultErrors },
      summary: "Arquiva, reativa ou liga ao n\xFAcleo o ve\xEDculo de um terceiro"
    },
    /*
     * `POST` com a placa no CORPO, e não no caminho: a placa é a pessoa por
     * outro nome, e no endereço ela vai para log e histórico do navegador
     * (`contract.test.ts`). Só lê.
     */
    findCoreVehiclesByPlate: {
      method: "POST",
      path: "/third-parties/vehicles/core-matches",
      body: z15.object({ plate: z15.string().trim().min(7).max(10) }),
      responses: { 200: z15.array(coreVehicleMatchSchema), ...defaultErrors },
      summary: "Procura no n\xFAcleo os ve\xEDculos com a placa \u2014 o terceiro que tamb\xE9m \xE9 associado"
    },
    listCompanies: {
      method: "GET",
      path: "/companies",
      query: paginationQuery.extend({
        type: companyTypeSchema.optional(),
        /** Só os tipos daquele grupo — a tela de Prestadores e a de Instituições. */
        category: companyCategorySchema.optional(),
        search: searchTerm,
        /** Arquivada fica de fora por omissão: não é quem se escolhe num sinistro novo. */
        includeArchived: z15.enum(["true", "false"]).optional()
      }),
      responses: { 200: paginated(companySchema), ...defaultErrors },
      summary: "Lista prestadores e institui\xE7\xF5es \u2014 por grupo, tipo ou busca"
    },
    createCompany: {
      method: "POST",
      path: "/companies",
      body: companyWriteSchema,
      responses: { 201: companySchema, ...defaultErrors },
      summary: "Cadastra uma empresa de apoio"
    },
    getCompany: {
      method: "GET",
      path: "/companies/:id",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: companySchema, ...defaultErrors },
      summary: "Uma empresa de apoio"
    },
    updateCompany: {
      method: "PATCH",
      path: "/companies/:id",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: companyWriteSchema.partial().extend({ archived: z15.boolean().optional() }),
      responses: { 200: companySchema, ...defaultErrors },
      summary: "Edita ou arquiva uma empresa de apoio"
    },
    listClaims: {
      method: "GET",
      path: "",
      query: paginationQuery.extend({
        /** Qualquer atributo da análise, sem acento e sem caixa (`claims.search.ts`). */
        search: z15.string().trim().max(120).optional(),
        currentDepartment: claimDepartmentSchema.optional(),
        currentPhase: z15.string().optional(),
        /**
         * As etapas do supervisor do Reparo (29/10/2026): `somente` é o quadro
         * de Supervisão › Supervisão do Reparo; `fora`, o quadro do Reparo.
         */
        supervisionStages: z15.enum(["somente", "fora"]).optional(),
        /**
         * `true`: só os parados (pendência ou pedido de desistência); `false`:
         * só os que não estão parados. Ausente, os dois. As esteiras alternam
         * entre os dois recortes (30/10/2026).
         */
        onHold: z15.enum(["true", "false"]).optional(),
        /** Só os sinistros nesta situação dentro da etapa (`ANALYSIS_SITUATIONS`). */
        situation: z15.string().trim().max(60).optional(),
        /** Só os sinistros com este resultado da CAF (`CAF_STATUSES`, 16/10/2026). */
        cafStatus: z15.string().trim().max(40).optional(),
        type: claimTypeSchema.optional(),
        /** `true` inclui os arquivados; por padrão o quadro mostra só os ativos. */
        includeArchived: z15.enum(["true"]).optional(),
        /** Só os sinistros que têm esta etiqueta agora. */
        labelId: z15.coerce.number().int().positive().optional(),
        /**
         * O recorte por prazo (07/10/2026): o SLA da fase expirado ou perto
         * de expirar, o vencimento passado, nas próximas 24h, ou ausente.
         */
        deadline: claimDeadlineFilterSchema.optional()
      }),
      responses: { 200: paginated(claimSummarySchema), ...defaultErrors },
      summary: "O quadro de sinistros"
    },
    createClaim: {
      method: "POST",
      path: "",
      body: createClaimSchema,
      responses: { 201: claimSchema, ...defaultErrors },
      summary: "Abre um sinistro \u2014 do associado ou de um terceiro j\xE1 cadastrado"
    },
    getClaim: {
      method: "GET",
      path: "/:id",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: claimSchema, ...defaultErrors },
      summary: "Um sinistro, e onde ele est\xE1"
    },
    updateClaim: {
      method: "PATCH",
      path: "/:id",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: updateClaimSchema,
      responses: { 200: claimSchema, ...defaultErrors },
      summary: "Corrige o que foi contado na abertura, ou arquiva o sinistro"
    },
    /**
     * Envia um arquivo ao aviso. O sinistro precisa existir antes: o
     * formulário abre o aviso e depois sobe os arquivos com o `id` dele, como
     * o compositor das demandas.
     */
    attachNoticeDocument: {
      method: "POST",
      path: "/:id/documents",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: attachClaimDocumentSchema,
      responses: { 201: claimDocumentSchema, ...defaultErrors },
      summary: "Envia um arquivo ao aviso \u2014 foto, v\xEDdeo ou qualquer outro"
    },
    /**
     * A URL assinada de um arquivo do sinistro, de validade curta. Arquivo de
     * outro sinistro ou de outra praça responde 404, não 403.
     */
    documentUrl: {
      method: "GET",
      path: "/:id/documents/:documentId/url",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        documentId: z15.coerce.number().int()
      }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de curta validade para um arquivo do sinistro"
    },
    /**
     * Arquiva um arquivo do aviso: some da ficha e da mesa, e a linha e o
     * objeto ficam — a operação não apaga (convenção 9). Sem reativar: mandar
     * o arquivo de novo é o caminho, e a trilha guarda os dois atos.
     */
    archiveNoticeDocument: {
      method: "POST",
      path: "/:id/documents/:documentId/archiving",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        documentId: z15.coerce.number().int()
      }),
      body: z15.object({}),
      /** Os arquivos do aviso que sobraram, na ordem da ficha. */
      responses: { 200: z15.array(claimDocumentSchema), ...defaultErrors },
      summary: "Arquiva um arquivo do aviso"
    },
    /* As etiquetas ------------------------------------------------------ */
    /**
     * O que o seletor de etiquetas do card oferece a quem pergunta: as
     * ativas de cada setor da pessoa. Quem não é de setor nenhum recebe a
     * lista vazia — e a tela diz por quê.
     */
    /* Os prazos ---------------------------------------------------------- */
    /**
     * O SLA de cada fase, por departamento (`claims_phase_deadlines`): toda
     * etapa de trabalho vem, com `deadlineHours` nulo quando não tem SLA.
     */
    listPhaseDeadlines: {
      method: "GET",
      path: "/phase-deadlines",
      responses: { 200: z15.array(phaseDeadlineSchema), ...defaultErrors },
      summary: "O SLA de cada fase do sinistro, nesta base"
    },
    /**
     * Quantos cards de cada fase do departamento estão com o SLA expirado —
     * o selo do cabeçalho da coluna. Fase sem nenhum não vem.
     */
    slaSummary: {
      method: "GET",
      path: "/sla-summary",
      query: z15.object({ department: z15.enum(SLA_DEPARTMENTS) }),
      responses: {
        200: z15.array(z15.object({ phase: z15.string(), slaExpired: z15.number().int() })),
        ...defaultErrors
      },
      summary: "Os SLAs expirados por fase de um departamento"
    },
    /**
     * Grava o SLA das fases de um departamento. Vale para quem ENTRAR na fase
     * depois — quem já está guarda o prazo com que entrou.
     */
    setPhaseDeadlines: {
      method: "PUT",
      path: "/phase-deadlines",
      body: z15.object({
        department: z15.enum(SLA_DEPARTMENTS),
        phases: z15.array(
          z15.object({
            phase: z15.string().min(1),
            deadlineHours: z15.number().int("Use horas inteiras.").positive("O SLA precisa ser maior que zero.").max(MAX_DEADLINE_HOURS, "No m\xE1ximo um ano.").nullable(),
            dayKind: z15.enum(SLA_DAY_KINDS)
          }).refine(
            (p) => p.dayKind !== "uteis" || p.deadlineHours === null || p.deadlineHours % 24 === 0,
            {
              message: "Em dias \xFAteis, o SLA \xE9 em dias inteiros.",
              path: ["deadlineHours"]
            }
          )
        ).max(50)
      }),
      responses: { 200: z15.array(phaseDeadlineSchema), ...defaultErrors },
      summary: "Grava o SLA das fases de um departamento"
    },
    /* O rodízio de responsáveis (20/10/2026) ------------------------------ */
    /** Os rodízios de todos os departamentos da base, com as fases e as pessoas. */
    listRotations: {
      method: "GET",
      path: "/rotations",
      responses: { 200: z15.array(claimRotationSchema), ...defaultErrors },
      summary: "Os rod\xEDzios de respons\xE1veis do sinistro, nesta base"
    },
    /**
     * Grava os rodízios de um departamento. Vale para o card que ENTRAR numa
     * fase depois — quem já está continua com o responsável que tem.
     */
    setRotations: {
      method: "PUT",
      path: "/rotations",
      body: setClaimRotationsSchema,
      responses: { 200: z15.array(claimRotationSchema), ...defaultErrors },
      summary: "Grava os rod\xEDzios de respons\xE1veis de um departamento"
    },
    /**
     * Quem pode entrar num rodízio: as pessoas ativas da base, sob a permissão
     * de quem configura — `/assignee-options` exige `claims.analyses:ver`, e
     * quem só configura veria a lista vazia.
     */
    listRotationAssigneeOptions: {
      method: "GET",
      path: "/rotation-assignee-options",
      responses: { 200: z15.array(claimAssigneeOptionSchema), ...defaultErrors },
      summary: "As pessoas que podem entrar num rod\xEDzio de respons\xE1veis"
    },
    listLabelOptions: {
      method: "GET",
      path: "/label-options",
      responses: { 200: z15.array(departmentLabelsSchema), ...defaultErrors },
      summary: "As etiquetas dos setores de quem pergunta"
    },
    /**
     * As etiquetas do card, do ponto de vista de quem grava: `labelIds` é o
     * estado final das dos setores DELA (`labelChanges`). As de outro setor
     * não saem. 409 `etiqueta_de_outro_setor` quando vem uma que a pessoa não
     * pode pôr.
     */
    /**
     * O vencimento do card no departamento onde ele está agora. `null` tira.
     * 409 `departamento_mudou` quando o card saiu do departamento que a tela
     * mostrava.
     */
    setClaimDueDate: {
      method: "PUT",
      path: "/:id/due-date",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: z15.object({
        /** O departamento que a tela mostrava — a trava contra gravar no errado. */
        department: z15.enum(CLAIM_DEPARTMENTS),
        dueAt: instantSchema.nullable()
      }),
      responses: { 200: claimSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Define, troca ou tira o vencimento do sinistro no departamento"
    },
    setClaimLabels: {
      method: "PUT",
      path: "/:id/labels",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: z15.object({ labelIds: z15.array(z15.number().int().positive()).max(100) }),
      responses: { 200: claimSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Grava as etiquetas do sinistro que s\xE3o dos setores de quem grava"
    },
    /* A mesa da Análise ------------------------------------------------- */
    /** Quem pode ser Analista Responsável: as pessoas ativas da base. */
    listAssigneeOptions: {
      method: "GET",
      path: "/assignee-options",
      responses: { 200: z15.array(claimAssigneeOptionSchema), ...defaultErrors },
      summary: "As pessoas que podem ser respons\xE1veis por um sinistro"
    },
    /**
     * Escolhe, troca ou tira o Analista Responsável do sinistro na Análise.
     * Trocar fecha a atribuição ativa e abre outra; nada é apagado. 409 quando
     * o sinistro não está na Análise.
     */
    setAnalysisAssignee: {
      method: "PUT",
      path: "/:id/analysis-assignee",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: setAnalysisAssigneeSchema,
      responses: { 200: claimSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Define o Analista Respons\xE1vel do sinistro na An\xE1lise"
    },
    /** Por onde o sinistro passou, da primeira passagem à aberta. */
    listPassages: {
      method: "GET",
      path: "/:id/passages",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: z15.array(claimPassageSchema), ...defaultErrors },
      summary: "As passagens de fase do sinistro, da mais antiga para a aberta"
    },
    /**
     * Move o sinistro na Análise: fecha a passagem aberta, abre a do destino e
     * atualiza onde ele está, numa transação. Avançar exige os obrigatórios da
     * fase atual; voltar não. 409 quando o sinistro já não está em `fromPhase`.
     */
    moveClaim: {
      method: "POST",
      path: "/:id/moves",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: moveClaimSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Move o sinistro de fase na An\xE1lise"
    },
    /**
     * Desfaz o último movimento: volta à fase de onde ele veio, numa passagem
     * nova — nada é apagado. Só a passagem mais recente, só quem moveu.
     */
    undoClaimMove: {
      method: "POST",
      path: "/:id/moves/undo",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: undoClaimMoveSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Desfaz o \xFAltimo movimento de fase"
    },
    /**
     * Registra uma tentativa de contato. Não muda a etapa; na Entrada, muda a
     * situação (sem resposta → aguardando retorno).
     */
    registerClaimContact: {
      method: "POST",
      path: "/:id/contacts",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: registerClaimContactSchema,
      responses: { 201: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Registra uma tentativa de contato com o associado"
    },
    /**
     * Para o sinistro na etapa: pendência ou pedido de desistência. Parado, ele
     * não avança nem encerra — o pedido de desistência só vira a Desistência.
     */
    holdClaim: {
      method: "POST",
      path: "/:id/holds",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: holdClaimSchema,
      responses: { 201: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Para o sinistro por pend\xEAncia ou pedido de desist\xEAncia"
    },
    /** Retoma o sinistro parado, na mesma etapa. */
    resumeClaim: {
      method: "POST",
      path: "/:id/holds/resume",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: resumeClaimSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Retoma o sinistro parado"
    },
    /** O formulário de uma fase da Análise — a atual, ou uma por onde ele passou. */
    getAnalysisForm: {
      method: "GET",
      path: "/:id/analysis-form/:phase",
      pathParams: z15.object({ id: z15.coerce.number().int(), phase: z15.string() }),
      responses: { 200: analysisFormSchema, ...defaultErrors },
      summary: "O formul\xE1rio de uma fase da An\xE1lise, com o \xFAltimo salvo nela"
    },
    /** Salva o formulário da fase ATUAL, numa transação, nas tabelas de assunto. */
    saveAnalysisForm: {
      method: "PUT",
      path: "/:id/analysis-form/:phase",
      pathParams: z15.object({ id: z15.coerce.number().int(), phase: z15.string() }),
      body: saveAnalysisFormSchema,
      responses: { 200: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Salva o formul\xE1rio da fase atual da An\xE1lise, ou corrige uma por onde ele j\xE1 passou"
    },
    /** Envia um arquivo a um campo de anexo da fase atual. */
    attachAnalysisFile: {
      method: "POST",
      path: "/:id/analysis-form/:phase/files/:field",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        phase: z15.string(),
        field: z15.string()
      }),
      body: attachClaimDocumentSchema,
      responses: { 201: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Envia um arquivo a um campo de anexo da fase atual, ou de uma por onde ele j\xE1 passou"
    },
    /** Arquiva um arquivo de um campo da fase atual. A linha e o objeto ficam. */
    archiveAnalysisFile: {
      method: "POST",
      path: "/:id/analysis-form/:phase/files/:field/:documentId/archiving",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        phase: z15.string(),
        field: z15.string(),
        documentId: z15.coerce.number().int()
      }),
      body: z15.object({}),
      responses: { 200: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Arquiva um arquivo de um campo da fase atual, ou de uma por onde ele j\xE1 passou"
    },
    /* A mesa do Reparo (07/10/2026) ------------------------------------- *
     * As mesmas rotas da Análise, sob a permissão do Reparo: o serviço é um
     * só (`ClaimsDeskService`), e a rota diz o departamento. */
    /** Quem pode ser o responsável do reparo: as pessoas ativas da base. */
    listRepairAssigneeOptions: {
      method: "GET",
      path: "/repair-assignee-options",
      responses: { 200: z15.array(claimAssigneeOptionSchema), ...defaultErrors },
      summary: "As pessoas que podem ser respons\xE1veis por um reparo"
    },
    /** Escolhe, troca ou tira o responsável do sinistro no Reparo. 409 fora dele. */
    setRepairAssignee: {
      method: "PUT",
      path: "/:id/repair-assignee",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: setAnalysisAssigneeSchema,
      responses: { 200: claimSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Define o respons\xE1vel do sinistro no Reparo"
    },
    /**
     * Move o sinistro no Reparo. Avançar exige os obrigatórios da etapa; voltar
     * não. Na Supervisão, só o supervisor (`claims.repairs:supervisionar`).
     */
    moveRepairClaim: {
      method: "POST",
      path: "/:id/repair-moves",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: moveClaimSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Move o sinistro de fase no Reparo"
    },
    /** Desfaz o último movimento do Reparo — inclusive o envio à Indenização. */
    undoRepairClaimMove: {
      method: "POST",
      path: "/:id/repair-moves/undo",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: undoClaimMoveSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Desfaz o \xFAltimo movimento de fase no Reparo"
    },
    /** Uma tentativa de contato no Reparo — com a oficina, o associado ou o terceiro. */
    registerRepairContact: {
      method: "POST",
      path: "/:id/repair-contacts",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: registerClaimContactSchema,
      responses: { 201: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Registra uma tentativa de contato no Reparo"
    },
    /** Para o sinistro na etapa do Reparo: pendência ou pedido de desistência. */
    holdRepairClaim: {
      method: "POST",
      path: "/:id/repair-holds",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: holdClaimSchema,
      responses: { 201: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Para o sinistro no Reparo"
    },
    /** Retoma o sinistro parado no Reparo. */
    resumeRepairClaim: {
      method: "POST",
      path: "/:id/repair-holds/resume",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: resumeClaimSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Retoma o sinistro parado no Reparo"
    },
    /** O bloco do reparo, no alto da mesa, e a gaveta dele. */
    getRepairSummary: {
      method: "GET",
      path: "/:id/repair-summary",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: repairSummarySchema, ...defaultErrors },
      summary: "A oficina, o prazo, o or\xE7amento, as pe\xE7as e as datas do reparo"
    },
    /** Os dois orçamentos item a item — o próprio e o da CILIA (03/11/2026). */
    getRepairQuotes: {
      method: "GET",
      path: "/:id/repair-quotes",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: repairQuotesSchema, ...defaultErrors },
      summary: "Os or\xE7amentos item a item do Reparo: o pr\xF3prio e o da CILIA"
    },
    /**
     * Grava um orçamento inteiro: o valor-hora e as linhas. As linhas vivas
     * são arquivadas e as novas gravadas; os totais vão para o cabeçalho.
     */
    saveRepairQuote: {
      method: "PUT",
      path: "/:id/repair-quotes/:source",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        source: z15.enum(REPAIR_QUOTE_SOURCES)
      }),
      body: saveRepairQuoteSchema,
      responses: { 200: repairQuotesSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Grava um or\xE7amento item a item do Reparo"
    },
    /** O formulário de uma etapa do Reparo — a atual, ou uma por onde ele passou. */
    getRepairForm: {
      method: "GET",
      path: "/:id/repair-form/:phase",
      pathParams: z15.object({ id: z15.coerce.number().int(), phase: z15.string() }),
      responses: { 200: analysisFormSchema, ...defaultErrors },
      summary: "O formul\xE1rio de uma etapa do Reparo"
    },
    /** Salva o formulário da etapa atual do Reparo, nas tabelas de assunto. */
    saveRepairForm: {
      method: "PUT",
      path: "/:id/repair-form/:phase",
      pathParams: z15.object({ id: z15.coerce.number().int(), phase: z15.string() }),
      body: saveAnalysisFormSchema,
      responses: { 200: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Salva o formul\xE1rio da etapa atual do Reparo, ou corrige uma anterior"
    },
    /** Envia um arquivo a um campo de anexo de uma etapa do Reparo. */
    attachRepairFile: {
      method: "POST",
      path: "/:id/repair-form/:phase/files/:field",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        phase: z15.string(),
        field: z15.string()
      }),
      body: attachClaimDocumentSchema,
      responses: { 201: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Envia um arquivo a um campo de anexo do Reparo"
    },
    /** Arquiva um arquivo de um campo de uma etapa do Reparo. */
    archiveRepairFile: {
      method: "POST",
      path: "/:id/repair-form/:phase/files/:field/:documentId/archiving",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        phase: z15.string(),
        field: z15.string(),
        documentId: z15.coerce.number().int()
      }),
      body: z15.object({}),
      responses: { 200: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Arquiva um arquivo de um campo do Reparo"
    },
    /** Todos os arquivos do sinistro, de todas as fases, sem os arquivados. */
    listClaimFiles: {
      method: "GET",
      path: "/:id/files",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: z15.array(claimFileSchema), ...defaultErrors },
      summary: "Os arquivos do sinistro, de todas as fases"
    },
    /**
     * As mensagens do sinistro — os scripts de envio, de cada departamento
     * (`CLAIM_MESSAGES`), na aba Mensagens da mesa (15/10/2026). A que a
     * integração ainda não escreveu vem com `body` nulo.
     */
    listClaimMessages: {
      method: "GET",
      path: "/:id/messages",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: z15.array(claimMessageSchema), ...defaultErrors },
      summary: "As mensagens de envio do sinistro, por departamento"
    },
    /** O link da CAF e a trava do botão de atualizá-lo (16/10/2026). */
    getCafLink: {
      method: "GET",
      path: "/:id/caf-link",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: claimCafLinkSchema, ...defaultErrors },
      summary: "O link da CAF do sinistro, e quando ele pode ser atualizado"
    },
    /** O acompanhamento da documentação da CAF, para a gaveta da Análise (20/10/2026). */
    getCafTracking: {
      method: "GET",
      path: "/:id/caf-tracking",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: claimCafTrackingSchema, ...defaultErrors },
      summary: "Os documentos e o resultado que a CAF mandou para o sinistro"
    },
    /**
     * Gera um link novo na CAF — o botão da Entrada. Trava 5 minutos depois de
     * usado (429 `caf_link_travado`); a recusa da CAF volta como 502
     * `caf_indisponivel`, com a frase dela.
     */
    refreshCafLink: {
      method: "POST",
      path: "/:id/caf-link",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: z15.object({}),
      responses: {
        200: claimCafLinkSchema,
        409: apiErrorSchema,
        429: apiErrorSchema,
        502: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Atualiza o link da CAF do sinistro"
    },
    /**
     * Busca o resultado na CAF à mão — quando a fila da chamada do webhook
     * falhou, ou para conferir. Só com transaction ID: sem ele, 409
     * `sem_transacao_caf`; quando a CAF não o acha, 409
     * `transacao_caf_nao_encontrada`.
     */
    fetchCafResult: {
      method: "POST",
      path: "/:id/caf-result",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: z15.object({}),
      responses: {
        200: claimCafLinkSchema,
        409: apiErrorSchema,
        502: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Busca na CAF o resultado da transa\xE7\xE3o do sinistro"
    },
    /** A linha do tempo: comentários e alterações, do mais novo para o mais velho. */
    listActivity: {
      method: "GET",
      path: "/:id/activity",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      responses: { 200: z15.array(claimActivitySchema), ...defaultErrors },
      summary: "A atividade do sinistro"
    },
    createComment: {
      method: "POST",
      path: "/:id/comments",
      pathParams: z15.object({ id: z15.coerce.number().int() }),
      body: writeClaimCommentSchema,
      responses: { 201: claimCommentSchema, ...defaultErrors },
      summary: "Comenta no sinistro \u2014 todos os departamentos veem"
    },
    /** Só quem escreveu edita. */
    updateComment: {
      method: "PATCH",
      path: "/:id/comments/:commentId",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        commentId: z15.coerce.number().int()
      }),
      body: writeClaimCommentSchema,
      responses: { 200: claimCommentSchema, ...defaultErrors },
      summary: "Edita o pr\xF3prio coment\xE1rio"
    },
    /** Só quem escreveu remove. Remover é arquivar: o texto nunca é apagado. */
    archiveComment: {
      method: "POST",
      path: "/:id/comments/:commentId/archiving",
      pathParams: z15.object({
        id: z15.coerce.number().int(),
        commentId: z15.coerce.number().int()
      }),
      body: z15.object({}),
      responses: { 200: claimCommentSchema, ...defaultErrors },
      summary: "Remove o pr\xF3prio coment\xE1rio"
    }
  },
  { pathPrefix: "/claims" }
);

// src/claims-peripherals.ts
import { initContract as initContract16 } from "@ts-rest/core";
import { z as z18 } from "zod";

// src/payment-links.ts
import { initContract as initContract15 } from "@ts-rest/core";
import { z as z17 } from "zod";
import {
  CARD_MAX_INSTALLMENTS,
  CHARGE_STATUSES as CHARGE_STATUSES2,
  PAYMENT_LINK_STATES,
  PAYMENT_METHODS
} from "@evogard/domain";

// src/charges.ts
import { initContract as initContract14 } from "@ts-rest/core";
import { z as z16 } from "zod";
import { CHARGE_STATUSES } from "@evogard/domain";
var c14 = initContract14();
var chargeStatusSchema = z16.enum(CHARGE_STATUSES);
var chargeSettlementSchema = z16.object({
  /** O que o associado pagou de fato — com juros e multa, se houve. */
  paidAmount: moneySchema.nullable(),
  /** O que caiu na conta, sem a tarifa do Asaas. */
  netAmount: moneySchema.nullable(),
  interestAmount: moneySchema.nullable(),
  originalAmount: moneySchema.nullable(),
  paymentDate: businessDateSchema.nullable(),
  clientPaymentDate: businessDateSchema.nullable(),
  confirmedDate: businessDateSchema.nullable(),
  /** A compensação: quando o dinheiro ficou disponível na conta. */
  creditDate: businessDateSchema.nullable(),
  /** A compensação prevista, enquanto não acontece. */
  estimatedCreditDate: businessDateSchema.nullable(),
  invoiceNumber: z16.string().nullable(),
  nossoNumero: z16.string().nullable(),
  pixTransactionId: z16.string().nullable(),
  cardBrand: z16.string().nullable(),
  cardLastDigits: z16.string().nullable(),
  /** A página pública do comprovante no Asaas. */
  receiptUrl: z16.string().nullable(),
  /** O PDF já está guardado na casa — o download é pela rota do comprovante. */
  receiptStored: z16.boolean(),
  receiptStoredAt: instantSchema.nullable()
});
var chargeSummarySchema = z16.object({
  id: z16.number().int(),
  /** A receita de que a cobrança é um pedido (29/10/2026). */
  revenueId: z16.number().int(),
  member: z16.object({
    id: z16.number().int(),
    code: z16.string(),
    name: z16.string().nullable()
  }),
  amount: moneySchema,
  dueDate: businessDateSchema,
  description: z16.string().nullable(),
  status: chargeStatusSchema,
  /** O `status` do Asaas como veio — a tela mostra quando a tradução é `desconhecida`. */
  providerStatus: z16.string().nullable(),
  /** A página onde o associado paga. Nula enquanto `emitindo` e quando `falhou`. */
  invoiceUrl: z16.string().nullable(),
  billingType: z16.string().nullable(),
  error: z16.string().nullable(),
  confirmedAt: instantSchema.nullable(),
  receivedAt: instantSchema.nullable(),
  syncedAt: instantSchema.nullable(),
  createdAt: instantSchema,
  settlement: chargeSettlementSchema.nullable()
});
var chargeMemberSchema = z16.object({
  id: z16.number().int(),
  code: z16.string(),
  name: z16.string().nullable(),
  /** O documento mascarado — a tela confirma a pessoa sem expor o número inteiro. */
  documentMasked: z16.string().nullable(),
  /** Sem documento o Asaas recusa: a tela diz isso antes de tentar. */
  hasDocument: z16.boolean()
});
var chargesContract = c14.router(
  {
    /*
     * POST, e o termo no CORPO: CPF não vai em caminho nem em query string
     * (`contract.test.ts`) — cai em log de acesso e em histórico.
     */
    findChargeMembers: {
      method: "POST",
      path: "/charges/members",
      body: z16.object({
        query: z16.string().trim().min(3, "Digite ao menos 3 caracteres.").max(120)
      }),
      responses: {
        200: z16.object({ items: z16.array(chargeMemberSchema) }),
        ...defaultErrors
      },
      summary: "Procura o associado pelo c\xF3digo, CPF ou nome"
    },
    refreshCharge: {
      method: "POST",
      path: "/charges/:id/refresh",
      pathParams: z16.object({ id: z16.coerce.number().int() }),
      body: z16.object({}).optional(),
      responses: { 200: chargeSummarySchema, ...defaultErrors },
      summary: "Rel\xEA a cobran\xE7a no Asaas agora"
    },
    chargeReceiptUrl: {
      method: "GET",
      path: "/charges/:id/receipt",
      pathParams: z16.object({ id: z16.coerce.number().int() }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "A URL assinada do PDF do comprovante guardado \u2014 cinco minutos"
    }
  },
  { pathPrefix: "/finance" }
);
var chargesWebhookContract = c14.router(
  {
    receiveAsaas: {
      method: "POST",
      path: "/asaas/:tenant",
      pathParams: z16.object({ tenant: z16.string().trim().min(1).max(40) }),
      /* O corpo é do Asaas, e ganha campos sem aviso — quem lê é o serviço. */
      body: z16.unknown(),
      responses: {
        200: z16.object({ received: z16.boolean(), duplicate: z16.boolean() }),
        /** token errado E base desconhecida: o mesmo corpo, para não enumerar praças. */
        401: z16.object({ received: z16.literal(false) })
      },
      summary: "Eventos de cobran\xE7a do Asaas"
    }
  },
  { pathPrefix: "/webhooks" }
);

// src/payment-links.ts
var c15 = initContract15();
var paymentLinkStateSchema = z17.enum(PAYMENT_LINK_STATES);
var paymentLinkMethodSchema = z17.enum(PAYMENT_METHODS);
var paymentRequestSchema = z17.object({
  requestId: z17.number().int(),
  state: paymentLinkStateSchema,
  /** A PI pedida — o líquido que a casa recebe. */
  amount: moneySchema,
  expiresAt: instantSchema,
  /** O link, só enquanto ele está aberto. */
  url: z17.string().nullable(),
  /** A cobrança viva, quando o associado já escolheu a forma. */
  charge: z17.object({
    status: z17.enum(CHARGE_STATUSES2),
    method: paymentLinkMethodSchema.nullable(),
    installments: z17.number().int().nullable(),
    /** O que o associado paga, com a taxa do cartão dentro. */
    total: moneySchema,
    fee: moneySchema.nullable(),
    /** A baixa e o comprovante, depois do pagamento. */
    settlement: chargeSettlementSchema.nullable()
  }).nullable()
});
var paymentOptionSchema = z17.object({
  method: paymentLinkMethodSchema,
  installments: z17.number().int().nullable(),
  installmentValue: moneySchema,
  total: moneySchema,
  fee: moneySchema
});
var paymentLinkViewSchema = z17.object({
  /** O código da solicitação, como a mesa o escreve (`#123`). */
  code: z17.string(),
  amount: moneySchema,
  expiresAt: instantSchema,
  state: paymentLinkStateSchema,
  /** As formas, só com o link aberto. O Pix vem primeiro. */
  options: z17.array(paymentOptionSchema),
  /** O cartão não pôde ser calculado agora: só o Pix aparece, e a página diz por quê. */
  cardUnavailable: z17.boolean(),
  /** A escolha já feita, quando há cobrança aberta ou paga. */
  current: z17.object({
    method: paymentLinkMethodSchema.nullable(),
    installments: z17.number().int().nullable(),
    total: moneySchema,
    invoiceUrl: z17.string().nullable()
  }).nullable()
});
var tokenSchema = z17.string().min(32).max(128);
var paymentLinksContract = c15.router(
  {
    viewPaymentLink: {
      method: "POST",
      path: "/view",
      body: z17.object({ token: tokenSchema }),
      responses: {
        200: paymentLinkViewSchema,
        404: defaultErrors[404],
        429: defaultErrors[400]
      },
      summary: "Mostra o valor, o c\xF3digo e as formas de pagar da PI, sem login"
    },
    choosePaymentMethod: {
      method: "POST",
      path: "/choose",
      /**
       * Só a ESCOLHA vem do corpo — a forma e o número de parcelas. O valor
       * nunca: o total é calculado pela API sobre a PI do pedido.
       */
      body: z17.object({
        token: tokenSchema,
        method: paymentLinkMethodSchema,
        installments: z17.number().int().min(1).max(CARD_MAX_INSTALLMENTS).nullable()
      }).refine(
        (b) => b.method === "pix" ? b.installments === null : b.installments !== null,
        {
          message: "No Pix n\xE3o h\xE1 parcelas; no cart\xE3o, informe em quantas vezes."
        }
      ),
      responses: {
        200: z17.object({ invoiceUrl: z17.string() }),
        400: defaultErrors[400],
        404: defaultErrors[404],
        409: defaultErrors[400],
        429: defaultErrors[400]
      },
      summary: "Cria, troca ou mant\xE9m a cobran\xE7a da PI na forma escolhida e devolve a p\xE1gina do Asaas"
    }
  },
  { pathPrefix: "/payment-links" }
);

// src/claims-peripherals.ts
import {
  PERIPHERAL_AVAILABILITIES,
  PERIPHERAL_INVOICING_TYPES,
  PERIPHERAL_ITEM_PHOTOS,
  PERIPHERAL_ITEM_TYPES,
  PERIPHERAL_PART_ORIGINS,
  PERIPHERAL_PORTAL_DESCRIPTION_MIN,
  PERIPHERAL_QUOTE_ORIGINS,
  PERIPHERAL_STAGES
} from "@evogard/domain";
var c16 = initContract16();
var idParam = z18.coerce.number().int();
var peripheralVehicleSchema = z18.object({
  id: z18.number().int(),
  /** `VE-000123`. */
  code: z18.string(),
  plate: z18.string().nullable(),
  brand: z18.string().nullable(),
  model: z18.string().nullable(),
  modelYear: z18.string().nullable(),
  memberId: z18.number().int(),
  memberName: z18.string().nullable(),
  /**
   * A situação do veículo no SGA, pelo vocabulário do núcleo — é o "Status
   * Financeiro" que o pipe pedia para conferir. Só se mostra: nada aqui decide
   * (§5.8).
   */
  status: z18.string().nullable(),
  /** O plano do veículo (`grupo_produto`) — ainda não diz a cobertura (D6). */
  plan: z18.string().nullable()
});
var peripheralSummarySchema = z18.object({
  id: z18.number().int(),
  stage: z18.enum(PERIPHERAL_STAGES),
  /** A situação dentro da etapa — a da passagem aberta. */
  situation: z18.string().nullable(),
  /** A parada — `pendencia`, `pre_desistencia` ou `juridico`. */
  hold: z18.string().nullable(),
  vehicleId: z18.number().int().nullable(),
  vehicleCode: z18.string(),
  /** Nulo quando o código ainda não foi projetado no núcleo — a tela diz isso. */
  vehicle: peripheralVehicleSchema.nullable(),
  occurrenceDescription: z18.string().nullable(),
  /** 60 ou 80 — nulo enquanto a Entrada não a informou. */
  coveragePercent: z18.number().int().nullable(),
  requestOrigin: z18.string().nullable(),
  assignee: z18.object({ id: z18.string(), name: z18.string() }).nullable(),
  itemCount: z18.number().int(),
  /**
   * As etiquetas do card que quem pergunta vê — as dos setores dele, como no
   * sinistro (`ClaimSummary.labels`).
   */
  labels: z18.array(appliedLabelSchema),
  /** O vencimento do card em Periféricos. Nulo = sem vencimento. */
  dueAt: instantSchema.nullable(),
  /**
   * O fim do SLA da etapa atual — copiado para a passagem quando o acionamento
   * entrou nela. Nulo = etapa sem SLA (15/10/2026).
   */
  phaseDeadlineAt: instantSchema.nullable(),
  /**
   * A entrada na etapa atual para o SLA: a do movimento, e não a da última
   * parada ou troca de situação (`stageEnteredAt`), que só copiam o prazo.
   */
  phaseEnteredAt: instantSchema.nullable(),
  /** Quando entrou na fase atual. */
  stageEnteredAt: instantSchema.nullable(),
  createdAt: instantSchema,
  archivedAt: instantSchema.nullable()
});
var peripheralQuoteSchema = z18.object({
  id: z18.number().int(),
  itemId: z18.number().int(),
  providerCompanyId: z18.number().int().nullable(),
  providerName: z18.string().nullable(),
  vendorName: z18.string().nullable(),
  vendorPhone: z18.string().nullable(),
  /** Nulo enquanto os valores não chegaram. */
  quotedValue: moneySchema.nullable(),
  brand: z18.string().nullable(),
  partOrigin: z18.string().nullable(),
  quoteOrigin: z18.string().nullable(),
  status: z18.string(),
  purchasePreference: z18.boolean().nullable(),
  invoicingType: z18.string().nullable(),
  availability: z18.string().nullable(),
  expectedArrival: businessDateSchema.nullable(),
  /** A de menor valor do item, entre as que não foram reprovadas — o selo "Menor valor". */
  lowest: z18.boolean(),
  createdAt: instantSchema
});
var peripheralItemSchema = z18.object({
  id: z18.number().int(),
  itemType: z18.string(),
  preExistingDamage: z18.boolean().nullable(),
  notes: z18.string().nullable(),
  /** As respostas do questionário técnico, pela coluna (`PERIPHERAL_ITEM_QUESTIONS`). */
  answers: z18.record(z18.string(), z18.string().nullable()),
  photos: z18.array(claimDocumentSchema),
  quotes: z18.array(peripheralQuoteSchema),
  createdAt: instantSchema
});
var peripheralTotalsSchema = z18.discriminatedUnion("ok", [
  z18.object({
    ok: z18.literal(true),
    totalQuoted: moneySchema,
    totalApproved: moneySchema,
    associationShare: moneySchema,
    memberShare: moneySchema,
    frozen: z18.boolean()
  }),
  z18.object({
    ok: z18.literal(false),
    reason: z18.enum(["sem_itens", "sem_cobertura", "item_sem_cotacao_aprovada"]),
    message: z18.string(),
    itemIds: z18.array(z18.number().int())
  })
]);
var peripheralSchema = peripheralSummarySchema.extend({
  requestWhatsapp: z18.string().nullable(),
  /**
   * O local onde o serviço será feito (`repair_address_id`). Hoje só o pedido
   * do portal o preenche (19/10/2026); nulo no que a mesa abriu.
   */
  repairAddress: addressSchema.nullable(),
  items: z18.array(peripheralItemSchema),
  totals: peripheralTotalsSchema,
  /** O pagamento da PI pelo link (19/10/2026). Nulo enquanto nenhum link saiu. */
  payment: paymentRequestSchema.nullable()
});
var createPeripheralSchema = z18.object({
  vehicleCode: z18.string().trim().min(1).max(40),
  occurrenceDescription: z18.string().trim().min(1).max(4e3)
});
var peripheralCoverageSchema = z18.discriminatedUnion("kind", [
  z18.object({
    kind: z18.literal("coberto"),
    /** Um de `PERIPHERAL_COVERAGE_PERCENTS`. */
    percent: z18.number().int(),
    productLabel: z18.string()
  }),
  z18.object({ kind: z18.literal("sem_periferico"), message: z18.string() }),
  z18.object({ kind: z18.literal("produtos_nao_lidos"), message: z18.string() }),
  z18.object({ kind: z18.literal("ilegivel"), message: z18.string() }),
  z18.object({ kind: z18.literal("sem_veiculo"), message: z18.string() })
]);
var peripheralVehicleCoverageQuerySchema = z18.object({
  /** Os códigos do SGA — os de uma página da busca de veículos. */
  vehicleCodes: z18.array(z18.string().trim().min(1).max(40)).min(1).max(20)
});
var peripheralVehicleCoverageSchema = z18.object({
  vehicleCode: z18.string(),
  coverage: peripheralCoverageSchema
});
var peripheralFormSchema = z18.object({
  phase: z18.string(),
  /** A atual, ou uma etapa anterior que se corrige (15/10/2026). */
  editable: z18.boolean(),
  /**
   * Na correção de uma etapa anterior, os campos que são da passagem ABERTA —
   * a situação —, que não se corrigem: a tela os mostra travados.
   */
  lockedFields: z18.array(z18.string()),
  values: z18.record(z18.string(), z18.string().nullable()),
  /** Quantos comentários cada campo de observação tem na fase — só os DAQUELE campo (20/10/2026). */
  counts: z18.record(z18.string(), z18.number().int()),
  /** Os comentários de cada campo de observação na fase, como no sinistro (20/10/2026). */
  comments: z18.record(z18.string(), z18.array(claimCommentSchema)),
  files: z18.record(z18.string(), z18.array(claimDocumentSchema)),
  assignees: z18.array(z18.object({ id: z18.string(), name: z18.string() })),
  situation: z18.string().nullable(),
  hold: z18.object({ type: z18.string(), text: z18.string().nullable(), since: instantSchema }).nullable(),
  /**
   * Os números e e-mails que a tentativa de contato oferece (19/10/2026): o
   * WhatsApp do pedido primeiro, depois o cadastro do dono do veículo.
   */
  contactSuggestions: z18.object({
    phones: z18.array(z18.string()),
    emails: z18.array(z18.string())
  })
});
var savePeripheralFormSchema = z18.object({
  values: z18.record(z18.string(), z18.string().nullable())
});
var movePeripheralSchema = z18.object({
  fromPhase: z18.string().min(1),
  toPhase: z18.string().min(1)
});
var peripheralMoveResultSchema = z18.object({
  peripheralId: z18.number().int(),
  stage: z18.enum(PERIPHERAL_STAGES),
  passage: claimPassageSchema
});
var answerValue = z18.string().trim().max(120).nullable();
var peripheralItemWriteSchema = z18.object({
  itemType: z18.enum(Object.keys(PERIPHERAL_ITEM_TYPES)),
  preExistingDamage: z18.boolean().nullable().optional(),
  notes: z18.string().trim().max(4e3).nullable().optional(),
  answers: z18.record(z18.string(), answerValue).optional()
});
var enumOf = (o) => z18.enum(Object.keys(o));
var peripheralQuoteWriteSchema = z18.object({
  providerCompanyId: z18.number().int().positive().nullable().optional(),
  vendorName: z18.string().trim().max(200).nullable().optional(),
  vendorPhone: z18.string().trim().max(40).nullable().optional(),
  quotedValue: moneySchema.nullable().optional(),
  brand: z18.string().trim().max(200).nullable().optional(),
  partOrigin: enumOf(PERIPHERAL_PART_ORIGINS).nullable().optional(),
  quoteOrigin: enumOf(PERIPHERAL_QUOTE_ORIGINS).nullable().optional(),
  purchasePreference: z18.boolean().nullable().optional(),
  invoicingType: enumOf(PERIPHERAL_INVOICING_TYPES).nullable().optional(),
  availability: enumOf(PERIPHERAL_AVAILABILITIES).nullable().optional(),
  expectedArrival: businessDateSchema.nullable().optional()
});
var peripheralQuoteDecisionSchema = z18.object({
  decision: z18.enum(["aprovado", "reprovado"])
});
var attachPeripheralFileSchema = attachClaimDocumentSchema.extend({
  phase: z18.string().min(1),
  field: z18.string().min(1)
});
var attachPeripheralItemPhotoSchema = attachClaimDocumentSchema.extend({
  kind: enumOf(PERIPHERAL_ITEM_PHOTOS)
});
var claimPeripheralsContract = c16.router(
  {
    listPeripherals: {
      method: "GET",
      path: "",
      query: paginationQuery.extend({
        search: z18.string().trim().max(120).optional(),
        stage: z18.enum(PERIPHERAL_STAGES).optional(),
        includeArchived: z18.enum(["true", "false"]).optional(),
        /** Só os acionamentos que têm esta etiqueta agora. */
        labelId: z18.coerce.number().int().positive().optional(),
        /** O recorte por prazo — o mesmo do quadro do sinistro. */
        deadline: claimDeadlineFilterSchema.optional(),
        /**
         * `true`: só os parados (pendência ou pedido de desistência); `false`:
         * só os que não estão parados. Ausente, os dois. As esteiras alternam
         * entre os dois recortes (30/10/2026).
         */
        onHold: z18.enum(["true", "false"]).optional()
      }),
      responses: { 200: paginated(peripheralSummarySchema), ...defaultErrors },
      summary: "Lista os acionamentos de Perif\xE9ricos \u2014 por fase, busca e arquivados"
    },
    createPeripheral: {
      method: "POST",
      path: "",
      body: createPeripheralSchema,
      responses: { 201: peripheralSchema, ...defaultErrors },
      summary: "Abre um acionamento de Perif\xE9ricos, na Entrada"
    },
    listPeripheralVehicleCoverage: {
      method: "POST",
      path: "/vehicle-coverage",
      body: peripheralVehicleCoverageQuerySchema,
      responses: { 200: z18.array(peripheralVehicleCoverageSchema), ...defaultErrors },
      summary: "A cobertura de Perif\xE9ricos de cada ve\xEDculo, lida do contrato \u2014 quem pode abrir acionamento"
    },
    /** As etiquetas dos setores de quem pergunta — o filtro do quadro e o seletor da mesa. */
    listPeripheralLabelOptions: {
      method: "GET",
      path: "/label-options",
      responses: { 200: z18.array(departmentLabelsSchema), ...defaultErrors },
      summary: "As etiquetas dos setores de quem pergunta"
    },
    /** Quantos acionamentos de cada etapa estão com o SLA expirado — o cabeçalho da coluna. */
    peripheralSlaSummary: {
      method: "GET",
      path: "/sla-summary",
      responses: {
        200: z18.array(z18.object({ phase: z18.string(), slaExpired: z18.number().int() })),
        ...defaultErrors
      },
      summary: "Os SLAs expirados por etapa de Perif\xE9ricos"
    },
    listPeripheralAssigneeOptions: {
      method: "GET",
      path: "/assignee-options",
      responses: {
        200: z18.array(z18.object({ id: z18.string(), name: z18.string() })),
        ...defaultErrors
      },
      summary: "Quem pode ser respons\xE1vel por um acionamento"
    },
    /**
     * Escolhe, troca ou tira o responsável do acionamento — o seletor do topo
     * da mesa, como o do sinistro (15/10/2026). Trocar fecha a atribuição
     * ativa e abre outra; nada é apagado.
     */
    setPeripheralAssignee: {
      method: "PUT",
      path: "/:id/assignee",
      pathParams: z18.object({ id: idParam }),
      body: setAnalysisAssigneeSchema,
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Define o respons\xE1vel do acionamento"
    },
    getPeripheral: {
      method: "GET",
      path: "/:id",
      pathParams: z18.object({ id: idParam }),
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Um acionamento: o ve\xEDculo, os itens, as cota\xE7\xF5es e a conta"
    },
    regeneratePaymentLink: {
      method: "POST",
      path: "/:id/payment-link",
      pathParams: z18.object({ id: idParam }),
      body: z18.object({}),
      responses: { 200: peripheralSchema, 409: defaultErrors[400], ...defaultErrors },
      summary: "Gera um link novo de pagamento da PI, arquiva o anterior e reenvia a mensagem"
    },
    paymentReceiptUrl: {
      method: "GET",
      path: "/:id/payment-receipt",
      pathParams: z18.object({ id: idParam }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "A URL assinada do comprovante da PI paga \u2014 cinco minutos"
    },
    archivePeripheral: {
      method: "POST",
      path: "/:id/archiving",
      pathParams: z18.object({ id: idParam }),
      body: z18.object({}),
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Arquiva um acionamento"
    },
    listPeripheralPassages: {
      method: "GET",
      path: "/:id/passages",
      pathParams: z18.object({ id: idParam }),
      responses: { 200: z18.array(claimPassageSchema), ...defaultErrors },
      summary: "Por onde o acionamento passou"
    },
    movePeripheral: {
      method: "POST",
      path: "/:id/moves",
      pathParams: z18.object({ id: idParam }),
      body: movePeripheralSchema,
      responses: { 200: peripheralMoveResultSchema, ...defaultErrors },
      summary: "Move o acionamento de fase"
    },
    holdPeripheral: {
      method: "POST",
      path: "/:id/holds",
      pathParams: z18.object({ id: idParam }),
      body: holdClaimSchema,
      responses: { 200: peripheralMoveResultSchema, ...defaultErrors },
      summary: "Para o acionamento: uma pend\xEAncia, o jur\xEDdico ou o pedido de desist\xEAncia"
    },
    /**
     * Registra uma tentativa de contato (19/10/2026), como na Análise: não
     * muda a etapa, e entra na Atividade. Com a oficina não há — o Periférico
     * não tem oficina do reparo —, e `companyId` é recusado.
     */
    registerPeripheralContact: {
      method: "POST",
      path: "/:id/contacts",
      pathParams: z18.object({ id: idParam }),
      body: registerClaimContactSchema,
      responses: { 201: peripheralMoveResultSchema, ...defaultErrors },
      summary: "Registra uma tentativa de contato com o associado"
    },
    resumePeripheral: {
      method: "POST",
      path: "/:id/resumption",
      pathParams: z18.object({ id: idParam }),
      body: resumeClaimSchema,
      responses: { 200: peripheralMoveResultSchema, ...defaultErrors },
      summary: "Retoma o acionamento parado"
    },
    getPeripheralForm: {
      method: "GET",
      path: "/:id/forms/:phase",
      pathParams: z18.object({ id: idParam, phase: z18.string() }),
      responses: { 200: peripheralFormSchema, ...defaultErrors },
      summary: "O formul\xE1rio de uma fase do acionamento"
    },
    savePeripheralForm: {
      method: "PUT",
      path: "/:id/forms/:phase",
      pathParams: z18.object({ id: idParam, phase: z18.string() }),
      body: savePeripheralFormSchema,
      responses: { 200: peripheralFormSchema, ...defaultErrors },
      summary: "Grava o formul\xE1rio da fase onde o acionamento est\xE1"
    },
    attachPeripheralFile: {
      method: "POST",
      path: "/:id/files",
      pathParams: z18.object({ id: idParam }),
      body: attachPeripheralFileSchema,
      responses: { 201: peripheralFormSchema, ...defaultErrors },
      summary: "Envia um arquivo a um campo de anexo da fase"
    },
    peripheralDocumentUrl: {
      method: "GET",
      path: "/:id/documents/:documentId/url",
      pathParams: z18.object({ id: idParam, documentId: idParam }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "A URL assinada de um arquivo do acionamento \u2014 cinco minutos"
    },
    archivePeripheralDocument: {
      method: "POST",
      path: "/:id/documents/:documentId/archiving",
      pathParams: z18.object({ id: idParam, documentId: idParam }),
      body: z18.object({}),
      responses: { 200: z18.object({ archived: z18.literal(true) }), ...defaultErrors },
      summary: "Arquiva um arquivo do acionamento \u2014 a linha e o objeto ficam"
    },
    /** Todos os arquivos do acionamento: os dos campos das fases e as fotos dos itens. */
    listPeripheralFiles: {
      method: "GET",
      path: "/:id/files",
      pathParams: z18.object({ id: idParam }),
      responses: { 200: z18.array(claimFileSchema), ...defaultErrors },
      summary: "Os arquivos do acionamento, de todas as fases e de todos os itens"
    },
    /** A linha do tempo: comentários e alterações, do mais novo para o mais velho. */
    listPeripheralActivity: {
      method: "GET",
      path: "/:id/activity",
      pathParams: z18.object({ id: idParam }),
      responses: { 200: z18.array(claimActivitySchema), ...defaultErrors },
      summary: "A atividade do acionamento"
    },
    createPeripheralComment: {
      method: "POST",
      path: "/:id/comments",
      pathParams: z18.object({ id: idParam }),
      body: writeClaimCommentSchema,
      responses: { 201: claimCommentSchema, ...defaultErrors },
      summary: "Comenta no acionamento"
    },
    /** Só quem escreveu edita. */
    updatePeripheralComment: {
      method: "PATCH",
      path: "/:id/comments/:commentId",
      pathParams: z18.object({ id: idParam, commentId: idParam }),
      body: writeClaimCommentSchema,
      responses: { 200: claimCommentSchema, ...defaultErrors },
      summary: "Edita o pr\xF3prio coment\xE1rio"
    },
    /** Só quem escreveu remove. Remover é arquivar: o texto nunca é apagado. */
    archivePeripheralComment: {
      method: "POST",
      path: "/:id/comments/:commentId/archiving",
      pathParams: z18.object({ id: idParam, commentId: idParam }),
      body: z18.object({}),
      responses: { 200: claimCommentSchema, ...defaultErrors },
      summary: "Remove o pr\xF3prio coment\xE1rio"
    },
    /**
     * As etiquetas do card, do ponto de vista de quem grava: `labelIds` é o
     * estado final das dos setores DELA. As de outro setor não saem.
     */
    setPeripheralLabels: {
      method: "PUT",
      path: "/:id/labels",
      pathParams: z18.object({ id: idParam }),
      body: z18.object({ labelIds: z18.array(z18.number().int().positive()).max(100) }),
      responses: { 200: peripheralSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Grava as etiquetas do acionamento que s\xE3o dos setores de quem grava"
    },
    /** O vencimento do card. `null` tira. */
    setPeripheralDueDate: {
      method: "PUT",
      path: "/:id/due-date",
      pathParams: z18.object({ id: idParam }),
      body: z18.object({ dueAt: instantSchema.nullable() }),
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Define, troca ou tira o vencimento do acionamento"
    },
    createPeripheralItem: {
      method: "POST",
      path: "/:id/items",
      pathParams: z18.object({ id: idParam }),
      body: peripheralItemWriteSchema,
      responses: { 201: peripheralSchema, ...defaultErrors },
      summary: "Aciona um item \u2014 farol, lanterna, retrovisor, vidro"
    },
    updatePeripheralItem: {
      method: "PATCH",
      path: "/:id/items/:itemId",
      pathParams: z18.object({ id: idParam, itemId: idParam }),
      body: peripheralItemWriteSchema.partial(),
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Corrige um item e o question\xE1rio dele"
    },
    archivePeripheralItem: {
      method: "POST",
      path: "/:id/items/:itemId/archiving",
      pathParams: z18.object({ id: idParam, itemId: idParam }),
      body: z18.object({}),
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Tira um item do acionamento \u2014 a linha fica"
    },
    attachPeripheralItemPhoto: {
      method: "POST",
      path: "/:id/items/:itemId/photos",
      pathParams: z18.object({ id: idParam, itemId: idParam }),
      body: attachPeripheralItemPhotoSchema,
      responses: { 201: peripheralSchema, ...defaultErrors },
      summary: "Envia uma foto do item"
    },
    createPeripheralQuote: {
      method: "POST",
      path: "/:id/items/:itemId/quotes",
      pathParams: z18.object({ id: idParam, itemId: idParam }),
      body: peripheralQuoteWriteSchema,
      responses: { 201: peripheralSchema, ...defaultErrors },
      summary: "Cadastra uma cota\xE7\xE3o do item"
    },
    updatePeripheralQuote: {
      method: "PATCH",
      path: "/:id/quotes/:quoteId",
      pathParams: z18.object({ id: idParam, quoteId: idParam }),
      body: peripheralQuoteWriteSchema,
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Corrige uma cota\xE7\xE3o \u2014 os valores que chegaram"
    },
    decidePeripheralQuote: {
      method: "POST",
      path: "/:id/quotes/:quoteId/decision",
      pathParams: z18.object({ id: idParam, quoteId: idParam }),
      body: peripheralQuoteDecisionSchema,
      responses: { 200: peripheralSchema, ...defaultErrors },
      summary: "Aprova ou reprova uma cota\xE7\xE3o \u2014 s\xF3 na Aprova\xE7\xE3o do Supervisor, uma aprovada por item"
    }
  },
  { pathPrefix: "/claims/peripherals" }
);
var portalPeripheralVehicleSchema = z18.object({
  /** `VE-000123` — o código da casa, que o pedido devolve. */
  code: z18.string(),
  plate: z18.string().nullable(),
  brand: z18.string().nullable(),
  model: z18.string().nullable(),
  modelYear: z18.string().nullable(),
  /** O tipo do SGA ("AUTOMOVEL", "MOTOCICLETA"…) — só para o ícone. */
  type: z18.string().nullable(),
  coverage: peripheralCoverageSchema
});
var portalMemberAddressSchema = z18.object({
  zipCode: z18.string().nullable(),
  state: z18.string().nullable(),
  city: z18.string().nullable(),
  neighborhood: z18.string().nullable(),
  street: z18.string().nullable(),
  number: z18.string().nullable(),
  complement: z18.string().nullable()
});
var portalPeripheralStartSchema = z18.discriminatedUnion("state", [
  z18.object({ state: z18.literal("sem_vinculo") }),
  z18.object({
    state: z18.literal("lida"),
    /** O celular do cadastro — o associado pode trocar só para este pedido. */
    whatsapp: z18.string().nullable(),
    email: z18.string().nullable(),
    /** Nulo quando o cadastro não tem cidade nem rua. */
    address: portalMemberAddressSchema.nullable(),
    vehicles: z18.array(portalPeripheralVehicleSchema)
  })
]);
var portalPeripheralUploadSchema = z18.object({
  token: z18.string(),
  fileName: z18.string(),
  sizeBytes: z18.number().int()
});
var uploadToken2 = z18.string().min(16).max(4e3);
var portalRepairPlaceSchema = z18.discriminatedUnion("kind", [
  z18.object({ kind: z18.literal("cadastro") }),
  z18.object({
    kind: z18.literal("informado"),
    zipCode: z18.string().trim().max(12).nullable(),
    /** Rua, número, cidade e UF são obrigatórios — o servidor recusa sem eles. */
    street: z18.string().trim().max(200),
    /** Texto, e não número: "S/N" e "120-A" existem. */
    number: z18.string().trim().max(20),
    complement: z18.string().trim().max(120).nullable(),
    neighborhood: z18.string().trim().max(120).nullable(),
    city: z18.string().trim().max(120),
    state: z18.string().trim().max(2),
    /** O link do mapa, opcional — ajuda quem vai até o carro. */
    locationLink: z18.string().trim().max(500).nullable()
  })
]);
var portalPeripheralItemSchema = z18.object({
  itemType: z18.enum(Object.keys(PERIPHERAL_ITEM_TYPES)),
  /** As respostas, pela coluna — códigos de opção do questionário. */
  answers: z18.record(z18.string(), z18.string().trim().max(120)),
  /** As três fotos do item, pelo `kind` (`PERIPHERAL_PORTAL_ITEM_PHOTOS`). */
  photos: z18.object({
    foto_peca_toda: uploadToken2,
    foto_item_angulo_1: uploadToken2,
    foto_item_angulo_2: uploadToken2
  })
});
var createPortalPeripheralSchema = z18.object({
  /** O `code` de um veículo de `start` — `VE-000123`. */
  vehicleCode: z18.string().trim().min(1).max(40),
  whatsapp: z18.string().trim().min(8).max(40),
  occurrenceDescription: z18.string().trim().min(PERIPHERAL_PORTAL_DESCRIPTION_MIN).max(4e3),
  frontPhoto: uploadToken2,
  budget: uploadToken2.nullable(),
  repairPlace: portalRepairPlaceSchema,
  /** "Meu WhatsApp e meu e-mail estão certos" — sem a marca, não envia. */
  contactConfirmed: z18.literal(true),
  items: z18.array(portalPeripheralItemSchema).min(1).max(12)
});
var portalPeripheralSummarySchema = z18.object({
  id: z18.number().int(),
  stage: z18.enum(PERIPHERAL_STAGES),
  plate: z18.string().nullable(),
  model: z18.string().nullable(),
  itemCount: z18.number().int(),
  createdAt: instantSchema
});
var claimPeripheralsPortalContract = c16.router(
  {
    start: {
      method: "GET",
      path: "/start",
      responses: { 200: portalPeripheralStartSchema, ...defaultErrors },
      summary: "Os ve\xEDculos do associado da sess\xE3o, com a cobertura de Perif\xE9ricos"
    },
    upload: {
      method: "POST",
      path: "/attachments",
      body: attachClaimDocumentSchema,
      responses: { 201: portalPeripheralUploadSchema, ...defaultErrors },
      summary: "Envia uma foto ou o or\xE7amento do pedido, antes de abrir"
    },
    create: {
      method: "POST",
      path: "",
      body: createPortalPeripheralSchema,
      responses: { 201: portalPeripheralSummarySchema, ...defaultErrors },
      summary: "O associado abre um acionamento de Perif\xE9ricos"
    },
    list: {
      method: "GET",
      path: "",
      responses: { 200: z18.array(portalPeripheralSummarySchema), ...defaultErrors },
      summary: "Os acionamentos de Perif\xE9ricos do associado da sess\xE3o"
    }
  },
  { pathPrefix: "/portal/claims/peripherals" }
);

// src/claims-compensation.ts
import { initContract as initContract17 } from "@ts-rest/core";
import { z as z19 } from "zod";
import { EXPENSE_STAGES } from "@evogard/domain";
var c17 = initContract17();
var compensationDocumentFileSchema = z19.object({
  id: z19.number().int(),
  fileName: z19.string(),
  mimeType: z19.string(),
  createdAt: instantSchema,
  validatedAt: instantSchema.nullable(),
  validatedByName: z19.string().nullable()
});
var compensationSummarySchema = z19.object({
  fipe: z19.object({
    value: moneySchema.nullable(),
    referenceDate: businessDateSchema.nullable()
  }),
  /** O evento — o furto ou roubo muda o checklist e o prazo. */
  theft: z19.boolean(),
  /** Uma linha por débito, a PI entre eles, sem os arquivados. */
  deductions: z19.array(
    z19.object({
      id: z19.number().int(),
      /** `participacao` · `mensalidade` · `ipva` · `multa`… */
      kind: z19.string(),
      description: z19.string().nullable(),
      amount: moneySchema,
      /** `apurado` · `quitado` · `pago_pelo_associado` · `dispensado` · `retido`… */
      status: z19.string(),
      /** Desconta da indenização? Só as situações ativas (I8). */
      counts: z19.boolean(),
      /** A quem se paga — o banco, o Detran, a Sefaz. Nulo: só desconta. */
      creditorCompanyId: z19.number().int().nullable(),
      creditorName: z19.string().nullable(),
      /** O mês da mensalidade, a data da multa. */
      referenceOn: businessDateSchema.nullable(),
      /** Só na depreciação: `leilao` · `chassi_remarcado` · `pcd`… */
      reason: z19.string().nullable(),
      notes: z19.string().nullable(),
      /**
       * A despesa de onde o débito nasceu (29/10/2026): o pago a um credor
       * entra pela gaveta de despesa e vai ao Financeiro. A fase dela decide se
       * o débito desconta (`deductionCounts`), e o débito não muda pela
       * Indenização. Nulo: o desconto que a Indenização incluiu.
       */
      expense: z19.object({ id: z19.number().int(), stage: z19.enum(EXPENSE_STAGES) }).nullable(),
      /** Quantos comprovantes ativos o débito tem. */
      receipts: z19.number().int(),
      /** Os comprovantes — o extrato da consulta ao Detran, à Sefaz, ao SNG. */
      receiptFiles: z19.array(
        z19.object({
          id: z19.number().int(),
          fileName: z19.string(),
          mimeType: z19.string(),
          createdAt: instantSchema
        })
      )
    })
  ),
  account: z19.object({ deducted: moneySchema, net: moneySchema.nullable() }),
  /**
   * O prazo, da passagem pela Aguardando Documentação: a data limite e a meta
   * interna (só da equipe). Nulos até a documentação ficar completa.
   */
  deadlines: z19.object({
    dueAt: instantSchema.nullable(),
    targetAt: instantSchema.nullable()
  }),
  checklist: z19.object({
    required: z19.number().int(),
    validated: z19.number().int(),
    received: z19.number().int(),
    items: z19.array(
      z19.object({
        kind: z19.string(),
        label: z19.string(),
        state: z19.enum(["validado", "recebido", "falta", "nao_se_aplica"]),
        files: z19.array(compensationDocumentFileSchema)
      })
    )
  }),
  /** O checklist se confere agora? Só com o sinistro na Aguardando Documentação. */
  checklistEditable: z19.boolean(),
  /** Os débitos se incluem e se corrigem agora? Só com o sinistro no Pagamento de Débito. */
  debtsEditable: z19.boolean()
});
var compensationDebtWriteSchema = z19.object({
  /** `mensalidade` · `licenciamento` · `ipva` · `multa` · `gravame`… */
  kind: z19.string().min(1),
  /** Obrigatória em Outros. */
  description: z19.string().trim().max(500).nullable(),
  amount: moneySchema,
  referenceOn: businessDateSchema.nullable(),
  /** Só na depreciação. */
  reason: z19.string().nullable(),
  /** Quem recebe o pagamento. Nulo: o débito só desconta. */
  creditorCompanyId: z19.number().int().nullable(),
  notes: z19.string().trim().max(4e3).nullable()
});
var compensationDebtStatusSchema = z19.object({ status: z19.string().min(1) });
var compensationCreditorOptionSchema = z19.object({
  id: z19.number().int(),
  name: z19.string(),
  type: z19.string()
});
var compensationDocumentValidationSchema = z19.object({
  validated: z19.boolean()
});
var claimCompensationsContract = c17.router(
  {
    /** Quem pode ser o analista da Indenização: as pessoas ativas da base. */
    listCompensationAssigneeOptions: {
      method: "GET",
      path: "/compensation-assignee-options",
      responses: { 200: z19.array(claimAssigneeOptionSchema), ...defaultErrors },
      summary: "As pessoas que podem ser analistas de uma indeniza\xE7\xE3o"
    },
    /** Escolhe, troca ou tira o analista do sinistro na Indenização. 409 fora dela. */
    setCompensationAssignee: {
      method: "PUT",
      path: "/:id/compensation-assignee",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      body: setAnalysisAssigneeSchema,
      responses: { 200: claimSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Define o analista do sinistro na Indeniza\xE7\xE3o"
    },
    /**
     * Move o sinistro na Indenização. Avançar exige os obrigatórios da etapa;
     * voltar e mandar ao Acordo (o envio de volta ao Reparo) não.
     */
    moveCompensationClaim: {
      method: "POST",
      path: "/:id/compensation-moves",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      body: moveClaimSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Move o sinistro de fase na Indeniza\xE7\xE3o"
    },
    /** Desfaz o último movimento da Indenização — inclusive o envio ao Acordo. */
    undoCompensationClaimMove: {
      method: "POST",
      path: "/:id/compensation-moves/undo",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      body: undoClaimMoveSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Desfaz o \xFAltimo movimento de fase na Indeniza\xE7\xE3o"
    },
    /** Uma tentativa de contato na Indenização, com o associado ou o terceiro. */
    registerCompensationContact: {
      method: "POST",
      path: "/:id/compensation-contacts",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      body: registerClaimContactSchema,
      responses: { 201: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Registra uma tentativa de contato na Indeniza\xE7\xE3o"
    },
    /** Para o sinistro na etapa da Indenização: pendência (com o tipo) ou pedido de desistência. */
    holdCompensationClaim: {
      method: "POST",
      path: "/:id/compensation-holds",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      body: holdClaimSchema,
      responses: { 201: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Para o sinistro na Indeniza\xE7\xE3o"
    },
    /** Retoma o sinistro parado na Indenização. */
    resumeCompensationClaim: {
      method: "POST",
      path: "/:id/compensation-holds/resume",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      body: resumeClaimSchema,
      responses: { 200: claimMoveResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Retoma o sinistro parado na Indeniza\xE7\xE3o"
    },
    /** O bloco do alto da mesa e a gaveta dele: a conta, o prazo e o checklist. */
    getCompensationSummary: {
      method: "GET",
      path: "/:id/compensation-summary",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      responses: { 200: compensationSummarySchema, ...defaultErrors },
      summary: "A conta, o prazo e o checklist de documentos da indeniza\xE7\xE3o"
    },
    /**
     * Confere (ou desfaz a conferência de) um documento do checklist. Só na
     * Aguardando Documentação, e a situação da etapa acompanha (I6).
     */
    setCompensationDocumentValidation: {
      method: "POST",
      path: "/:id/compensation-documents/:documentId/validation",
      pathParams: z19.object({
        id: z19.coerce.number().int(),
        documentId: z19.coerce.number().int()
      }),
      body: compensationDocumentValidationSchema,
      responses: {
        200: compensationSummarySchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Confere um documento do checklist da indeniza\xE7\xE3o"
    },
    /** Os credores que um débito pode ter — os órgãos e as instituições da base. */
    listCompensationCreditorOptions: {
      method: "GET",
      path: "/compensation-creditor-options",
      responses: { 200: z19.array(compensationCreditorOptionSchema), ...defaultErrors },
      summary: "Os credores que um d\xE9bito da indeniza\xE7\xE3o pode ter"
    },
    /** Inclui um débito. Só no Pagamento de Débito (409 fora dele). */
    createCompensationDebt: {
      method: "POST",
      path: "/:id/compensation-debts",
      pathParams: z19.object({ id: z19.coerce.number().int() }),
      body: compensationDebtWriteSchema,
      responses: {
        201: compensationSummarySchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Inclui um d\xE9bito da indeniza\xE7\xE3o"
    },
    /** Corrige um débito — o antes e o depois vão para a Atividade. */
    updateCompensationDebt: {
      method: "PUT",
      path: "/:id/compensation-debts/:debtId",
      pathParams: z19.object({
        id: z19.coerce.number().int(),
        debtId: z19.coerce.number().int()
      }),
      body: compensationDebtWriteSchema,
      responses: {
        200: compensationSummarySchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Corrige um d\xE9bito da indeniza\xE7\xE3o"
    },
    /** Muda a situação do débito: apurado, pago pelo associado ou dispensado. */
    setCompensationDebtStatus: {
      method: "POST",
      path: "/:id/compensation-debts/:debtId/status",
      pathParams: z19.object({
        id: z19.coerce.number().int(),
        debtId: z19.coerce.number().int()
      }),
      body: compensationDebtStatusSchema,
      responses: {
        200: compensationSummarySchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Muda a situa\xE7\xE3o de um d\xE9bito da indeniza\xE7\xE3o"
    },
    /** Anexa um comprovante ao débito — `comprovante_debito`, com `deduction_id`. */
    attachCompensationDebtReceipt: {
      method: "POST",
      path: "/:id/compensation-debts/:debtId/receipts",
      pathParams: z19.object({
        id: z19.coerce.number().int(),
        debtId: z19.coerce.number().int()
      }),
      body: attachClaimDocumentSchema,
      responses: {
        201: compensationSummarySchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Anexa um comprovante a um d\xE9bito da indeniza\xE7\xE3o"
    },
    /** Arquiva um comprovante do débito. */
    archiveCompensationDebtReceipt: {
      method: "POST",
      path: "/:id/compensation-debts/:debtId/receipts/:documentId/archiving",
      pathParams: z19.object({
        id: z19.coerce.number().int(),
        debtId: z19.coerce.number().int(),
        documentId: z19.coerce.number().int()
      }),
      body: z19.object({}),
      responses: {
        200: compensationSummarySchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Arquiva um comprovante de um d\xE9bito da indeniza\xE7\xE3o"
    },
    /** O formulário de uma etapa da Indenização — a atual, ou uma por onde ele passou. */
    getCompensationForm: {
      method: "GET",
      path: "/:id/compensation-form/:phase",
      pathParams: z19.object({ id: z19.coerce.number().int(), phase: z19.string() }),
      responses: { 200: analysisFormSchema, ...defaultErrors },
      summary: "O formul\xE1rio de uma etapa da Indeniza\xE7\xE3o"
    },
    /** Salva o formulário da etapa atual da Indenização, nas tabelas de assunto. */
    saveCompensationForm: {
      method: "PUT",
      path: "/:id/compensation-form/:phase",
      pathParams: z19.object({ id: z19.coerce.number().int(), phase: z19.string() }),
      body: saveAnalysisFormSchema,
      responses: { 200: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Salva o formul\xE1rio da etapa atual da Indeniza\xE7\xE3o, ou corrige uma anterior"
    },
    /** Envia um arquivo a um campo de anexo de uma etapa da Indenização. */
    attachCompensationFile: {
      method: "POST",
      path: "/:id/compensation-form/:phase/files/:field",
      pathParams: z19.object({
        id: z19.coerce.number().int(),
        phase: z19.string(),
        field: z19.string()
      }),
      body: attachClaimDocumentSchema,
      responses: { 201: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Envia um arquivo a um campo de anexo da Indeniza\xE7\xE3o"
    },
    /** Arquiva um arquivo de um campo de uma etapa da Indenização. */
    archiveCompensationFile: {
      method: "POST",
      path: "/:id/compensation-form/:phase/files/:field/:documentId/archiving",
      pathParams: z19.object({
        id: z19.coerce.number().int(),
        phase: z19.string(),
        field: z19.string(),
        documentId: z19.coerce.number().int()
      }),
      body: z19.object({}),
      responses: { 200: analysisFormSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Arquiva um arquivo de um campo da Indeniza\xE7\xE3o"
    }
  },
  { pathPrefix: "/claims" }
);

// src/claims-closures.ts
import { initContract as initContract18 } from "@ts-rest/core";
import { z as z20 } from "zod";
import { CLAIM_CLOSURE_KINDS, CLAIM_CLOSURE_ORIGINS } from "@evogard/domain";
var c18 = initContract18();
var claimClosureKindSchema = z20.enum(CLAIM_CLOSURE_KINDS);
var claimClosureOriginSchema = z20.enum(CLAIM_CLOSURE_ORIGINS);
var claimClosureSourceSchema = z20.enum(["sinistro", "periferico"]);
var claimClosureSchema = z20.object({
  source: claimClosureSourceSchema,
  id: z20.number().int(),
  /** Só no sinistro: o do associado ou o do terceiro. */
  claimType: claimTypeSchema.nullable(),
  /** No do terceiro, o sinistro do associado a que ele se liga. */
  parentClaimId: z20.number().int().nullable(),
  /** O associado, ou o terceiro no sinistro do terceiro. */
  name: z20.string().nullable(),
  plate: z20.string().nullable(),
  kind: claimClosureKindSchema,
  origin: claimClosureOriginSchema,
  /** A fase de desfecho onde ele está — "Negados com Posse", "Concluídos". */
  phase: z20.string(),
  /**
   * A fase de onde ele saiu, no mesmo departamento — é para lá que reabrir
   * devolve a negativa e a desistência.
   */
  fromPhase: z20.string().nullable(),
  closedAt: instantSchema,
  closedByName: z20.string().nullable(),
  /**
   * Por quê, em texto: o motivo do pedido de desistência, o motivo da
   * negativa (com o detalhe). Nulo no concluído, e quando não foi escrito.
   */
  reason: z20.string().nullable(),
  /** O parecer jurídico da negativa, a observação da sugestão de negativa. */
  notes: z20.string().nullable()
});
var claimClosuresQuerySchema = paginationQuery.extend({
  kind: claimClosureKindSchema.optional(),
  origin: claimClosureOriginSchema.optional(),
  /** O número, o nome ou a placa — sem acento e sem caixa. */
  search: z20.string().trim().max(120).optional(),
  /**
   * Encerrados a partir deste dia. Sem ele, os últimos
   * `CLAIM_CLOSURES_DEFAULT_DAYS` dias.
   */
  since: businessDateSchema.optional(),
  /** Até este dia, inclusive. */
  until: businessDateSchema.optional()
});
var claimClosuresPageSchema = z20.object({
  items: z20.array(claimClosureSchema),
  total: z20.number().int(),
  page: z20.number().int(),
  perPage: z20.number().int(),
  /** O período de fato aplicado — o padrão, quando não veio `since`. */
  since: businessDateSchema,
  until: businessDateSchema.nullable(),
  /**
   * Quantos de cada desfecho no período e na origem, sem o filtro de desfecho
   * — uma chave por desfecho, cada conclusão na sua (22/10/2026).
   */
  counts: z20.record(claimClosureKindSchema, z20.number().int())
});
var reopenClaimClosureSchema = z20.object({
  /** A fase de desfecho que a tela viu — se mudou, 409. */
  fromPhase: z20.string().min(1),
  /**
   * Por que volta. Na negativa e na desistência, vira comentário, e a
   * Atividade mostra; na garantia, é o "Motivo de Retorno" de Retorno/Garantia.
   */
  reason: z20.string().trim().min(1, "Escreva por que ele volta.").max(2e3)
});
var claimReopenResultSchema = z20.object({
  source: claimClosureSourceSchema,
  id: z20.number().int(),
  origin: claimClosureOriginSchema,
  phase: z20.string()
});
var claimClosuresContract = c18.router(
  {
    listClaimClosures: {
      method: "GET",
      path: "/closures",
      query: claimClosuresQuerySchema,
      responses: { 200: claimClosuresPageSchema, ...defaultErrors },
      summary: "Os sinistros e acionamentos encerrados, do mais recente ao mais antigo"
    },
    /**
     * Reabre o sinistro pelo ato que o desfecho aceita (`claimClosureActionOf`,
     * 22/10/2026): a negativa e a desistência voltam à fase de onde saíram; o
     * concluído do Reparo vai a Retorno/Garantia. Prazo novo nos dois. 409 se
     * ele não está encerrado, se é um concluído sem volta, ou se não está mais
     * na fase que a tela viu.
     */
    reopenClaim: {
      method: "POST",
      path: "/closures/:id/reopen",
      pathParams: z20.object({ id: z20.coerce.number().int() }),
      body: reopenClaimClosureSchema,
      responses: { 200: claimReopenResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Reabre um sinistro encerrado"
    },
    /**
     * Reabre o acionamento de Periféricos na fase de onde ele saiu — a negativa
     * e a desistência; o concluído não volta (409).
     */
    reopenPeripheral: {
      method: "POST",
      path: "/closures/peripherals/:id/reopen",
      pathParams: z20.object({ id: z20.coerce.number().int() }),
      body: reopenClaimClosureSchema,
      responses: { 200: claimReopenResultSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Reabre um acionamento de Perif\xE9ricos encerrado"
    }
  },
  { pathPrefix: "/claims" }
);

// src/claims-management.ts
import { initContract as initContract19 } from "@ts-rest/core";
import { z as z21 } from "zod";
import {
  BRAZILIAN_STATES,
  CLAIM_DEPARTMENTS as CLAIM_DEPARTMENTS2,
  CLAIM_FLOW_NODES,
  CLAIM_FLOW_PERIODS,
  CLAIM_FLOW_DEFAULT_PERIOD,
  EXPENSE_STAGES as EXPENSE_STAGES2,
  MANAGEMENT_LINES,
  PERIPHERAL_COVERAGE_PERCENTS,
  PERIPHERAL_PART_ORIGINS as PERIPHERAL_PART_ORIGINS2,
  PERIPHERAL_WORK_STAGES
} from "@evogard/domain";
var c19 = initContract19();
var managementLineSchema = z21.enum(MANAGEMENT_LINES);
var managementClaimSchema = z21.object({
  source: claimClosureSourceSchema,
  id: z21.number().int(),
  line: managementLineSchema,
  type: claimTypeSchema.nullable(),
  parentClaimId: z21.number().int().nullable(),
  claimType: z21.string().nullable(),
  name: z21.string().nullable(),
  plate: z21.string().nullable(),
  phase: z21.string(),
  situation: z21.string().nullable(),
  hold: z21.string().nullable(),
  stalled: z21.boolean(),
  phaseEnteredAt: instantSchema.nullable(),
  phaseDeadlineAt: instantSchema.nullable(),
  slaExpired: z21.boolean(),
  matched: z21.boolean(),
  createdAt: instantSchema
});
var statsShape = {
  total: z21.number().int(),
  slaExpired: z21.number().int(),
  thirdParty: z21.number().int(),
  onHold: z21.number().int(),
  matches: z21.number().int().nullable(),
  ages: z21.array(z21.number().int()),
  averageDays: z21.number().int(),
  oldestDays: z21.number().int()
};
var groupSchema2 = z21.object({ ...statsShape, items: z21.array(managementClaimSchema) });
var phaseSchema = z21.object({
  ...statsShape,
  phase: z21.string(),
  station: groupSchema2,
  steps: z21.array(groupSchema2.extend({ situation: z21.string() }))
});
var managementMapSchema = z21.object({
  totals: z21.object({
    open: z21.number().int(),
    fresh: z21.number().int(),
    slaExpired: z21.number().int(),
    thirdParty: z21.number().int()
  }),
  lines: z21.array(
    z21.object({
      line: managementLineSchema,
      total: z21.number().int(),
      slaExpired: z21.number().int(),
      fresh: z21.number().int(),
      thirdParty: z21.number().int(),
      phases: z21.array(phaseSchema),
      stalls: z21.array(
        z21.object({
          kind: z21.enum(["parada", "situacao"]),
          code: z21.string(),
          total: z21.number().int()
        })
      ),
      closures: z21.array(
        z21.object({ kind: claimClosureKindSchema, total: z21.number().int() })
      )
    })
  ),
  found: z21.array(managementClaimSchema),
  closuresSince: businessDateSchema
});
var managementMapQuerySchema = z21.object({
  /** O número, o nome ou a placa — e não a busca ampla dos quadros, que lê todo atributo. */
  search: z21.string().trim().max(120).optional()
});
var managementGroupQuerySchema = paginationQuery.extend({
  line: managementLineSchema,
  /** A etapa. */
  phase: z21.string().min(1),
  /**
   * O ponto do trilho: `estacao`, ou o código da situação do marco. Sem ele, a
   * etapa inteira.
   */
  step: z21.string().min(1).optional(),
  /** Só os de SLA expirado, só os de terceiro ou só os travados. */
  only: z21.enum(["sla_expirado", "terceiro", "parado"]).optional(),
  search: z21.string().trim().max(120).optional()
});
var managementGroupPageSchema = paginated(managementClaimSchema);
var claimFlowNodeSchema = z21.enum(CLAIM_FLOW_NODES);
var managementFlowSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  total: z21.number().int(),
  thirdParty: z21.number().int(),
  links: z21.array(
    z21.object({
      from: claimFlowNodeSchema,
      to: claimFlowNodeSchema,
      total: z21.number().int()
    })
  ),
  inProcess: z21.array(
    z21.object({ department: z21.enum(CLAIM_DEPARTMENTS2), total: z21.number().int() })
  )
});
var managementFlowQuerySchema = z21.object({
  /** Os últimos N dias, um de `CLAIM_FLOW_PERIODS`; ausente, o padrão. */
  days: z21.coerce.number().int().refine((d) => CLAIM_FLOW_PERIODS.includes(d)).default(CLAIM_FLOW_DEFAULT_PERIOD)
});
var managementStatesSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  total: z21.number().int(),
  states: z21.array(
    z21.object({
      state: z21.enum(BRAZILIAN_STATES),
      total: z21.number().int(),
      withThirdParty: z21.number().int()
    })
  ),
  withThirdParty: z21.number().int(),
  withoutLocation: z21.number().int(),
  unknownState: z21.number().int()
});
var managementTypesSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  total: z21.number().int(),
  types: z21.array(z21.object({ type: z21.string(), total: z21.number().int() }))
});
var managementDurationsSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  total: z21.number().int(),
  departments: z21.array(
    z21.object({
      department: z21.enum(CLAIM_DEPARTMENTS2),
      done: z21.number().int(),
      meanDays: z21.number().nullable(),
      medianDays: z21.number().nullable(),
      inProgress: z21.number().int(),
      inProgressMeanDays: z21.number().nullable()
    })
  )
});
var managementDeadlinesSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  stages: z21.array(
    z21.object({
      department: z21.enum(CLAIM_DEPARTMENTS2),
      phase: z21.string(),
      withinDeadline: z21.number().int(),
      expired: z21.number().int(),
      noDeadline: z21.number().int(),
      done: z21.number().int(),
      meanDays: z21.number().nullable(),
      medianDays: z21.number().nullable(),
      inProgressMeanDays: z21.number().nullable()
    })
  )
});
var moneyCountSchema = z21.object({ count: z21.number().int(), total: moneySchema });
var managementRepairMoneySchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  expenses: z21.object({
    requested: moneyCountSchema,
    approved: moneyCountSchema,
    paid: moneyCountSchema,
    refused: moneyCountSchema,
    byStage: z21.array(moneyCountSchema.extend({ stage: z21.enum(EXPENSE_STAGES2) }))
  }),
  quotes: z21.object({
    claims: z21.number().int(),
    withoutAmount: z21.number().int(),
    gross: moneySchema,
    approved: moneySchema,
    parts: moneySchema,
    labor: moneySchema,
    associationShare: moneySchema,
    memberShare: moneySchema
  })
});
var managementConsultantsSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  total: z21.number().int(),
  consultants: z21.array(
    z21.object({
      volunteerId: z21.number().int().nullable(),
      erpCode: z21.string().nullable(),
      name: z21.string().nullable(),
      total: z21.number().int(),
      open: z21.number().int()
    })
  ),
  withoutVehicle: z21.number().int()
});
var managementWeeksSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  today: businessDateSchema,
  weeks: z21.array(
    z21.object({
      week: businessDateSchema,
      member: z21.number().int(),
      thirdParty: z21.number().int(),
      partial: z21.boolean()
    })
  )
});
var managementExitsSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  total: z21.number().int(),
  exits: z21.array(
    z21.object({
      kind: z21.enum(["negado", "desistencia"]),
      department: z21.enum(CLAIM_DEPARTMENTS2),
      phase: z21.string().nullable(),
      total: z21.number().int()
    })
  )
});
var workStageSchema = z21.enum(PERIPHERAL_WORK_STAGES);
var partOriginKeys = Object.keys(PERIPHERAL_PART_ORIGINS2);
var managementPeripheralsSchema = z21.object({
  days: z21.number().int(),
  since: businessDateSchema,
  today: businessDateSchema,
  total: z21.number().int(),
  inProcess: z21.number().int(),
  durations: z21.array(
    z21.object({
      phase: workStageSchema,
      done: z21.number().int(),
      meanDays: z21.number().nullable(),
      medianDays: z21.number().nullable(),
      inProgress: z21.number().int(),
      inProgressMeanDays: z21.number().nullable()
    })
  ),
  deadlines: z21.array(
    z21.object({
      phase: workStageSchema,
      withinDeadline: z21.number().int(),
      expired: z21.number().int(),
      noDeadline: z21.number().int()
    })
  ),
  weeks: z21.array(
    z21.object({
      week: businessDateSchema,
      origins: z21.object({
        chatbot: z21.number().int(),
        administrativo: z21.number().int(),
        portal: z21.number().int(),
        unknown: z21.number().int()
      }),
      partial: z21.boolean()
    })
  ),
  money: z21.object({
    priced: z21.number().int(),
    pending: z21.number().int(),
    totalApproved: moneySchema,
    associationShare: moneySchema,
    memberShare: moneySchema,
    byCoverage: z21.array(
      z21.object({
        percent: z21.union(
          PERIPHERAL_COVERAGE_PERCENTS.map((p) => z21.literal(p))
        ),
        peripherals: z21.number().int(),
        totalApproved: moneySchema,
        associationShare: moneySchema
      })
    )
  }),
  quotes: z21.object({
    approvedItems: z21.number().int(),
    atLowest: z21.number().int(),
    approvedTotal: moneySchema,
    lowestTotal: moneySchema,
    aboveLowest: moneySchema,
    origins: z21.array(
      z21.object({
        origin: z21.enum([...partOriginKeys, "unknown"]),
        items: z21.number().int(),
        total: moneySchema
      })
    ),
    providers: z21.array(
      z21.object({
        name: z21.string().nullable(),
        items: z21.number().int(),
        total: moneySchema
      })
    )
  }),
  itemTypes: z21.array(z21.object({ type: z21.string(), total: z21.number().int() })),
  states: z21.object({
    states: z21.array(
      z21.object({ state: z21.enum(BRAZILIAN_STATES), total: z21.number().int() })
    ),
    withoutLocation: z21.number().int(),
    unknownState: z21.number().int()
  })
});
var claimManagementContract = c19.router(
  {
    managementMap: {
      method: "GET",
      path: "/management/map",
      query: managementMapQuerySchema,
      responses: { 200: managementMapSchema, ...defaultErrors },
      summary: "O mapa da Gest\xE3o: cada departamento, cada etapa, quem est\xE1 em cada uma"
    },
    /**
     * Os acionamentos de uma etapa, da estação ou de um marco, o mais antigo
     * na fase primeiro: a lista da pilha.
     */
    managementGroup: {
      method: "GET",
      path: "/management/group",
      query: managementGroupQuerySchema,
      responses: { 200: managementGroupPageSchema, ...defaultErrors },
      summary: "Os acionamentos de uma etapa na Gest\xE3o, do mais antigo ao mais novo"
    },
    managementFlow: {
      method: "GET",
      path: "/management/flow",
      query: managementFlowQuerySchema,
      responses: { 200: managementFlowSchema, ...defaultErrors },
      summary: "O fluxo do sinistro: dos abertos no per\xEDodo, por onde passaram e como terminaram"
    },
    managementStates: {
      method: "GET",
      path: "/management/states",
      query: managementFlowQuerySchema,
      responses: { 200: managementStatesSchema, ...defaultErrors },
      summary: "Os sinistros abertos no per\xEDodo, por estado do local do ocorrido"
    },
    managementTypes: {
      method: "GET",
      path: "/management/types",
      query: managementFlowQuerySchema,
      responses: { 200: managementTypesSchema, ...defaultErrors },
      summary: "Os sinistros abertos no per\xEDodo, por tipo do evento"
    },
    managementDurations: {
      method: "GET",
      path: "/management/durations",
      query: managementFlowQuerySchema,
      responses: { 200: managementDurationsSchema, ...defaultErrors },
      summary: "Quantos dias os sinistros abertos no per\xEDodo ficam em cada departamento"
    },
    managementRepairMoney: {
      method: "GET",
      path: "/management/repair-money",
      query: managementFlowQuerySchema,
      responses: { 200: managementRepairMoneySchema, ...defaultErrors },
      summary: "A despesa e o or\xE7amento do Reparo lan\xE7ados no per\xEDodo"
    },
    managementConsultants: {
      method: "GET",
      path: "/management/consultants",
      query: managementFlowQuerySchema,
      responses: { 200: managementConsultantsSchema, ...defaultErrors },
      summary: "Os sinistros de associado abertos no per\xEDodo, por consultor do carro"
    },
    managementDeadlines: {
      method: "GET",
      path: "/management/deadlines",
      query: managementFlowQuerySchema,
      responses: { 200: managementDeadlinesSchema, ...defaultErrors },
      summary: "Dos abertos no per\xEDodo, quem est\xE1 em cada etapa dentro e fora do prazo"
    },
    managementWeeks: {
      method: "GET",
      path: "/management/weeks",
      query: managementFlowQuerySchema,
      responses: { 200: managementWeeksSchema, ...defaultErrors },
      summary: "Os sinistros abertos no per\xEDodo, por semana"
    },
    managementExits: {
      method: "GET",
      path: "/management/exits",
      query: managementFlowQuerySchema,
      responses: { 200: managementExitsSchema, ...defaultErrors },
      summary: "Dos abertos no per\xEDodo, as negativas e desist\xEAncias por etapa"
    },
    /** O Painel da Gestão dos periféricos (28/10/2026), numa resposta só. */
    managementPeripherals: {
      method: "GET",
      path: "/management/peripherals",
      query: managementFlowQuerySchema,
      responses: { 200: managementPeripheralsSchema, ...defaultErrors },
      summary: "Os acionamentos de Perif\xE9ricos abertos no per\xEDodo: tempo, prazo, dinheiro e perfil"
    }
  },
  { pathPrefix: "/claims" }
);

// src/beneficiaries.ts
import { initContract as initContract20 } from "@ts-rest/core";
import { z as z22 } from "zod";
import { BENEFICIARY_ACCOUNT_TYPES, BENEFICIARY_REF_TYPES } from "@evogard/domain";
var c20 = initContract20();
var beneficiaryRefTypeSchema = z22.enum(BENEFICIARY_REF_TYPES);
var beneficiaryAccountTypeSchema = z22.enum(BENEFICIARY_ACCOUNT_TYPES);
var beneficiaryOwnerSchema = z22.object({
  /** Nulo quando não foi possível ler — ver `readable`. */
  name: z22.string().nullable(),
  document: z22.string().nullable(),
  /** Só no ramo `companhia`: oficina, fornecedor de peças... */
  companyType: companyTypeSchema.nullable(),
  /**
   * `false` quando o dono não foi encontrado ou o espelho do ERP não
   * respondeu. "Não sei o nome" é diferente de "não tem nome".
   */
  readable: z22.boolean()
});
var beneficiarySchema = z22.object({
  id: z22.number().int(),
  beneficiaryRefType: beneficiaryRefTypeSchema,
  beneficiaryRefId: z22.string(),
  owner: beneficiaryOwnerSchema,
  bankCode: z22.string().nullable(),
  bankName: z22.string().nullable(),
  /** Mascarada (`••34`) quando `bankDataMasked` — ver abaixo. */
  agency: z22.string().nullable(),
  /** Mascarada (`•••89-0`) quando `bankDataMasked` — ver abaixo. */
  accountNumber: z22.string().nullable(),
  accountType: beneficiaryAccountTypeSchema.nullable(),
  pixKey: z22.string().nullable(),
  accountHolderName: z22.string().nullable(),
  /**
   * CPF (11 dígitos) ou CNPJ (14) de quem recebe — o documento do PAGAMENTO.
   * Na empresa e no associado é cópia do documento do dono; no terceiro, é o
   * que o operador digitou. Nulo em beneficiário cadastrado antes de 24/09.
   */
  document: z22.string().nullable(),
  /**
   * `true` quando agência e conta vieram mascaradas. A LISTA sempre mascara;
   * o detalhe (`getBeneficiary`) traz o número inteiro e exige
   * `platform.beneficiaries:ver_dados_bancarios`. A máscara é da API, e não da
   * tela: mascarar no navegador mandaria o número na resposta da lista.
   */
  bankDataMasked: z22.boolean(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema
});
var beneficiaryBankFields = {
  bankCode: z22.string().trim().max(10).nullable().optional(),
  bankName: z22.string().trim().max(200).nullable().optional(),
  agency: z22.string().trim().max(20).nullable().optional(),
  accountNumber: z22.string().trim().max(30).nullable().optional(),
  accountType: beneficiaryAccountTypeSchema.nullable().optional(),
  pixKey: z22.string().trim().max(120).nullable().optional(),
  accountHolderName: z22.string().trim().max(200).nullable().optional(),
  /**
   * CPF ou CNPJ, com ou sem máscara — chega ao serviço só com os dígitos, e
   * só se passar na conta. Ausente numa empresa, a API copia o documento dela.
   */
  document: documentSchema.nullable().optional()
};
var beneficiaryOwnerInputSchema = z22.discriminatedUnion("mode", [
  z22.object({
    mode: z22.literal("existente"),
    beneficiaryRefType: beneficiaryRefTypeSchema,
    beneficiaryRefId: z22.string().trim().min(1).max(40)
  }),
  z22.object({ mode: z22.literal("nova_companhia"), company: companyWriteSchema }),
  z22.object({ mode: z22.literal("novo_terceiro"), thirdParty: thirdPartyWriteSchema })
]);
var createBeneficiarySchema = z22.object({
  owner: beneficiaryOwnerInputSchema,
  ...beneficiaryBankFields
});
var updateBeneficiarySchema = z22.object({
  ...beneficiaryBankFields,
  archived: z22.boolean().optional()
});
var beneficiaryMemberSchema = z22.object({
  /** O `codigo_associado` — vira o `beneficiaryRefId`. */
  code: z22.string(),
  name: z22.string().nullable(),
  document: z22.string().nullable()
});
var beneficiaryConsultantSchema = z22.object({
  /** O id em `sales_consultants` — vira o `beneficiaryRefId`. */
  id: z22.number().int(),
  name: z22.string(),
  erpVolunteerCode: z22.string()
});
var beneficiariesContract = c20.router(
  {
    listBeneficiaries: {
      method: "GET",
      path: "/beneficiaries",
      query: paginationQuery.extend({
        beneficiaryRefType: beneficiaryRefTypeSchema.optional(),
        /** Nome ou documento do dono (empresa e terceiro), ou o código do associado. */
        search: z22.string().trim().min(1).max(120).optional(),
        includeArchived: z22.enum(["true", "false"]).optional()
      }),
      responses: { 200: paginated(beneficiarySchema), ...defaultErrors },
      summary: "Lista benefici\xE1rios, com o nome do dono de cada um"
    },
    createBeneficiary: {
      method: "POST",
      path: "/beneficiaries",
      body: createBeneficiarySchema,
      responses: { 201: beneficiarySchema, ...defaultErrors },
      summary: "Cadastra um benefici\xE1rio \u2014 para um dono que j\xE1 existe, ou junto com ele"
    },
    /*
     * `POST` com o documento no CORPO, e não no caminho: CPF na URL acaba em
     * log de servidor, histórico do navegador e `Referer` — `contract.test.ts`
     * recusa, e `searchInErp` (`members.ts`) já foi corrigida pelo mesmo motivo.
     * Antes de `/beneficiaries/:id`, para `:id` nunca capturar `member-search`.
     */
    findBeneficiaryMember: {
      method: "POST",
      path: "/beneficiaries/member-search",
      body: z22.object({
        /** CPF ou CNPJ, com ou sem máscara — a normalização é da porta do ERP. */
        document: z22.string().trim().min(11).max(20)
      }),
      responses: { 200: beneficiaryMemberSchema, ...defaultErrors },
      summary: "Procura no ERP o associado de um CPF/CNPJ, para ser o dono do benefici\xE1rio"
    },
    /*
     * Os consultores ATIVOS da praça — só para escolher o dono de um
     * beneficiário novo, por isso `criar`. Antes de `/beneficiaries/:id`.
     */
    listBeneficiaryConsultants: {
      method: "GET",
      path: "/beneficiaries/consultants",
      responses: { 200: z22.array(beneficiaryConsultantSchema), ...defaultErrors },
      summary: "Os consultores ativos da pra\xE7a, para ser o dono de um benefici\xE1rio"
    },
    getBeneficiary: {
      method: "GET",
      path: "/beneficiaries/:id",
      pathParams: z22.object({ id: z22.coerce.number().int() }),
      responses: { 200: beneficiarySchema, ...defaultErrors },
      summary: "Um benefici\xE1rio, com ag\xEAncia e conta sem m\xE1scara \u2014 e o acesso fica na auditoria"
    },
    updateBeneficiary: {
      method: "PATCH",
      path: "/beneficiaries/:id",
      pathParams: z22.object({ id: z22.coerce.number().int() }),
      body: updateBeneficiarySchema,
      responses: { 200: beneficiarySchema, ...defaultErrors },
      summary: "Edita os dados banc\xE1rios, ou arquiva/reativa o benefici\xE1rio"
    }
  },
  { pathPrefix: "/platform" }
);

// src/contaazul.ts
import { initContract as initContract21 } from "@ts-rest/core";
import { z as z23 } from "zod";
import {
  ACCOUNT_ACCESS_REASONS,
  digitsOnly as digitsOnly2,
  isValidCnpj as isValidCnpj2,
  jwtExpiry,
  opaqueSecretProblem
} from "@evogard/domain";
var c21 = initContract21();
var cnpjSchema = z23.string().trim().max(20).transform((v) => digitsOnly2(v) ?? "").refine((d) => isValidCnpj2(d), "CNPJ inv\xE1lido: confira os d\xEDgitos.");
var contaazulAccountSchema = z23.object({
  id: z23.number().int(),
  name: z23.string(),
  /** só dígitos — a tela formata */
  document: z23.string(),
  /** o identificador do app no Conta Azul; não é segredo */
  clientId: z23.string(),
  hasClientSecret: z23.boolean(),
  clientSecretUpdatedAt: instantSchema.nullable(),
  hasAccessToken: z23.boolean(),
  /** o `exp` do JWT colado; nulo sem token, ou quando o valor não é JWT */
  accessTokenExpiresAt: instantSchema.nullable(),
  /** o último "testar conexão" */
  verifiedAt: instantSchema.nullable(),
  verifiedOk: z23.boolean().nullable(),
  verifiedDetail: z23.string().nullable(),
  /** a empresa que o Conta Azul confirmou no último teste bem-sucedido */
  connectedCompany: z23.object({ id: z23.string(), name: z23.string().nullable() }).nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});
var opaqueField = (what, max) => z23.string().trim().max(max).superRefine((value, ctx) => {
  const problem = opaqueSecretProblem(value, what);
  if (problem) ctx.addIssue({ code: "custom", message: problem });
});
var accessTokenField = z23.string().trim().min(1).max(8e3).refine((value) => jwtExpiry(value) !== null, {
  message: "Access token inv\xE1lido: ele \xE9 um JWT \u2014 tr\xEAs partes separadas por ponto, com o vencimento dentro. Confira se colou o access token, e n\xE3o o refresh token."
});
var createContaazulAccountSchema = z23.object({
  name: z23.string().trim().min(1, "Informe o nome da conta.").max(120),
  document: cnpjSchema,
  clientId: opaqueField("o client id", 500),
  clientSecret: opaqueField("o client secret", 2e3),
  /** opcional: a conta pode nascer sem token, e ganhá-lo na edição */
  accessToken: accessTokenField.optional()
});
var updateContaazulAccountSchema = z23.object({
  name: z23.string().trim().min(1, "Informe o nome da conta.").max(120).optional(),
  document: cnpjSchema.optional(),
  clientId: opaqueField("o client id", 500).optional(),
  clientSecret: opaqueField("o client secret", 2e3).optional(),
  /** colar um novo substitui o anterior e zera o último teste */
  accessToken: accessTokenField.optional(),
  archived: z23.literal(true).optional()
});
var exceptionStatusSchema = z23.enum(["permitir", "negar"]);
var contaazulAccountAccessSchema = z23.object({
  groups: z23.array(
    z23.object({
      id: z23.number().int(),
      name: z23.string(),
      granted: z23.boolean(),
      /** grupo arquivado aparece só se ainda estiver autorizado — para poder tirar */
      archived: z23.boolean()
    })
  ),
  members: z23.array(
    z23.object({
      userId: z23.string(),
      name: z23.string(),
      email: z23.string(),
      groupId: z23.number().int().nullable(),
      groupName: z23.string().nullable(),
      /** nulo é "segue o grupo" */
      exception: exceptionStatusSchema.nullable(),
      allowed: z23.boolean(),
      reason: z23.enum(ACCOUNT_ACCESS_REASONS)
    })
  )
});
var contaazulContract = c21.router(
  {
    listContaazulAccounts: {
      method: "GET",
      path: "/integrations/contaazul/accounts",
      responses: {
        200: z23.object({ accounts: z23.array(contaazulAccountSchema) }),
        ...defaultErrors
      },
      summary: "As contas do Conta Azul desta base \u2014 s\xF3 as ativas"
    },
    createContaazulAccount: {
      method: "POST",
      path: "/integrations/contaazul/accounts",
      body: createContaazulAccountSchema,
      responses: { 201: contaazulAccountSchema, ...defaultErrors },
      summary: "Cadastra uma conta do Conta Azul nesta base"
    },
    updateContaazulAccount: {
      method: "PATCH",
      path: "/integrations/contaazul/accounts/:id",
      pathParams: z23.object({ id: z23.coerce.number().int() }),
      body: updateContaazulAccountSchema,
      responses: { 200: contaazulAccountSchema, ...defaultErrors },
      summary: "Edita a conta, troca o segredo, ou arquiva"
    },
    /*
     * 200 também quando o teste FALHA (`result.ok: false`), como em
     * `testIntegration`: "o token venceu" é uma resposta que a tela mostra como
     * texto, e não um erro da nossa API.
     */
    testContaazulAccount: {
      method: "POST",
      path: "/integrations/contaazul/accounts/:id/test",
      pathParams: z23.object({ id: z23.coerce.number().int() }),
      body: z23.object({}),
      responses: {
        200: z23.object({
          result: z23.object({ ok: z23.boolean(), detail: z23.string() }),
          account: contaazulAccountSchema
        }),
        ...defaultErrors
      },
      summary: "L\xEA a empresa conectada no Conta Azul com o access token da conta"
    },
    getContaazulAccountAccess: {
      method: "GET",
      path: "/integrations/contaazul/accounts/:id/access",
      pathParams: z23.object({ id: z23.coerce.number().int() }),
      responses: { 200: contaazulAccountAccessSchema, ...defaultErrors },
      summary: "Quem pode usar a conta: grupos autorizados e exce\xE7\xF5es"
    },
    setContaazulAccountGroups: {
      method: "PUT",
      path: "/integrations/contaazul/accounts/:id/access/groups",
      pathParams: z23.object({ id: z23.coerce.number().int() }),
      /* A lista inteira: o que não vem deixa de estar autorizado. */
      body: z23.object({ groupIds: z23.array(z23.number().int()).max(500) }),
      responses: { 200: contaazulAccountAccessSchema, ...defaultErrors },
      summary: "Define os grupos autorizados a usar a conta"
    },
    setContaazulAccountUserPermission: {
      method: "PUT",
      path: "/integrations/contaazul/accounts/:id/access/users",
      pathParams: z23.object({ id: z23.coerce.number().int() }),
      /*
       * A pessoa vai no CORPO, nunca no caminho (`contract.test.ts`): caminho
       * cai em log de acesso, histórico de navegador e cabeçalho `Referer`.
       * `status: null` volta a "seguir o grupo" — apaga a exceção.
       */
      body: z23.object({
        userId: z23.string().min(1),
        status: exceptionStatusSchema.nullable()
      }),
      responses: { 200: contaazulAccountAccessSchema, ...defaultErrors },
      summary: "Grava, troca ou remove a exce\xE7\xE3o de uma pessoa nesta conta"
    }
  },
  { pathPrefix: "/platform" }
);

// src/opasuite.ts
import { initContract as initContract22 } from "@ts-rest/core";
import { z as z24 } from "zod";
import { normalizePhone as normalizePhone2, opasuiteBaseUrl, opasuiteBaseUrlProblem } from "@evogard/domain";
var c22 = initContract22();
var externalId = z24.string().trim().min(1).max(200);
var mediaUrl = z24.string().trim().max(2e3).refine((v) => {
  try {
    return new URL(v).protocol === "https:";
  } catch {
    return false;
  }
}, "A m\xEDdia alternativa precisa ser um link https.");
var opasuiteSettingsSchema = z24.object({
  /** A origem — `https://evogard.opasuite.com.br`. Nulo quando a base não informou. */
  baseUrl: z24.string().nullable(),
  updatedAt: instantSchema.nullable()
});
var opasuiteTemplateSchema = z24.object({
  id: z24.number().int(),
  name: z24.string(),
  channelId: z24.string(),
  channelName: z24.string(),
  templateId: z24.string(),
  shortcut: z24.string().nullable(),
  /** O texto do template, como o OpaSuite respondeu no cadastro. */
  text: z24.string(),
  /** As variáveis do texto, na ordem — `templateVariables`. */
  variables: z24.array(z24.string()),
  fallbackMediaUrl: z24.string().nullable(),
  allowStartedService: z24.boolean(),
  openCustomerService: z24.boolean(),
  /** Os pontos de uso que disparam este template. Arquivar exige zero. */
  purposes: z24.array(z24.string()),
  createdAt: instantSchema,
  updatedAt: instantSchema
});
var opasuitePurposeSchema = z24.object({
  /** A chave de `WHATSAPP_PURPOSES`. */
  purpose: z24.string(),
  label: z24.string(),
  /** O que cada posição recebe, na ordem. */
  variables: z24.array(z24.string()),
  /** O template ligado, ou nulo — e aí o ponto não dispara. */
  templateId: z24.number().int().nullable()
});
var opasuiteOverviewSchema = z24.object({
  settings: opasuiteSettingsSchema,
  templates: z24.array(opasuiteTemplateSchema),
  purposes: z24.array(opasuitePurposeSchema)
});
var opasuiteChannelSchema = z24.object({
  id: z24.string(),
  name: z24.string(),
  status: z24.string().nullable(),
  provider: z24.string().nullable()
});
var opasuiteChannelTemplateSchema = z24.object({
  id: z24.string(),
  shortcut: z24.string().nullable(),
  text: z24.string(),
  variables: z24.array(z24.string()),
  /**
   * `false` quando o tipo do template não é enviado pela casa — hoje, o
   * `paymentDetailTemplate`, cujas variáveis são um objeto de pagamento. A
   * tela o mostra e não deixa escolher, com o motivo ao lado.
   */
  supported: z24.boolean()
});
var live = (item) => z24.object({
  available: z24.boolean(),
  detail: z24.string().nullable(),
  items: z24.array(item)
});
var createOpasuiteTemplateSchema = z24.object({
  name: z24.string().trim().min(1, "Informe o nome do template.").max(120),
  channelId: externalId,
  templateId: externalId,
  fallbackMediaUrl: mediaUrl.nullable().optional(),
  allowStartedService: z24.boolean(),
  openCustomerService: z24.boolean()
});
var updateOpasuiteTemplateSchema = z24.object({
  name: z24.string().trim().min(1, "Informe o nome do template.").max(120).optional(),
  fallbackMediaUrl: mediaUrl.nullable().optional(),
  allowStartedService: z24.boolean().optional(),
  openCustomerService: z24.boolean().optional(),
  archived: z24.literal(true).optional()
});
var opasuiteSendResultSchema = z24.object({
  situacao: z24.enum([
    "enviada",
    "repetida",
    "falhou",
    "contato_bloqueado",
    "telefone_invalido",
    "template_nao_configurado",
    "template_incompativel",
    "integracao_nao_configurada"
  ]),
  detail: z24.string().nullable(),
  messageId: z24.string().nullable(),
  /**
   * A resposta do OpaSuite como veio — status HTTP e corpo em texto —, para
   * quem configura ver o que ele disse. Nulo quando não houve chamada (número
   * inválido, contato bloqueado, falta configuração) ou ele não respondeu.
   */
  providerResponse: z24.object({ status: z24.number().int(), body: z24.string() }).nullable()
});
var opasuiteContract = c22.router(
  {
    getOpasuite: {
      method: "GET",
      path: "/integrations/opasuite",
      responses: { 200: opasuiteOverviewSchema, ...defaultErrors },
      summary: "O endere\xE7o, os templates cadastrados e os pontos de uso desta base"
    },
    saveOpasuiteSettings: {
      method: "PUT",
      path: "/integrations/opasuite/settings",
      body: z24.object({
        baseUrl: z24.string().trim().max(500).superRefine((value, ctx) => {
          const problem = opasuiteBaseUrlProblem(value);
          if (problem) ctx.addIssue({ code: "custom", message: problem });
        }).transform((value) => opasuiteBaseUrl(value) ?? value)
      }),
      responses: { 200: opasuiteOverviewSchema, ...defaultErrors },
      summary: "Grava o endere\xE7o do OpaSuite desta base"
    },
    listOpasuiteChannels: {
      method: "GET",
      path: "/integrations/opasuite/channels",
      responses: { 200: live(opasuiteChannelSchema), ...defaultErrors },
      summary: "Os canais de WhatsApp da conta, lidos do OpaSuite agora"
    },
    listOpasuiteChannelTemplates: {
      method: "GET",
      path: "/integrations/opasuite/channels/:channelId/templates",
      pathParams: z24.object({ channelId: externalId }),
      responses: { 200: live(opasuiteChannelTemplateSchema), ...defaultErrors },
      summary: "Os templates de um canal, lidos do OpaSuite agora"
    },
    createOpasuiteTemplate: {
      method: "POST",
      path: "/integrations/opasuite/templates",
      body: createOpasuiteTemplateSchema,
      responses: { 201: opasuiteOverviewSchema, ...defaultErrors },
      summary: "Cadastra um template do OpaSuite nesta base, conferindo-o no OpaSuite"
    },
    updateOpasuiteTemplate: {
      method: "PATCH",
      path: "/integrations/opasuite/templates/:id",
      pathParams: z24.object({ id: z24.coerce.number().int() }),
      body: updateOpasuiteTemplateSchema,
      responses: { 200: opasuiteOverviewSchema, ...defaultErrors },
      summary: "Renomeia, ajusta as op\xE7\xF5es do envio, ou arquiva o template"
    },
    setOpasuitePurposeTemplate: {
      method: "PUT",
      path: "/integrations/opasuite/purposes/:purpose",
      pathParams: z24.object({ purpose: z24.string().trim().min(1).max(120) }),
      /* `null` desliga o ponto: ele passa a recusar com `template_nao_configurado`. */
      body: z24.object({ templateId: z24.number().int().nullable() }),
      responses: { 200: opasuiteOverviewSchema, ...defaultErrors },
      summary: "Escolhe o template que um ponto de uso dispara"
    },
    /*
     * O número vai no CORPO, nunca no caminho (`contract.test.ts`): caminho cai
     * em log de acesso e em histórico de navegador.
     */
    sendOpasuiteTest: {
      method: "POST",
      path: "/integrations/opasuite/templates/:id/test",
      pathParams: z24.object({ id: z24.coerce.number().int() }),
      body: z24.object({
        phone: z24.string().trim().max(40).refine((v) => normalizePhone2(v) !== null, "Informe um celular com DDD."),
        variables: z24.array(z24.string().trim().min(1, "Preencha todas as vari\xE1veis.").max(1e3)).max(50)
      }),
      responses: { 200: opasuiteSendResultSchema, ...defaultErrors },
      summary: "Envia o template agora para um n\xFAmero, como teste"
    }
  },
  { pathPrefix: "/platform" }
);

// src/asaas.ts
import { initContract as initContract23 } from "@ts-rest/core";
import { z as z25 } from "zod";
import { ASAAS_ENVIRONMENTS } from "@evogard/domain";
var c23 = initContract23();
var asaasEnvironmentSchema = z25.enum(ASAAS_ENVIRONMENTS);
var asaasOverviewSchema = z25.object({
  environment: asaasEnvironmentSchema,
  /** `false` fora de produção: a API recusa `producao`, e a tela desliga a opção. */
  productionAllowed: z25.boolean(),
  /** O endereço para onde ESTA API espera os eventos desta base. */
  expectedWebhookUrl: z25.string(),
  webhook: z25.object({
    registered: z25.boolean(),
    /** O endereço que foi registrado. Diferente do esperado: registrar de novo. */
    url: z25.string().nullable(),
    email: z25.string().nullable(),
    registeredAt: instantSchema.nullable()
  }),
  updatedAt: instantSchema.nullable()
});
var asaasWebhookStatusSchema = z25.object({
  available: z25.boolean(),
  /** A frase de quando não deu para ler: sem chave, sem webhook, Asaas fora. */
  detail: z25.string().nullable(),
  enabled: z25.boolean().nullable(),
  /** `true` depois de 15 falhas seguidas: o Asaas parou de entregar. */
  interrupted: z25.boolean().nullable(),
  url: z25.string().nullable()
});
var asaasContract = c23.router(
  {
    getAsaas: {
      method: "GET",
      path: "/integrations/asaas",
      responses: { 200: asaasOverviewSchema, ...defaultErrors },
      summary: "O ambiente e o webhook do Asaas desta base"
    },
    saveAsaasSettings: {
      method: "PUT",
      path: "/integrations/asaas/settings",
      body: z25.object({ environment: asaasEnvironmentSchema }),
      responses: { 200: asaasOverviewSchema, ...defaultErrors },
      summary: "Escolhe o ambiente do Asaas desta base"
    },
    /*
     * Registra, ou atualiza o que já foi registrado. É também o que retoma a
     * fila interrompida: o registro vai com `interrupted: false`.
     */
    registerAsaasWebhook: {
      method: "POST",
      path: "/integrations/asaas/webhook",
      body: z25.object({
        email: z25.string().trim().email("Informe um e-mail v\xE1lido.").max(200)
      }),
      responses: { 200: asaasOverviewSchema, ...defaultErrors },
      summary: "Registra o webhook desta base no Asaas, com um token gerado pela casa"
    },
    getAsaasWebhookStatus: {
      method: "GET",
      path: "/integrations/asaas/webhook/status",
      responses: { 200: asaasWebhookStatusSchema, ...defaultErrors },
      summary: "O webhook desta base, lido do Asaas agora"
    }
  },
  { pathPrefix: "/platform" }
);

// src/claims-caf.ts
import { initContract as initContract24 } from "@ts-rest/core";
import { z as z26 } from "zod";
var c24 = initContract24();
var cafIntegrationContract = c24.router(
  {
    getCafWebhook: {
      method: "GET",
      path: "/webhook",
      responses: {
        200: z26.object({
          url: z26.string(),
          /** `false` quando a CAF não alcança o endereço — `localhost`, sem túnel. */
          reachable: z26.boolean(),
          /** `false` quando o endereço é de outro sistema: o resultado não volta para cá. */
          receivesHere: z26.boolean()
        })
      },
      summary: "O endere\xE7o do webhook da CAF desta base"
    }
  },
  { pathPrefix: "/platform/integrations/caf" }
);
var claimsCafWebhookContract = c24.router(
  {
    receiveCaf: {
      method: "POST",
      path: "/caf/:tenant/:code",
      pathParams: z26.object({
        tenant: z26.coerce.number().int().positive(),
        code: z26.string().trim().min(1).max(128)
      }),
      /* O corpo é da CAF, e ganha campos sem aviso — quem lê é o serviço. */
      body: z26.unknown(),
      responses: {
        /** A CAF espera resposta rápida: o trabalho vai para a fila. */
        202: z26.object({ received: z26.literal(true) }),
        /** Código errado e base desconhecida: o mesmo corpo, para não enumerar praças. */
        401: z26.object({ received: z26.literal(false) })
      },
      summary: "Avisos da CAF sobre a transa\xE7\xE3o de um link de documentos"
    }
  },
  { pathPrefix: "/webhooks" }
);

// src/revenues.ts
import { initContract as initContract26 } from "@ts-rest/core";
import { z as z28 } from "zod";
import {
  PAYMENT_ACCOUNT_PROVIDERS,
  REVENUE_DESCRIPTION_MAX,
  REVENUE_NOTES_MAX,
  REVENUE_SOURCE_TYPES,
  REVENUE_STATUSES,
  parseMoneyInput
} from "@evogard/domain";

// src/expenses.ts
import { initContract as initContract25 } from "@ts-rest/core";
import { z as z27 } from "zod";
import {
  VEHICLE_SITUATION_TONES,
  ASSISTANCE_SERVICE_TYPES,
  COMPENSATION_PAID_DEBT_KINDS,
  EXPENSE_FINANCE_ACTS,
  EXPENSE_PAYMENT_METHODS as EXPENSE_PAYMENT_METHODS3,
  EXPENSE_REQUEST_STAGES,
  EXPENSE_SOURCE_TYPES,
  EXPENSE_SUPERVISOR_ACTS,
  FINANCE_BOARD_SOURCES,
  FINANCE_BOARD_STAGES,
  SUPERVISOR_EXPENSE_STAGES
} from "@evogard/domain";
var c25 = initContract25();
var expenseRequestStageSchema = z27.enum(EXPENSE_REQUEST_STAGES);
var supervisorExpenseStageSchema = z27.enum(SUPERVISOR_EXPENSE_STAGES);
var expenseSupervisorActSchema = z27.enum(EXPENSE_SUPERVISOR_ACTS);
var supervisedExpenseSchema = z27.object({
  id: z27.number().int(),
  sourceType: z27.enum(EXPENSE_SOURCE_TYPES),
  /**
   * O departamento do sinistro que pediu (`aviso`, `reparo`…), ou nulo na
   * assistência, que é um departamento só.
   */
  stage: expenseRequestStageSchema.nullable(),
  /** O card: o id do sinistro, ou o do acionamento de Periféricos. Nulo na assistência. */
  originId: z27.string().nullable(),
  beneficiaryId: z27.number().int(),
  /** O dono do beneficiário, ou o titular da conta. Nulo quando nenhum dos dois é legível. */
  beneficiaryName: z27.string().nullable(),
  amount: moneySchema,
  /** Nulo só no lote da Assistência 24h, até o Financeiro autorizar. */
  dueDate: businessDateSchema.nullable(),
  competencyDate: businessDateSchema.nullable(),
  paymentMethod: z27.enum(EXPENSE_PAYMENT_METHODS3),
  description: z27.string().nullable(),
  notes: z27.string().nullable(),
  status: supervisorExpenseStageSchema,
  /** O à vista da Assistência: pago pelo líder, à espera da conciliação (`expenseAlreadyPaid`). */
  alreadyPaid: z27.boolean(),
  paidAmount: moneySchema.nullable(),
  hasTaxInvoice: z27.boolean(),
  pendingReason: z27.string().nullable(),
  refusalReason: z27.string().nullable(),
  createdAt: instantSchema,
  /** Quem solicitou a despesa; nulo é o sistema (o lançamento do consultor interno). */
  createdByName: z27.string().nullable(),
  /**
   * O líder que aprovou a despesa e quando (`department_approver_id`). Nulos
   * enquanto ninguém aprovou.
   */
  departmentApproverName: z27.string().nullable(),
  departmentApprovedAt: instantSchema.nullable(),
  /**
   * Desde quando a despesa está na fase de agora, e quem a pôs lá — o último
   * `despesa_movida` para `status` (28/10/2026). Na pendência, é quem
   * pendenciou e quando. Nulos na fase em que a despesa nasceu.
   */
  statusSince: instantSchema.nullable(),
  statusByName: z27.string().nullable(),
  /**
   * Os atos que ESTA pessoa tem sobre a despesa agora — vazio para quem só
   * lança, e para a fase que está com o Financeiro.
   */
  acts: z27.array(expenseSupervisorActSchema)
});
var cardExpensesSchema = z27.object({
  expenses: z27.array(supervisedExpenseSchema),
  /** A pessoa pode lançar despesa neste departamento (`lancar_despesa`). */
  canLaunch: z27.boolean()
});
var expenseFileSchema = z27.union([
  z27.object({ documentId: z27.number().int().positive() }),
  z27.object({ upload: attachClaimDocumentSchema })
]);
var launchExpenseSchema = z27.object({
  beneficiaryId: z27.number().int().positive(),
  amount: moneySchema.refine((v) => Number(v) > 0, "O valor precisa ser maior que zero."),
  paymentMethod: z27.enum(EXPENSE_PAYMENT_METHODS3),
  /**
   * A categoria do Conta Azul (o id), do catálogo da base, entre as do grupo
   * do departamento e as gerais (`contaAzulCategoriesFor`). Obrigatória na
   * base que tem catálogo; na que não tem, vai nula. O Financeiro a recebe
   * preenchida e pode trocar ao autorizar.
   */
  category: z27.string().trim().min(1).max(80).nullable().optional(),
  competencyDate: businessDateSchema,
  dueDate: businessDateSchema,
  /** O documento a pagar — obrigatório. */
  document: expenseFileSchema,
  /** A nota fiscal, se já houver. */
  taxInvoice: expenseFileSchema.nullable().optional(),
  notes: z27.string().trim().max(2e3).nullable().optional(),
  /**
   * O débito da Indenização que esta despesa paga (29/10/2026). Obrigatório
   * quando o sinistro está no Pagamento de Débito — toda despesa lançada ali é
   * um débito e desconta da FIPE —, e recusado em qualquer outro lugar.
   */
  debt: z27.object({
    kind: z27.enum(COMPENSATION_PAID_DEBT_KINDS),
    /** O mês da mensalidade, a data da multa. */
    referenceOn: businessDateSchema.nullable()
  }).nullable().optional()
});
var expenseSupervisorActBodySchema = z27.object({
  act: expenseSupervisorActSchema,
  reason: z27.string().trim().max(2e3).nullable().optional()
});
var cardParams2 = z27.object({
  stage: expenseRequestStageSchema,
  originId: z27.string().regex(/^\d+$/)
});
var claimExpensesContract = c25.router(
  {
    cardExpenses: {
      method: "GET",
      path: "/cards/:stage/:originId",
      pathParams: cardParams2,
      responses: { 200: cardExpensesSchema, ...defaultErrors },
      summary: "As despesas do card \u2014 no sinistro, as de todos os departamentos \u2014, com o status do supervisor"
    },
    launchExpense: {
      method: "POST",
      path: "/cards/:stage/:originId",
      pathParams: cardParams2,
      body: launchExpenseSchema,
      responses: { 201: supervisedExpenseSchema, ...defaultErrors },
      summary: "Lan\xE7a uma despesa no card \u2014 ela nasce em Solicita\xE7\xE3o, com o supervisor"
    },
    supervisorBoard: {
      method: "GET",
      path: "/board/:stage",
      pathParams: z27.object({ stage: expenseRequestStageSchema }),
      responses: { 200: z27.array(supervisedExpenseSchema), ...defaultErrors },
      summary: "O quadro do supervisor do departamento"
    },
    supervisorAct: {
      method: "POST",
      path: "/:id/acts",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      body: expenseSupervisorActBodySchema,
      responses: { 200: supervisedExpenseSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Aprova, pendencia, reprova ou resolve a pend\xEAncia da despesa"
    }
  },
  { pathPrefix: "/claims/expenses" }
);
var assistanceExpensesContract = c25.router(
  {
    supervisorBoard: {
      method: "GET",
      path: "/board",
      responses: { 200: z27.array(supervisedExpenseSchema), ...defaultErrors },
      summary: "O quadro do supervisor da Assist\xEAncia 24h"
    },
    supervisorAct: {
      method: "POST",
      path: "/:id/acts",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      body: expenseSupervisorActBodySchema,
      responses: { 200: supervisedExpenseSchema, 409: apiErrorSchema, ...defaultErrors },
      summary: "Aprova, pendencia, reprova ou resolve a pend\xEAncia da despesa da assist\xEAncia"
    }
  },
  { pathPrefix: "/assistance/expenses" }
);
var financeBoardStageSchema = z27.enum(FINANCE_BOARD_STAGES);
var expenseFinanceActSchema = z27.enum(EXPENSE_FINANCE_ACTS);
var financeExpenseSchema = supervisedExpenseSchema.omit({ status: true, acts: true, sourceType: true }).extend({
  sourceType: z27.enum(FINANCE_BOARD_SOURCES),
  status: financeBoardStageSchema,
  /** O id da categoria no Conta Azul — nulo enquanto ninguém escolheu. */
  category: z27.string().nullable(),
  /** O id do centro de custo no Conta Azul — nulo até autorizar. */
  costCenter: z27.string().nullable(),
  /** O id da conta de pagamento no Conta Azul — nulo até autorizar. */
  paymentAccount: z27.string().nullable(),
  /** Os atos que ESTA pessoa tem sobre a despesa agora. */
  acts: z27.array(expenseFinanceActSchema),
  /** As etiquetas da despesa que esta pessoa vê — as dos setores dela. */
  labels: z27.array(appliedLabelSchema),
  /** Quem cuida da despesa no Financeiro. */
  assignee: z27.object({ id: z27.string(), name: z27.string() }).nullable()
});
var expenseHistoryEntrySchema = z27.object({
  at: instantSchema,
  /** Quem moveu; nulo é o sistema (o job do Conta Azul). */
  actorName: z27.string().nullable(),
  from: z27.string(),
  to: z27.string(),
  reason: z27.string().nullable()
});
var contaAzulLaunchSchema = z27.object({
  state: z27.enum(["nao_enviada", "enviando", "recusada", "lancada"]),
  eventCode: z27.string().nullable(),
  message: z27.string().nullable()
});
var financeExpenseOriginSchema = z27.discriminatedUnion("kind", [
  z27.object({
    kind: z27.literal("sinistro"),
    stage: expenseRequestStageSchema,
    claimId: z27.number().int()
  }),
  z27.object({ kind: z27.literal("periferico"), peripheralId: z27.number().int() }),
  z27.object({ kind: z27.literal("veiculo"), vehicleCode: z27.string() }),
  z27.object({
    kind: z27.literal("assistencia"),
    settlementId: z27.number().int(),
    settlementKind: z27.enum(["a_vista", "mensal"]),
    /** O mês do fechamento — só no fechamento do mês, que o lote substituiu em 30/10/2026. */
    competence: businessDateSchema.nullable(),
    providerName: z27.string().nullable(),
    dispatches: z27.array(
      z27.object({
        id: z27.number().int(),
        /** `AC-000123`. */
        code: z27.string(),
        serviceType: z27.enum(ASSISTANCE_SERVICE_TYPES),
        executedAt: instantSchema.nullable(),
        amount: moneySchema.nullable()
      })
    )
  })
]);
var financeExpenseDetailSchema = financeExpenseSchema.extend({
  history: z27.array(expenseHistoryEntrySchema),
  contaAzul: contaAzulLaunchSchema,
  origin: financeExpenseOriginSchema.nullable()
});
var financeExpensesQuerySchema = z27.object({
  sourceType: z27.enum(FINANCE_BOARD_SOURCES).optional(),
  /** O departamento do sinistro que pediu. */
  stage: expenseRequestStageSchema.optional(),
  beneficiaryId: z27.coerce.number().int().positive().optional(),
  dueFrom: businessDateSchema.optional(),
  dueTo: businessDateSchema.optional(),
  /** O número da solicitação ("29" ou "#29"), o nome do beneficiário, a descrição ou as observações. */
  search: z27.string().trim().min(1).max(120).optional()
});
var expenseFinanceActBodySchema = z27.object({
  act: expenseFinanceActSchema,
  reason: z27.string().trim().max(2e3).nullable().optional(),
  category: z27.string().trim().min(1).max(80).nullable().optional(),
  costCenter: z27.string().trim().min(1).max(80).nullable().optional(),
  paymentAccount: z27.string().trim().min(1).max(80).nullable().optional(),
  /** O vencimento — obrigatório ao autorizar a despesa que chegou sem ele (o lote da assistência). */
  dueDate: businessDateSchema.nullable().optional()
});
var contaAzulSyncResultSchema = z27.object({
  /** Quantas autorizadas, que ainda não estavam lá, foram lançadas agora. */
  launched: z27.number().int(),
  examined: z27.number().int(),
  paid: z27.number().int(),
  late: z27.number().int(),
  unchanged: z27.number().int(),
  notes: z27.array(z27.object({ expenseId: z27.number().int(), message: z27.string() }))
});
var financeExpenseCommentSchema = z27.object({
  id: z27.number().int(),
  body: z27.string(),
  authorId: z27.string().nullable(),
  authorName: z27.string().nullable(),
  createdAt: instantSchema,
  /** Nulo = nunca editado. */
  editedAt: instantSchema.nullable(),
  /** Remover é arquivar: o texto fica, e a tela diz "removido". */
  archivedAt: instantSchema.nullable()
});
var financeExpenseActivitySchema = z27.discriminatedUnion("kind", [
  z27.object({
    kind: z27.literal("comentario"),
    key: z27.string(),
    at: instantSchema,
    comment: financeExpenseCommentSchema
  }),
  z27.object({
    kind: z27.literal("alteracao"),
    key: z27.string(),
    at: instantSchema,
    type: z27.string(),
    actorName: z27.string().nullable(),
    /** O que aconteceu, em uma frase. */
    summary: z27.string(),
    /** O motivo, a mensagem do Conta Azul, o nome do arquivo — quando há. */
    detail: z27.string().nullable()
  })
]);
var expenseDocumentKindSchema = z27.enum(["nota_fiscal", "comprovante", "outro"]);
var financeExpenseFileSchema = z27.object({
  /** O que a rota da URL recebe: `documento`, `nota_fiscal`, `comprovante` ou o id do anexo. */
  key: z27.string(),
  slot: z27.enum(["documento", "nota_fiscal", "comprovante", "anexo"]),
  kind: expenseDocumentKindSchema.nullable(),
  fileName: z27.string(),
  mimeType: z27.string().nullable(),
  sizeBytes: z27.number().int().nullable(),
  createdAt: instantSchema.nullable(),
  createdByName: z27.string().nullable()
});
var attachFinanceExpenseFileSchema = attachClaimDocumentSchema.extend({
  kind: expenseDocumentKindSchema
});
var financeExpenseBeneficiarySchema = z27.object({
  owner: z27.object({
    refType: beneficiaryRefTypeSchema,
    /** Só na empresa: oficina, fornecedor de peças, prestador... */
    companyType: companyTypeSchema.nullable(),
    /** Nulo quando o dono não foi achado — ver `readable`. */
    name: z27.string().nullable(),
    document: z27.string().nullable(),
    email: z27.string().nullable(),
    phone: z27.string().nullable(),
    whatsapp: z27.string().nullable(),
    /** O endereço numa linha, como se escreve num envelope. */
    address: z27.string().nullable(),
    /** O código do associado no ERP, ou o do voluntário no consultor. */
    code: z27.string().nullable(),
    readable: z27.boolean()
  }),
  accounts: z27.array(
    z27.object({
      id: z27.number().int(),
      /** A conta desta despesa. */
      current: z27.boolean(),
      bankCode: z27.string().nullable(),
      bankName: z27.string().nullable(),
      agency: z27.string().nullable(),
      accountNumber: z27.string().nullable(),
      accountType: beneficiaryAccountTypeSchema.nullable(),
      pixKey: z27.string().nullable(),
      accountHolderName: z27.string().nullable(),
      document: z27.string().nullable(),
      archivedAt: instantSchema.nullable()
    })
  ),
  masked: z27.boolean()
});
var financeExpenseClaimSchema = z27.object({
  id: z27.number().int(),
  /** "Colisão", "Roubo ou furto"; o código cru quando o catálogo não o conhece. */
  eventType: z27.string(),
  department: z27.string(),
  phase: z27.string(),
  occurredAt: instantSchema,
  notifiedAt: instantSchema,
  description: z27.string(),
  /** Quem dirigia, quando não era o associado. */
  driverName: z27.string().nullable(),
  member: z27.object({
    name: z27.string().nullable(),
    code: z27.string(),
    document: z27.string().nullable(),
    phone: z27.string().nullable()
  }).nullable(),
  vehicle: z27.object({
    plate: z27.string().nullable(),
    /** "VW GOL 1.0 · 2020/2021". */
    model: z27.string().nullable(),
    /** O código da casa (`VE-000123`), ou o do SGA quando o veículo não está no núcleo. */
    code: z27.string().nullable(),
    /**
     * A situação do carro no ERP, e a cor dela (`vehicleSituationTone`): só do
     * que a casa já julgou. Nula quando o carro não está no núcleo.
     */
    situation: z27.object({ label: z27.string(), tone: z27.enum(VEHICLE_SITUATION_TONES) }).nullable()
  }),
  expenses: z27.array(
    z27.object({
      id: z27.number().int(),
      /** A despesa que está aberta na mesa. */
      current: z27.boolean(),
      stage: expenseRequestStageSchema,
      status: z27.string(),
      amount: moneySchema,
      beneficiaryName: z27.string().nullable()
    })
  )
});
var EXPENSE_PENDING_EVENT = "despesa:pendente";
var expensePendingNoticeSchema = z27.object({
  expenseId: z27.number().int(),
  sourceType: z27.enum(EXPENSE_SOURCE_TYPES),
  subject: z27.string()
});
var financeExpensesContract = c25.router(
  {
    listFinanceExpenses: {
      method: "GET",
      path: "",
      query: financeExpensesQuerySchema,
      responses: { 200: z27.array(financeExpenseSchema), ...defaultErrors },
      summary: "As despesas do sinistro e da assist\xEAncia, de Aprova\xE7\xE3o Financeiro em diante"
    },
    syncContaAzul: {
      method: "POST",
      path: "/conta-azul/sync",
      body: z27.object({}),
      responses: { 200: contaAzulSyncResultSchema, ...defaultErrors },
      summary: "Confere no Conta Azul as despesas lan\xE7adas e atualiza a fase delas"
    },
    /* As listas da mesa vêm ANTES de `/:id`, para `/:id` não capturar o nome. */
    financeLabelOptions: {
      method: "GET",
      path: "/label-options",
      responses: { 200: z27.array(departmentLabelsSchema), ...defaultErrors },
      summary: "As etiquetas que esta pessoa p\xF5e na despesa \u2014 as dos setores dela"
    },
    financeAssigneeOptions: {
      method: "GET",
      path: "/assignee-options",
      responses: {
        200: z27.array(z27.object({ id: z27.string(), name: z27.string() })),
        ...defaultErrors
      },
      summary: "Quem pode ser o respons\xE1vel \u2014 quem decide no Financeiro desta base"
    },
    financeExpenseBeneficiary: {
      method: "GET",
      path: "/:id/beneficiary",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      responses: { 200: financeExpenseBeneficiarySchema, ...defaultErrors },
      summary: "O benefici\xE1rio da despesa: o dono, os contatos e todas as contas dele"
    },
    financeExpenseClaim: {
      method: "GET",
      path: "/:id/claim",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      responses: { 200: financeExpenseClaimSchema, ...defaultErrors },
      summary: "O sinistro que pediu a despesa: situa\xE7\xE3o, ocorrido, associado, ve\xEDculo e despesas"
    },
    getFinanceExpense: {
      method: "GET",
      path: "/:id",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      responses: { 200: financeExpenseDetailSchema, ...defaultErrors },
      summary: "Uma despesa, com o caminho que ela fez"
    },
    financeExpenseActivity: {
      method: "GET",
      path: "/:id/activity",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      responses: { 200: z27.array(financeExpenseActivitySchema), ...defaultErrors },
      summary: "A Atividade da despesa: o hist\xF3rico e os coment\xE1rios, do mais novo ao mais velho"
    },
    commentFinanceExpense: {
      method: "POST",
      path: "/:id/comments",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      body: z27.object({ body: z27.string().trim().min(1).max(4e3) }),
      responses: { 201: financeExpenseCommentSchema, ...defaultErrors },
      summary: "Comenta na despesa"
    },
    updateFinanceExpenseComment: {
      method: "PATCH",
      path: "/:id/comments/:commentId",
      pathParams: z27.object({
        id: z27.coerce.number().int(),
        commentId: z27.coerce.number().int()
      }),
      body: z27.union([
        z27.object({ body: z27.string().trim().min(1).max(4e3) }),
        z27.object({ archived: z27.literal(true) })
      ]),
      responses: { 200: financeExpenseCommentSchema, ...defaultErrors },
      summary: "Corrige ou remove o pr\xF3prio coment\xE1rio"
    },
    setFinanceExpenseLabels: {
      method: "PUT",
      path: "/:id/labels",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      body: z27.object({ labelIds: z27.array(z27.number().int()).max(50) }),
      responses: {
        200: z27.array(appliedLabelSchema),
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "P\xF5e e tira as etiquetas dos setores de quem grava"
    },
    setFinanceExpenseAssignee: {
      method: "PUT",
      path: "/:id/assignee",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      body: z27.object({ userId: z27.string().nullable() }),
      responses: {
        200: z27.object({ id: z27.string(), name: z27.string() }).nullable(),
        ...defaultErrors
      },
      summary: "Define ou tira o respons\xE1vel pela despesa"
    },
    listFinanceExpenseFiles: {
      method: "GET",
      path: "/:id/files",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      responses: { 200: z27.array(financeExpenseFileSchema), ...defaultErrors },
      summary: "Os arquivos da despesa: os tr\xEAs dela e os anexados"
    },
    attachFinanceExpenseFile: {
      method: "POST",
      path: "/:id/files",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      body: attachFinanceExpenseFileSchema,
      responses: { 201: financeExpenseFileSchema, ...defaultErrors },
      summary: "Anexa um arquivo \xE0 despesa"
    },
    financeExpenseFileUrl: {
      method: "GET",
      path: "/:id/files/:key/url",
      pathParams: z27.object({ id: z27.coerce.number().int(), key: z27.string() }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "A URL assinada de um arquivo da despesa"
    },
    archiveFinanceExpenseFile: {
      method: "POST",
      path: "/:id/files/:fileId/archiving",
      pathParams: z27.object({
        id: z27.coerce.number().int(),
        fileId: z27.coerce.number().int()
      }),
      body: z27.object({}),
      responses: { 200: z27.object({ ok: z27.literal(true) }), ...defaultErrors },
      summary: "Arquiva um anexo da despesa \u2014 os tr\xEAs dela n\xE3o se arquivam"
    },
    financeAct: {
      method: "POST",
      path: "/:id/acts",
      pathParams: z27.object({ id: z27.coerce.number().int() }),
      body: expenseFinanceActBodySchema,
      responses: {
        200: financeExpenseDetailSchema,
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "Autoriza, pendencia, recusa ou devolve a despesa"
    }
  },
  { pathPrefix: "/finance/expenses" }
);

// src/revenues.ts
var c26 = initContract26();
var revenueStatusSchema = z28.enum(REVENUE_STATUSES);
var revenueSourceTypeSchema = z28.enum(REVENUE_SOURCE_TYPES);
var moneyInputSchema = z28.string().trim().max(20).refine((v) => {
  const money = parseMoneyInput(v);
  return money !== null && money > 0;
}, "Informe um valor maior que zero.");
var revenuePayerSchema = z28.object({
  kind: z28.enum(["associado", "beneficiario"]),
  id: z28.number().int(),
  /** O código do associado (`AS-000123`); nulo no beneficiário. */
  code: z28.string().nullable(),
  name: z28.string().nullable(),
  documentMasked: z28.string().nullable()
});
var optionSchema = z28.object({ id: z28.string(), name: z28.string() });
var revenueSummarySchema = z28.object({
  id: z28.number().int(),
  sourceType: revenueSourceTypeSchema,
  payer: revenuePayerSchema,
  amount: moneySchema,
  dueDate: businessDateSchema,
  competencyDate: businessDateSchema,
  description: z28.string(),
  status: revenueStatusSchema,
  /** O nome da categoria no catálogo da base; nulo quando não há categoria ou ela saiu do catálogo. */
  categoryName: z28.string().nullable(),
  paymentAccount: z28.object({ id: z28.number().int(), name: z28.string() }),
  receivedAmount: moneySchema.nullable(),
  receivedDate: businessDateSchema.nullable(),
  /** A situação da cobrança mais recente, quando houver — a coluna ao lado da situação da receita. */
  lastChargeStatus: chargeSummarySchema.shape.status.nullable(),
  /** As etiquetas dos setores de quem pergunta — o cartão do quadro as mostra. */
  labels: z28.array(appliedLabelSchema),
  assignee: z28.object({ id: z28.string(), name: z28.string() }).nullable(),
  createdAt: instantSchema
});
var revenueContaAzulSchema = z28.object({
  state: z28.enum(["nao_enviada", "enviando", "lancada", "recusada"]),
  eventCode: z28.string().nullable(),
  settled: z28.boolean(),
  message: z28.string().nullable()
});
var revenueOriginSchema = z28.discriminatedUnion("kind", [
  z28.object({ kind: z28.literal("avulsa"), createdByName: z28.string().nullable() }),
  z28.object({
    kind: z28.literal("periferico"),
    peripheralId: z28.number().int(),
    /** O pedido do link, ainda vivo ou substituído por um novo. */
    linkReplaced: z28.boolean()
  }),
  /** O crachá não foi achado — a tela diz, em vez de inventar. */
  z28.object({ kind: z28.literal("desconhecida") })
]);
var revenueDetailSchema = revenueSummarySchema.extend({
  origin: revenueOriginSchema,
  contaAzul: revenueContaAzulSchema,
  category: optionSchema.nullable(),
  notes: z28.string().nullable(),
  cancelReason: z28.string().nullable(),
  /** O comprovante da baixa à mão. A cobrança paga tem o dela, na própria cobrança. */
  receiptFileName: z28.string().nullable(),
  /** Da mais nova para a mais velha. */
  charges: z28.array(chargeSummarySchema),
  /** O que esta pessoa pode fazer com ela agora — a tela não deduz. */
  can: z28.object({
    charge: z28.boolean(),
    settle: z28.boolean(),
    cancel: z28.boolean(),
    /** Mandar ao Conta Azul agora — o lançamento e a baixa que estejam pendentes. */
    syncContaAzul: z28.boolean()
  }),
  /** Por que não pode cobrar, quando não pode — a tela diz em vez de esconder. */
  chargeBlockedReason: z28.string().nullable()
});
var revenueFileSchema = z28.object({
  /** O que a rota da URL recebe: `comprovante` ou o id do anexo. */
  key: z28.string(),
  slot: z28.enum(["comprovante", "anexo"]),
  fileName: z28.string(),
  mimeType: z28.string().nullable(),
  sizeBytes: z28.number().int().nullable(),
  createdAt: instantSchema.nullable(),
  createdByName: z28.string().nullable()
});
var createRevenueSchema = z28.object({
  payer: z28.discriminatedUnion("kind", [
    z28.object({ kind: z28.literal("associado"), memberId: z28.number().int().positive() }),
    z28.object({
      kind: z28.literal("beneficiario"),
      beneficiaryId: z28.number().int().positive()
    })
  ]),
  amount: moneyInputSchema,
  dueDate: businessDateSchema,
  competencyDate: businessDateSchema,
  /** O id da categoria de receita no Conta Azul da base. */
  category: z28.string().trim().min(1, "Escolha a categoria."),
  description: z28.string().trim().min(1, "Escreva a descri\xE7\xE3o.").max(REVENUE_DESCRIPTION_MAX),
  notes: z28.string().trim().max(REVENUE_NOTES_MAX).nullable().optional(),
  /** Já emite a cobrança no provedor ao lançar. Só para o associado. */
  charge: z28.boolean().default(false)
});
var settleRevenueSchema = z28.object({
  amount: moneyInputSchema,
  date: businessDateSchema,
  file: attachClaimDocumentSchema
});
var cancelRevenueSchema = z28.object({
  reason: z28.string().trim().min(3, "Escreva o motivo.").max(500)
});
var revenueBeneficiarySchema = z28.object({
  id: z28.number().int(),
  name: z28.string(),
  documentMasked: z28.string().nullable()
});
var paymentAccountSchema = z28.object({
  id: z28.number().int(),
  name: z28.string(),
  provider: z28.enum(PAYMENT_ACCOUNT_PROVIDERS),
  isDefault: z28.boolean(),
  /** A conta financeira no Conta Azul da base; nula até o Financeiro escolher. */
  contaazulAccount: optionSchema.nullable()
});
var updatePaymentAccountSchema = z28.object({
  name: z28.string().trim().min(2, "Escreva o nome.").max(80),
  contaazulAccountId: z28.string().trim().min(1).nullable()
});
var idParams2 = z28.object({ id: z28.coerce.number().int() });
var revenuesContract = c26.router(
  {
    listRevenues: {
      method: "GET",
      path: "/revenues",
      query: z28.object({
        status: revenueStatusSchema.optional(),
        /** Descrição, observação, nome de quem paga ou o número da receita. */
        search: z28.string().trim().max(120).optional(),
        sourceType: revenueSourceTypeSchema.optional(),
        dueFrom: businessDateSchema.optional(),
        dueTo: businessDateSchema.optional(),
        page: z28.coerce.number().int().min(1).default(1)
      }),
      responses: {
        200: z28.object({
          items: z28.array(revenueSummarySchema),
          total: z28.number().int(),
          pageSize: z28.number().int()
        }),
        ...defaultErrors
      },
      summary: "As receitas desta base, da mais nova para a mais velha \u2014 at\xE9 500 por p\xE1gina, para o quadro"
    },
    revenueLabelOptions: {
      method: "GET",
      path: "/revenues/label-options",
      responses: { 200: z28.array(departmentLabelsSchema), ...defaultErrors },
      summary: "As etiquetas dos setores de quem pergunta, para a receita"
    },
    revenueAssigneeOptions: {
      method: "GET",
      path: "/revenues/assignee-options",
      responses: {
        200: z28.array(z28.object({ id: z28.string(), name: z28.string() })),
        ...defaultErrors
      },
      summary: "Quem lan\xE7a receita nesta base \u2014 e por isso pode ser o respons\xE1vel"
    },
    revenueActivity: {
      method: "GET",
      path: "/revenues/:id/activity",
      pathParams: idParams2,
      responses: { 200: z28.array(financeExpenseActivitySchema), ...defaultErrors },
      summary: "O caminho da receita e os coment\xE1rios, do mais novo para o mais velho"
    },
    commentRevenue: {
      method: "POST",
      path: "/revenues/:id/comments",
      pathParams: idParams2,
      body: z28.object({ body: z28.string().trim().min(1).max(4e3) }),
      responses: { 201: financeExpenseCommentSchema, ...defaultErrors },
      summary: "Comenta na receita"
    },
    updateRevenueComment: {
      method: "PATCH",
      path: "/revenues/:id/comments/:commentId",
      pathParams: z28.object({
        id: z28.coerce.number().int(),
        commentId: z28.coerce.number().int()
      }),
      body: z28.union([
        z28.object({ body: z28.string().trim().min(1).max(4e3) }),
        z28.object({ archived: z28.literal(true) })
      ]),
      responses: { 200: financeExpenseCommentSchema, ...defaultErrors },
      summary: "Corrige ou remove o pr\xF3prio coment\xE1rio"
    },
    setRevenueLabels: {
      method: "PUT",
      path: "/revenues/:id/labels",
      pathParams: idParams2,
      body: z28.object({ labelIds: z28.array(z28.number().int()).max(50) }),
      responses: {
        200: z28.array(appliedLabelSchema),
        409: apiErrorSchema,
        ...defaultErrors
      },
      summary: "P\xF5e e tira as etiquetas dos setores de quem grava"
    },
    setRevenueAssignee: {
      method: "PUT",
      path: "/revenues/:id/assignee",
      pathParams: idParams2,
      body: z28.object({ userId: z28.string().nullable() }),
      responses: {
        200: z28.object({ id: z28.string(), name: z28.string() }).nullable(),
        ...defaultErrors
      },
      summary: "Define ou tira o respons\xE1vel pela receita"
    },
    listRevenueFiles: {
      method: "GET",
      path: "/revenues/:id/files",
      pathParams: idParams2,
      responses: { 200: z28.array(revenueFileSchema), ...defaultErrors },
      summary: "O comprovante da baixa \xE0 m\xE3o e os anexos da receita"
    },
    attachRevenueFile: {
      method: "POST",
      path: "/revenues/:id/files",
      pathParams: idParams2,
      body: attachClaimDocumentSchema,
      responses: { 201: revenueFileSchema, ...defaultErrors },
      summary: "Anexa um arquivo \xE0 receita"
    },
    revenueFileUrl: {
      method: "GET",
      path: "/revenues/:id/files/:key/url",
      pathParams: z28.object({ id: z28.coerce.number().int(), key: z28.string() }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "A URL assinada de um arquivo da receita"
    },
    archiveRevenueFile: {
      method: "POST",
      path: "/revenues/:id/files/:fileId/archiving",
      pathParams: z28.object({
        id: z28.coerce.number().int(),
        fileId: z28.coerce.number().int()
      }),
      body: z28.object({}),
      responses: { 200: z28.object({ ok: z28.literal(true) }), ...defaultErrors },
      summary: "Arquiva um anexo da receita"
    },
    getRevenue: {
      method: "GET",
      path: "/revenues/:id",
      pathParams: idParams2,
      responses: { 200: revenueDetailSchema, ...defaultErrors },
      summary: "A receita, com as cobran\xE7as e o caminho dela"
    },
    revenueCategories: {
      method: "GET",
      path: "/revenues/categories",
      responses: { 200: z28.object({ items: z28.array(optionSchema) }), ...defaultErrors },
      summary: "As categorias de receita do Conta Azul desta base"
    },
    findRevenueBeneficiaries: {
      method: "POST",
      path: "/revenues/beneficiaries",
      body: z28.object({
        query: z28.string().trim().min(3, "Digite ao menos 3 caracteres.").max(120)
      }),
      responses: {
        200: z28.object({ items: z28.array(revenueBeneficiarySchema) }),
        ...defaultErrors
      },
      summary: "Procura o benefici\xE1rio da plataforma pelo nome ou documento"
    },
    createRevenue: {
      method: "POST",
      path: "/revenues",
      body: createRevenueSchema,
      responses: { 201: revenueDetailSchema, ...defaultErrors },
      summary: "Lan\xE7a uma receita avulsa, e emite a cobran\xE7a se pedido"
    },
    chargeRevenue: {
      method: "POST",
      path: "/revenues/:id/charge",
      pathParams: idParams2,
      body: z28.object({}).optional(),
      responses: { 200: revenueDetailSchema, ...defaultErrors },
      summary: "Emite a cobran\xE7a da receita avulsa no provedor da conta dela"
    },
    settleRevenue: {
      method: "POST",
      path: "/revenues/:id/settle",
      pathParams: idParams2,
      body: settleRevenueSchema,
      responses: { 200: revenueDetailSchema, ...defaultErrors },
      summary: "D\xE1 a baixa \xE0 m\xE3o, com comprovante"
    },
    cancelRevenue: {
      method: "POST",
      path: "/revenues/:id/cancel",
      pathParams: idParams2,
      body: cancelRevenueSchema,
      responses: { 200: revenueDetailSchema, ...defaultErrors },
      summary: "Cancela a receita avulsa aberta, e a cobran\xE7a aberta dela"
    },
    syncRevenueContaAzul: {
      method: "POST",
      path: "/revenues/:id/conta-azul",
      pathParams: idParams2,
      body: z28.object({}).optional(),
      responses: { 200: revenueDetailSchema, ...defaultErrors },
      summary: "Lan\xE7a a receita no Conta Azul e d\xE1 a baixa que estiver pendente, agora"
    },
    revenueReceiptUrl: {
      method: "GET",
      path: "/revenues/:id/receipt",
      pathParams: idParams2,
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "A URL assinada do comprovante da baixa \xE0 m\xE3o \u2014 cinco minutos"
    },
    listPaymentAccounts: {
      method: "GET",
      path: "/payment-accounts",
      responses: {
        200: z28.object({
          items: z28.array(paymentAccountSchema),
          /** As contas financeiras do Conta Azul da base, para escolher. Vazio sem catálogo. */
          contaazulAccounts: z28.array(optionSchema)
        }),
        ...defaultErrors
      },
      summary: "As contas de recebimento desta base"
    },
    updatePaymentAccount: {
      method: "PATCH",
      path: "/payment-accounts/:id",
      pathParams: idParams2,
      body: updatePaymentAccountSchema,
      responses: { 200: paymentAccountSchema, ...defaultErrors },
      summary: "Muda o nome e a conta financeira do Conta Azul"
    }
  },
  { pathPrefix: "/finance" }
);

// src/finance-dashboard.ts
import { initContract as initContract27 } from "@ts-rest/core";
import { z as z29 } from "zod";
import { FINANCE_DASHBOARD_DATE_KINDS } from "@evogard/domain";
var c27 = initContract27();
var tallySchema = z29.object({
  count: z29.number().int(),
  amount: moneySchema,
  /** A parte já paga (despesa) ou recebida (receita). */
  settled: moneySchema
});
var financeDashboardRowSchema = z29.object({
  key: z29.string(),
  label: z29.string(),
  /** A linha do que o painel não sabe: sem veículo, sem endereço, sem time. */
  gap: z29.boolean(),
  revenue: tallySchema,
  expense: tallySchema
});
var openSchema = z29.object({
  revenue: z29.object({ count: z29.number().int(), amount: moneySchema }),
  expense: z29.object({ count: z29.number().int(), amount: moneySchema })
});
var financeDashboardSchema = z29.object({
  revenue: tallySchema,
  expense: tallySchema,
  /**
   * Os lançamentos do período ainda em aberto (`financeDashboardOpenKind`):
   * vencidos, e aguardando dentro do prazo. Pela data do pagamento os dois
   * vêm zerados — o que está em aberto não tem essa data.
   */
  overdue: openSchema,
  awaiting: openSchema,
  /** O período anterior, de mesmo tamanho, e o que entrou e saiu nele — a variação do cartão. */
  previous: z29.object({
    from: businessDateSchema,
    to: businessDateSchema,
    revenue: moneySchema,
    expense: moneySchema
  }),
  /** Os doze meses que terminam no fim do período, do mais velho para o mais novo. */
  trend: z29.array(
    z29.object({ month: z29.string(), revenue: moneySchema, expense: moneySchema })
  ),
  byState: z29.array(financeDashboardRowSchema),
  byCity: z29.array(financeDashboardRowSchema),
  byConsultant: z29.array(financeDashboardRowSchema),
  byTeam: z29.array(financeDashboardRowSchema),
  byVehicleType: z29.array(financeDashboardRowSchema),
  byVehiclePlan: z29.array(financeDashboardRowSchema)
});
var financeDashboardContract = c27.router(
  {
    dashboard: {
      method: "GET",
      path: "/dashboard",
      query: z29.object({
        from: businessDateSchema,
        to: businessDateSchema,
        basis: z29.enum(FINANCE_DASHBOARD_DATE_KINDS).default("competencia")
      }).refine((q) => q.from <= q.to, {
        message: "O in\xEDcio do per\xEDodo vem depois do fim.",
        path: ["to"]
      }),
      responses: { 200: financeDashboardSchema, ...defaultErrors },
      summary: "A receita e a despesa do per\xEDodo, por cidade, consultor e ve\xEDculo"
    }
  },
  { pathPrefix: "/finance" }
);

// src/team-map.ts
import { initContract as initContract28 } from "@ts-rest/core";
import { z as z30 } from "zod";
import {
  CLAIMS_TEAM_MAP_METRICS,
  FINANCE_DASHBOARD_DATE_KINDS as FINANCE_DASHBOARD_DATE_KINDS2,
  FINANCE_TEAM_MAP_METRICS,
  TEAM_MAP_NODE_KINDS
} from "@evogard/domain";
var c28 = initContract28();
var tallySchema2 = z30.object({ count: z30.number().int(), amount: moneySchema });
var valuesOf = (metrics) => z30.object(
  Object.fromEntries(metrics.map((m) => [m, tallySchema2]))
);
var nodeOf = (values) => z30.object({
  key: z30.string(),
  parentKey: z30.string().nullable(),
  kind: z30.enum(TEAM_MAP_NODE_KINDS),
  label: z30.string(),
  sub: z30.string(),
  leader: z30.boolean(),
  /** A foto do consultor ou do gestor — URL assinada, de uma hora. Nula: as iniciais. */
  photoUrl: z30.string().nullable(),
  values
});
var financeTeamMapNodeSchema = nodeOf(valuesOf(FINANCE_TEAM_MAP_METRICS));
var claimsTeamMapNodeSchema = nodeOf(valuesOf(CLAIMS_TEAM_MAP_METRICS));
var inOrder = (q) => q.from <= q.to;
var outOfOrder = { message: "O in\xEDcio do per\xEDodo vem depois do fim.", path: ["to"] };
var financeTeamMapContract = c28.router(
  {
    teamMap: {
      method: "GET",
      path: "/team-map",
      query: z30.object({
        from: businessDateSchema,
        to: businessDateSchema,
        basis: z30.enum(FINANCE_DASHBOARD_DATE_KINDS2).default("competencia")
      }).refine(inOrder, outOfOrder),
      responses: {
        200: z30.object({ nodes: z30.array(financeTeamMapNodeSchema) }),
        ...defaultErrors
      },
      summary: "A receita e a despesa do per\xEDodo, pela montagem de hoje do time"
    }
  },
  { pathPrefix: "/finance" }
);
var claimsTeamMapContract = c28.router(
  {
    teamMap: {
      method: "GET",
      path: "/management/team-map",
      query: z30.object({ from: businessDateSchema, to: businessDateSchema }).refine(inOrder, outOfOrder),
      responses: {
        200: z30.object({ nodes: z30.array(claimsTeamMapNodeSchema) }),
        ...defaultErrors
      },
      summary: "As ocorr\xEAncias do per\xEDodo e o custo delas, pela montagem de hoje do time"
    }
  },
  { pathPrefix: "/claims" }
);

// src/useful-links.ts
import { initContract as initContract29 } from "@ts-rest/core";
import { z as z31 } from "zod";
import { USEFUL_LINK_ICONS, usefulLinkUrl } from "@evogard/domain";
var c29 = initContract29();
var usefulLinkIconSchema = z31.enum(USEFUL_LINK_ICONS);
var usefulLinkSchema = z31.object({
  /** `bigserial`, e todo id de bigserial viaja como texto na plataforma. */
  id: z31.string(),
  title: z31.string(),
  url: z31.string(),
  description: z31.string().nullable(),
  icon: usefulLinkIconSchema,
  updatedAt: instantSchema
});
var editableUsefulLinkSchema = z31.object({
  title: z31.string().trim().min(1).max(80),
  url: z31.string().max(2e3).transform((raw, ctx) => {
    const url = usefulLinkUrl(raw);
    if (url === null) {
      ctx.addIssue({
        code: z31.ZodIssueCode.custom,
        message: "Informe o endere\xE7o de um site, como https://exemplo.com.br."
      });
      return z31.NEVER;
    }
    return url;
  }),
  description: z31.string().trim().max(160).nullable().transform((v) => v === "" ? null : v),
  icon: usefulLinkIconSchema
});
var usefulLinksContract = c29.router(
  {
    listUsefulLinks: {
      method: "GET",
      path: "/useful-links",
      responses: { 200: z31.array(usefulLinkSchema), ...defaultErrors },
      summary: "Lista os links \xFAteis da base atual, em ordem alfab\xE9tica"
    },
    createUsefulLink: {
      method: "POST",
      path: "/useful-links",
      body: editableUsefulLinkSchema,
      responses: { 201: usefulLinkSchema, ...defaultErrors },
      summary: "Cadastra um link \xFAtil na base atual"
    },
    editUsefulLink: {
      method: "PUT",
      path: "/useful-links/:id",
      pathParams: z31.object({ id: z31.string() }),
      body: editableUsefulLinkSchema,
      responses: { 200: usefulLinkSchema, ...defaultErrors },
      summary: "Edita nome, endere\xE7o, descri\xE7\xE3o ou \xEDcone de um link \xFAtil"
    },
    /** Arquiva (convenção 9): o link some da tela de todo mundo. */
    removeUsefulLink: {
      method: "DELETE",
      path: "/useful-links/:id",
      pathParams: z31.object({ id: z31.string() }),
      body: z31.object({}).optional(),
      responses: { 200: z31.array(usefulLinkSchema), ...defaultErrors },
      summary: "Remove um link \xFAtil da base atual"
    }
  },
  { pathPrefix: "/platform" }
);

// src/holidays.ts
import { initContract as initContract30 } from "@ts-rest/core";
import { z as z32 } from "zod";
var c30 = initContract30();
var holidaySchema = z32.object({
  /** `bigserial`, e todo id de bigserial viaja como texto na plataforma. */
  id: z32.string(),
  /** `AAAA-MM-DD`. */
  date: z32.string(),
  name: z32.string(),
  /** Repete o dia e o mês todo ano. */
  recurring: z32.boolean(),
  updatedAt: instantSchema
});
var createHolidaySchema = z32.object({
  date: z32.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Informe a data no formato AAAA-MM-DD."),
  name: z32.string().trim().min(1).max(80),
  recurring: z32.boolean()
});
var holidaysContract = c30.router(
  {
    listHolidays: {
      method: "GET",
      path: "/holidays",
      responses: { 200: z32.array(holidaySchema), ...defaultErrors },
      summary: "Lista os feriados da base atual, por data"
    },
    createHoliday: {
      method: "POST",
      path: "/holidays",
      body: createHolidaySchema,
      responses: { 201: z32.array(holidaySchema), ...defaultErrors },
      summary: "Cadastra um feriado na base atual"
    },
    /** Arquiva (convenção 9): o dia volta a ser útil para o que for contado depois. */
    removeHoliday: {
      method: "DELETE",
      path: "/holidays/:id",
      pathParams: z32.object({ id: z32.string() }),
      body: z32.object({}).optional(),
      responses: { 200: z32.array(holidaySchema), ...defaultErrors },
      summary: "Remove um feriado da base atual"
    }
  },
  { pathPrefix: "/platform" }
);

// src/maps.ts
import { initContract as initContract31 } from "@ts-rest/core";
import { z as z33 } from "zod";
var c31 = initContract31();
var geocodedAddressSchema = z33.object({
  /** O endereço como o Google o escreveu. */
  label: z33.string(),
  lat: z33.number().min(-90).max(90),
  lng: z33.number().min(-180).max(180),
  /**
   * O Google achou o PONTO (o lote, ou o número na faixa da rua) — e não só o
   * centro da rua, do CEP ou da cidade. Posição imprecisa não entra sozinha no
   * cadastro: a tela pede para marcar no mapa.
   */
  precise: z33.boolean()
});
var mapsContract = c31.router(
  {
    geocode: {
      method: "POST",
      path: "/geocode",
      body: z33.object({ address: z33.string().trim().min(3).max(300) }),
      /* 404 é "o Google não achou", e não falha: quem pede decide o que dizer. */
      responses: { 200: geocodedAddressSchema, ...defaultErrors },
      summary: "Acha a posi\xE7\xE3o de um endere\xE7o, pelo Google, e diz se \xE9 o ponto exato"
    }
  },
  { pathPrefix: "/platform" }
);

// src/assistance.ts
import { initContract as initContract32 } from "@ts-rest/core";
import { z as z34 } from "zod";
import {
  ASSISTANCE_PRICINGS,
  ASSISTANCE_SERVICE_TYPES as ASSISTANCE_SERVICE_TYPES2,
  PROVIDER_PAYMENT_MODES as PROVIDER_PAYMENT_MODES2
} from "@evogard/domain";
var c32 = initContract32();
var assistanceServiceTypeSchema = z34.enum(ASSISTANCE_SERVICE_TYPES2);
var providerPaymentModeSchema = z34.enum(PROVIDER_PAYMENT_MODES2);
var assistancePricingSchema = z34.enum(ASSISTANCE_PRICINGS);
var assistanceCatalogServiceSchema = z34.object({
  id: z34.number().int(),
  code: z34.string(),
  name: z34.string(),
  serviceType: assistanceServiceTypeSchema,
  /** Só no residencial: o motivo que o serviço já carrega. */
  fixedVariant: z34.string().nullable(),
  /**
   * O adicional (02/11/2026): não se aciona sozinho, soma ao serviço escolhido
   * do mesmo grupo — o patins no reboque. Só com valor fixo, e sem cota.
   */
  addon: z34.boolean(),
  sortOrder: z34.number().int(),
  archivedAt: instantSchema.nullable(),
  /** Quantos prestadores ativos da base atendem este serviço. */
  providerCount: z34.number().int()
});
var assistanceCatalogSchema = z34.array(
  z34.object({
    serviceType: assistanceServiceTypeSchema,
    name: z34.string(),
    needsDestination: z34.boolean(),
    services: z34.array(assistanceCatalogServiceSchema)
  })
);
var assistanceServiceWriteSchema = z34.object({
  serviceType: assistanceServiceTypeSchema,
  name: z34.string().trim().min(3).max(80),
  /** Obrigatório no residencial — é ele que escolhe a categoria do Conta Azul. */
  fixedVariant: z34.string().nullable().default(null)
});
var assistanceServicePatchSchema = z34.object({
  name: z34.string().trim().min(3).max(80),
  sortOrder: z34.number().int().min(0).max(999),
  /** `true` arquiva: some do acionamento e do cadastro; o histórico fica. */
  archived: z34.boolean()
}).partial();
var assistancePriceSchema = z34.object({
  id: z34.number().int(),
  serviceId: z34.number().int(),
  serviceCode: z34.string(),
  serviceName: z34.string(),
  /** O grupo do serviço. */
  serviceType: assistanceServiceTypeSchema,
  /** O serviço foi arquivado: a linha fica, e a tela pede para ligar a um que vale. */
  serviceArchived: z34.boolean(),
  pricing: assistancePricingSchema,
  /** A saída, ou o preço fixo; nulo no a combinar. */
  flatAmount: moneySchema.nullable(),
  /** Metros cobertos pela saída — só em `saida_e_km`. */
  includedM: z34.number().int().nullable(),
  /** O valor do km além da saída — só em `saida_e_km`. */
  perKmAmount: moneySchema.nullable(),
  updatedAt: instantSchema
});
var assistanceCompanySchema = z34.object({
  id: z34.number().int(),
  name: z34.string(),
  document: z34.string(),
  phone: z34.string().nullable(),
  /** Para onde vai o script e o link (`assistencia-04`); nulo, a tela avisa. */
  whatsapp: z34.string().nullable(),
  /** "Messejana, Fortaleza" — o que o cadastro tem do endereço da base. */
  locationLabel: z34.string().nullable(),
  /**
   * Sem coordenada não há rota até o associado, e o prestador fica fora do
   * ranking pela chegada. A tela diz isso em vez de esconder o prestador.
   */
  hasCoordinates: z34.boolean(),
  archivedAt: instantSchema.nullable()
});
var assistanceProviderSchema = z34.object({
  id: z34.number().int(),
  company: assistanceCompanySchema,
  paymentMode: providerPaymentModeSchema,
  serviceRadiusM: z34.number().int().nullable(),
  prices: z34.array(assistancePriceSchema),
  archivedAt: instantSchema.nullable()
});
var assistanceProviderWriteSchema = z34.object({
  companyId: z34.number().int().positive(),
  paymentMode: providerPaymentModeSchema,
  /** Em metros; nulo é sem limite. */
  serviceRadiusM: z34.number().int().positive().nullable().optional()
});
var assistanceProviderPatchSchema = z34.object({
  paymentMode: providerPaymentModeSchema,
  serviceRadiusM: z34.number().int().positive().nullable(),
  /** `true` tira da assistência (arquiva a ficha); `false` volta. A empresa não muda. */
  archived: z34.boolean()
}).partial();
var assistancePriceWriteSchema = z34.discriminatedUnion("pricing", [
  z34.object({
    pricing: z34.literal("saida_e_km"),
    flatAmount: moneySchema,
    includedM: z34.number().int().min(0),
    perKmAmount: moneySchema
  }),
  z34.object({ pricing: z34.literal("fixo"), flatAmount: moneySchema }),
  /** Atende sem preço de tabela: o valor de cada acionamento é informado depois. */
  z34.object({ pricing: z34.literal("a_combinar") })
]);
var assistanceProviderServicesWriteSchema = z34.object({
  services: z34.array(
    z34.discriminatedUnion("pricing", [
      z34.object({
        serviceId: z34.number().int().positive(),
        pricing: z34.literal("saida_e_km"),
        flatAmount: moneySchema,
        includedM: z34.number().int().min(0),
        perKmAmount: moneySchema
      }),
      z34.object({
        serviceId: z34.number().int().positive(),
        pricing: z34.literal("fixo"),
        flatAmount: moneySchema
      }),
      z34.object({
        serviceId: z34.number().int().positive(),
        pricing: z34.literal("a_combinar")
      })
    ])
  ).max(200).refine((list) => new Set(list.map((s) => s.serviceId)).size === list.length, {
    message: "O mesmo servi\xE7o apareceu duas vezes."
  })
});
var providerParams = z34.object({ id: z34.coerce.number().int() });
var assistanceContract = c32.router(
  {
    listProviders: {
      method: "GET",
      path: "/providers",
      query: z34.object({
        search: z34.string().trim().max(80).optional(),
        /*
         * `z.enum` e não `z.coerce.boolean()`: na query string tudo é string, e
         * `Boolean('false')` é `true` — "Só os que atendem" mostrava os
         * retirados (achado no percurso de `assistencia-26`, 22/10/2026).
         */
        includeArchived: z34.enum(["true", "false"]).default("false").transform((v) => v === "true")
      }),
      responses: { 200: z34.array(assistanceProviderSchema), ...defaultErrors },
      summary: "Os prestadores da assist\xEAncia, com a tabela de pre\xE7o"
    },
    providerCandidates: {
      method: "GET",
      path: "/providers/candidates",
      query: z34.object({ search: z34.string().trim().max(80).optional() }),
      responses: { 200: z34.array(assistanceCompanySchema), ...defaultErrors },
      summary: "As empresas prestadoras do cadastro que ainda n\xE3o atendem a assist\xEAncia"
    },
    /*
     * O prestador de uma empresa (30/10/2026): a seção "Assistência 24h" da
     * ficha da empresa pergunta por aqui. 404 é "esta empresa não está na
     * assistência", e a seção oferece pôr.
     */
    getProvider: {
      method: "GET",
      path: "/providers/:id",
      pathParams: providerParams,
      responses: { 200: assistanceProviderSchema, ...defaultErrors },
      summary: "A empresa como prestador da assist\xEAncia, com a tabela de pre\xE7o"
    },
    createProvider: {
      method: "POST",
      path: "/providers",
      body: assistanceProviderWriteSchema,
      responses: { 201: assistanceProviderSchema, ...defaultErrors },
      summary: "P\xF5e uma empresa prestadora na assist\xEAncia"
    },
    updateProvider: {
      method: "PATCH",
      path: "/providers/:id",
      pathParams: providerParams,
      body: assistanceProviderPatchSchema,
      responses: { 200: assistanceProviderSchema, ...defaultErrors },
      summary: "Muda a forma de pagamento, o raio, ou tira da assist\xEAncia"
    },
    setProviderServices: {
      method: "PUT",
      path: "/providers/:id/services",
      pathParams: providerParams,
      body: assistanceProviderServicesWriteSchema,
      responses: { 200: assistanceProviderSchema, ...defaultErrors },
      summary: "Grava os servi\xE7os que o prestador atende, com a forma e os valores de cada um"
    },
    /*
     * O catálogo (02/11/2026): os grupos e os serviços da base. Ver é de quem
     * aciona — o formulário pergunta por aqui —; mudar é de quem configura a
     * assistência, ao lado das cotas e dos prazos.
     */
    listServices: {
      method: "GET",
      path: "/services",
      responses: { 200: assistanceCatalogSchema, ...defaultErrors },
      summary: "Os grupos e os servi\xE7os da base, com quantos prestadores atendem cada um"
    },
    createService: {
      method: "POST",
      path: "/services",
      body: assistanceServiceWriteSchema,
      responses: { 201: assistanceCatalogServiceSchema, ...defaultErrors },
      summary: "Cria um servi\xE7o dentro de um grupo"
    },
    updateService: {
      method: "PATCH",
      path: "/services/:id",
      pathParams: providerParams,
      body: assistanceServicePatchSchema,
      responses: { 200: assistanceCatalogServiceSchema, ...defaultErrors },
      summary: "Renomeia, reordena, arquiva ou volta um servi\xE7o"
    }
  },
  { pathPrefix: "/assistance" }
);

// src/assistance-dispatches.ts
import { initContract as initContract33 } from "@ts-rest/core";
import { z as z35 } from "zod";
import {
  ALL_SERVICE_VARIANTS,
  DISPATCH_FILE_KINDS,
  DISPATCH_PROGRESS,
  DISPATCH_PROGRESS_STEPS,
  MEMBER_TRACKING_STAGES,
  DISPATCH_STATUSES,
  DISPATCH_STEP_KINDS,
  PROVIDER_BUTTON_STEPS,
  DISPATCH_VALUATION_SOURCES,
  SETTLEMENT_METHODS,
  TOW_KINDS,
  VEHICLE_SITUATION_TONES as VEHICLE_SITUATION_TONES2,
  serviceVariantFits
} from "@evogard/domain";
var c33 = initContract33();
var latLngSchema = z35.object({
  lat: z35.number().min(-90).max(90),
  lng: z35.number().min(-180).max(180)
});
var placeSchema = latLngSchema.extend({
  label: z35.string().trim().min(3).max(300)
});
var legSchema = z35.object({ m: z35.number().int(), s: z35.number().int() });
var vehicleSituationSchema = z35.object({ label: z35.string(), tone: z35.enum(VEHICLE_SITUATION_TONES2) }).nullable();
var assistanceVehicleSchema = z35.object({
  vehicleId: z35.number().int(),
  code: z35.string(),
  plate: z35.string().nullable(),
  label: z35.string(),
  situation: vehicleSituationSchema,
  memberId: z35.number().int(),
  memberName: z35.string().nullable(),
  memberPhone: z35.string().nullable(),
  /**
   * Os nomes dos produtos do veículo — "GUINCHO 1000KM" — para a central ler a
   * cobertura. O sistema não lê km de dentro do nome (P3, regra 14).
   */
  products: z35.array(z35.string()),
  /** O rascunho aberto desta placa, se houver — um acionamento em `rascunho` (P15). */
  draft: z35.object({
    id: z35.number().int(),
    code: z35.string(),
    serviceType: assistanceServiceTypeSchema,
    createdByName: z35.string().nullable(),
    updatedAt: instantSchema
  }).nullable()
});
var vehicleSearchResultSchema = z35.array(
  z35.object({
    memberId: z35.number().int(),
    memberName: z35.string().nullable(),
    document: z35.string().nullable(),
    vehicles: z35.array(
      z35.object({
        vehicleId: z35.number().int(),
        code: z35.string(),
        plate: z35.string().nullable(),
        label: z35.string(),
        situation: vehicleSituationSchema
      })
    )
  })
);
var quoteRequestSchema = z35.object({
  /** O serviço do catálogo (02/11/2026): só quem o atende entra na lista. */
  serviceId: z35.number().int().positive(),
  /** O adicional (o patins, 02/11/2026): só quem o tem também entra na lista. */
  addonServiceId: z35.number().int().positive().nullable().default(null),
  origin: latLngSchema,
  destination: latLngSchema.nullable(),
  /** Metros cobertos pelo plano; nulo é sem limite. */
  coverageM: z35.number().int().min(0).nullable()
});
var quotedProviderSchema = z35.object({
  providerId: z35.number().int(),
  name: z35.string(),
  phone: z35.string().nullable(),
  whatsapp: z35.string().nullable(),
  locationLabel: z35.string().nullable(),
  /** A base do prestador, para o pino no mapa; nula sem coordenada no cadastro. */
  depot: latLngSchema.nullable(),
  paymentMode: providerPaymentModeSchema,
  price: assistancePriceSchema.nullable(),
  /** Da base até o associado — nulo sem coordenada ou sem rota. */
  approach: legSchema.nullable(),
  /** Do destino (ou do associado) de volta à base. */
  back: legSchema.nullable(),
  totalM: z35.number().int().nullable(),
  /**
   * Nulo quando não há preço para o serviço, ou não há como calcular o
   * percurso. Com adicional, já soma o valor dele.
   */
  evogardAmount: moneySchema.nullable(),
  /** O valor fixo do adicional na tabela deste prestador; nulo sem adicional. */
  addonAmount: moneySchema.nullable(),
  associateAmount: moneySchema.nullable(),
  /** Por que este prestador não pode ser escolhido — "fora do raio", "sem coordenada". */
  unavailable: z35.string().nullable()
});
var quoteSchema = z35.object({
  /** O trecho do reboque, do associado ao destino — o mesmo para todos. */
  tow: legSchema.nullable(),
  providers: z35.array(quotedProviderSchema),
  /** Os prestadores ativos da base que não atendem este serviço e ficaram de fora. */
  notServing: z35.number().int()
});
var dispatchFileSchema = z35.object({
  id: z35.number().int(),
  kind: z35.enum(DISPATCH_FILE_KINDS),
  fileName: z35.string(),
  mimeType: z35.string(),
  takenAt: instantSchema.nullable(),
  latitude: z35.number().nullable(),
  longitude: z35.number().nullable(),
  accuracyM: z35.number().int().nullable(),
  createdAt: instantSchema
});
var dispatchSettlementSchema = z35.object({
  id: z35.number().int(),
  kind: providerPaymentModeSchema,
  competence: businessDateSchema.nullable(),
  closedAt: instantSchema.nullable()
});
var dispatchStatusSchema = z35.enum(DISPATCH_STATUSES);
var dispatchRowSchema = z35.object({
  id: z35.number().int(),
  code: z35.string(),
  status: dispatchStatusSchema,
  serviceType: assistanceServiceTypeSchema,
  /** O serviço do catálogo — "Reboque Moto". */
  serviceName: z35.string(),
  plate: z35.string().nullable(),
  memberName: z35.string().nullable(),
  providerName: z35.string(),
  paymentMode: providerPaymentModeSchema,
  originLabel: z35.string(),
  destinationLabel: z35.string().nullable(),
  /** Nulo no a combinar, até o valor ser informado. */
  amount: moneySchema.nullable(),
  valuationSource: z35.enum(DISPATCH_VALUATION_SOURCES),
  scheduledFor: instantSchema.nullable(),
  executedAt: instantSchema.nullable(),
  canceledAt: instantSchema.nullable(),
  files: z35.object({
    hasOrigin: z35.boolean(),
    hasDestination: z35.boolean(),
    total: z35.number().int()
  }),
  settled: z35.boolean(),
  createdAt: instantSchema,
  createdByName: z35.string().nullable()
});
var dispatchProgressSchema = z35.enum(DISPATCH_PROGRESS);
var dispatchProgressSummarySchema = z35.object({
  progress: dispatchProgressSchema,
  /** O marco de roteiro mais recente, ou o acionamento quando não há nenhum. */
  since: instantSchema,
  /** A última posição recebida — marco com lugar ou ponto do rastro. */
  lastSignalAt: instantSchema.nullable(),
  /** Ainda não chegou e passou do prazo da base. Sem prazo, nunca. */
  late: z35.boolean(),
  /** Em curso e sem posição há mais que o prazo da base. Sem prazo, nunca. */
  signalLost: z35.boolean(),
  /** Os erros de placa esgotaram: o prestador espera quem acionou destravar. */
  plateLocked: z35.boolean()
});
var dispatchStepSchema = z35.object({
  id: z35.number().int(),
  kind: z35.enum(DISPATCH_STEP_KINDS),
  at: instantSchema,
  latitude: z35.number().nullable(),
  longitude: z35.number().nullable(),
  accuracyM: z35.number().int().nullable(),
  distanceM: z35.number().int().nullable(),
  etaS: z35.number().int().nullable(),
  typedPlate: z35.string().nullable(),
  reason: z35.string().nullable(),
  /** Quem destravou a placa; nulo quando foi o prestador, pelo link. */
  actorName: z35.string().nullable()
});
var dispatchProgressDetailSchema = dispatchProgressSummarySchema.extend({
  steps: z35.array(dispatchStepSchema),
  /** O próximo passo do roteiro; nulo depois de finalizar. */
  next: z35.enum(DISPATCH_PROGRESS_STEPS).nullable(),
  /** O roteiro deste serviço, para a tela desenhar os passos que faltam. */
  route: z35.array(z35.enum(DISPATCH_PROGRESS_STEPS)),
  arrival: z35.object({
    googleAt: instantSchema.nullable(),
    deadline: instantSchema.nullable()
  }),
  lastPosition: z35.object({
    at: instantSchema,
    lat: z35.number(),
    lng: z35.number(),
    accuracyM: z35.number().int().nullable()
  }).nullable(),
  /** O rastro, em ordem, para o mapa. */
  track: z35.array(z35.object({ at: instantSchema, lat: z35.number(), lng: z35.number() })),
  plateAttemptsLeft: z35.number().int(),
  /** Só quem acionou destrava a placa (decisão 1 de 29/10). */
  canUnlockPlate: z35.boolean(),
  /** A mensagem "abra o link" para copiar, enquanto o link vale. */
  reminder: z35.string().nullable()
});
var progressSettingsSchema = z35.object({
  arrivalPrepMin: z35.number().int().min(0).max(600).nullable(),
  arrivalSlackPct: z35.number().int().min(0).max(500).nullable(),
  noSignalMin: z35.number().int().min(1).max(240).nullable()
});
var ASSISTANCE_PROGRESS_EVENT = "assistencia:andamento";
var DISPATCH_LIST_FILTERS = [...DISPATCH_STATUSES, "descartado"];
var dispatchListRowSchema = z35.object({
  id: z35.number().int(),
  code: z35.string(),
  status: dispatchStatusSchema,
  discarded: z35.boolean(),
  serviceType: assistanceServiceTypeSchema,
  /** Nulo no rascunho que ainda não escolheu o serviço. */
  serviceName: z35.string().nullable(),
  plate: z35.string().nullable(),
  memberName: z35.string().nullable(),
  providerName: z35.string().nullable(),
  paymentMode: providerPaymentModeSchema.nullable(),
  originLabel: z35.string().nullable(),
  destinationLabel: z35.string().nullable(),
  amount: moneySchema.nullable(),
  scheduledFor: instantSchema.nullable(),
  executedAt: instantSchema.nullable(),
  canceledAt: instantSchema.nullable(),
  files: z35.object({
    hasOrigin: z35.boolean(),
    hasDestination: z35.boolean(),
    total: z35.number().int()
  }),
  settled: z35.boolean(),
  missing: z35.array(z35.string()),
  /** O andamento na rua — só do acionado. */
  progress: dispatchProgressSummarySchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
  createdByName: z35.string().nullable()
});
var dispatchEventSchema = z35.object({
  at: instantSchema,
  type: z35.string(),
  actorName: z35.string().nullable(),
  data: z35.record(z35.unknown())
});
var serviceVariantSchema = z35.enum(ALL_SERVICE_VARIANTS);
var dispatchDetailSchema = dispatchRowSchema.extend({
  serviceId: z35.number().int(),
  /** O adicional (o patins), com o valor fixo do dia — já somado em `amount`. */
  addon: z35.object({ serviceId: z35.number().int(), name: z35.string(), amount: moneySchema }).nullable(),
  towKind: z35.enum(TOW_KINDS).nullable(),
  serviceVariant: serviceVariantSchema.nullable(),
  vehicleKind: z35.string().nullable(),
  vehicleLabel: z35.string(),
  memberId: z35.number().int(),
  opaSuiteProtocol: z35.string(),
  origin: placeSchema,
  destination: placeSchema.nullable(),
  callbackPhone: z35.string(),
  description: z35.string(),
  providerNotes: z35.string().nullable(),
  provider: z35.object({
    id: z35.number().int(),
    name: z35.string(),
    phone: z35.string().nullable(),
    /** O WhatsApp da empresa (`assistencia-04`): para onde vai o script. */
    whatsapp: z35.string().nullable(),
    locationLabel: z35.string().nullable(),
    depot: latLngSchema.nullable()
  }),
  price: z35.object({
    pricing: assistancePricingSchema,
    flatAmount: moneySchema.nullable(),
    includedM: z35.number().int().nullable(),
    perKmAmount: moneySchema.nullable()
  }).nullable(),
  approach: legSchema.nullable(),
  tow: legSchema.nullable(),
  back: legSchema.nullable(),
  routePolyline: z35.string().nullable(),
  planCoverageM: z35.number().int().nullable(),
  countedM: z35.number().int().nullable(),
  associateExcessAmount: moneySchema,
  /** O "estou ciente" do associado pelo link: quando, e o valor que ele viu. */
  memberExcessAck: z35.object({ at: instantSchema, amount: moneySchema }).nullable(),
  serviceScript: z35.string(),
  /** O link do prestador enquanto vale — para copiar e mandar. */
  linkUrl: z35.string().nullable(),
  linkExpiresAt: instantSchema.nullable(),
  /**
   * O link do ASSOCIADO acompanhar até o prestador chegar (30/10/2026) —
   * outro código, que não troca quando o do prestador é reenviado.
   */
  memberLinkUrl: z35.string().nullable(),
  providerReportedM: z35.number().int().nullable(),
  cancelReason: z35.string().nullable(),
  settlement: dispatchSettlementSchema.nullable(),
  fileList: z35.array(dispatchFileSchema),
  history: z35.array(dispatchEventSchema),
  /** O andamento na rua — só do acionado. */
  progress: dispatchProgressDetailSchema.nullable()
});
var dispatchWriteSchema = z35.object({
  /** O grupo. O serviço tem de ser dele — a API confere, e o banco também. */
  serviceType: assistanceServiceTypeSchema,
  /** O serviço do catálogo (02/11/2026): é dele o preço de cada prestador. */
  serviceId: z35.number().int().positive(),
  /** O adicional do mesmo grupo — o patins no reboque (02/11/2026). */
  addonServiceId: z35.number().int().positive().nullable().default(null),
  vehicleId: z35.number().int().positive(),
  opaSuiteProtocol: z35.string().trim().min(3).max(60),
  origin: placeSchema,
  destination: placeSchema.nullable(),
  towKind: z35.enum(TOW_KINDS).nullable(),
  /** O motivo do reboque ou o serviço residencial — escolhe a categoria do Conta Azul. */
  serviceVariant: serviceVariantSchema.nullable(),
  vehicleKind: z35.string().trim().max(60).nullable(),
  callbackPhone: phoneSchema,
  description: z35.string().trim().min(3).max(2e3),
  providerNotes: z35.string().trim().max(1e3).nullable(),
  providerId: z35.number().int().positive(),
  /** Metros cobertos pelo plano, digitados pela central (P8); nulo é sem limite. */
  planCoverageM: z35.number().int().min(0).nullable(),
  /** O valor combinado, quando o serviço não tem preço na tabela do prestador. */
  informedAmount: moneySchema.nullable(),
  /** Agendar: o instante marcado. Nulo é acionar agora. */
  scheduledFor: instantSchema.nullable(),
  /**
   * O rascunho que está sendo enviado — o id de um acionamento em
   * `rascunho`. Ele muda de status na mesma linha, e o código `AC-` fica.
   */
  draftId: z35.number().int().positive().nullable(),
  /**
   * O motivo de quem libera acima da cota (01/11/2026). Só vale quando a cota
   * acabou, e só para quem tem `assistance.dispatches:liberar_cota`.
   */
  usageReleaseReason: z35.string().trim().min(3).max(500).nullable().default(null),
  /** O reboque emergencial de que este é o segundo reboque: não gasta cota. */
  continuesDispatchId: z35.number().int().positive().nullable().default(null)
}).refine(
  (d) => d.serviceType === "reboque" === (d.destination !== null && d.towKind !== null),
  {
    message: "Reboque pede destino e tipo de reboque; os outros servi\xE7os n\xE3o t\xEAm destino.",
    path: ["destination"]
  }
).refine((d) => serviceVariantFits(d.serviceType, d.serviceVariant), {
  message: "Escolha o motivo do reboque ou o servi\xE7o residencial.",
  path: ["serviceVariant"]
});
var dispatchFileUploadSchema = z35.object({
  nome: z35.string().min(1).max(255),
  typeMime: z35.string().min(3).max(160),
  content: z35.string().min(1)
});
var draftSchema = z35.object({
  id: z35.number().int(),
  code: z35.string(),
  serviceType: assistanceServiceTypeSchema,
  serviceId: z35.number().int().nullable(),
  addonServiceId: z35.number().int().nullable(),
  vehicleId: z35.number().int().nullable(),
  plate: z35.string().nullable(),
  memberName: z35.string().nullable(),
  opaSuiteProtocol: z35.string().nullable(),
  origin: placeSchema.nullable(),
  destination: placeSchema.nullable(),
  towKind: z35.enum(TOW_KINDS).nullable(),
  serviceVariant: serviceVariantSchema.nullable(),
  vehicleKind: z35.string().nullable(),
  callbackPhone: z35.string().nullable(),
  description: z35.string().nullable(),
  providerNotes: z35.string().nullable(),
  providerId: z35.number().int().nullable(),
  providerName: z35.string().nullable(),
  planCoverageM: z35.number().int().nullable(),
  informedAmount: moneySchema.nullable(),
  scheduledFor: instantSchema.nullable(),
  /** O que falta para enviar, na ordem do formulário (`draftMissing`). */
  missing: z35.array(z35.string()),
  archivedAt: instantSchema.nullable(),
  createdByName: z35.string().nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});
var draftWriteSchema = z35.object({
  serviceType: assistanceServiceTypeSchema,
  serviceId: z35.number().int().positive().nullable().default(null),
  addonServiceId: z35.number().int().positive().nullable().default(null),
  vehicleId: z35.number().int().positive().nullable(),
  opaSuiteProtocol: z35.string().trim().max(60).nullable(),
  origin: placeSchema.nullable(),
  destination: placeSchema.nullable(),
  towKind: z35.enum(TOW_KINDS).nullable(),
  serviceVariant: serviceVariantSchema.nullable(),
  vehicleKind: z35.string().trim().max(60).nullable(),
  callbackPhone: z35.string().trim().max(20).nullable(),
  description: z35.string().trim().max(2e3).nullable(),
  providerNotes: z35.string().trim().max(1e3).nullable(),
  providerId: z35.number().int().positive().nullable(),
  planCoverageM: z35.number().int().min(0).nullable(),
  informedAmount: moneySchema.nullable(),
  scheduledFor: instantSchema.nullable()
});
var pendingSettlementsSchema = z35.object({
  /** À vista, executados e sem acerto: o líder paga um a um. */
  cash: z35.array(dispatchRowSchema),
  /**
   * O a combinar sem valor (02/11/2026), só o À VISTA: não entra em pagamento
   * até alguém informar o valor que o prestador devolveu. O mensal sem valor
   * não vem aqui — ele entra no lote, e a líder digita o valor ao fechar.
   */
  awaitingAmount: z35.array(
    z35.object({
      id: z35.number().int(),
      code: z35.string(),
      executedAt: instantSchema,
      providerName: z35.string(),
      paymentMode: providerPaymentModeSchema,
      serviceName: z35.string(),
      plate: z35.string().nullable()
    })
  ),
  /**
   * Mensal: por prestador, todo acionamento executado e sem acerto, de
   * qualquer mês — é daqui que a líder marca o lote (30/10/2026). Desde
   * 02/11/2026 vem junto o que saiu SEM VALOR: o operador não sabe o valor do
   * mensal, o prestador repassa no fim do mês, e a líder o digita no lote.
   */
  monthly: z35.array(
    z35.object({
      providerId: z35.number().int(),
      providerName: z35.string(),
      dispatchCount: z35.number().int(),
      /** A soma do que já tem valor; os sem valor entram quando a líder digitar. */
      total: moneySchema,
      /** Quantos acionamentos estão sem valor. */
      withoutAmount: z35.number().int(),
      /** Sem dados bancários, a despesa não tem para quem nascer. */
      hasBeneficiary: z35.boolean(),
      /**
       * Os acionamentos, do mais antigo ao mais novo — só o que a lista
       * mostra: o `DispatchRow` inteiro aqui passa do limite de tipo do
       * contrato (TS7056).
       */
      dispatches: z35.array(
        z35.object({
          id: z35.number().int(),
          code: z35.string(),
          executedAt: instantSchema,
          memberName: z35.string().nullable(),
          plate: z35.string().nullable(),
          /** Nulo quando saiu sem valor: a líder digita no lote. */
          amount: moneySchema.nullable(),
          serviceName: z35.string(),
          /** A categoria do Conta Azul, curta; nula quando falta a variante. */
          category: z35.string().nullable()
        })
      )
    })
  )
});
var settlementSchema = z35.object({
  id: z35.number().int(),
  providerName: z35.string(),
  kind: providerPaymentModeSchema,
  competence: businessDateSchema.nullable(),
  total: moneySchema,
  dispatchCount: z35.number().int(),
  method: z35.enum(SETTLEMENT_METHODS).nullable(),
  /** O dia em que o líder pagou o à vista. */
  paidOn: businessDateSchema.nullable(),
  closedAt: instantSchema.nullable(),
  closedByName: z35.string().nullable(),
  financialExpenseId: z35.number().int().nullable()
});
var idParams3 = z35.object({ id: z35.coerce.number().int() });
var assistanceDispatchesContract = c33.router(
  {
    vehicleByPlate: {
      method: "GET",
      path: "/vehicles",
      /* Pela placa, ou pelo `id` do veículo escolhido na busca — a placa repete no cadastro. */
      query: z35.object({
        plate: z35.string().trim().min(5).max(10).optional(),
        id: z35.coerce.number().int().positive().optional()
      }),
      responses: { 200: assistanceVehicleSchema, ...defaultErrors },
      summary: "O ve\xEDculo e o associado pela placa ou pelo id, do n\xFAcleo"
    },
    searchVehicles: {
      method: "GET",
      path: "/vehicles/search",
      query: z35.object({ q: z35.string().trim().min(3).max(80) }),
      responses: { 200: vehicleSearchResultSchema, ...defaultErrors },
      summary: "Os associados e os ve\xEDculos pela placa, pelo nome ou pelo CPF/CNPJ"
    },
    placesAutocomplete: {
      method: "POST",
      path: "/places/autocomplete",
      body: z35.object({
        input: z35.string().trim().min(3).max(200),
        session: z35.string().min(8).max(64)
      }),
      responses: {
        200: z35.array(z35.object({ placeId: z35.string(), label: z35.string() })),
        ...defaultErrors
      },
      summary: "O que o Google sugere enquanto a central digita o endere\xE7o"
    },
    placeDetails: {
      method: "POST",
      path: "/places/details",
      body: z35.object({
        placeId: z35.string().min(1).max(300),
        session: z35.string().min(8).max(64)
      }),
      responses: { 200: placeSchema, ...defaultErrors },
      summary: "A sugest\xE3o escolhida vira endere\xE7o com coordenada"
    },
    placeFromLocation: {
      method: "POST",
      path: "/places/from-location",
      /* O link do Google Maps ou do WhatsApp, ou a coordenada solta (29/10/2026). */
      body: z35.object({ text: z35.string().trim().min(3).max(2e3) }),
      responses: { 200: placeSchema, ...defaultErrors },
      summary: "A localiza\xE7\xE3o que o associado mandou vira endere\xE7o com coordenada"
    },
    routePreview: {
      method: "POST",
      path: "/route-preview",
      body: z35.object({
        providerId: z35.number().int().positive(),
        origin: latLngSchema,
        destination: latLngSchema.nullable()
      }),
      responses: {
        200: z35.object({
          polyline: z35.string(),
          legs: z35.array(legSchema),
          depot: latLngSchema
        }),
        ...defaultErrors
      },
      summary: "A rota do prestador escolhido, para o mapa antes de acionar"
    },
    quote: {
      method: "POST",
      path: "/quotes",
      body: quoteRequestSchema,
      responses: { 200: quoteSchema, ...defaultErrors },
      summary: "Os prestadores para o servi\xE7o, pela chegada, com o valor de cada um"
    },
    listDispatches: {
      method: "GET",
      path: "/dispatches",
      query: paginationQuery.extend({
        /*
         * A tabela única (29/10/2026): o status é filtro, e não lista à parte.
         * Sem ele, todos menos os descartados. O período é o da abertura.
         */
        status: z35.enum(DISPATCH_LIST_FILTERS).optional(),
        from: businessDateSchema.optional(),
        to: businessDateSchema.optional(),
        serviceType: assistanceServiceTypeSchema.optional(),
        search: z35.string().trim().max(80).optional()
      }),
      responses: { 200: paginated(dispatchListRowSchema), ...defaultErrors },
      summary: "Os acionamentos em todos os status, do mais novo para o mais antigo"
    },
    agenda: {
      method: "GET",
      path: "/dispatches/agenda",
      query: z35.object({ from: businessDateSchema, to: businessDateSchema }),
      responses: { 200: z35.array(dispatchRowSchema), ...defaultErrors },
      summary: "Os agendamentos ainda n\xE3o executados nem cancelados"
    },
    createDispatch: {
      method: "POST",
      path: "/dispatches",
      body: dispatchWriteSchema,
      responses: { 201: dispatchDetailSchema, ...defaultErrors },
      summary: "Aciona agora, ou agenda"
    },
    getDispatch: {
      method: "GET",
      path: "/dispatches/:id",
      pathParams: idParams3,
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "O acionamento, com as fotos e o hist\xF3rico"
    },
    updateScheduled: {
      method: "PATCH",
      path: "/dispatches/:id",
      pathParams: idParams3,
      body: z35.object({
        scheduledFor: instantSchema,
        description: z35.string().trim().min(3).max(2e3),
        providerNotes: z35.string().trim().max(1e3).nullable(),
        callbackPhone: phoneSchema
      }).partial(),
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "Remarca ou corrige um agendamento ainda n\xE3o executado"
    },
    executeDispatch: {
      method: "POST",
      path: "/dispatches/:id/execute",
      pathParams: idParams3,
      /* A cota é conferida de novo aqui: o motivo libera, como no acionamento. */
      body: z35.object({
        usageReleaseReason: z35.string().trim().min(3).max(500).nullable().optional()
      }),
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "Executa o agendamento: passa a contar e o link do prestador nasce"
    },
    setServiceVariant: {
      method: "POST",
      path: "/dispatches/:id/service-variant",
      pathParams: idParams3,
      body: z35.object({ serviceVariant: serviceVariantSchema }),
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "Preenche o motivo do reboque ou o servi\xE7o residencial de um acionamento ainda sem pagamento"
    },
    cancelDispatch: {
      method: "POST",
      path: "/dispatches/:id/cancel",
      pathParams: idParams3,
      body: z35.object({
        reason: z35.string().trim().min(3).max(500),
        /** O prestador já tinha saído? Se sim, ele é pago (P11). */
        providerLeft: z35.boolean(),
        amount: moneySchema.nullable()
      }).refine((b) => !b.providerLeft || b.amount !== null, {
        message: "Informe o valor pago ao prestador que j\xE1 tinha sa\xEDdo.",
        path: ["amount"]
      }),
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "Cancela; se o prestador j\xE1 saiu, o valor informado entra no pagamento"
    },
    informAmount: {
      method: "POST",
      path: "/dispatches/:id/amount",
      pathParams: idParams3,
      body: z35.object({ amount: moneySchema }),
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "Informa o valor de um acionamento a combinar, que passa a entrar no pagamento"
    },
    resendLink: {
      method: "POST",
      path: "/dispatches/:id/link",
      pathParams: idParams3,
      body: z35.object({}),
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "Troca o link do prestador; o anterior para de abrir"
    },
    scriptCopied: {
      method: "POST",
      path: "/dispatches/:id/script-copied",
      pathParams: idParams3,
      body: z35.object({}),
      responses: { 204: z35.null(), ...defaultErrors },
      summary: "Registra que a central copiou o script \u2014 copiado, e n\xE3o enviado"
    },
    unlockPlate: {
      method: "POST",
      path: "/dispatches/:id/plate-unlock",
      pathParams: idParams3,
      body: z35.object({ reason: z35.string().trim().min(3).max(500) }),
      responses: { 200: dispatchDetailSchema, ...defaultErrors },
      summary: "Quem acionou destrava a placa do prestador depois de tr\xEAs erros"
    },
    progressSettings: {
      method: "GET",
      path: "/progress-settings",
      responses: { 200: progressSettingsSchema, ...defaultErrors },
      summary: "Os prazos do acompanhamento da base"
    },
    saveProgressSettings: {
      method: "PUT",
      path: "/progress-settings",
      body: progressSettingsSchema,
      responses: { 200: progressSettingsSchema, ...defaultErrors },
      summary: "Grava os prazos do acompanhamento; vazio \xE9 n\xE3o pintar de vermelho"
    },
    dispatchFileUrl: {
      method: "GET",
      path: "/dispatches/:id/files/:fileId",
      pathParams: z35.object({
        id: z35.coerce.number().int(),
        fileId: z35.coerce.number().int()
      }),
      responses: { 200: z35.object({ url: z35.string() }), ...defaultErrors },
      summary: "O endere\xE7o tempor\xE1rio de uma foto do prestador"
    },
    listDrafts: {
      method: "GET",
      path: "/drafts",
      query: z35.object({
        /*
         * `z.enum` e não `z.coerce.boolean()`: `Boolean('false')` é `true`, e
         * a lista de abertos mostrava os descartados (achado no percurso de
         * `assistencia-26`, 22/10/2026).
         */
        discarded: z35.enum(["true", "false"]).default("false").transform((v) => v === "true")
      }),
      responses: { 200: z35.array(draftSchema), ...defaultErrors },
      summary: "Os acionamentos em rascunho, da equipe: abertos, ou os descartados"
    },
    createDraft: {
      method: "POST",
      path: "/drafts",
      body: draftWriteSchema,
      responses: { 201: draftSchema, ...defaultErrors },
      summary: "Salva o formul\xE1rio pela metade como acionamento em rascunho"
    },
    updateDraft: {
      method: "PATCH",
      path: "/drafts/:id",
      pathParams: idParams3,
      body: draftWriteSchema,
      responses: { 200: draftSchema, ...defaultErrors },
      summary: "Atualiza um rascunho aberto, na mesma linha"
    },
    discardDraft: {
      method: "POST",
      path: "/drafts/:id/discard",
      pathParams: idParams3,
      body: z35.object({}),
      responses: { 200: draftSchema, ...defaultErrors },
      summary: "Descarta: arquiva, e nada \xE9 apagado (P12)"
    },
    pendingSettlements: {
      method: "GET",
      path: "/settlements/pending",
      responses: { 200: pendingSettlementsSchema, ...defaultErrors },
      summary: "O que falta pagar: o \xE0 vista um a um, e o mensal por prestador e m\xEAs"
    },
    listSettlements: {
      method: "GET",
      path: "/settlements",
      responses: { 200: z35.array(settlementSchema), ...defaultErrors },
      summary: "Os acertos feitos, do mais novo para o mais antigo"
    },
    payCash: {
      method: "POST",
      path: "/settlements/cash",
      body: z35.object({
        dispatchId: z35.number().int().positive(),
        /** Hoje ou antes: o líder pode informar depois de pagar. */
        paidOn: businessDateSchema,
        method: z35.enum(SETTLEMENT_METHODS),
        receipt: dispatchFileUploadSchema
      }),
      responses: { 201: settlementSchema, ...defaultErrors },
      summary: "O l\xEDder informa o \xE0 vista que pagou: a despesa nasce em Aguardando Pagamento"
    },
    createBatch: {
      method: "POST",
      path: "/settlements/batch",
      body: z35.object({
        providerId: z35.number().int().positive(),
        dispatchIds: z35.array(z35.number().int().positive()).min(1).max(500),
        /**
         * O valor de cada acionamento do lote que saiu sem valor (02/11/2026),
         * o que o prestador repassou. Todo escolhido sem valor precisa do seu;
         * o que já tem valor não aceita outro aqui.
         */
        amounts: z35.array(
          z35.object({ dispatchId: z35.number().int().positive(), amount: moneySchema })
        ).max(500).default([]),
        /** A nota do prestador — opcional por ora (30/10/2026). */
        invoice: dispatchFileUploadSchema.nullable()
      }),
      responses: { 201: settlementSchema, ...defaultErrors },
      summary: "O lote mensal de um prestador: a despesa nasce em Aprova\xE7\xE3o Financeiro"
    }
  },
  { pathPrefix: "/assistance" }
);
var linkTokenSchema = z35.string().min(32).max(128);
var pointFields = {
  latitude: z35.number().min(-90).max(90).nullable(),
  longitude: z35.number().min(-180).max(180).nullable(),
  accuracyM: z35.number().int().min(0).nullable()
};
var providerLinkViewSchema = z35.object({
  code: z35.string(),
  serviceType: assistanceServiceTypeSchema,
  plate: z35.string().nullable(),
  vehicleLabel: z35.string(),
  memberName: z35.string().nullable(),
  callbackPhone: z35.string(),
  origin: placeSchema,
  destination: placeSchema.nullable(),
  providerName: z35.string(),
  description: z35.string(),
  providerNotes: z35.string().nullable(),
  expiresAt: instantSchema,
  files: z35.array(
    z35.object({ kind: z35.enum(DISPATCH_FILE_KINDS), takenAt: instantSchema.nullable() })
  ),
  providerReportedM: z35.number().int().nullable(),
  /** O roteiro deste serviço e o que já foi marcado. */
  route: z35.array(z35.enum(DISPATCH_PROGRESS_STEPS)),
  done: z35.array(z35.object({ kind: z35.enum(DISPATCH_PROGRESS_STEPS), at: instantSchema })),
  next: z35.enum(DISPATCH_PROGRESS_STEPS).nullable(),
  /** Por que o próximo passo ainda não pode — a foto que falta. Nulo quando pode. */
  nextBlockedBy: z35.string().nullable(),
  /** Entre sair e finalizar a página manda a posição. */
  tracking: z35.boolean(),
  plateAttemptsLeft: z35.number().int(),
  /** O cadastro não tem placa válida: o prestador confirma o veículo pelo modelo. */
  plateMissing: z35.boolean()
});
var memberLinkViewSchema = z35.object({
  code: z35.string(),
  serviceType: assistanceServiceTypeSchema,
  vehicleLabel: z35.string(),
  plate: z35.string().nullable(),
  providerName: z35.string(),
  stage: z35.enum(MEMBER_TRACKING_STAGES),
  steps: z35.array(
    z35.object({ stage: z35.enum(MEMBER_TRACKING_STAGES), at: instantSchema.nullable() })
  ),
  /** Quando o Google diz que ele chega. Nulo sem rota, e depois que chegou. */
  arrivalAt: instantSchema.nullable(),
  /**
   * O que passa da cobertura do plano (02/11/2026): os km além dela e quanto o
   * associado paga direto ao prestador. Nulo quando não passa, ou quando o
   * valor não veio da tabela. `acknowledgedAt` é o "estou ciente" dele.
   */
  excess: z35.object({
    totalM: z35.number().int(),
    coverageM: z35.number().int().nullable(),
    /** Até onde a Evogard paga: o maior entre a cobertura e a saída do prestador. */
    paidUpToM: z35.number().int(),
    excessM: z35.number().int(),
    perKmAmount: moneySchema,
    amount: moneySchema,
    acknowledgedAt: instantSchema.nullable()
  }).nullable()
});
var assistanceLinksContract = c33.router(
  {
    viewProviderLink: {
      method: "POST",
      path: "/view",
      body: z35.object({ token: linkTokenSchema }),
      responses: {
        200: providerLinkViewSchema,
        404: defaultErrors[404],
        429: defaultErrors[400]
      },
      summary: "O servi\xE7o, como o prestador o v\xEA pelo link"
    },
    viewMemberLink: {
      method: "POST",
      path: "/member-view",
      body: z35.object({ token: linkTokenSchema }),
      responses: {
        200: memberLinkViewSchema,
        404: defaultErrors[404],
        429: defaultErrors[400]
      },
      summary: "O atendimento, como o associado o acompanha at\xE9 o prestador chegar"
    },
    acknowledgeMemberExcess: {
      method: "POST",
      path: "/member-excess-ack",
      /** O valor que estava na tela: se a conta mudou, a confirmação é recusada. */
      body: z35.object({ token: linkTokenSchema, amount: moneySchema }),
      responses: {
        200: memberLinkViewSchema,
        400: defaultErrors[400],
        404: defaultErrors[404],
        429: defaultErrors[400]
      },
      summary: "O associado confirma que paga ao prestador o que passa da cobertura"
    },
    sendProviderPhoto: {
      method: "POST",
      path: "/photo",
      body: z35.object({
        token: linkTokenSchema,
        kind: z35.enum(DISPATCH_FILE_KINDS),
        file: dispatchFileUploadSchema,
        takenAt: instantSchema.nullable(),
        latitude: z35.number().min(-90).max(90).nullable(),
        longitude: z35.number().min(-180).max(180).nullable(),
        accuracyM: z35.number().int().min(0).nullable()
      }),
      responses: { 201: providerLinkViewSchema, ...defaultErrors },
      summary: "O prestador manda uma foto, com hora e lugar"
    },
    sendProviderKm: {
      method: "POST",
      path: "/km",
      body: z35.object({ token: linkTokenSchema, km: z35.number().min(0).max(5e3) }),
      responses: { 200: providerLinkViewSchema, ...defaultErrors },
      summary: "O prestador informa o km do servi\xE7o"
    },
    sendProviderStep: {
      method: "POST",
      path: "/step",
      body: z35.object({
        token: linkTokenSchema,
        step: z35.enum(PROVIDER_BUTTON_STEPS),
        at: instantSchema,
        ...pointFields
      }),
      responses: { 200: providerLinkViewSchema, ...defaultErrors },
      summary: "O prestador marca um passo do servi\xE7o, com a posi\xE7\xE3o do clique"
    },
    checkProviderPlate: {
      method: "POST",
      path: "/plate",
      body: z35.object({
        token: linkTokenSchema,
        /** Vazio só quando o cadastro não tem placa válida: confirma o veículo. */
        plate: z35.string().trim().max(10),
        at: instantSchema
      }),
      responses: { 200: providerLinkViewSchema, ...defaultErrors },
      summary: "O prestador digita a placa que est\xE1 vendo; tr\xEAs erros travam"
    },
    sendProviderPositions: {
      method: "POST",
      path: "/positions",
      body: z35.object({
        token: linkTokenSchema,
        points: z35.array(
          z35.object({
            at: instantSchema,
            latitude: z35.number().min(-90).max(90),
            longitude: z35.number().min(-180).max(180),
            accuracyM: z35.number().int().min(0).nullable()
          })
        ).min(1).max(200)
      }),
      responses: {
        200: z35.object({ accepted: z35.number().int(), tracking: z35.boolean() }),
        ...defaultErrors
      },
      summary: "Os pontos do rastro, em lote \u2014 o que ficou na fila sem rede vem junto"
    },
    sendProviderScreen: {
      method: "POST",
      path: "/screen",
      body: z35.object({ token: linkTokenSchema, visible: z35.boolean(), at: instantSchema }),
      responses: { 204: z35.null(), ...defaultErrors },
      summary: "A p\xE1gina saiu da tela ou voltou \u2014 a central sabe por que o rastro parou"
    }
  },
  { pathPrefix: "/assistance-links" }
);

// src/assistance-management.ts
import { initContract as initContract34 } from "@ts-rest/core";
import { z as z36 } from "zod";
import { DISPATCH_STATUSES as DISPATCH_STATUSES2, MANAGEMENT_STAGES } from "@evogard/domain";
var c34 = initContract34();
var managementStageSchema = z36.enum(MANAGEMENT_STAGES);
var managementDispatchSchema = z36.object({
  id: z36.number().int(),
  code: z36.string(),
  status: z36.enum(DISPATCH_STATUSES2),
  stage: managementStageSchema,
  serviceType: assistanceServiceTypeSchema,
  plate: z36.string().nullable(),
  memberName: z36.string().nullable(),
  providerName: z36.string().nullable(),
  originLabel: z36.string().nullable(),
  destinationLabel: z36.string().nullable(),
  /** Nula quando o endereço não tem coordenada — o serviço não vai ao mapa. */
  origin: latLngSchema.nullable(),
  destination: latLngSchema.nullable(),
  createdAt: instantSchema,
  executedAt: instantSchema.nullable(),
  scheduledFor: instantSchema.nullable(),
  canceledAt: instantSchema.nullable(),
  /** Desde quando está na etapa: o último marco, o acionamento ou a marcação. */
  since: instantSchema,
  late: z36.boolean(),
  signalLost: z36.boolean(),
  plateLocked: z36.boolean(),
  /** Aberto dentro do período escolhido — conta no mapa e nos tipos. */
  inPeriod: z36.boolean(),
  /** Acionado nas últimas 24 h e ainda não finalizado — conta no andamento de agora. */
  onStreet: z36.boolean(),
  /** Agendado para as próximas 24 h. */
  scheduledSoon: z36.boolean()
});
var managementOverviewSchema = z36.object({
  generatedAt: instantSchema,
  /** O início do período: a meia-noite do primeiro dia, no relógio da casa. */
  periodStart: instantSchema,
  /** Os prazos da base existem? Sem eles, ninguém fica "atrasado" nem "sem sinal". */
  hasArrivalDeadline: z36.boolean(),
  hasNoSignalLimit: z36.boolean(),
  items: z36.array(managementDispatchSchema)
});
var assistanceManagementContract = c34.router(
  {
    overview: {
      method: "GET",
      path: "/overview",
      query: z36.object({
        days: z36.coerce.number().int().pipe(z36.union([z36.literal(1), z36.literal(7), z36.literal(30)]))
      }),
      responses: { 200: managementOverviewSchema, ...defaultErrors },
      summary: "O andamento de agora, o mapa e a contagem por tipo da pra\xE7a \u2014 uma lista, a tela conta"
    }
  },
  { pathPrefix: "/assistance/management" }
);

// src/assistance-usage.ts
import { initContract as initContract35 } from "@ts-rest/core";
import { z as z37 } from "zod";
import {
  ALL_SERVICE_VARIANTS as ALL_SERVICE_VARIANTS2,
  USAGE_RULE_KINDS,
  USAGE_WINDOW_UNITS
} from "@evogard/domain";
var c35 = initContract35();
var serviceVariantSchema2 = z37.enum(ALL_SERVICE_VARIANTS2);
var usageWindowSchema = z37.object({
  amount: z37.number().int().min(1).max(3650),
  unit: z37.enum(USAGE_WINDOW_UNITS)
});
var usageRowSchema = z37.object({
  serviceType: assistanceServiceTypeSchema,
  serviceVariant: serviceVariantSchema2.nullable(),
  label: z37.string(),
  ruleId: z37.number().int().nullable(),
  kind: z37.enum(USAGE_RULE_KINDS),
  /** A cota; nula quando não há limite ou a base não definiu. */
  maxUses: z37.number().int().nullable(),
  window: usageWindowSchema,
  used: z37.number().int(),
  usedCodes: z37.array(z37.string()),
  canDispatch: z37.boolean(),
  regulationText: z37.string().nullable()
});
var vehicleUsageSchema = z37.object({
  rows: z37.array(usageRowSchema),
  /** A janela do título da tabela. */
  window: usageWindowSchema,
  /** A base ainda não definiu nenhuma regra: a tela diz, em vez de mostrar zeros. */
  anyRule: z37.boolean(),
  continuations: z37.array(
    z37.object({ id: z37.number().int(), code: z37.string(), executedAt: instantSchema })
  )
});
var usageRuleLineSchema = z37.object({
  serviceType: assistanceServiceTypeSchema,
  serviceVariant: serviceVariantSchema2.nullable(),
  label: z37.string(),
  kind: z37.enum(USAGE_RULE_KINDS),
  maxUses: z37.number().int().nullable(),
  window: usageWindowSchema.nullable(),
  regulationText: z37.string().nullable(),
  /** Desde quando vale; nulo quando a base nunca definiu. */
  validFrom: businessDateSchema.nullable(),
  /** O que já está programado para depois de hoje. */
  upcoming: z37.object({ validFrom: businessDateSchema, kind: z37.enum(USAGE_RULE_KINDS) }).nullable()
});
var usageRulesSchema = z37.object({
  lines: z37.array(usageRuleLineSchema),
  cancelGraceMin: z37.number().int().nullable()
});
var usageRulesWriteSchema = z37.object({
  /** O dia em que as mudanças passam a valer: hoje ou depois. */
  validFrom: businessDateSchema,
  cancelGraceMin: z37.number().int().min(0).max(1440).nullable(),
  lines: z37.array(
    z37.object({
      serviceType: assistanceServiceTypeSchema,
      serviceVariant: serviceVariantSchema2.nullable(),
      kind: z37.enum(USAGE_RULE_KINDS),
      maxUses: z37.number().int().min(1).max(999).nullable(),
      window: usageWindowSchema.nullable(),
      regulationText: z37.string().trim().max(500).nullable()
    }).refine(
      (l) => l.kind === "limitado" ? l.maxUses !== null && l.window !== null : l.maxUses === null && l.window === null,
      {
        message: "S\xF3 o servi\xE7o com limite tem quantidade e per\xEDodo.",
        path: ["kind"]
      }
    )
  ).min(1)
});
var assistanceUsageContract = c35.router(
  {
    rules: {
      method: "GET",
      path: "/rules",
      responses: { 200: usageRulesSchema, ...defaultErrors },
      summary: "As cotas do regulamento desta base, como valem hoje"
    },
    saveRules: {
      method: "PUT",
      path: "/rules",
      body: usageRulesWriteSchema,
      responses: { 200: usageRulesSchema, ...defaultErrors },
      summary: "Grava as cotas que mudaram, a valer na data escolhida; a regra anterior fica"
    },
    vehicleUsage: {
      method: "GET",
      path: "/vehicles/:vehicleId",
      pathParams: z37.object({ vehicleId: z37.coerce.number().int().positive() }),
      responses: { 200: vehicleUsageSchema, ...defaultErrors },
      summary: "O que esta placa j\xE1 usou de cada servi\xE7o, e o que ainda pode acionar"
    }
  },
  { pathPrefix: "/assistance/usage" }
);

// src/breaks.ts
import { initContract as initContract36 } from "@ts-rest/core";
import { z as z38 } from "zod";
import {
  BREAK_ADJUSTMENT_STATUSES,
  BREAK_MODES,
  BREAK_STATUSES,
  BREAK_REFUSAL_MESSAGES
} from "@evogard/domain";
var c36 = initContract36();
var refusalSchema = z38.enum(
  Object.keys(BREAK_REFUSAL_MESSAGES)
);
var breakWindowSchema = z38.object({
  startHour: z38.number().int().min(0).max(23),
  endHour: z38.number().int().min(1).max(24)
}).refine((w) => w.startHour < w.endHour, {
  message: "O in\xEDcio da janela precisa vir antes do fim.",
  path: ["endHour"]
});
var slotSchema = z38.string().regex(/^([01]\d|2[0-3]):(00|15|30|45)$/, "Use HH:MM, de 15 em 15 minutos.");
var activeBreakSchema = z38.object({
  id: z38.number().int(),
  startedAt: instantSchema,
  expectedEndAt: instantSchema
});
var breakCurrentSchema = z38.object({
  day: businessDateSchema,
  /**
   * `fixo` no horário atribuído; `livre` para o LÍDER de departamento, que
   * começa a qualquer hora da janela em que caibam os 15 minutos.
   */
  mode: z38.enum(BREAK_MODES),
  /** O horário atribuído nesta base, ou `null` — no `fixo`, quem não tem não tira intervalo. */
  slot: slotSchema.nullable(),
  /** No `fixo`, o começo e o fim do horário; no `livre`, a abertura e o fecho da janela. */
  slotStartsAt: instantSchema.nullable(),
  slotEndsAt: instantSchema.nullable(),
  /** Só no `livre`: o último instante em que ainda se pode começar (fim da janela − 15 min). */
  lastStartAt: instantSchema.nullable(),
  usedToday: z38.boolean(),
  /** Por que não pode ativar agora, ou `null` quando pode. */
  refusal: refusalSchema.nullable(),
  active: activeBreakSchema.nullable(),
  /** O relógio do servidor, para a contagem não depender do relógio da máquina. */
  now: instantSchema
});
var breakPersonSchema = z38.object({
  id: z38.string(),
  name: z38.string(),
  email: z38.string(),
  /** Lidera algum departamento — tira o intervalo livre, e o horário dela não vale. */
  leader: z38.boolean()
});
var BREAK_SCOPES = ["base", "lider", "proprio"];
var breakScopeSchema = z38.enum(BREAK_SCOPES);
var breakSettingsSchema = z38.object({
  /** `base` configura a janela e todos; `lider` só os horários da própria equipe. */
  scope: z38.enum(["base", "lider"]),
  window: z38.object({ startHour: z38.number().int(), endHour: z38.number().int() }),
  /** `true` quando a base nunca gravou janela e vale a padrão. */
  isDefaultWindow: z38.boolean(),
  /** Os horários que a janela oferece. */
  slots: z38.array(z38.string()),
  assignments: z38.array(breakPersonSchema.extend({ slot: z38.string() })),
  /** Quem pode receber horário: a base inteira, ou a equipe de quem lidera. */
  people: z38.array(breakPersonSchema)
});
var breakAdjustmentFileSchema = z38.object({
  id: z38.number().int(),
  name: z38.string(),
  mimeType: z38.string(),
  sizeBytes: z38.number().int()
});
var breakAdjustmentSchema = z38.object({
  id: z38.number().int(),
  status: z38.enum(BREAK_ADJUSTMENT_STATUSES),
  previousStartedAt: instantSchema,
  previousEndedAt: instantSchema.nullable(),
  requestedStartedAt: instantSchema,
  requestedEndedAt: instantSchema,
  justification: z38.string(),
  requestedBy: z38.string().nullable(),
  reviewedBy: z38.string().nullable(),
  reviewedAt: instantSchema.nullable(),
  reviewNote: z38.string().nullable(),
  createdAt: instantSchema,
  files: z38.array(breakAdjustmentFileSchema)
});
var breakRecordSchema = z38.object({
  id: z38.number().int(),
  userId: z38.string(),
  userName: z38.string(),
  day: businessDateSchema,
  mode: z38.enum(BREAK_MODES),
  /** Nulo no intervalo livre do líder. */
  slot: z38.string().nullable(),
  startedAt: instantSchema,
  expectedEndAt: instantSchema,
  endedAt: instantSchema.nullable(),
  status: z38.enum(BREAK_STATUSES),
  /** Minutos inteiros entre o começo e o fim — `null` enquanto aberto. */
  durationMinutes: z38.number().int().nullable(),
  manualReason: z38.string().nullable(),
  adjustedAt: instantSchema.nullable(),
  /** O ajuste mais relevante: o pendente primeiro, senão o último. */
  adjustment: breakAdjustmentSchema.nullable()
});
var breakHistoryDaySchema = z38.object({
  day: businessDateSchema,
  total: z38.number().int(),
  punctual: z38.number().int(),
  overrun: z38.number().int(),
  ongoing: z38.number().int(),
  /** Média em minutos dos encerrados, ou `null` quando nenhum encerrou. */
  averageMinutes: z38.number().nullable()
});
var breakOverrunSchema = z38.object({
  id: z38.number().int(),
  userId: z38.string(),
  userName: z38.string(),
  startedAt: instantSchema,
  expectedEndAt: instantSchema,
  notifiedAt: instantSchema
});
var fileUploadSchema3 = z38.object({
  nome: z38.string().min(1).max(255),
  typeMime: z38.string().min(3).max(160),
  content: z38.string().min(1)
});
var adjustmentBodySchema = z38.object({
  startedAt: instantSchema,
  endedAt: instantSchema,
  justification: z38.string().trim().min(5, "Escreva a justificativa.").max(2e3),
  file: fileUploadSchema3
}).refine((b) => new Date(b.startedAt) < new Date(b.endedAt), {
  message: "O fim precisa vir depois do come\xE7o.",
  path: ["endedAt"]
});
var departmentFilter = z38.coerce.number().int().min(0).optional();
var recordsQuery = paginationQuery.extend({
  /** 10 por página, o padrão das tabelas da casa — a resposta diz o total. */
  perPage: z38.coerce.number().int().min(1).max(200).default(10),
  from: businessDateSchema,
  to: businessDateSchema,
  /** Só vale para quem lê os registros de todos; os outros veem os próprios. */
  userId: z38.string().max(80).optional(),
  departmentId: departmentFilter
});
var historyDays = z38.coerce.number().int().refine((d) => [7, 14, 30, 90].includes(d), "Use 7, 14, 30 ou 90.");
var breakDepartmentRowSchema = z38.object({
  /** `null` é "sem departamento". */
  departmentId: z38.number().int().nullable(),
  name: z38.string(),
  /** Quantas pessoas do departamento tiraram intervalo no período. */
  people: z38.number().int(),
  total: z38.number().int(),
  punctual: z38.number().int(),
  overrun: z38.number().int(),
  ongoing: z38.number().int(),
  averageMinutes: z38.number().nullable()
});
var BREAK_OVERRUN_EVENT = "intervalo:estourado";
var BREAK_CLOSED_EVENT = "intervalo:encerrado";
var breakOverrunNoticeSchema = z38.object({
  recordId: z38.number().int(),
  userName: z38.string(),
  subject: z38.string()
});
var breakClosedNoticeSchema = z38.object({ recordId: z38.number().int() });
var BREAK_ADJUSTMENT_REQUESTED_EVENT = "intervalo:ajuste_solicitado";
var breakAdjustmentRequestedNoticeSchema = z38.object({
  recordId: z38.number().int(),
  subject: z38.string()
});
var conflict = { 409: z38.object({ code: z38.string(), message: z38.string() }) };
var breaksContract = c36.router(
  {
    /* ----- a própria pessoa ----- */
    current: {
      method: "GET",
      path: "/current",
      responses: { 200: breakCurrentSchema, ...defaultErrors },
      summary: "O estado do intervalo da pessoa da sess\xE3o, hoje, nesta base"
    },
    activate: {
      method: "POST",
      path: "/activate",
      body: z38.object({}),
      responses: { 201: breakCurrentSchema, ...defaultErrors, ...conflict },
      summary: "Ativa o intervalo \u2014 s\xF3 dentro do pr\xF3prio hor\xE1rio, uma vez por dia"
    },
    end: {
      method: "POST",
      path: "/:id/end",
      pathParams: z38.object({ id: z38.coerce.number().int() }),
      body: z38.object({}),
      responses: { 200: breakCurrentSchema, ...defaultErrors },
      summary: "Encerra o PR\xD3PRIO intervalo aberto"
    },
    requestAdjustment: {
      method: "POST",
      path: "/:id/adjustment-requests",
      pathParams: z38.object({ id: z38.coerce.number().int() }),
      body: adjustmentBodySchema,
      responses: { 201: breakRecordSchema, ...defaultErrors, ...conflict },
      summary: "Pede ajuste do hor\xE1rio de um intervalo PR\xD3PRIO, com comprovante"
    },
    /* ----- a relação — de todos com `breaks.records:ver`, senão só os próprios ----- */
    records: {
      method: "GET",
      path: "/records",
      query: recordsQuery,
      responses: {
        200: paginated(breakRecordSchema).extend({
          /** Até onde a pessoa vê — a base, a equipe que lidera, ou só a si. */
          scope: breakScopeSchema
        }),
        ...defaultErrors
      },
      summary: "A rela\xE7\xE3o detalhada dos intervalos num per\xEDodo"
    },
    history: {
      method: "GET",
      path: "/history",
      query: z38.object({
        days: historyDays,
        userId: z38.string().max(80).optional(),
        departmentId: departmentFilter
      }),
      responses: { 200: z38.array(breakHistoryDaySchema), ...defaultErrors },
      summary: "Os intervalos agregados por dia \u2014 o gr\xE1fico da rela\xE7\xE3o"
    },
    departmentsReport: {
      method: "GET",
      path: "/departments-report",
      query: z38.object({ days: historyDays }),
      responses: { 200: z38.array(breakDepartmentRowSchema), ...defaultErrors },
      summary: "Os intervalos do per\xEDodo por departamento \u2014 todo departamento ativo, com zero quando n\xE3o houve"
    },
    fileUrl: {
      method: "GET",
      path: "/adjustment-files/:id/url",
      pathParams: z38.object({ id: z38.coerce.number().int() }),
      responses: { 200: attachmentUrlSchema, ...defaultErrors },
      summary: "URL assinada de um comprovante \u2014 de todos, ou s\xF3 dos pr\xF3prios"
    },
    /* ----- quem gere ----- */
    adjust: {
      method: "PATCH",
      path: "/:id/times",
      pathParams: z38.object({ id: z38.coerce.number().int() }),
      body: adjustmentBodySchema,
      responses: { 200: breakRecordSchema, ...defaultErrors },
      summary: "Ajusta direto o hor\xE1rio de um intervalo, com comprovante"
    },
    reviewAdjustment: {
      method: "POST",
      path: "/adjustments/:id/review",
      pathParams: z38.object({ id: z38.coerce.number().int() }),
      body: z38.object({
        decision: z38.enum(["aprovado", "reprovado"]),
        note: z38.string().trim().max(2e3).optional()
      }),
      responses: { 200: breakRecordSchema, ...defaultErrors, ...conflict },
      summary: "Aprova (aplica os hor\xE1rios) ou reprova uma solicita\xE7\xE3o de ajuste"
    },
    manual: {
      method: "POST",
      path: "/manual",
      body: z38.object({
        userId: z38.string().min(1).max(80),
        day: businessDateSchema,
        startedAt: instantSchema,
        endedAt: instantSchema,
        reason: z38.string().trim().min(5, "Escreva o motivo.").max(2e3)
      }).refine((b) => new Date(b.startedAt) < new Date(b.endedAt), {
        message: "O fim precisa vir depois do come\xE7o.",
        path: ["endedAt"]
      }),
      responses: { 201: breakRecordSchema, ...defaultErrors, ...conflict },
      summary: "Registra um intervalo j\xE1 encerrado para outra pessoa \u2014 ocupa o dia dela"
    },
    overruns: {
      method: "GET",
      path: "/overruns",
      responses: { 200: z38.array(breakOverrunSchema), ...defaultErrors },
      summary: "Os estouros em aberto: avisados, sem encerrar e sem dispensar"
    },
    dismissOverrun: {
      method: "POST",
      path: "/overruns/:id/dismiss",
      pathParams: z38.object({ id: z38.coerce.number().int() }),
      body: z38.object({}),
      responses: { 204: z38.null(), ...defaultErrors },
      summary: "Dispensa um aviso de estouro \u2014 o hist\xF3rico fica"
    },
    settings: {
      method: "GET",
      path: "/settings",
      responses: { 200: breakSettingsSchema, ...defaultErrors },
      summary: "A janela e os hor\xE1rios \u2014 da base inteira (breaks.settings:editar) ou da equipe de quem lidera"
    },
    saveWindow: {
      method: "PUT",
      path: "/window",
      body: breakWindowSchema,
      responses: { 200: breakSettingsSchema, ...defaultErrors },
      summary: "Grava a janela de intervalos da base"
    },
    saveSlots: {
      method: "PUT",
      path: "/slots",
      body: z38.object({
        assignments: z38.array(z38.object({ userId: z38.string().min(1).max(80), slot: slotSchema })).max(2e3)
      }),
      responses: { 200: breakSettingsSchema, ...defaultErrors },
      summary: "Grava os hor\xE1rios de quem est\xE1 no alcance de quem grava \u2014 quem do alcance n\xE3o vier perde o hor\xE1rio; quem est\xE1 fora n\xE3o \xE9 tocado"
    }
  },
  { pathPrefix: "/breaks" }
);

// src/index.ts
var c37 = initContract37();
var contractCore = c37.router(
  {
    health: {
      method: "GET",
      path: "/health",
      responses: {
        200: z39.object({
          ok: z39.boolean(),
          version: z39.string(),
          database: z39.enum(["ok", "falha"])
        })
      },
      summary: "Verifica\xE7\xE3o de sa\xFAde da API"
    },
    platform: platformContract,
    /*
     * As contas do Conta Azul de cada base, sob `/platform/integrations` — a
     * tela é a de credenciais. Só cadastro: nenhuma rota fala com o Conta Azul.
     */
    contaazul: contaazulContract,
    /*
     * O card do OpaSuite, sob `/platform/integrations/opasuite`: endereço,
     * templates, pontos de uso e envio de teste. O token é a credencial de
     * sempre, em `platform`.
     */
    opasuite: opasuiteContract,
    /*
     * O card do Asaas, sob `/platform/integrations/asaas`: o ambiente e o
     * webhook. A chave é a credencial `gateway`, pelas rotas de sempre.
     */
    asaas: asaasContract,
    /** As cobranças no provedor — reler e comprovante (`/finance/charges`). */
    charges: chargesContract,
    /*
     * O quadro de despesas do Financeiro (`/finance/expenses`, 26/10/2026):
     * o sinistro e a assistência, de Aprovação Financeiro em diante.
     */
    financeExpenses: financeExpensesContract,
    /** O webhook do Asaas (`/webhooks/asaas/:tenant`) — público, protegido pelo token. */
    chargesWebhook: chargesWebhookContract,
    /*
     * Os links úteis da base, sob `/platform/useful-links`: todo mundo da base
     * lê, quem edita a Configuração da base cadastra.
     */
    usefulLinks: usefulLinksContract,
    /*
     * Os feriados da praça (15/10/2026) — dado da plataforma, lido por quem
     * conta dias úteis. A tela do cadastro mora, por ora, no SLA do sinistro.
     */
    holidays: holidaysContract,
    /*
     * O endereço → a posição no mapa (29/10/2026). Da plataforma, porque todo
     * campo de endereço a pede — o da empresa, o do sinistro, o do acionamento.
     */
    maps: mapsContract,
    portal: portalContract,
    tickets: ticketsContract,
    /*
     * O que o associado enxerga de demandas mora no módulo dono do assunto, e
     * não em `portal`: `modules/portal` cuida de QUEM ele é; o que ele consulta
     * é de quem tem a tabela. A chave é separada porque o prefixo do caminho é
     * outro — `/portal/tickets`, não `/tickets`.
     */
    ticketsPortal: ticketsPortalContract,
    /*
     * `/members` é interno, com permissão de grupo — não `/portal/members`.
     * `/portal/*` é o namespace da sessão do associado, e juntar os dois é o
     * primeiro passo para uma rota interna aceitar cookie de associado.
     */
    members: membersContract,
    /*
     * A mesa da Indenização (09/10/2026) — chave própria pelo mesmo motivo de
     * `claimPeripherals` (TS7056). O prefixo é o do sinistro (`/claims`).
     */
    claimCompensations: claimCompensationsContract,
    /*
     * Os encerrados (13/10/2026) — chave própria pelo mesmo motivo de
     * `claimPeripherals` (TS7056). O prefixo é o do sinistro (`/claims`).
     */
    claimClosures: claimClosuresContract,
    /*
     * A Gestão do sinistro (23/10/2026) — chave própria pelo mesmo motivo de
     * `claimPeripherals` (TS7056). O prefixo é o do sinistro (`/claims`).
     */
    claimManagement: claimManagementContract,
    /*
     * O intervalo diário de 15 minutos (09/10/2026), sob `/breaks`. Qualquer
     * pessoa logada usa o próprio; quem tem `breaks.*` configura e acompanha.
     */
    breaks: breaksContract,
    /*
     * A Assistência 24h (21/10/2026), sob `/assistance`: o prestador e a tabela
     * de preço, na primeira fatia. Plano em `docs/modulos/assistencia/`.
     */
    assistance: assistanceContract
  },
  { pathPrefix: "/api" }
);
var contractFinance = c37.router(
  {
    /*
     * `billing` é a mesa humana e a esteira automática sobre um estado só — e é
     * o módulo onde a regra 7 deixa de ser combinado: a identidade do boleto é
     * `(tenant_id, provedor, id_externo)`, e nenhum schema daqui tem
     * `nossoNumero`.
     */
    billing: billingContract,
    /*
     * Os boletos do associado, `@Portal()` no módulo dono do assunto — a mesma
     * razão de `ticketsPortal` e `erpPortal` (`ARQUITETURA.md`, fronteira 3).
     * Chave separada porque o prefixo é outro: `/portal/billing`.
     */
    billingPortal: billingPortalContract,
    /*
     * A baixa por webhook do gateway. Chave e router próprios porque o prefixo é
     * `/webhooks` — e porque manter a única superfície `@Publico()` deste módulo
     * fora do router interno deixa isso visível no endereço, em vez de depender
     * de alguém reparar num decorador diferente no meio do controller.
     */
    billingWebhooks: billingWebhooksContract,
    /*
     * A Mesa que substitui o `KanbanCobranca` (19/10/2026). Router próprio porque
     * a base vem no caminho (`/billing/desk/:tenant/...`) e porque ela convive
     * com as rotas antigas da mesa até a mescla da esteira — ver o cabeçalho de
     * `billing-desk.ts`.
     */
    billingDesk: billingDeskContract,
    /*
     * A premiação da cobrança (21/10/2026), que substitui a
     * `PremiacaoCobrancaRetencao`. A base no caminho, como a Mesa — ver o
     * cabeçalho de `billing-awards.ts`.
     */
    billingAwards: billingAwardsContract,
    /*
     * `sales` é o módulo com mais dinheiro do repositório, e a chave carrega
     * a disciplina que isso exige: **onde a casa ainda não calcula, a resposta
     * diz que não calcula.** Oito das onze perguntas do levantamento seguem
     * abertas, e cada uma vira um estado declarado — `nao_apuravel`,
     * `nao_gravado`, `nao_apurada`, `nao_sabemos` —, nunca um zero.
     *
     * **Não há `salesPortal`, e a ausência é a decisão.** O associado não
     * tem nada a ver com a remuneração de quem vendeu para ele: a carteira dele
     * é `erpPortal`, os boletos são `billingPortal` e o cancelamento é
     * `retentionPortal`. Uma rota de comercial no namespace do portal seria a
     * primeira porta por onde o desempenho de um consultor sairia da casa.
     */
    sales: salesContract,
    /*
     * A despesa lançada a partir de uma venda (02/10/2026) — indicação, troca,
     * vistoria extra, inativo e reembolso —, num quadro próprio do comercial e
     * não em `financial_expenses`. Chave própria porque o router de `sales` já
     * é o maior do contrato; o prefixo continua sendo `/sales/expenses`.
     */
    saleExpenses: saleExpensesContract,
    /*
     * `management` é a gestão — e, por enquanto, só o pagamento da premiação:
     * a nota fiscal do consultor, a conferência do Financeiro e o comprovante.
     * É o único lugar da casa que diz que uma premiação foi paga (P8).
     */
    management: managementContract
  },
  { pathPrefix: "/api" }
);
var contractClaims = c37.router(
  {
    /*
     * A onda dos sinistros. Cobre o sinistro, Terceiro, Análise, Reparo,
     * Periféricos e Empresa de apoio — Indenização e as rotas
     * da despesa ficam de fora, e o cabeçalho de `claims.ts` diz por quê.
     */
    claims: claimsContract,
    /*
     * O cadastro de beneficiário — os dados bancários de quem recebe. É da
     * plataforma (`/platform/beneficiaries`), e não do Financeiro: todo
     * departamento cadastra quem vai pagar.
     */
    beneficiaries: beneficiariesContract,
    /*
     * O webhook da CAF (`/webhooks/caf/:tenant/:code`, 16/10/2026) — público,
     * protegido pelo código no caminho. O resultado vem de consultar a CAF.
     */
    claimsCafWebhook: claimsCafWebhookContract,
    /** O painel da CAF em Integrações: o endereço do webhook, só leitura. */
    cafIntegration: cafIntegrationContract,
    /*
     * A página pública do pagamento da PI do Periférico (`/payment-links`,
     * 19/10/2026) — sem login, protegida pelo código do link no corpo.
     */
    paymentLinks: paymentLinksContract,
    /*
     * O acionamento de Periféricos pelo associado (19/10/2026), `@Portal()` no
     * módulo dono do assunto — a mesma razão de `ticketsPortal`. Chave
     * separada porque o prefixo é outro, e nesta
     * parte porque a primeira está no limite do que o compilador serializa
     * (TS7056): `/portal/claims/peripherals`.
     */
    /*
     * Periféricos (08/10/2026) — o acionamento que não se liga ao sinistro.
     * Chave própria pelo mesmo motivo de `saleExpenses`. Morou na primeira
     * parte até 19/10/2026, quando a ficha ganhou o local do serviço e a
     * primeira parte passou do que o compilador serializa (TS7056). O prefixo
     * continua sendo do módulo (`/claims/peripherals`).
     */
    claimPeripherals: claimPeripheralsContract,
    claimPeripheralsPortal: claimPeripheralsPortalContract,
    /*
     * A despesa dentro do card e o quadro do supervisor (26/10/2026), sob
     * `/claims/expenses` — `docs/modulos/financeiro/supervisao-da-despesa.md`.
     */
    claimExpenses: claimExpensesContract
  },
  { pathPrefix: "/api" }
);
var contractErp = c37.router(
  {
    /*
     * `erp` é a integração vista de fora: pedir a atualização de um registro,
     * ler a saúde da integração e manter a classificação de situações. Nenhuma
     * rota dele fala a linguagem do fornecedor, e nenhuma aceita a base pelo
     * cliente — ver o cabeçalho de `erp.ts`.
     */
    erp: erpContract,
    /*
     * A carteira do associado é `@Portal()` no módulo dono do assunto — o
     * veículo é entidade do espelho do ERP, e `modules/portal` cuida de QUEM ele
     * é, não DO QUE ele consulta (`ARQUITETURA.md`, fronteira 3). Ela não mora
     * em `retention` porque consumir a carteira é da retenção e declará-la não é:
     * a cobrança e o comercial vão precisar dos mesmos veículos, e nenhum dos
     * dois deve buscá-los numa rota de cancelamento.
     *
     * A chave é separada porque o prefixo é outro: `/portal/erp`, não `/erp`. É
     * a ÚNICA rota do módulo que a sessão do associado alcança, e a única
     * escopada pela pessoa além da praça — ver o cabeçalho da seção da carteira
     * em `erp.ts`.
     */
    erpPortal: erpPortalContract
  },
  { pathPrefix: "/api" }
);
var contractRevenues = c37.router(
  {
    revenues: revenuesContract,
    /** O painel do Financeiro (`/finance/dashboard`, 29/10/2026). */
    financeDashboard: financeDashboardContract,
    /*
     * O mapa do time (29/10/2026): a montagem de hoje do comercial com os
     * números de cada consultor — a receita e a despesa no Financeiro
     * (`/finance/team-map`) e as ocorrências no Sinistro
     * (`/claims/management/team-map`). Aqui pelo mesmo TS7056, e porque as
     * duas rotas leem a mesma árvore.
     */
    financeTeamMap: financeTeamMapContract,
    claimsTeamMap: claimsTeamMapContract
  },
  { pathPrefix: "/api" }
);
var contractAssistance = c37.router(
  {
    /* O acionamento, a agenda, o rascunho e o acerto, sob `/assistance`. */
    assistanceDispatches: assistanceDispatchesContract,
    /* A página do prestador, pública, sob `/assistance-links`. */
    assistanceLinks: assistanceLinksContract,
    /* A Gestão da assistência (`/assistance/management`, 30/10/2026). */
    assistanceManagement: assistanceManagementContract,
    /* A cota do regulamento (`/assistance/usage`, 01/11/2026). */
    assistanceUsage: assistanceUsageContract,
    /*
     * O quadro do supervisor da despesa da assistência (26/10/2026), sob
     * `/assistance/expenses`. A forma da despesa é a mesma do sinistro
     * (`expenses.ts`). Morou no núcleo até 30/10/2026, quando o nome e o
     * e-mail do usuário (`getUser`, `editUser`) fizeram o tipo inferido de
     * `contractCore` passar do que o compilador serializa (TS7056).
     */
    assistanceExpenses: assistanceExpensesContract
  },
  { pathPrefix: "/api" }
);
var contractRetention = c37.router(
  {
    retention: retentionContract,
    /*
     * O cancelamento visto pelo associado mora no módulo dono do assunto, e não
     * em `portal` — a mesma razão de `ticketsPortal`. A chave é separada
     * porque o prefixo é outro: `/portal/retention`.
     */
    retentionPortal: retentionPortalContract
  },
  { pathPrefix: "/api" }
);
var contract = {
  ...contractCore,
  ...contractFinance,
  ...contractClaims,
  ...contractErp,
  ...contractRevenues,
  ...contractAssistance,
  ...contractRetention
};
export {
  ASSISTANCE_PROGRESS_EVENT,
  BILLING_CHANNELS,
  BOLETO_PAID_EVENT,
  BREAKER_STATES,
  BREAK_ADJUSTMENT_REQUESTED_EVENT,
  BREAK_CLOSED_EVENT,
  BREAK_OVERRUN_EVENT,
  BREAK_SCOPES,
  CHANGE_SUBJECTS,
  COMMISSION_LADDERS,
  CONSULTANT_PROFILES,
  CORE_ENTITIES,
  DISPATCH_DISCARD_REASONS,
  DISPATCH_LIST_FILTERS,
  ENTRY_TYPES,
  ERP_CORE_CHANGED_EVENT,
  ERP_PIPELINE_EVENT,
  EXPENSE_PENDING_EVENT,
  HISTORY_ENTITIES,
  MIN_PASSWORD_LENGTH,
  MIRRORED_ENTITIES,
  PAYMENT_UPDATED_EVENT,
  PLATE_CLASSIFICATIONS,
  PORTAL_TICKET_VIEWS,
  PORTAL_TICKET_VIEW_STATUSES,
  PRIORITIES,
  RANKING_COUNTINGS,
  RANKING_ORDERS,
  REASONS_NOT_TO_SETTLE,
  RECORD_ENTITIES,
  SALES_COMPARISON_SESSIONS,
  SENDABLE_CHANNELS,
  STATUS_DOMAINS,
  TARGET_SCOPES,
  TICKET_OPENED_EVENT,
  TICKET_REPLIED_EVENT,
  TICKET_STATUSES,
  WEBHOOK_REFUSAL,
  activeBreakSchema,
  activityChangeSchema,
  addressSchema,
  addressWriteSchema,
  agendaMemberDetailSchema,
  agendaMemberSchema,
  agendaSchema,
  agreementProofSchema,
  analysisFormSchema,
  apiErrorSchema,
  appliedLabelSchema,
  appliedTierSchema,
  asaasContract,
  asaasEnvironmentSchema,
  asaasOverviewSchema,
  asaasWebhookStatusSchema,
  assistanceCatalogSchema,
  assistanceCatalogServiceSchema,
  assistanceCompanySchema,
  assistanceContract,
  assistanceDispatchesContract,
  assistanceExpensesContract,
  assistanceLinksContract,
  assistanceManagementContract,
  assistancePriceSchema,
  assistancePriceWriteSchema,
  assistancePricingSchema,
  assistanceProviderPatchSchema,
  assistanceProviderSchema,
  assistanceProviderServicesWriteSchema,
  assistanceProviderWriteSchema,
  assistanceServicePatchSchema,
  assistanceServiceTypeSchema,
  assistanceServiceWriteSchema,
  assistanceUsageContract,
  assistanceVehicleSchema,
  attachClaimDocumentSchema,
  attachFinanceExpenseFileSchema,
  attachPeripheralFileSchema,
  attachPeripheralItemPhotoSchema,
  attachmentSchema,
  attachmentUrlSchema,
  auditTrailQuery,
  awardBasisSourceSchema,
  awardCategorySchema,
  awardDailyRowSchema,
  awardDailySchema,
  awardFamilySchema,
  awardGrantInputSchema,
  awardGrantSchema,
  awardInputSchema,
  awardLineSchema,
  awardPortfolioRowSchema,
  awardPortfolioSchema,
  awardProductCombinationSchema,
  awardProductDiscountSchema,
  awardProductSchema,
  awardRefusalSchema,
  awardSchema,
  awardStateSchema,
  awardTemplateInputSchema,
  awardTemplateSchema,
  awardTierSchema,
  beneficiariesContract,
  beneficiaryAccountTypeSchema,
  beneficiaryConsultantSchema,
  beneficiaryMemberSchema,
  beneficiaryOwnerInputSchema,
  beneficiaryOwnerSchema,
  beneficiaryRefTypeSchema,
  beneficiarySchema,
  billingAwardBoletoCategorySchema,
  billingAwardBoletoRowSchema,
  billingAwardBoletoTypeSchema,
  billingAwardClosingSchema,
  billingAwardConfigSchema,
  billingAwardCountedLinkSchema,
  billingAwardEnabledSchema,
  billingAwardKindSchema,
  billingAwardLinkRowSchema,
  billingAwardLinkStatusSchema,
  billingAwardLinksQuerySchema,
  billingAwardLogRowSchema,
  billingAwardOperatorSchema,
  billingAwardOperatorStatsSchema,
  billingAwardPeriodSchema,
  billingAwardPeriodViewSchema,
  billingAwardRecordRowSchema,
  billingAwardRuleSchema,
  billingAwardSaveBoletoTypesSchema,
  billingAwardSaveOperatorsSchema,
  billingAwardSaveRuleSchema,
  billingAwardSyncResultSchema,
  billingAwardTierSchema,
  billingAwardsContract,
  billingAwardsResultSchema,
  billingCaseSchema,
  billingChannelSchema,
  billingContract,
  billingDeskContract,
  billingLadderSchema,
  billingNumbersSchema,
  billingPortalContract,
  billingWebhooksContract,
  boardStatusSchema,
  boletoCopySchema,
  boletoMemberSchema,
  boletoPaidNoticeSchema,
  boletoStatusSchema,
  boletoTotalSchema,
  boletoVehicleLabelSchema,
  boletoVehicleSchema,
  breakAdjustmentFileSchema,
  breakAdjustmentRequestedNoticeSchema,
  breakAdjustmentSchema,
  breakClosedNoticeSchema,
  breakCurrentSchema,
  breakDepartmentRowSchema,
  breakHistoryDaySchema,
  breakOverrunNoticeSchema,
  breakOverrunSchema,
  breakPersonSchema,
  breakRecordSchema,
  breakScopeSchema,
  breakSettingsSchema,
  breakWindowSchema,
  breakerSchema,
  breakerStateSchema,
  breaksContract,
  businessDateSchema,
  cancelRevenueSchema,
  cardExpensesSchema,
  caseActionSchema,
  cashBookSchema,
  cashEntrySchema,
  catalogBoletoDetailSchema,
  catalogBoletoErpSchema,
  catalogBoletoResultSchema,
  catalogBoletoSchema,
  catalogBoletoSearchSchema,
  catalogBoletoTypeOptionSchema,
  catalogBoletoTypeResultSchema,
  catalogBoletoTypeSchema,
  catalogBoletoTypeSearchSchema,
  catalogBoletoTypesSchema,
  catalogChangeDetailSchema,
  catalogChangeResultSchema,
  catalogChangeSchema,
  catalogChangeSearchSchema,
  catalogMemberDetailSchema,
  catalogMemberResultSchema,
  catalogMemberSchema,
  catalogMemberSearchSchema,
  catalogOwnerDetailSchema,
  catalogOwnerSchema,
  catalogPlanResultSchema,
  catalogPlanSchema,
  catalogPlanSearchSchema,
  catalogProductResultSchema,
  catalogProductSchema,
  catalogProductSearchSchema,
  catalogProductVehicleResultSchema,
  catalogProductVehicleSchema,
  catalogProductVehicleSearchSchema,
  catalogServiceRecordResultSchema,
  catalogServiceRecordSchema,
  catalogServiceRecordSearchSchema,
  catalogStatusSchema,
  catalogVehicleDetailSchema,
  catalogVehicleFilterSchema,
  catalogVehicleFiltersSchema,
  catalogVehicleProductSchema,
  catalogVehicleResultSchema,
  catalogVehicleSchema,
  catalogVehicleSearchSchema,
  catalogVolunteerResultSchema,
  catalogVolunteerSchema,
  catalogVolunteerSearchSchema,
  categorySchema,
  changeSubjectSchema,
  channelsSchema,
  chargeMemberSchema,
  chargeSettlementSchema,
  chargeStatusSchema,
  chargeSummarySchema,
  chargesContract,
  chargesWebhookContract,
  claimActivitySchema,
  claimAssigneeOptionSchema,
  claimCafLinkSchema,
  claimCafTrackingSchema,
  claimClosureKindSchema,
  claimClosureOriginSchema,
  claimClosureSchema,
  claimClosureSourceSchema,
  claimClosuresContract,
  claimClosuresPageSchema,
  claimClosuresQuerySchema,
  claimCommentSchema,
  claimCompensationsContract,
  claimContactAttemptSchema,
  claimDeadlineFilterSchema,
  claimDepartmentSchema,
  claimDocumentSchema,
  claimExpensesContract,
  claimFieldsSchema,
  claimFileSchema,
  claimManagementContract,
  claimMessageSchema,
  claimMoveResultSchema,
  claimPassageSchema,
  claimPeripheralsContract,
  claimPeripheralsPortalContract,
  claimReopenResultSchema,
  claimRotationSchema,
  claimSchema,
  claimSummarySchema,
  claimTypeSchema,
  claimsContract,
  claimsTeamMapContract,
  claimsTeamMapNodeSchema,
  classificationSchema,
  classificationStateSchema,
  commissionBreakdownSchema,
  commissionExportSchema,
  commissionLadderNameSchema,
  commissionLadderSchema,
  commissionPaymentSchema,
  commissionSchema,
  companyCategorySchema,
  companyDocumentTypeSchema,
  companySchema,
  companyTypeSchema,
  companyWriteSchema,
  compensationCreditorOptionSchema,
  compensationDebtStatusSchema,
  compensationDebtWriteSchema,
  compensationDocumentFileSchema,
  compensationDocumentValidationSchema,
  compensationSummarySchema,
  configurableKeySchema,
  consultantCodeCorrectionSchema,
  consultantPhotoUploadSchema,
  consultantPositionSchema,
  consultantProfileSchema,
  consultantSchema,
  consultantSummarySchema,
  contaAzulLaunchSchema,
  contaAzulSyncResultSchema,
  contaazulAccountAccessSchema,
  contaazulAccountSchema,
  contaazulContract,
  contactBlockSchema,
  contextSchema,
  contract,
  contractAssistance,
  contractClaims,
  contractCore,
  contractErp,
  contractFinance,
  contractRetention,
  contractRevenues,
  coreEntitySchema,
  coreProjectionSchema,
  coreVehicleMatchSchema,
  correctSaleExpenseSchema,
  cpfSchema,
  createBeneficiarySchema,
  createClaimSchema,
  createContaazulAccountSchema,
  createHolidaySchema,
  createLabelSchema,
  createOpasuiteTemplateSchema,
  createPeripheralSchema,
  createPortalPeripheralSchema,
  createRevenueSchema,
  createSaleExpenseSchema,
  dailyUsageSchema,
  defaultErrors,
  departmentAssigneeSchema,
  departmentLabelsSchema,
  departmentPersonSchema,
  departmentRefSchema,
  departmentSchema,
  deskAutoImportSchema,
  deskBandSchema,
  deskBoardCardSchema,
  deskBoletoSchema,
  deskCanSchema,
  deskCardDetailSchema,
  deskCardSchema,
  deskCardsPageSchema,
  deskColumnSchema,
  deskCreateCardSchema,
  deskCreditSchema,
  deskDashboardPersonSchema,
  deskDashboardQuerySchema,
  deskDashboardSchema,
  deskDistributeResultSchema,
  deskDistributeSchema,
  deskEditCardSchema,
  deskEventSchema,
  deskFlowSchema,
  deskGroupSchema,
  deskImportDetailSchema,
  deskImportItemSchema,
  deskImportSummarySchema,
  deskLabelOptionSchema,
  deskLabelSchema,
  deskLabelWriteSchema,
  deskMoveRowSchema,
  deskMovesPageSchema,
  deskMovesQuerySchema,
  deskOperatorSchema,
  deskOperatorsConfigSchema,
  deskPaidOutcomeSchema,
  deskPaySchema,
  deskPersonSchema,
  deskPlateLookupSchema,
  deskProofUploadSchema,
  deskProofUrlSchema,
  deskReconcileResultSchema,
  deskSaveOperatorsSchema,
  deskSaveSettingsSchema,
  deskSchema,
  deskSettingsSchema,
  deskSummarySchema,
  discardReasonSchema,
  discardSchema,
  dispatchDetailSchema,
  dispatchEventSchema,
  dispatchFileSchema,
  dispatchFileUploadSchema,
  dispatchListRowSchema,
  dispatchProgressDetailSchema,
  dispatchProgressSchema,
  dispatchProgressSummarySchema,
  dispatchRowSchema,
  dispatchSettlementSchema,
  dispatchStatusSchema,
  dispatchStepSchema,
  dispatchWriteSchema,
  documentSchema,
  draftSchema,
  draftWriteSchema,
  editableDepartmentSchema,
  editableGroupSchema,
  editableUsefulLinkSchema,
  entitySyncSchema,
  entryTypeSchema,
  erpCandidateSchema,
  erpContract,
  erpCoreChangedNoticeSchema,
  erpFailedJobSchema,
  erpHealthSchema,
  erpPipelineNoticeSchema,
  erpPortalContract,
  erpQueueStateSchema,
  erpWorkerSchema,
  eventSchema,
  expenseDocumentKindSchema,
  expenseFileSchema,
  expenseFinanceActBodySchema,
  expenseFinanceActSchema,
  expenseHistoryEntrySchema,
  expensePendingNoticeSchema,
  expenseRequestStageSchema,
  expenseSupervisorActBodySchema,
  expenseSupervisorActSchema,
  financeBoardStageSchema,
  financeDashboardContract,
  financeDashboardRowSchema,
  financeDashboardSchema,
  financeExpenseActivitySchema,
  financeExpenseBeneficiarySchema,
  financeExpenseClaimSchema,
  financeExpenseCommentSchema,
  financeExpenseDetailSchema,
  financeExpenseFileSchema,
  financeExpenseOriginSchema,
  financeExpenseSchema,
  financeExpensesContract,
  financeExpensesQuerySchema,
  financeTeamMapContract,
  financeTeamMapNodeSchema,
  geocodedAddressSchema,
  groupSchema,
  historyEntitySchema,
  historyRefreshDoneSchema,
  holdClaimSchema,
  holidaySchema,
  holidaysContract,
  instantSchema,
  integrationCredentialSchema,
  invoiceWindowSchema,
  labelColorSchema,
  labelSchema,
  ladderRangeSchema,
  ladderStepSchema,
  lastIntakeSchema,
  latLngSchema,
  launchExpenseSchema,
  legSchema,
  localTimeSchema,
  losingBoletoTypesInputSchema,
  losingBoletoTypesSchema,
  managementClaimSchema,
  managementConsultantsSchema,
  managementContract,
  managementDeadlinesSchema,
  managementDispatchSchema,
  managementDurationsSchema,
  managementExitsSchema,
  managementFlowQuerySchema,
  managementFlowSchema,
  managementGroupPageSchema,
  managementGroupQuerySchema,
  managementLineSchema,
  managementMapQuerySchema,
  managementMapSchema,
  managementOverviewSchema,
  managementPanelAwardsSchema,
  managementPanelSchema,
  managementPeripheralsSchema,
  managementRepairMoneySchema,
  managementStageSchema,
  managementStatesSchema,
  managementTypesSchema,
  managementWeeksSchema,
  mapsContract,
  memberBeneficiarySchema,
  memberBoletoDetailResponseSchema,
  memberBoletoDetailSchema,
  memberBoletoSchema,
  memberBoletosSchema,
  memberContactSchema,
  memberDetailSchema,
  memberFiltersSchema,
  memberLinkViewSchema,
  memberPortfolioSchema,
  memberPreferencesSchema,
  memberRegistrySchema,
  memberSchema,
  memberSessionSchema,
  memberStatus,
  memberStatusSchema,
  membersContract,
  messageSchema,
  mirroredEntitySchema,
  moneySchema,
  moveClaimSchema,
  movePeripheralSchema,
  myPaymentDetailSchema,
  myPaymentSchema,
  myPaymentStagesSchema,
  myPaymentsSchema,
  mySaleExpenseStagesSchema,
  newClassificationSchema,
  notificationSchema,
  numbersSchema,
  occurrenceAddressWriteSchema,
  opasuiteChannelSchema,
  opasuiteChannelTemplateSchema,
  opasuiteContract,
  opasuiteOverviewSchema,
  opasuitePurposeSchema,
  opasuiteSendResultSchema,
  opasuiteSettingsSchema,
  opasuiteTemplateSchema,
  openingCategorySchema,
  operatorSchema,
  ownAwardGrantsSchema,
  paginated,
  paginationQuery,
  paymentAccountSchema,
  paymentActionSchema,
  paymentCategorySchema,
  paymentContaAzulSchema,
  paymentDetailSchema,
  paymentFileSchema,
  paymentLinkMethodSchema,
  paymentLinkStateSchema,
  paymentLinkViewSchema,
  paymentLinksContract,
  paymentMethodSchema,
  paymentNoteSchema,
  paymentOptionSchema,
  paymentRequestSchema,
  paymentSchema,
  paymentSourceSchema,
  paymentStageEventSchema,
  paymentStageSchema,
  paymentStagesSchema,
  paymentStatusSchema,
  paymentTransitionSchema,
  paymentUpdatedNoticeSchema,
  paymentsPageSchema,
  pendingSettlementsSchema,
  periodBonusSchema,
  periodCommissionsSchema,
  periodSchema,
  periodTotalsSchema,
  peripheralCoverageSchema,
  peripheralFormSchema,
  peripheralItemSchema,
  peripheralItemWriteSchema,
  peripheralMoveResultSchema,
  peripheralQuoteDecisionSchema,
  peripheralQuoteSchema,
  peripheralQuoteWriteSchema,
  peripheralSchema,
  peripheralSummarySchema,
  peripheralTotalsSchema,
  peripheralVehicleCoverageQuerySchema,
  peripheralVehicleCoverageSchema,
  peripheralVehicleSchema,
  permissionExceptionSchema,
  personSchema,
  phaseDeadlineSchema,
  phoneSchema,
  pipelinePauseSchema,
  pipelinePreviewSchema,
  pipelineSchema,
  pipelineStageSchema,
  placeSchema,
  plateClassificationSchema,
  plateTallySchema,
  platformContract,
  portalContextSchema,
  portalContract,
  portalMemberAddressSchema,
  portalMessageSchema,
  portalPeripheralItemSchema,
  portalPeripheralStartSchema,
  portalPeripheralSummarySchema,
  portalPeripheralUploadSchema,
  portalPeripheralVehicleSchema,
  portalRepairPlaceSchema,
  portalTicketSchema,
  portalTicketSummarySchema,
  portalTicketViewSchema,
  portfolioVehicleDetailResponseSchema,
  portfolioVehicleDetailSchema,
  portfolioVehicleSchema,
  preferencesSchema,
  previewAttachmentSchema,
  prioritySchema,
  progressSettingsSchema,
  proofUrlSchema,
  providerLinkViewSchema,
  providerPaymentModeSchema,
  providerSchema,
  quoteRequestSchema,
  quoteSchema,
  quotedProviderSchema,
  rankingConsultantSalesSchema,
  rankingCountingSchema,
  rankingOrderSchema,
  rankingSaleSchema,
  rankingSchema,
  rankingSegmentSchema,
  reasonNotToSettleSchema,
  receivedSchema,
  recordEntitySchema,
  refreshCountsSchema,
  registerClaimContactSchema,
  reopenClaimClosureSchema,
  repairQuoteItemSchema,
  repairQuoteSchema,
  repairQuotesSchema,
  repairSummarySchema,
  reportAssigneeSchema,
  reportCategoryOpenSchema,
  reportRowSchema,
  reportSliceSchema,
  resumeClaimSchema,
  retentionAttachmentSchema,
  retentionCardCanSchema,
  retentionCardDetailSchema,
  retentionCardSchema,
  retentionChannelSchema,
  retentionContract,
  retentionFinalPhaseSchema,
  retentionHistoryEntrySchema,
  retentionLabelSchema,
  retentionLabelWriteSchema,
  retentionMemberRequestSchema,
  retentionOperatorSchema,
  retentionPhaseSchema,
  retentionPortalContract,
  retentionPortalFileSchema,
  retentionPortalOpenSchema,
  retentionPortalOpenedSchema,
  retentionPortalStartSchema,
  retentionPortalUploadSchema,
  retentionPortalVehicleSchema,
  retentionReportRowSchema,
  retentionSummarySchema,
  revenueBeneficiarySchema,
  revenueContaAzulSchema,
  revenueDetailSchema,
  revenueFileSchema,
  revenueOriginSchema,
  revenuePayerSchema,
  revenueSourceTypeSchema,
  revenueStatusSchema,
  revenueSummarySchema,
  revenuesContract,
  saleActionSchema,
  saleExpenseActionSchema,
  saleExpenseDetailSchema,
  saleExpenseFileSchema,
  saleExpenseLaunchAccessSchema,
  saleExpenseSchema,
  saleExpenseStageSchema,
  saleExpenseStagesSchema,
  saleExpenseStatusSchema,
  saleExpenseTransitionSchema,
  saleExpenseTypeSchema,
  saleExpensesContract,
  saleExpensesPageSchema,
  saleMemberLabelSchema,
  saleSchema,
  saleVehicleLabelSchema,
  salesComparisonMonthSchema,
  salesComparisonSchema,
  salesComparisonSessionSchema,
  salesContract,
  salesImportSchema,
  salesNumbersSchema,
  salesPanelConsultantSchema,
  salesPanelFiguresSchema,
  salesPanelSchema,
  salesTargetSchema,
  salesTeamSchema,
  saveAnalysisFormSchema,
  savePaymentStageSchema,
  savePeripheralFormSchema,
  saveRepairQuoteSchema,
  serviceRecordFiltersSchema,
  sessionSchema,
  setAnalysisAssigneeSchema,
  setClaimRotationsSchema,
  settleRevenueSchema,
  settlementResultSchema,
  settlementSchema,
  statementAfterClosureSchema,
  statementBalancePartSchema,
  statementBalanceSchema,
  statementClosingSchema,
  statementDaySchema,
  statementEntryContextSchema,
  statementEntrySchema,
  statementEntryServiceSchema,
  statementKindSchema,
  statementPayoutDaySchema,
  statementPayoutSchema,
  statementSchema,
  statusDomainSchema,
  supervisedExpenseSchema,
  supervisorExpenseStageSchema,
  supervisorLinkSchema,
  sweepResultSchema,
  syncDoneSchema,
  syncPendingSchema,
  syncResultSchema,
  targetAchievementSchema,
  targetScopeSchema,
  teamBoardSchema,
  teamDiagramNodeSchema,
  teamMemberPerformanceSchema,
  teamOverviewFiguresSchema,
  teamOverviewRowSchema,
  teamPerformanceSchema,
  teamPerformanceTotalsSchema,
  teamSubtreeSchema,
  teamsOverviewSchema,
  technologyFeeLotSchema,
  technologyFeeTierSchema,
  tenantConfigurationSchema,
  tenantMemberSchema,
  tenantSchema,
  tenantUserSchema,
  testResultSchema,
  themeSchema,
  thirdPartySchema,
  thirdPartyVehicleSchema,
  thirdPartyWithVehiclesSchema,
  thirdPartyWriteSchema,
  ticketDashboardSchema,
  ticketDepartmentSchema,
  ticketFilterQuery,
  ticketOpenedNoticeSchema,
  ticketPreviewSchema,
  ticketRepliedNoticeSchema,
  ticketReportExportSchema,
  ticketReportSchema,
  ticketSchema,
  ticketStatusSchema,
  ticketSummarySchema,
  ticketVehicleSchema,
  ticketsContract,
  ticketsPortalContract,
  towDestinationTypeSchema,
  trackingLinkSchema,
  trackingSchema,
  unclassifiedStatusSchema,
  undoClaimMoveSchema,
  updateBeneficiarySchema,
  updateClaimSchema,
  updateContaazulAccountSchema,
  updateLabelSchema,
  updateOpasuiteTemplateSchema,
  updatePaymentAccountSchema,
  usageRowSchema,
  usageRuleLineSchema,
  usageRulesSchema,
  usageRulesWriteSchema,
  usageWindowSchema,
  usefulLinkIconSchema,
  usefulLinkSchema,
  usefulLinksContract,
  userSchema,
  userSummarySchema,
  validInviteSchema,
  validUserInviteSchema,
  vehicleSearchResultSchema,
  vehicleSituationSchema,
  vehicleStatusSchema,
  vehicleUsageSchema,
  vocabularyEntrySchema,
  volunteerLinkSchema,
  walletActionsSchema,
  walletCalculatorMonthSchema,
  walletCalculatorSchema,
  walletForecastSchema,
  walletHistoryMonthSchema,
  walletInsightsSchema,
  walletMixSliceSchema,
  walletOpenBoletoSchema,
  walletOpenBoletosSchema,
  walletPaymentRateSchema,
  walletPendingContractSchema,
  walletRowSchema,
  walletVehicleBoletoSchema,
  walletVehicleBoletosSchema,
  walletsSchema,
  webhookAcceptanceSchema,
  webhookRefusalSchema,
  windowStateSchema,
  writeClaimCommentSchema
};
//# sourceMappingURL=index.js.map